import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { unitNorm, unitKey, unitLegacy, unitOk, unitVsVin, isVinPlaceholder,
         transposeCandidates, keyToDisplay, unitFromVin, vinClean, unitYear } from "./unitValid";
// #FLEET_CPSTRIP_V1 — the DUC validator standing rule ("CP stripped") lives in ducValid.js and every
// other module's front door already applies it. Fleet never did — it compared raw uppercased DUCs
// against the Job List, so a job typed/imported as `CP-E4523906` never matched the Job List's
// `E4523906` and read as "Not on the Job List" even though it's the same real job.
import { stripCP } from "./ducValid";
import { useLiveKeys } from "./appShared";   // #FLEET_MASTER_LIVE_V1
import { renameUnitEverywhere, unitRenameFallbackKeys } from "./unitRename";   // #FLEET_UNIT_RENAME_V1
import { serviceState, svcSummary, svcShort, svcNormDos, svcTodayMDY, svcLogEntry, svcParseDate } from "./fleetService";   // #FLEET_SVC_STATE_V1
import FleetMaint from "./FleetMaint";
import FleetLibrary from "./FleetLibrary";   // #FLEET_LIBRARY_V1 — vendor / OEM docs on file
import { OEM_KEY, OEM_BRANDS, oemLabel, isApiSrc, oemIndex, oemForUnit, oemWeekHours, oemWeekAt, oemBrandStatus } from "./oemFeed";   // #FLEET_OEM_HOURS_V1 — Ditch Witch / Vermeer / Kobelco factory feeds
import FleetMapsMini from "./FleetMapsMini";
import { useCalName } from "./staffName";   // #STAFF_CALNAME_V1
import { GeoIdleTrucksPanel } from "./GeotabWeekly";   // #GEO_WEEKLY_LISTS_V1 — trucks over 25 idle events / week
import FuelCardsPanel, { useFuelCardIndex } from "./FuelCards";   // #FLEET_FUEL_CARDS_V1 — Voyager cards + Impac fills by unit   // #YARD_MAP_V1 — the one integrated map on Jerry's Yard dash
import FleetTelemList, { useTelemIssues, telemFlagIndex, telemFlagFor, TelemFlag } from "./FleetTelemAlert";   // #FLEET_TELEM_ALERT_V1 / #FLEET_TELEM_HOT_V1 — Camera / GPS not reporting: dash tile + its own screen
import FleetGpsMap from "./FleetGpsMap";   // #FLEET_GPS_MAP_V1 — the Geotab map on the Fleet dash (trucks, VACs, trailers), separate from the iron map
import { LOC_KEY, LOC_DAYS_KEY, locIndex, locDayIndex, locCell, locForVin, locPlace } from "./fleetLocations";   // #FLEET_SHOPRPT_V2 — locForVin/locPlace for the report's Location column   // #FLEET_LOCATIONS_V1 — where each unit is (api/fleet-locations-sync.js)
import FleetRigBoard from "./FleetRigBoard";   // #FLEET_RIG_BOARD_V1 — the rig cards on Bob's dash (hours, idle, rods, service, fault codes)
import FleetOwnerTiles from "./FleetOwnerTiles";   // #FLEET_OWNER_TILES_SHARED_V1   // #FLEET_MAINT_V1
import { requestNewSafetyReport } from "./SafetyModule";   // #FLEET_DASH_SAFETYNAT_V1 — same quick-file handoff the PM board uses
import { FleetTravelApprovals } from "./FleetTravel";
import { openPrintView } from "./printView";   // #EQUIP_LIST_EXPORT_V1   // #FLEET_TRAVEL_V1
import ShopDash, { PartsReqQueue, ShopStaffStrip, REQ_KEY, findEmp, SHOP_VIEWS, FleetSafetyInbox, BobWoQueue, TeamTimeReview, TeamScorecard } from "./ShopDash";
import { UnitWorkHistory } from "./WorkOrders";   // #FLEET_WO_TABLES_V1 — WOs + POs + service + shop + DVIRs stacked per unit   // #FLEET_SHOPDASH_V1 — the shop mechanic's own dash
// #FLEET_FUELWEEKLY_V2 — shared week-numbering convention (Mon-Sun, ISO week) already used by the
// Rig Schedule elsewhere in App.js, reused here so the Fuel log's WK numbers line up with the rest
// of the app instead of inventing a second definition of "what week is it."
import { isoWeek, isoWeekYear } from "./weekOf";   // mondayOf not imported — this file already has its own (identical) local mondayOf() below
import FUEL_IMPAC_SEED from "./fuelImpacHistorySeed.json";   // #FLEET_FUELWEEKLY_V3 — Bill's real 34-week Impac history (2025-12-29 through 2026-08-22), one-time self-seed into fleet-fuel-log
import FUEL_YARD_SEED from "./fuelYardHistorySeed.json";
import CONSIGN_MSC_SEED from "./consignMscSeed.json";   // #YARD_CONSIGN_SPLIT_V1 — MSC 10/01/24–10/01/26 sales history (acct 379754), 316 repeat-order items, one-time self-seed
import CONSIGN_VERMEER_SEED from "./consignVermeerSeed.json";   // #YARD_CONSIGN_V1 — Vermeer 09/25/26 consignment report, 243 lines / $153,854.57, one-time self-seed   // #FLEET_FUELWEEKLY_V4 — Bill's real 458-delivery Avenue tank-log history (2022-01-04 through 2026-08-27, pre-2022 rows dropped: no cost data), one-time self-seed into fleet-fuel-log alongside FUEL_IMPAC_SEED
import CHEM_SPEND_HIST from "./chemSpendHistorySeed.json";   // #YARD_CHEMTREND_V1 — chemical spend by month, 2021 → Aug 2026 (history totals); later months come from the live log
import { ChemicalsYardDashboard, YardConsignment, YardTimeClock, YardChemSpend, ShopLaborWeekly, YardFuelWeekly, FuelWeeklyPage, YardStaffCalendar, YardInventoryList, YardCelebrations } from "./YardModule";   // #SPLIT_YARD_V1
import { iso, num, addDays, cents, isoWeekNum, fmtDate, RivetCorners, money2, AutoCompleteInput, fmtMD, money, CONSIGN_KEY, _csgnNum, _csgnHas, consignTotal, glow, mondayOf, pad2 } from "./fleetShared";   // #SPLIT_YARD_V1
import { PmKitDesk, PmKitBanner, svcDueSnapshot, PM_SVC_DUE_KEY, postPendingServiced } from "./PmKits";   // #PM_KIT_FLOW_V1

// ══════════════════════════════════════════════════════════════════════════════
//  FLEET  (#FLEET_V1)  — owner: Bob Macy, Fleet Manager
//
//  Replaces the MASTER EQUIP 2026.xlsx workbook. Built in the order Bill asked for:
//  DAILY first (the thing that goes into use), then the MASTER it reads from, then the
//  data-quality flags, then import.
//
//  THE ONE IDEA THAT MAKES THIS WORTH BUILDING
//  On the spreadsheet, Daily's COST column is a VLOOKUP against the master — and it has
//  been silently drifting. Checked against the real data: the master's `ducDay` matched
//  Daily's COST on 294 of 294 units, exactly. So cost is not a field anyone types or that
//  any lookup can miss — it is READ FROM THE MASTER at render time and never stored on the
//  daily record. Change a rate on the master and every day recomputes. It cannot drift.
//
//  Daily therefore holds only what a human actually decides: which DUC the unit worked,
//  and who had it. Everything else is derived.
//
//  STORAGE
//    fleet-equipment          packed { v, cols, active[[]], retired[[]] } — columnar, ~40% smaller
//    fleet-daily-<mondayISO>  { "YYYY-MM-DD": { UNITKEY: { duc, assigned } } }  one key per week
//    fleet-rental-rpo         RETIRED #FLEET_RENTAL_V2 — Bill, 2026-08-21: "should this be pipped
//                              into Equipment master and then follow through to Daily to be assigned
//                              to a JOB." Rental/RPO units are now real Equipment Master `active`
//                              records carrying rentalType/rentalVendor/monthlyRate/weeklyRate/
//                              rentalStart/rentalDue — that is what makes them assignable on Daily
//                              and costed everywhere else for free (see `rate()` below). The 7 items
//                              Bill had on the old list were migrated in-place (2026-08-21); this key
//                              is left emptied rather than deleted so nothing 404s if something old
//                              still reads it.
// ══════════════════════════════════════════════════════════════════════════════

// #FLEET_THEME_V1 (2026-08-28) — Bill: "add a Fleet Theme, maybe Steel, rivets and/or industrial."
// Light touch: this one shared palette drives every screen in FleetModule (Daily, Yard, Digital
// Board, Rental, Master, etc.), so re-tinting it here re-tints the whole module with no structural
// changes. Reuses the same steel/welding-shop-tough tones Bill already approved for Bob's Fleet
// Manager dashboard (#FLEET_BOBDASH_THEME_V1's local SHOP object) instead of inventing a new look —
// gunmetal backgrounds, a riveted-steel border gray, and the same industrial-orange accent.
const F = {
  bg:'#101317', card:'#1C2024', cardHi:'#262B30', line:'#3A3F45', text:'#EDEEF0', muted:'#98A2AA',
  accent:'#FF7A1A', gold:'#E7B73B', green:'#34D399', red:'#F87171', amber:'#FBBF24',
  orange:'#FB923C', violet:'#A78BFA',
};

// Overhead buckets from Bill's Daily sheet — 64 idle, 27 overhead, 12 shop on the sample day.
// These are valid DUC values that will never be on the Job List, so the gate must know them.
const OVERHEAD = [
  ['A-IDLE',  'Idle',        F.muted],
  ['A-SHOP',  'In repairs',  F.orange],
  ['A-OH',    'Overhead',    F.violet],
];
const OVERHEAD_SET = new Set(OVERHEAD.map(o => o[0]));

// #FLEET_YARD_BOARD_V2 (2026-08-27) — the digital board's fixed "yard status" lanes, matching the
// section names on the physical magnetic board (Bill's photos): Ennis Yard, Shop, Out of Service,
// Dealership, For Sale. Supervisor lanes are dynamic (added/removed by whoever runs the board).
const BOARD_GROUPS = [
  ['ennis','Ennis Yard'], ['shop','Shop'], ['oos','Out of Service'],
  ['dealer','Dealership'], ['forsale','For Sale'],
];
// #FLEET_BOARD_RIGTYPE_V1 (2026-08-27) — Bill, after seeing the first (flat, 4-item) Placard pass:
// "Make Tiles for the Type of RIG currently 26 RIGS named by their type [11 real types, exact counts
// given]... header Boxes for RIGs and place Supervisors under... equipment that is already assigned
// from the Daily Inventory... to indicate JOB as all equipment already assigned to each Supervisor."
// Replaces the flat Placard lanes entirely — verified directly against the live Equipment Master
// (Supabase `fleet-equipment`, 303 rows): every one of the 26 Drill-R1/R2/R3/R4 rigs' free-text
// description already contains its size, just inconsistently formatted ("40AT" vs "AT40", "D100x140"
// vs "100x140", trailing model noise like "DRS3"/"III"/"S3") — so type is classified automatically
// from `desc`, no new field needed. Classified counts matched Bill's own tally almost exactly (off by
// one on 40AT — one duplicate unit row in the Master). Drill-AU (Auger/Bore machines — Ronald Auger's
// own supervisor lane already covers these) is deliberately excluded, not one of the "26 RIGS."
const RIG_TYPE_ORDER = ['10x15','23x30','24x40','32AT','40x55','40AT','100x140','AT120','220x300','220x500','330x500'];
const RIG_TYPE_PATTERNS = [
  ['220x300', /220\s*x\s*300/i], ['220x500', /220\s*x\s*500/i], ['330x500', /330\s*x\s*500/i],
  ['100x140', /100\s*x\s*140/i], ['40x55', /40\s*x\s*55/i], ['24x40', /24\s*x\s*40/i],
  ['23x30', /23\s*x\s*30/i], ['10x15', /10\s*x\s*15/i], ['AT120', /AT\s*120/i],
  ['40AT', /(?:\b40\s*AT\b|\bAT\s*40\b)/i], ['32AT', /(?:\b32\s*AT\b|\bAT\s*32\b)/i],
];
// Genuinely unrecognized text returns null rather than a guess — surfaced as its own "Unclassified"
// section below (see the Rig Fleet by Type render) so a bad/missing description is visible, not silently dropped.
const classifyRigType = (desc) => {
  const s = String(desc || '');
  for (const [label, re] of RIG_TYPE_PATTERNS) { if (re.test(s)) return label; }
  return null;
};
// Deterministic color per equipment category — same category always lands on the same color without
// needing a stored mapping, so "every rig is the same color" holds even for a category no one's
// assigned a color to yet.
const BOARD_PALETTE = ['#38BDF8','#FB923C','#34D399','#A78BFA','#F87171','#FBBF24','#22D3EE','#F472B6','#84CC16','#818CF8'];
const catColor = (cat) => {
  const s = String(cat||'').trim().toUpperCase();
  if (!s) return '#94A3B8';
  let h = 0; for (let i=0;i<s.length;i++) h = (h*31 + s.charCodeAt(i)) >>> 0;
  return BOARD_PALETTE[h % BOARD_PALETTE.length];
};
// #FLEET_BOARD_SUPJOB_V6/V7 — shared name-normalizer so a person's name matches the same way
// whether it's being compared against Daily's "assigned" field for the Rig Fleet by Type panel or
// for a Digital Board Name Tag: trim, uppercase, collapse internal whitespace.
const normPersonName = (s) => String(s||'').trim().toUpperCase().replace(/\s+/g,' ');

// #FLEET_NOSAT_V1 — Bill: drop Saturday from the work week so Daily/Weekly line up with the
// Consolidated Weekly Report going to job costing. Historical Saturday data already saved under
// past weeks is untouched — it just no longer has a column to show/edit going forward.
const DAYS = ['Mon','Tue','Wed','Thu','Fri'];


// #FLEET_WKNUM_V1 (2026-10-16) — Bill: a weekly reading is taken Sunday night / Monday morning and CLOSES the Mon–Sun week before it.
// Label it the way RU, payroll and every other report do: WK number + start–end (week ends Sunday).
const hrsWeekOf = (mondayISO) => { const k = new Date(mondayISO + 'T12:00:00'); const s = addDays(k, -7), e = addDays(k, -1);
  return { wk: isoWeekNum(s), s, e, label: `WK ${isoWeekNum(s)} · ${fmtMD(s)} – ${fmtMD(e)}`, read: fmtMD(k) }; };


// #FLEET_SHOP3P_V1 — a typed In Shop "Date In" is stored as YYYY-MM-DD (what a date field hands back); anything else = not set.
const shopIso = (v) => { const t = String(v==null?'':v).trim(); return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : ''; };
// #FLEET_WEEKLYINV_NUMFMT_V1 (2026-09-17) — Bill: "Hours, miles to have a ',' in the number." A
// native <input type="number"> can never show thousands separators (a browser limitation), so the
// Weekly Inventory's live hours/miles entry fields are plain text inputs that display comma-grouped
// but store/parse the same clean numeric string as before — fmtNumInput for display, numInputStrip
// to clean commas back out before it's saved. Everywhere the reading is only ever displayed (Weekly
// Δ, MTD/QTD/YTD, Service Due) already used .toLocaleString(), so those were already comma-grouped.
const fmtNumInput = (v) => { if (v === '' || v == null) return ''; const n = Number(v); return isNaN(n) ? String(v) : n.toLocaleString(undefined, { maximumFractionDigits: 2 }); };
const numInputStrip = (v) => String(v).replace(/,/g, '');

// #FLEET_ASSETMGMT_V1 (2026-09-11) — impac Fleet (Cathy Tan) telematics bundle rates, per the cost
// email Bill forwarded: GPS-only, GPS+Camera, and the unpowered Asset Tracker (trailers, etc.) are
// three separate monthly bundles, not a per-unit menu — a unit is on one bundle or the other, never
// both. Reference only (shown as a hint in the entry form); the actual $/mo typed per unit is what
// drives Total Value and every cost rollup, since Bill may renegotiate/adjust over time.
const TELEM_RATES = { gps: 18.80, cam: 43.00, asset: 11.31 };
// #FLEET_SHOPDASH_V1 — the three Master fields the Camera / GPS working list writes. Kept as a list so the
// mechanic's writer and persistMaster's keep-their-change merge (#FLEET_TELEM_MERGE_V1) agree on them.
const TELEM_FIELDS = ['cameraStatus','gpsStatus','gpsSerial'];
// #FLEET_SHOPDASH_SCOPE_V1 (2026-10-06) — Bill: "Their view should be limited to what their Dashboard holds."
// The Fleet screens a shop-staff login may open (list lives in ShopDash.js next to the dash's own shortcuts).
const SHOP_VIEW_SET = new Set([...SHOP_VIEWS.map(v => v[0]), 'telem']);   // #STAFFDASH_V2 — Cam / GPS down list opens from the staff dash (it bounced back to My Dash before)
const telemSnap = (list) => { const m = {}; (list||[]).forEach(r => { const o = {}; TELEM_FIELDS.forEach(f => { o[f] = String(r[f]==null?'':r[f]); }); m[unitKey(r.unit)] = o; }); return m; };
// Bill: "these will each have a 3 year life and start date will be AUG 1 as a placeholder" — no real
// per-unit install date exists yet, so every unit's telematics device is assumed to have started
// 08/01/26 until someone records the actual date on that unit.
const TELEM_DEFAULT_START = '08/01/26';
const TELEM_LIFE_MONTHS = 36;

// packed columnar {cols, rows[[]]} → array of objects
const expand = (cols, rows) => (rows||[]).map(r => {
  const o = {};
  (cols||[]).forEach((c,i) => { const v = r[i]; if (v !== '' && v != null) o[c] = v; });
  return o;
});
const packRows = (cols, list) => (list||[]).map(o => cols.map(c => (o[c] == null ? '' : o[c])));

// #ASSETLEDGER_V1 (2026-09-16) - Fleet Arrival / Decommission checklists, straight off Ashlie's real
// PURCHASED/SOLD workbook columns. Kept as their own small `arrival{}`/`decommission{}` objects on
// the Master row, deliberately separate from gpsStatus/cameraStatus and the Toll Tag dataset - this
// answers "did it go on/come off at intake," a different lifecycle question than "is it working right
// now," same reasoning Fleet already used to keep Toll Tags/Inspections as their own datasets instead
// of merging into the Master.
const ARR_ITEMS = [['bed','Bed'],['runningBoards','Running Boards'],['dragUpTank','Drag Up Tank'],['lights','Lights'],
  ['floorCovers','Floor Covers'],['gps','GPS'],['fabOther','Fab / Other'],['fireExt','Fire Ext'],['decals','Decals'],
  ['tollTag','Toll Tag'],['fuelMan','Fuel Man']];
const DEC_ITEMS = [['lights','Lights removed'],['dragUpTank','Drag Up Tank removed'],['gps','GPS removed'],
  ['fireExt','Fire Ext'],['decals','Decals'],['tollTag','Toll Tag'],['fuelMan','Fuel Man']];
const arrivalComplete = (a) => ARR_ITEMS.every(([k]) => a && a[k]);
const decomComplete = (d) => DEC_ITEMS.every(([k]) => d && d[k]);


const STEEL_BG = 'repeating-linear-gradient(135deg, #12151A 0px, #14181D 2px, #101317 5px, #12151A 7px)';


// ══════════════════════════════════════════════════════════════════════════════
//  #FLEET_WEEKLYHRS_V1 (2026-09-17) — Weekly Inventory / HRS
//
//  Replaces Bob's `WK ## - INV M.D.YY.xlsx` workbook (INV./HRS/COST/OWN tabs) — Bill: "we have sent
//  this and reviewed a few times and not clear where this landed... need to build this template for
//  BOB to use EXCLUSIVELY moving forward and get these off these many list." COST and OWN are
//  explicitly out of scope here — Bill: "not an item or a concern (please ignore)... Cost is the
//  PIVOT from his PO System... an entire few sessions on its own."
//
//  ONE READING A WEEK, CHAINED — Bill: "provide a start & end with one weekly pull." Bob keys in one
//  current hour-meter/odometer reading per unit per week (Sunday night or Monday morning). That
//  reading is the week's END; the PRIOR week's stored reading is automatically this week's START —
//  no separate Start field to fill in, and it can never drift out of sync with itself. Weekly usage
//  is just end-start. Nothing here assumes Geotab yet — `reading` is hand-typed today for every
//  bucket, including Trucks/18-Wheeler that are "supposed to" pipe in — because the Fleet phase of the
//  Geotab sync (Trip.Odometer/EngineHours) isn't wired yet (see geotab-api-integration-notes.md,
//  Safety/Phase 1 only so far). Same storage shape either way, so a future sync job can write
//  `reading` here directly with zero UI change. RIGs stay manual permanently either way — Bill:
//  "RIGs still manual as Vermeer/Ditchwitch not wired."
//
//  ASSIGNED, STATIC — Bill: "Assigned to (From latest Daily inventory from prior Friday and static
//  until the next week)." Pulled once from that week's Friday `fleet-daily-<mon>` record (Daily
//  Inventory already tracks who has each unit) and frozen — never silently re-derives mid-week, only
//  a manual override moves it.
//
//  UNITS COME FROM THE MASTER — every active `fleet-equipment` row gets a row here, full stop.
//  Bill: "Equipment master is the TRUTH to fleet." No separate unit list to drift out of sync.
//
//  STORAGE
//    fleet-weekly-hrs   { v, weeks: { <mondayISO>: { <unitKey>: { reading, assignedOverride?,
//                          enteredAt, enteredBy } } } }  — one blob, all weeks (mirrors
//                          fleet-shop-history / fleet-fuel-log; small per-unit-per-week payload, not
//                          the heavier per-day Daily allocation that earns fleet-daily its own
//                          per-week keys).
//    fleet-hrs-buckets  { v, map: { <normalized cat> : <bucket key> } } — overrides on top of
//                          HRS_CAT_DEFAULT below, same "ship the tool, not a guess" pattern as the
//                          Master's own Categories panel.
//    fleet-service      { v, units: { <unitKey> : { serviced?, dos?, note?, updatedAt, updatedBy } } }
//                          — Serviced / DOS / Note per unit. Persistent facts, NOT week-scoped like
//                          fleet-weekly-hrs (a serviced reading stays true until the next service, it
//                          doesn't reset every Monday) — its own key on purpose. Service Due is never
//                          stored: it's always serviced + SERVICE_INTERVAL_DEFAULT[bucket], computed
//                          live in hrsRows, same "read the Master live" principle as bucketOf() below.
//                          Seeded once (2026-09-17) from WK 38 - INV 9.20.26.xlsx — 194 of 208 rows
//                          matched a live Master unit; the rest were retired units or numbers that
//                          don't resolve to a current Master row (see geotab-api-integration-notes.md
//                          / the module-completion doc for the skipped list) — Bob can still type
//                          those in by hand, same as any other unit.
// ══════════════════════════════════════════════════════════════════════════════

// #FLEET_WEEKLYINV_BUCKETORDER_V2 (2026-09-17) — Bill: "For Ease of Transition we need to place them
// in this order. RIGS, 18 WHeelers, Trucks, Cleaners, VACs, Forklifts, Excavators, Mixing system,
// trailers and maybe Other." Same order now used both on-screen and on the printed HRS sheet — one
// place, so it can never drift between the two again. Forklifts and Trailers are now their own
// buckets (previously both fell into "Other" — Forklifts still had its own service-interval rule by
// category regardless, Trailers were deliberately unscheduled either way — see SERVICE_CAT_RULE /
// SERVICE_INTERVAL_DEFAULT below, unchanged by this reorder).
const HRS_BUCKETS = [
  ['rigs','RIGS','hrs'], ['18wheeler','18 Wheeler','mi'], ['trucks','Trucks','mi'],
  ['cleaners','Cleaners','hrs'], ['vacs','VACs','hrs'], ['forklifts','Forklifts','hrs'],
  ['excavators','Excavators','hrs'], ['mixing','Mixing Systems','hrs'],
  ['trailers','Trailers',''], ['other','Other',''],
];
const HRS_BUCKET_ORDER  = HRS_BUCKETS.map(([k]) => k);
const HRS_BUCKET_LABEL  = Object.fromEntries(HRS_BUCKETS.map(([k,l]) => [k,l]));
const HRS_BUCKET_METRIC = Object.fromEntries(HRS_BUCKETS.map(([k,,m]) => [k,m]));
const hrsMetricLabel = (m) => m === 'hrs' ? 'Hours' : m === 'mi' ? 'Miles' : 'Hrs / Mi';

const normCat = (c) => String(c || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Default category → bucket map, built off the live Equipment Master category distribution (296
// active units, 60 distinct spellings) the day this was wired. Anything not listed here falls into
// 'other' rather than guessing at a curation call — see the Buckets panel to move one.
// #FLEET_MASTER_CATCOLLAPSE_V1 (2026-09-17) — Bill collapsed the Master's category field to match
// the WK 38 INV sheet's own section names (his call, via AskUserQuestion: "keep it clean and
// consistent" rather than one-by-one edits) for every bucket EXCEPT rigs — Drill-R1/R2/R3/R4/AU
// was already clean and is the R-class Revenue view sorts by, so those were left alone on purpose
// (see RIG_R_CLASS_ORDER below). The old finer-grained spellings above still work — a retired unit,
// or anything Bob adds by hand before this collapse reaches it, still resolves correctly — the new
// flat names are just added alongside them, not a replacement.
const HRS_CAT_DEFAULT = {
  'drill-r1':'rigs', 'drill-r2':'rigs', 'drill-r3':'rigs', 'drill-r4':'rigs', 'drill-au':'rigs',
  'reclaimer':'cleaners', 'trailer-cleaner':'cleaners', 'cleaners':'cleaners',
  'mixing system':'mixing', 'trailer-mixing':'mixing', 'mixing systems':'mixing',
  'truck-vac':'vacs', 'trailer-vac':'vacs', 'hydro vac':'vacs', 'vacs':'vacs',
  'exc':'excavators', 'exc-hammer':'excavators', 'excavators':'excavators',
  'truck 1t':'trucks', 'truck-water':'trucks', 'truck-3/4t':'trucks', 'truck-1/2t':'trucks',
  'truck-haul':'trucks', 'dm truck':'trucks', 'truck':'trucks', 'truck-tractor':'trucks',
  'truck-mechanics':'trucks', 'truck-dump':'trucks', 'trucks':'trucks',
  '18wheeler':'18wheeler', '18 wheelers':'18wheeler',
  // #FLEET_WEEKLYINV_BUCKETORDER_V2 — Forklifts and Trailers now get their own bucket instead of
  // falling into "Other" (Forklifts kept its service rule the whole time via SERVICE_CAT_RULE below,
  // matched by category regardless of bucket — this just gives it its own labeled section too).
  'forklift':'forklifts', 'forklifts':'forklifts',
  'trailer':'trailers', 'trailers':'trailers',
};
// #FLEET_MASTER_CATCOLLAPSE_V1 — Bill: rigs need to "be sorted by TYPE/YEAR... Type RIG Meaning
// Size smallest to largest so it mirrors Revenue view weekly into the R Categories." R1 is the
// smallest bore, R4 the largest, Auger last (a different drilling method, not a size step) — this
// is the exact order Drill-R1..R4/AU already sort in and the order the rigs bucket now sorts by
// (see the rigs-bucket sort in hrsRows / printHrsSummary), so it stays in one place rather than
// re-typed at each sort site.
const RIG_R_CLASS_ORDER = ['drill-r1','drill-r2','drill-r3','drill-r4','drill-au'];
const rigRClassRank = (cat) => { const i = RIG_R_CLASS_ORDER.indexOf(normCat(cat)); return i === -1 ? RIG_R_CLASS_ORDER.length : i; };
// #FLEET_RIG_MODEL_ORDER_V1 (2026-10-01) — Bill: "Each RIG Group should be listed in order of CLASS then Year.
// Should mirror the Description line up in attached snip" (his rig sheet): 10x15 · 23x30 · 24x40 · 32AT ·
// 40AT · 40x55 · AT120 · 100x140 · 220x300 · 220x500 · 330x500 · Auger. R-class still leads (it already
// agrees with that order); inside an R-class the rig MODEL now groups together, then unit # (year-first).
// A model not on this list sorts after the known ones in its R-class, grouped by description — add it here.
const RIG_MODEL_ORDER = [
  /(^|\D)10\s*x\s*15(\D|$)/i, /(^|\D)23\s*x\s*30(\D|$)/i, /(^|\D)24\s*x\s*40(\D|$)/i,
  /(^|\D)32\s*AT|AT\s*32(\D|$)/i, /(^|\D)40\s*AT|AT\s*40(\D|$)/i, /(^|\D)40\s*x\s*55(\D|$)/i,
  /(^|\D)120\s*AT|AT\s*120(\D|$)/i, /(^|\D)100\s*x\s*140(\D|$)/i, /(^|\D)220\s*x\s*300(\D|$)/i,
  /(^|\D)220\s*x\s*500(\D|$)/i, /(^|\D)330\s*x\s*500(\D|$)/i, /auger|barco|bore/i,
];
const rigModelRank = (desc) => { const d = String(desc || ''); const i = RIG_MODEL_ORDER.findIndex(re => re.test(d)); return i === -1 ? RIG_MODEL_ORDER.length : i; };
const rigSort = (a, b) => {   // a, b = master records (cat, desc); callers tie-break on unit #
  const ma = rigModelRank(a.desc), mb = rigModelRank(b.desc);
  return (rigRClassRank(a.cat) - rigRClassRank(b.cat)) || (ma - mb)
    || (ma === RIG_MODEL_ORDER.length ? String(a.desc || '').trim().localeCompare(String(b.desc || '').trim(), undefined, { sensitivity:'base' }) : 0);
};
// #FLEET_HRSPRINT_VIEWER_V1 (2026-10-01) — Bill: the 11x17 "automatically opens into a Printer spoo[ler] and
// don't think it has time to load all data… unable to close on mobile device unless you just close out app."
//  • No auto-print: the sheet opens as a preview with its own Print / Close bar, once ALL its data is in.
//  • Desktop keeps the separate window and the exact same markup, so the locked 11x17 layout is untouched.
//  • Phones / tablets (and a blocked pop-up) get an in-app viewer instead of a new window — a blank window
//    inside the home-screen app has no browser chrome, so there was nothing to close it with.
const hrsViewerIsMobile = () => { try { const ua = navigator.userAgent || ''; return /Android|iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) || window.navigator.standalone === true; } catch { return false; } };
const hrsViewerBar = (doc, title) => {
  const bar = doc.createElement('div');
  const t = doc.createElement('span'); t.textContent = title; t.style.cssText = 'flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
  const note = doc.createElement('span'); note.style.cssText = 'font-weight:400;color:#FFB020';
  const mk = (txt, primary) => { const b = doc.createElement('button'); b.type = 'button'; b.textContent = txt;
    b.style.cssText = 'font:700 13px Arial;padding:7px 14px;border-radius:6px;cursor:pointer;color:#fff;border:1px solid ' + (primary ? '#C8651B;background:#C8651B' : '#777;background:#2a2a2a'); return b; };
  bar.appendChild(t); bar.appendChild(note);
  return { bar, note, mk };
};
const hrsViewerWindow = (title, printLabel) => {   // printLabel: #FLEET_SHOPREPORT_V1 (letter-size sheets reuse this viewer)
  const w = window.open('', '_blank'); if (!w) return null;
  w.document.write('<p style="font:16px Arial;padding:24px">Loading fleet data…</p>');
  return { show(html, warn) {
    w.document.open(); w.document.write(html); w.document.close();
    const d = w.document, { bar, note, mk } = hrsViewerBar(d, title);
    bar.className = 'vbar'; note.textContent = warn || '';
    const pb = mk(printLabel || '🖨 Print · 11x17', true), cb = mk('✕ Close');
    pb.onclick = () => { w.focus(); w.print(); }; cb.onclick = () => w.close();
    bar.appendChild(pb); bar.appendChild(cb);
    d.body.insertBefore(bar, d.body.firstChild);
    w.focus();
  } };
};
const hrsViewerOverlay = (title, sheetW) => {   // sheetW: #FLEET_SHOPREPORT_V1
  const old = document.getElementById('fleet-hrs-viewer'); if (old) old.remove();
  const ov = document.createElement('div'); ov.id = 'fleet-hrs-viewer';
  ov.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:2147483000;background:#fff;display:flex;flex-direction:column';
  const { bar, note, mk } = hrsViewerBar(document, title);
  bar.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;background:#111;color:#fff;font:700 14px Arial;padding:8px 10px;padding-top:calc(8px + env(safe-area-inset-top, 0px))';
  const zb = mk('🔍 Zoom'), pb = mk('🖨 Print', true), cb = mk('✕ Close');
  zb.style.display = 'none'; pb.style.display = 'none';
  bar.appendChild(zb); bar.appendChild(pb); bar.appendChild(cb);
  const pane = document.createElement('div');
  pane.style.cssText = 'flex:1;min-height:0;overflow:auto;-webkit-overflow-scrolling:touch;background:#fff';
  pane.innerHTML = '<p style="font:16px Arial;padding:24px;color:#111">Loading fleet data…</p>';
  ov.appendChild(bar); ov.appendChild(pane); document.body.appendChild(ov);
  const prevOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden';
  let layout = () => {};
  const onResize = () => layout();
  window.addEventListener('resize', onResize);
  cb.onclick = () => { window.removeEventListener('resize', onResize); document.body.style.overflow = prevOverflow; ov.remove(); };
  return { show(html, warn) {
    const W = sheetW || 1594;   // the 17in sheet minus its 5mm margins, in CSS px — the width the layout is fitted to
    pane.innerHTML = '';
    const holder = document.createElement('div'); holder.style.cssText = 'position:relative;overflow:hidden';
    const fr = document.createElement('iframe'); fr.title = title;
    fr.style.cssText = 'position:absolute;left:0;top:0;border:0;background:#fff;width:' + W + 'px;height:1100px;transform-origin:0 0';
    holder.appendChild(fr); pane.appendChild(holder);
    const d = fr.contentDocument; d.open(); d.write(html); d.close();
    const steps = ['fit', 0.6, 1]; let zi = 0, H = 1100;   // Zoom cycles: fit the screen width → 60% → full size
    layout = () => {
      const sc = steps[zi] === 'fit' ? Math.min(1, pane.clientWidth / W) : steps[zi];
      fr.style.height = H + 'px'; fr.style.transform = 'scale(' + sc + ')';
      holder.style.width = Math.ceil(W * sc) + 'px'; holder.style.height = Math.ceil(H * sc) + 'px';
    };
    const measure = () => { try { H = Math.max(600, fr.contentDocument.documentElement.scrollHeight, fr.contentDocument.body.scrollHeight); } catch {} layout(); };
    measure(); setTimeout(measure, 150); setTimeout(measure, 800);
    zb.style.display = pane.clientWidth < W ? '' : 'none'; pb.style.display = '';
    zb.onclick = () => { zi = (zi + 1) % steps.length; layout(); };
    pb.onclick = () => { try { fr.contentWindow.focus(); fr.contentWindow.print(); } catch {} };
    note.textContent = warn || '';
  } };
};
// #FLEET_HRS_MERGE_WRITE_V1 (2026-10-01) — cell-level edits to the fleet-weekly-hrs blob.
// patch = { mondayISO: { unitKey: cell | null } } (null deletes the cell). onlyIfBlank = auto-fills never
// replace a reading that is already stored.
const hrsApplyCells = (data, patch, onlyIfBlank) => {
  const weeks = { ...((data && data.weeks) || {}) };
  Object.keys(patch || {}).forEach(mon => {
    const wk = { ...(weeks[mon] || {}) };
    Object.keys(patch[mon] || {}).forEach(uk => {
      const cell = patch[mon][uk];
      if (cell == null) { delete wk[uk]; return; }
      if (onlyIfBlank && wk[uk] && wk[uk].reading != null) return;
      wk[uk] = cell;
    });
    weeks[mon] = wk;
  });
  return { v:1, weeks };
};
// A live Geotab stamp ('geotab-sync') is only true for the week it was taken in. On Sep 21 the old auto-fill
// stamped that day's reading into every week from 8/24 to 11/16 as weeks were paged through — seven of them in
// the future — which froze truck miles (Weekly change = 0, and the future weeks would never have refreshed).
// Returns a delete-patch for every such stamp, or null when the data is clean. Past weeks then refill from
// the Geotab history pull, the current week from the live feed, and future weeks stay empty until they arrive.
const hrsStaleStamps = (data) => {
  const patch = {}; let n = 0;
  Object.keys((data && data.weeks) || {}).forEach(mon => {
    const start = new Date(mon + 'T00:00:00').getTime(); if (isNaN(start)) return;
    const end = start + 7 * 86400000;
    const wk = data.weeks[mon] || {};
    Object.keys(wk).forEach(uk => {
      const c = wk[uk]; if (!c || c.enteredBy !== 'geotab-sync' || !c.enteredAt) return;
      const t = new Date(c.enteredAt).getTime(); if (isNaN(t) || (t >= start && t < end)) return;
      (patch[mon] = patch[mon] || {})[uk] = null; n++;
    });
  });
  return n ? patch : null;
};
const bucketOf = (cat, overrides) => {
  const n = normCat(cat);
  if (overrides && overrides[n]) return overrides[n];
  return HRS_CAT_DEFAULT[n] || 'other';
};

// #FLEET_SERVICE_DUE_V1 — Bill, 2026-09-17: "RIGS Last + 250 Hours for next planned (Then needs to
// be flagged). 18 Wheelers Last plus 15,000 miles, Trucks Last + 6,000 Miles, Cleaners/Forklifts/
// Excavators/Mixing systems Last Plus 250 Hours. Trailers will not be listed for service schedule.
// BUt all need a Note section." Checked against the full INV file before wiring, not just his list —
// every interval below matched the real sheet history (Trucks 64/66 rows at +6,000mi exactly; the 2
// outliers were +12,000mi, i.e. one skipped cycle on those two units, not a different rule; VACs
// wasn't in Bill's list but the sheet shows the same +250hrs pattern as Cleaners/Excavators/Mixing).
// 'other' has no rule by default — that's what correctly excludes Trailers with zero special-casing.
// Forklifts are the one exception: they don't have their own bucket (still grouped under "Other" on
// the grid — 3 units, see the Buckets panel) but Bill wants them scheduled, so they're matched by
// CATEGORY here instead of bucket. Any unit with no rule still gets the free Note field.
const SERVICE_INTERVAL_DEFAULT = {
  rigs: 250, cleaners: 250, mixing: 250, vacs: 250, excavators: 250, forklifts: 250,
  trucks: 6000, '18wheeler': 15000,
};
const SERVICE_CAT_RULE = {
  // #FLEET_MASTER_CATCOLLAPSE_V1 — Master's Forklift units now read "FORKLIFTS" (the INV sheet's
  // section name) instead of "FORKLIFT" — keeping the old singular here too so this doesn't quietly
  // stop working if a unit still carries the pre-collapse spelling.
  forklift: { interval: 250, metric: 'hrs' },
  forklifts: { interval: 250, metric: 'hrs' },
};
// #FLEET_SVC_STATE_V1 (2026-10-05) — Bob's note: "big trucks 15,000 miles or 6 months. Which ever comes
// 1st." A date rule on top of the reading rule, counted from DOS. Only 18-Wheelers carry one today;
// add a bucket here to give it one.
const SERVICE_MONTHS_DEFAULT = { '18wheeler': 6 };
const serviceRuleFor = (bucket, cat) => {
  const cr = SERVICE_CAT_RULE[normCat(cat)];
  if (cr) return cr;
  const interval = SERVICE_INTERVAL_DEFAULT[bucket];
  if (!interval) return null;
  return { interval, metric: HRS_BUCKET_METRIC[bucket] || 'hrs', months: SERVICE_MONTHS_DEFAULT[bucket] || null };
};

const quarterOf = (d) => Math.floor(d.getMonth() / 3) + 1;
const monthKey  = (d) => `${d.getFullYear()}-${pad2(d.getMonth()+1)}`;
const quarterKey = (d) => `${d.getFullYear()}-Q${quarterOf(d)}`;
const yearKey   = (d) => String(d.getFullYear());

// #FLEET_RIG_BOARD_V1 — false = the rig board shows only for Bill (preview). true = everyone who sees Bob's dash sees it.
// 2026-10-08 3:30 PM — switched ON. Bill showed it to Hanna and Mark T from their desktops and they could not see it:
// "I hope the Owners & DMs can view this new & improved Fleet Dashboard."
const RIG_BOARD_PUBLIC = true;
export default function FleetModule({ sg, ss, jobs, userRole, canEdit, upn, pendingUnit, onPendingConsumed, pendingView, onPendingViewConsumed, initialView, restrictToYard, supaUrl, supaKey, getToken, poPanel }) {
  const editable = canEdit !== false;
  // #FLEET_MOBILE_HDR_V1 (2026-10-16) — Bill: phone banner wasted real estate. phone = under 900px (same break as the rest of DOPE).
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  useEffect(() => { const f = () => setPhone(window.innerWidth < 900); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  // Bill: "There will be a MASTER Sheet that will house the MAIN file and only few can edit."
  // The tab matrix hands equipment:'edit' to PM / DM / MGMT / Admin / Foreman as well, which is
  // right for Daily but far too wide for the master. Narrow it here rather than in RBAC.
  // #FLEET_MASTER_MGMT_V1 (2026-10-06) — Bill: Fleet is the MASTER dash and Bob (Fleet Manager) owns Fleet & Yard,
  // but his login sits under MGMT in the Access Matrix, not 'Fleet' — so this gate locked him out of his own master.
  // MGMT now carries the master. The 'Fleet' role means Bob's shop staff (6-7 people) and no longer does: staff get
  // what the matrix gives them, never the master (retire, merge, rentals, time-clock approval, deletes).
  const masterEdit = editable && ['Owner','MGMT'].includes(String(userRole||''));
  // #FLEET_YARD_SPLIT_V1 (2026-08-27) — Bill: split Jerry (Yard) off Bob's (Fleet) full role. Jerry
  // still needs to add/edit HIS OWN Inspections and Toll Tags (he's the source of that data — see
  // fleet-master-rules-and-drift.md), so those two sections gate on yardOpsEdit, not masterEdit.
  const isYard = String(userRole||'') === 'Yard';
  const yardOpsEdit = editable && (isYard || masterEdit);
  // #FLEET_SHOP_HOT_V1 (2026-10-23) — Bill: "hot button for fire like we did on DM Plan for fleet for units that are
  // down that Bob can add to add focus on fleet team. Only he can add such or remove." Bob sets AND clears; Bill /
  // Aaron can only clear (backup so a flag never sits stuck while Bob is out). Everyone else just sees the 🔥.
  const _hotMe = String(upn||'').trim().toLowerCase();
  const hotSetter = !!editable && _hotMe.startsWith('bob.macy@');
  const hotClearer = hotSetter || (!!editable && (_hotMe.startsWith('bill.payne@') || _hotMe.startsWith('aaron.graff@')));
  // #FLEET_YARD_PORTAL_V1 (2026-09-01) — Bill: entering via the separate "Yard" nav tab should stay
  // a focused Jerry-style portal even when Owner/Fleet use that same entry point to check on him —
  // "these should live on the Fleet Dash... he needs to stay focused on this yard one only." Distinct
  // from `isYard` (a real role check, still governs edit rights below) — this only narrows which
  // tabs are offered to look at.
  const yardScoped = isYard || restrictToYard;
  // #FLEET_ADDGATE_V1 (2026-08-27) — was Owner-only ("Ashlie's call"), narrower than masterEdit on purpose.
  // #FLEET_ADDGATE_V2 (2026-10-06) — Bill, looking at Bob's view of the Master: "Unable to add units as we should
  // allow him access to own this file... We can maybe track changes made." Adding now follows the master itself
  // (Owner + MGMT); the control that replaces the old gate is the change log (#FLEET_MASTER_LOG_V1) and the
  // 'fleet-added' origin stamp, which puts Bob's adds on Ashlie's "Needs paperwork" list on the Owner Dash.
  const canAddMaster = masterEdit;

  // #ASSETLEDGER_V1 (2026-09-16) - Bill: "The Yard will also need a place to add only never remove."
  // A unit can physically arrive and go into service before Ashlie's paperwork exists; Jerry needs to
  // flag it into the Master himself (he knows the VIN/unit-numbering sequence). Deliberately NARROWER
  // than canAddMaster: creates a minimal placeholder row only (unit/desc/vin/cat), no retire/remove
  // capability anywhere - Retire stays masterEdit-only (Owner/Fleet), unchanged. Scoped to Yard only
  // for now (not Fleet/Bob) since that's specifically who Bill named; widen later if he wants Bob to
  // have it too.
  const canFlagUnit = editable && isYard;

  const [cols, setCols]       = useState([]);
  const [active, setActive]   = useState(null);   // null = still loading
  const [retired, setRetired] = useState([]);
  const [view, setView]       = useState(() => ((initialView === 'fleetdash' && String(userRole||'') === 'Fleet') ? 'mydash' : (initialView || (String(userRole||'')==='Yard' ? 'home' : 'daily'))));  // #FLEET_YARD_SPLIT_V1 — Jerry lands on the Yard hub; #FLEET_BOBDASH_V1 — Bob's dashboard tab forces 'fleetdash'
  // #FLEET_SHOPDASH_V1 (2026-10-06) — Bill: "Fleet Mechanic Kenny is logged into system as himself (Intro says
  // Bob?) and his view or Dashboard should be personalized". The 'Fleet' role is Bob's shop staff: they land on
  // their OWN dash ('mydash', ShopDash.js), never on Bob's Fleet Manager rollup — which every Fleet login was
  // being dropped onto, name and all, because initialView forced it and the view itself had no gate.
  // Bob / Owner (masterEdit) reach any one person's dash from the Shop Staff strip on Bob's dash.
  const isShopStaff = String(userRole||'') === 'Fleet' && !restrictToYard;
  const [staffView, setStaffView] = useState('');   // Bob / Owner: whose dash is open ('' = first on the list)
  // #FLEET_TIMECARD_EACH_V1 (2026-10-06) — Bill, opening Time Card from Kenny's dash as Owner: it "takes him to Jerrys
  // Yard Dash and Jerrys Time card. These should be independent time cards to roll up into Bobs master eventually."
  // A manager's Time Card button on someone's dash now carries that person through to the Time Clock.
  const [tcFor, setTcFor] = useState('');
  useEffect(() => { if (view !== 'timeclock' && tcFor) setTcFor(''); }, [view, tcFor]);
  const [reqKick, setReqKick] = useState(0);        // bumped when a parts requisition is filled → Consignment re-reads
  const [openReqN, setOpenReqN] = useState(0);      // open parts requisitions, for the Yard hub banner
  const telemBaseRef = useRef({});                  // #FLEET_TELEM_MERGE_V1 — Camera/GPS values as THIS tab last read or wrote them
  useEffect(() => {
    // #FLEET_SHOPDASH_SCOPE_V1 — any Fleet screen outside the shop list (Bob's dash, Equipment Master, Rental,
    // Fuel, Import …) sends a shop login back to its own dash, however it got there.
    if (isShopStaff && !SHOP_VIEW_SET.has(view)) setView('mydash');
    else if (view === 'mydash' && !isShopStaff && !masterEdit) setView('fleetdash');
  }, [view, isShopStaff, masterEdit]);
  useEffect(() => {
    if (view !== 'home' && view !== 'fleetdash') return;   // #FLEET_YARD_PORTAL_V1 — the YARD button on Bob's dash counts them too
    let alive = true;
    (async () => {
      try {
        const v = await sg(REQ_KEY);
        const n = (v && Array.isArray(v.reqs) ? v.reqs : []).filter(x => (x.lines||[]).some(l => l.status === 'requested' || l.status === 'ordered')).length;
        if (alive) setOpenReqN(n);
      } catch {}
    })();
    return () => { alive = false; };
  }, [view, reqKick, sg]);
  const [q, setQ]             = useState('');   // Daily filter
  // #FLEET_BUCKETDRILL_V1 — click a bucket tile to see exactly which units are in it. The strip used
  // to state "15 units" in Shop with no way to ask which fifteen, which is the only question that
  // number provokes. null = no bucket filter.
  const [bucket, setBucket]   = useState(null);   // null | 'job' | 'A-IDLE' | 'A-SHOP' | 'A-OH'
  const [dlogs, setDlogs]     = useState(null);  // #FLEET_DAILYTRUTH_V1 — daily-logs, keyed by DUC
  // #FLEET_QSPLIT_V1 — Daily and Master had ONE search box between them. Jumping from a Daily row
  // into the Master left the unit number in the shared filter, so coming back showed a one-row Daily
  // and no obvious way out. Two views, two filters.
  const [qM, setQM]           = useState('');   // Master search
  const [catPanel, setCatPanel] = useState(false);
  const [mlogOpen, setMlogOpen] = useState(false);   // #FLEET_MASTER_LOG_V1 — Equipment Master change log panel
  const [mlogQ, setMlogQ] = useState('');
  const [masterLog, setMasterLog] = useState(null);  // null = not loaded yet
  const [catF, setCatF]       = useState('all');
  // #FLEET_CAMGPS_V1 (2026-09-08) — Bill: "Need a Camera / GPS List to work from and the ability to
  // void some units that will not have either and display a % completed in both categories and
  // combined." Manual checklist (Geotab's own camera/dashcam add-on + GPS device, tracked by hand
  // until the Geotab sync itself is fully resolved — see geotab-api-integration-notes.md). Two
  // independent tri-state fields per unit: '' (pending), 'Installed', 'Void' (legitimately never
  // getting one — same idea as "Mark info complete" for VIN/Plate, just per-category here since a
  // unit can be void on one and still pending/installed on the other).
  const [camGpsPendingOnly, setCamGpsPendingOnly] = useState(false);
  const [edit, setEdit]       = useState(null);   // equipment record being edited
  const [busy, setBusy]       = useState('');
  // #FLEET_EQUIPFILES_V1 — Attachments panel state: which tag a pending upload gets, per-path signed
  // URL cache for the "Open" links, and a status line for upload/delete feedback.
  const [equipDocKind, setEquipDocKind] = useState('Invoice');
  const [equipDocMsg, setEquipDocMsg] = useState('');
  const [equipDocUrls, setEquipDocUrls] = useState({});
  // #FLEET_RENTAL_V2 — Bill, 2026-08-21: rentals now live ON the Equipment Master (see the STORAGE
  // note above) as `active` rows carrying `rentalType`. `rentalEdit` holds the small rental-specific
  // form (Vendor, Monthly Charge, Start, Due) — on save it writes into `active` via persistMaster,
  // same as the ordinary unit editor's `edit` state does.
  const [rentalEdit, setRentalEdit]   = useState(null);
  const [flagEdit, setFlagEdit]       = useState(null);   // #ASSETLEDGER_V1 - Jerry's add-only "flag new unit" mini-form
  const [msort, setMsort] = useState({ key:'', dir:'asc' });   // #FLEET_MSORT_V1 — Master table sort
  // #FLEET_INSHOP_V1 — Bill, 2026-08-21: "a report to combined Rental & units down to be developed
  // for weekly review inside of Fleet" — modeled directly on Bob's actual Friday-meeting spreadsheet
  // (Unit / Description / Diagnosis / Date In / Days Down / Est Date Out / Mechanic), replacing it.
  // Unit/Description/Date In/Days Down are already known (fleet-summary's shop digest, same source as
  // the Owner Dash "In Shop" tile) — only Diagnosis/Mechanic/Est-Out are new, so that's the only new
  // storage: a small notes-per-unit map, edited in place, same pattern as Rental/RPO's own editor.
  const [shopSum, setShopSum] = useState(null);       // fleet-summary digest (shop[] + at)
  const [shopNotes, setShopNotes] = useState({});     // { unitKey: { diagnosis, mechanic, estOut, updatedAt } }
  const [shopEdit, setShopEdit] = useState(null);      // { unit, diagnosis, mechanic, estOut, _key }
  // #FLEET_SHOPREPORT_V1 (2026-10-02) — the two typed pieces at the bottom of Bob's weekly "Fleet
  // Maintenance Report" sheet: PO spending and the discussion items. Stored as fleet-shop-report.
  const [shopReport, setShopReport] = useState({ po:'', notes:'' });   // { po, notes, by, updatedAt }
  const [shopReportDraft, setShopReportDraft] = useState(null);         // open editor, or null
  // #FLEET_SHOPHISTORY_V1 (2026-09-02) — Bill: completed shop visits should fall off In Shop (they
  // already do — this tab is a live read of current location) but the diagnosis/days-down/mechanic/
  // cost record was being lost the moment they did, so a chronically-down unit was invisible. Records
  // are archived here on a manual "Mark Complete" click (not automatic on location change) so whoever
  // closes it out can lock in final notes first. `shopDismissed` hides a completed stay from In Shop
  // even if the location digest hasn't caught up yet — keyed by unit+dateIn so a FRESH shop stay for
  // the same unit still shows.
  const [shopHistory, setShopHistory] = useState([]);       // [{ unit, desc, dateIn, dateOut, days, diagnosis, mechanic, estOut, rate, cost, completedAt, completedBy }]
  // #FLEET_RIG_FILE_V1 (2026-10-09) — the rig file's "Log a service in Maintenance" button hands Maintenance the unit to
  // open on; Maintenance takes it once and hands it back (onFocusUsed) so a later visit starts clean.
  const [maintFocus, setMaintFocus] = useState(null);
  // #FLEET_RATE_BUDGET_V1 (2026-10-21) — Bill: fill INS / TERP / TAX / MTX on the Equipment Master and total them on top.
  //   INS  = Auto Liability premium ($1,053,036/yr) spread evenly over every owned unit, 260 DUC Days a year (5-day week)
  //   TERP = 1.5% of the DUC Day rate · TAX = 8.25% of the rate, only on units with no license plate (heavy iron)
  //   MTX  = working maintenance budget ($/yr, editable — started at $2.6M = 2026 repair-PO pace) spread by rate
  // Rentals / RPO are not ours and get none. Settings live in fleet-rate-config (Owner / MGMT edit).
  const RATE_CFG_DEF = { autoPremium:1053036, mtxBudget:2600000, days:260, terpPct:1.5, taxPct:8.25 };
  const [rateCfg, setRateCfg] = useState(RATE_CFG_DEF);
  useEffect(() => { (async () => { try { const c = await sg('fleet-rate-config'); if (c && typeof c === 'object' && !Array.isArray(c)) setRateCfg(p => ({ ...p, ...c })); } catch {} })(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveRateCfg = async (patch) => { const n = { ...rateCfg, ...patch }; setRateCfg(n); try { await ss('fleet-rate-config', { ...n, by: upn || '', at: new Date().toISOString() }); } catch {} };
  // #FLEET_HRS_REPAIR_PO_V1 (2026-09-24) — Bill: "Not pulling in Repair PO expenses for UNIT cost." Repair
  // on the HRS print = this year's PO actuals per unit (invoices summed, credits netted) across every PO
  // user's live log + Bob's legacy history for the current year. Preloaded so the print stays one click.
  // #FLEET_HRS_REPAIR_LIFE_V1 (2026-10-01) — Bill: "330x500 Cost nears $353,857 in repairs but opened report shows
  // only $155,418." Both numbers were right: the print showed 2026 only, his WK INV sheet shows every year of
  // Bob's PO log. Repair on the print is now ALL years (2021 → today), which matches the sheet to the penny.
  // Loaded when Print is clicked instead of on every Fleet mount (it is ~4MB of history): prior years are
  // fetched once per session; this year's history + every live PO log are re-read on EACH print, so a PO
  // entered a minute ago is on the sheet. (Supersedes the mount-time preload of #FLEET_HRS_REPAIR_WAIT_V1.)
  const poRepairOld = useRef(null);   // promise of the prior-year history lists (read-mostly)
  const loadPoRepair = async () => {
    const yr = new Date().getFullYear(); const out = {};
    const add = (r) => {
      if (!r || r.ducType !== 'fleet-unit') return;
      const inv = Array.isArray(r.invoices) ? r.invoices.filter(x => x && x.amount != null && !isNaN(Number(x.amount))) : [];
      const a = inv.length ? inv.reduce((t,x) => t + Number(x.amount), 0) : (r.actual != null ? Number(r.actual) : 0);
      const k = unitKey(r.ducOrUnit || r.unit); if (!k || !a) return;
      out[k] = (out[k] || 0) + a;
    };
    if (!poRepairOld.current) {
      const oldKeys = []; for (let y = 2021; y < yr; y++) oldKeys.push(`po-hist-bm-${y}`);
      poRepairOld.current = Promise.all(oldKeys.map(k => sg(k).catch(() => null)));
    }
    const liveKeys = ['bm','cj','ct','pg','rm','gw','jk','kc','cv','nv','ap'].map(c => `po-log-${c}`).concat([`po-hist-bm-${yr}`, 'po-hist-bm-undated']);
    const [old, live] = await Promise.all([poRepairOld.current, Promise.all(liveKeys.map(k => sg(k).catch(() => null)))]);
    const missed = old.some(x => !Array.isArray(x));   // a prior year failed to load — don't keep that for the session
    if (missed) poRepairOld.current = null;
    old.concat(live).forEach(x => { if (Array.isArray(x)) x.forEach(add); });
    return { map: out, missed };
  };
  const [shopDismissed, setShopDismissed] = useState({});   // { 'unitKey__dateIn': isoTimestamp }
  const [shSort, setShSort] = useState({ key:'', dir:'asc' });   // Shop History table sort
  const [shQ, setShQ] = useState('');                         // Shop History search
  // #PM_EQFLAG_V1 — Bill: "PM to review and deselect items... trigger an alert back to fleet for given
  // day to reassign or correct allocation... lets try report 1st." PMs raise these from their Dash
  // (PMDashboard.js #PM_EQFLAG_V1); this is Fleet's side of that same report.
  const [pmFlags, setPmFlags] = useState([]);
  const [yardOpen, setYardOpen] = useState(false);   // #FLEET_YARD_PORTAL_V1 — Bob's YARD button drawer
  const [tcReviewOpen, setTcReviewOpen] = useState(false);   // #FLEET_TC_REVIEW_V1 — consolidated week of time cards

  // ── daily state ────────────────────────────────────────────────────────────
  const [wkStart, setWkStart] = useState(() => iso(mondayOf(new Date())));
  const [dayIx, setDayIx]     = useState(() => Math.min(4, (new Date().getDay()+6)%7));   // #FLEET_NOSAT_V1 — 5-day week now (was 6)
  const [week, setWeek]       = useState({});     // { dateISO: { unitKey: {duc, assigned} } }
  const [dirty, setDirty]     = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const [saveErr, setSaveErr] = useState('');   // #FLEET_AUTOSAVE_V1
  const [sort, setSort]       = useState({ key:'', dir:'asc' });   // '' = sheet order (category, unit)
  // #FLEET_EDITPIN_V1 — Bill: "Odd sort also. When editing a row, needs to stay in place until edit
  // is complete regardless of sort." Typing a DUC or SUP live changes that row's Status/Assigned
  // rank, which used to re-sort the table on every keystroke — the row you're typing into jumps out
  // from under the cursor. editingKey pins the row's ON-SCREEN position (not its data — that still
  // updates live) to where it sat when focus landed, until focus leaves the row entirely.
  const [editingKey, setEditingKey] = useState(null);
  const editOrderRef = useRef({ key: null, order: null });
  const saveTimer = useRef(null);          // #FLEET_AUTOSAVE_V1
  const firstLoadRef = useRef(true);
  // #FLEET_SAVEMERGE_V1 (2026-09-04) — day -> Set(unitKey) of rows this tab deleted down to nothing
  // (setCell's delete-when-empty branch) since the last load/save. persistWeek re-applies these on
  // top of a freshly re-fetched server copy so an explicit local delete always wins, even though the
  // merge itself otherwise favors "whatever's on the server that this tab never touched."
  const pendingRemovalsRef = useRef({});

  // ── weekly inventory / HRS state (#FLEET_WEEKLYHRS_V1) ───────────────────────
  const [hrsWkStart, setHrsWkStart]   = useState(() => iso(mondayOf(new Date())));   // its own week pointer — independent of Daily's wkStart
  const [hrsData, setHrsData]         = useState(null);   // { v, weeks:{ mondayISO:{ unitKey:{reading,assignedOverride,enteredAt,enteredBy} } } } — null = still loading
  const [hrsBucketMap, setHrsBucketMap] = useState({});   // normalized cat -> bucket override
  const [hrsFridayAssigned, setHrsFridayAssigned] = useState({});   // unitKey -> assigned, from the prior Friday's Daily Inventory
  const hrsLoadFlags = useRef({ buckets:false, fri:false });   // #FLEET_HRSPRINT_VIEWER_V1 — the print waits on these two as well
  const [hrsEdits, setHrsEdits]       = useState({});     // unsaved edits this session: unitKey -> { reading?, assignedOverride? }
  const [hrsQ, setHrsQ]               = useState('');
  const [hrsBucketFilter, setHrsBucketFilter] = useState(null);
  const [hrsBusy, setHrsBusy]         = useState('');
  const [hrsSavedAt, setHrsSavedAt]   = useState('');
  const [hrsAutoErr, setHrsAutoErr]   = useState('');   // #FLEET_HRS_AUTOSAVE_V1 — why the last autosave did not go through
  const [hrsBucketPanel, setHrsBucketPanel] = useState(false);
  const [hrsVinPanel, setHrsVinPanel] = useState(false);   // #FLEET_VIN_TRIAGE_V1 (2026-09-22)
  // #FLEET_GEOTAB_HOURS_V1 (2026-09-17) — Bill: "we already wired the GEO TAB API... will need the
  // Hours that are pulling to populate what it can." Raw sync payload from the new
  // api/geotab-fleet-sync.js (odometer + engine hours per device, keyed by VIN — see that file for
  // why StatusData/the two standard diagnostic IDs, not Trip). null = still loading.
  const [hrsGeotab, setHrsGeotab]     = useState(null);
  const [locNow, setLocNow]           = useState(null);   // #FLEET_LOCATIONS_V1 — `fleet-locations`: every tracker's latest fix + town; null = still loading
  const [locDays, setLocDays]         = useState(null);   // #FLEET_LOCATIONS_V1 — `fleet-location-days`: where each unit was, per day, 14 days back
  const [hrsOem, setHrsOem]           = useState(null);   // #FLEET_OEM_HOURS_V1 — the factory feeds' record (fleet-oem-hours); null = still loading
  // #FLEET_GEOTAB_HISTORY_V1 (2026-09-22) — the one-time historical weekly pull (baselines +
  // weekly snapshots), separate from hrsGeotab's "latest only" cache above.
  const [hrsGeotabHistory, setHrsGeotabHistory] = useState(null);
  const [hrsBackfillMsg, setHrsBackfillMsg] = useState('');
  const [hrsService, setHrsService]   = useState(null);   // { v, units:{ unitKey:{ serviced?, dos?, note?, updatedAt, updatedBy } } } — persistent, not week-scoped (#FLEET_SERVICE_DUE_V1)
  const hrsAutoFillRef = useRef({ wk: null, done: false });   // guards the auto-fill effect so it writes at most once per week per data-load, never fights a manual edit
  // #FLEET_WEEKLYINV_STICKY_V2 (2026-09-22) — measured (not guessed) column-header height, so the
  // sticky bucket ribbon below it locks at the header's REAL bottom edge instead of a hardcoded
  // pixel guess that didn't quite match and let the ribbon cover the top of the first row.
  const hrsHdrRef = useRef(null);
  const [hrsHdrH, setHrsHdrH] = useState(33);

  // #FLEET_SUP_LIST_V1 — Bill: "this should also come from the supervisor list but ability to hand
  // type names as some are assigned to mgmt or others." So it is a datalist, never a <select>: the
  // roster is the suggestion, not the constraint. Same `crew-lists` key the Revenue module curates,
  // so adding a supervisor there lights them up here with no second list to maintain.
  // Bill: "pull from Revenue Supervisors (Important as this will support piping into sups dash when
  // TA is turned on and correct spelling)". Spelling is the whole point — a hand-typed "NORBERTO "
  // and the roster's "NORBERTO" are two different supervisors to every downstream roll-up, so the
  // roster is offered first and free text is the exception, not the habit.
  const [sups, setSups] = useState([]);
  const [dist, setDist] = useState([]);      // #FLEET_DIST_V1 — saved distribution list
  const [holidays, setHolidays] = useState([]);   // #FLEET_HOLIDAY_V1 (2026-08-27) — Bill/Jerry: "How
  // to handle Monday Holidays as start of the week." Dates the yard is closed. Copy Prev treats a
  // listed holiday with nothing stored as a skip day (bridges to the last real work day) instead of a
  // dead end, and carries the FULL prior record — not just Assigned — across that bridge, since
  // equipment cost keeps accruing on a paid holiday and every unit still needs a job/bucket to land on.
  // #FLEET_ALIAS_V1 — Bill 2026-08-19: "that Custom number coming back again?" A job added on the
  // fly (Revenue "Add New Job") is stored under a `custom-<ts>` key until it gets a real DUC; once it
  // does, Revenue/Fork stamp the mapping into `fork-alias` (#FORK_AUTO_V1) so THEIR views show the
  // real number instead of the storage key. Daily Log entries filed while the job was still custom
  // stay keyed under that original `custom-<ts>` forever — the alias is what lets anything reading
  // `daily-logs` translate it back. Fleet's cross-check (dailyIdx) and the SUP auto-fill (supIdx)
  // both read `daily-logs` raw, so both need this same resolution or a resolved job resurfaces here
  // as an unrecognised "CUSTOM-…" DUC — the exact drift Revenue already solved for itself.
  const [alias, setAlias] = useState({});
  useEffect(() => { (async () => {
    try {
      const cl = await sg('crew-lists');
      const list = (cl && Array.isArray(cl.sups) ? cl.sups : []).map(s => String(s||'').trim().toUpperCase()).filter(Boolean);
      setSups([...new Set(list)].sort((a,b)=>a.localeCompare(b)));
    } catch { setSups([]); }
    try { const d = await sg('fleet-dist-list'); setDist(Array.isArray(d) ? d : []); } catch { setDist([]); }
    try { const h = await sg('fleet-holidays'); setHolidays(Array.isArray(h) ? h : []); } catch { setHolidays([]); }
    try { const fa = await sg('fork-alias'); setAlias(fa && typeof fa==='object' ? fa : {}); } catch { setAlias({}); }
  })(); }, [sg]);
  // Resolve a `daily-logs` storage key (its raw casing — alias keys are stored exactly as typed,
  // usually `custom-<ts>`) to the real DUC if Revenue has already linked one, else just uppercase it.
  const resolveLogDuc = (rawKey) => {
    const real = alias[rawKey] && alias[rawKey].duc;
    return stripCP(real || rawKey || '');   // #FLEET_CPSTRIP_V1
  };


  // ── load master ────────────────────────────────────────────────────────────
  // #FLEET_MASTER_LIVE_V1 (2026-10-26) — Bill changed GPS on 08-1423 etc. to N/A but Yard Walk kept showing NO GPS: the Master was
  // read ONCE when Fleet opened, so any screen left open never saw edits made elsewhere. Now it re-reads when fleet-equipment
  // changes (shared 12-second change check), so Yard Walk, Daily, In Shop and the shop dashes all pick up Master edits live.
  const [masterTick, setMasterTick] = useState(0);
  useLiveKeys(['fleet-equipment'], () => setMasterTick(t => t + 1));
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-equipment');
      if (s && s.cols) {
        // #FLEET_ARCHIVE_WHERE_V1 / #FLEET_RENTAL_V2 — new columns get backfilled into the persisted
        // schema here so the next save doesn't silently drop them (persistMaster reuses whatever cols
        // was loaded with). Any column missing from an older save is appended, in order.
        const need = ['saleTo','rentalType','rentalVendor','monthlyRate','weeklyRate','rentalStart','rentalDue','needsFollowUp','addedAt','addedBy','docs','cameraStatus','gpsStatus','gpsSerial','gpsCamCost','assetCost','telemStart',
          // #ASSETLEDGER_V1 (2026-09-16) - Ashlie's Purchased/Sold pink-cell fields (Owner Dash asset
          // ledger) plus the Fleet Arrival/Decommission checklist and Jerry's add-only flag origin.
          'origin','insuredValue','cashLoan','addedToIns','addedToICRental','addedInQB','invoiceAttached',
          'paymentRecd','removedFromIns','removedFromICRental','removedFromQB','billOfSaleAttached','folderMoved',
          'arrival','decommission','decommissionPending','valueAdds'];
        const c = [...s.cols, ...need.filter(n => !s.cols.includes(n))];
        setCols(c);
        const act0 = expand(c, s.active);
        telemBaseRef.current = telemSnap(act0);   // #FLEET_TELEM_MERGE_V1
        setActive(act0);
        setRetired(expand(c, s.retired));
      } else if (!masterTick) { setActive([]); }
    } catch { if (!masterTick) setActive([]); }
  })(); }, [sg, masterTick]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #PM_EQFLAG_V1 — load PM-raised equipment flags.
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-pm-flags');
      setPmFlags(s && Array.isArray(s.items) ? s.items : []);
    } catch { setPmFlags([]); }
  })(); }, [sg]);

  // #FLEET_WEEKLYHRS_V1 — load the whole weekly-hours blob once (all weeks; needed for the
  // MTD/QTD/YTD rollups, not just the week on screen) and the bucket overrides.
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-weekly-hrs');
      const base = s && s.weeks && typeof s.weeks === 'object' ? s : { v:1, weeks:{} };
      const stale = hrsStaleStamps(base);   // #FLEET_HRS_MERGE_WRITE_V1 — drop frozen Geotab stamps (see hrsStaleStamps)
      if (stale) { setHrsData(hrsApplyCells(base, stale)); writeHrsCells(stale).catch(() => {}); }
      else setHrsData(base);
    } catch { setHrsData({ v:1, weeks:{} }); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-hrs-buckets');
      hrsLoadFlags.current.buckets = true;
      setHrsBucketMap(s && s.map && typeof s.map === 'object' ? s.map : {});
    } catch { hrsLoadFlags.current.buckets = true; setHrsBucketMap({}); }
  })(); }, [sg]);
  // #FLEET_GEOTAB_HOURS_V1 — the Fleet Geotab sync's latest odometer/engine-hours per device.
  // Re-checked whenever the Weekly Inventory week changes (not just once) so a fresh Monday sync run
  // shows up without needing a full page reload.
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-geotab-hours');
      setHrsGeotab(s && Array.isArray(s.devices) ? s : { devices: [] });
    } catch { setHrsGeotab({ devices: [] }); }
  })(); }, [sg, hrsWkStart]);
  // #FLEET_OEM_HOURS_V1 — the factory feeds' record written by api/oem-hours-sync.js. Re-checked per
  // week like hrsGeotab so a fresh Monday pull shows without a page reload. A brand with no key yet
  // simply has no machines in here.
  // #FLEET_RIG_FAULT_WATCH_V1 (2026-10-08) — Bill: fault codes "as it happens." The pull now runs every 15 minutes,
  // so the record is re-read every 5 while Fleet is open; a new code shows on the Rig Board without a page reload.
  // A re-read that fails keeps what is already on screen. (The Weekly Inventory auto-fill still writes once per
  // week per page load — its own guard — so this never re-stamps hours.)
  useEffect(() => {
    let dead = false;
    const load = async (first) => {
      try {
        const s = await sg(OEM_KEY);
        if (!dead) setHrsOem(s && Array.isArray(s.devices) ? s : { devices: [], providers: (s && s.providers) || {} });
      } catch { if (!dead && first) setHrsOem({ devices: [], providers: {} }); }
    };
    load(true);
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => { dead = true; clearInterval(t); };
  }, [sg, hrsWkStart]);
  // #FLEET_LOCATIONS_V1 (2026-10-09) — Bill: "pipe in a GEO tab map to Fleet" + "time stamp location of unit on this
  // … inventory sheet." api/fleet-locations-sync.js writes every tracker's latest fix and town every 15 minutes.
  // Only two screens use it — the Fleet dash (map) and the Daily sheet (GPS column) — so it is only read, and only
  // re-read every 5 minutes, while one of those is open. A re-read that fails keeps what is on screen.
  const locWanted = view === 'fleetdash' || view === 'daily' || view === 'home';   // #TILE_LOC_V1 — Jerry's Yard home shows the tiles too
  // #FLEET_TELEM_HOT_V1 (2026-10-12) — Camera / GPS not reporting: the red Fleet summary tile + the 'telem' screen.
  const telem = useTelemIssues(sg, active, retired, view === 'fleetdash' || view === 'telem' || view === 'mydash' || view === 'daily' || view === 'yard' || view === 'inshop', view === 'telem');   /* #SHOPDASH_YARD_MAPS_V1 — staff dash shows the Cam / GPS down count */   // #FLEET_TELEM_ASSIGN_V1 — Daily names only loaded on the screen
  const tflags = useMemo(() => telemFlagIndex(telem.res, active), [telem.res, active]);   // #TELEM_FLAG_V1 — GPS / camera warning beside every unit # (Daily, Yard Walk, In Shop)
  useEffect(() => {
    if (!locWanted) return undefined;
    let dead = false;
    const load = async (first) => {
      try {
        const s = await sg(LOC_KEY);
        if (!dead) setLocNow(s && Array.isArray(s.units) ? s : { units: [] });
      } catch { if (!dead && first) setLocNow(prev => prev || { units: [] }); }
    };
    load(true);
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => { dead = true; clearInterval(t); };
  }, [sg, locWanted]);
  // The per-day record is bigger and only the Daily sheet reads it; it changes once an hour.
  useEffect(() => {
    if (view !== 'daily') return undefined;
    let dead = false;
    const load = async () => { try { const s = await sg(LOC_DAYS_KEY); if (!dead && s && s.days) setLocDays(s); } catch { /* keep what is on screen */ } };
    load();
    const t = setInterval(load, 30 * 60 * 1000);
    return () => { dead = true; clearInterval(t); };
  }, [sg, view]);
  // #FLEET_GEOTAB_HISTORY_V1 — loaded once on mount (not re-checked per week like hrsGeotab above,
  // since a history pull is a manual one-off run, not a live weekly sync) — backfillBaselines below
  // reads this.
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-geotab-history');
      setHrsGeotabHistory(s && Array.isArray(s.devices) ? s : { devices: [] });
    } catch { setHrsGeotabHistory({ devices: [] }); }
  })(); }, [sg]);
  // #FLEET_SERVICE_DUE_V1 — Serviced / DOS / Note, persistent per-unit facts (see the storage note
  // up top for why this isn't folded into fleet-weekly-hrs).
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-service');
      setHrsService(s && s.units && typeof s.units === 'object' ? s : { v:1, units:{} });
    } catch { setHrsService({ v:1, units:{} }); }
  })(); }, [sg]);
  // The prior Friday's Daily Inventory assignment — one week's assignments are frozen for the whole
  // report period, only re-pulled when hrsWkStart moves. Same `fleet-daily-<mondayISO>` key Daily
  // Inventory itself reads/writes; this just looks at last week's Friday inside it.
  useEffect(() => { (async () => {
    hrsLoadFlags.current.fri = false;
    try {
      const priorMon = iso(addDays(new Date(hrsWkStart+'T12:00:00'), -7));
      const priorFri = iso(addDays(new Date(hrsWkStart+'T12:00:00'), -3));
      const w = await sg('fleet-daily-' + priorMon);
      const day = (w && typeof w === 'object') ? (w[priorFri] || {}) : {};
      const m = {};
      Object.keys(day).forEach(k => { if (day[k] && String(day[k].assigned||'').trim()) m[k] = String(day[k].assigned).trim(); });
      hrsLoadFlags.current.fri = true;
      setHrsFridayAssigned(m);
    } catch { hrsLoadFlags.current.fri = true; setHrsFridayAssigned({}); }
  })(); }, [sg, hrsWkStart]);
  // Edits are per on-screen week — switching weeks with unsaved edits would otherwise silently carry
  // them onto the wrong week's record.
  // #FLEET_HRS_AUTOSAVE_V1 — used to simply drop them. Now anything typed is saved onto the week being left
  // (or on leaving Fleet altogether) before the screen moves on.
  const hrsEditsRef = useRef({});
  const hrsPersistRef = useRef(null);
  useEffect(() => {
    const wkLeaving = hrsWkStart;
    return () => {
      const pend = hrsEditsRef.current;
      if (pend && Object.keys(pend).length && hrsPersistRef.current) { try { hrsPersistRef.current(pend, wkLeaving).catch(() => {}); } catch {} }
      setHrsEdits({});
    };
  }, [hrsWkStart]);   // eslint-disable-line react-hooks/exhaustive-deps

  const resolvePmFlag = async (f) => {
    const next = pmFlags.map(x => x === f ? { ...x, status:'resolved', resolvedAt:new Date().toISOString() } : x);
    setPmFlags(next);
    try { await ss('fleet-pm-flags', { v:1, items: next }); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  // #PM_EQFLAG_CLEAR_V1 (2026-08-24) — Bill: a PM flag should also reallocate/reassign the unit, not
  // just sit as a report. Flags raised after #PM_EQFLAG_DAYS_V1 carry the exact week + day(s) the unit
  // showed under the wrong job; this clears the DUC on just those day(s) — same as deleting it by hand
  // on Daily Inventory — so it drops to blank/unassigned and is ready for Bob/Jerry to put on the right
  // job. Only touches a day if it's STILL showing the flagged job (in case someone already fixed it by
  // hand in the meantime), so it can never clobber a correction that already happened.
  const clearPmFlagAllocation = async (f) => {
    if (!f.wk || !Array.isArray(f.dates) || !f.dates.length) {
      alert(`This flag was raised before day-tracking was added, so there's no day to auto-clear.\nCorrect ${f.unit} by hand on Daily Inventory, then Mark reviewed.`);
      return;
    }
    if (!window.confirm(`Clear ${f.unit} off ${f.job} for ${f.dates.length} day(s) (${f.dates.join(', ')})?\n\nThis un-assigns it back to blank on those days so it's ready to put on the right job.`)) return;
    try {
      const wkData = (await sg('fleet-daily-' + f.wk)) || {};
      const k = unitKey(f.unit);
      const next = { ...wkData };
      f.dates.forEach(d => {
        const day = { ...(next[d]||{}) };
        const cur = day[k];
        if (cur && stripCP(cur.duc||'') === stripCP(f.duc||'')) {
          const patched = { ...cur, duc:'' };
          if (!String(patched.assigned||'').trim() && !String(patched.note||'').trim()) delete day[k];
          else day[k] = patched;
          next[d] = day;
        }
      });
      await ss('fleet-daily-' + f.wk, next);
      if (f.wk === wkStart) setWeek(next);   // reflect immediately if that's the week already open here
      await resolvePmFlag(f);
    } catch (e) { alert('Could not clear allocation: ' + (e.message||e)); }
  };

  // #FLEET_INSHOP_V1 — load the shop digest (same fleet-summary the Owner Dash "In Shop" tile reads)
  // and the diagnosis/mechanic/est-out notes.
  const loadShopSum = async () => {
    try { const s = await sg('fleet-summary'); setShopSum(s && typeof s==='object' ? s : null); } catch { setShopSum(null); }
  };
  useEffect(() => { loadShopSum(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-shop-notes');
      setShopNotes(s && s.notes && typeof s.notes==='object' ? s.notes : {});
    } catch { setShopNotes({}); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-shop-history');
      setShopHistory(s && Array.isArray(s.records) ? s.records : []);
    } catch { setShopHistory([]); }
  })(); }, [sg]);
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-shop-dismissed');
      setShopDismissed(s && s.dismissed && typeof s.dismissed==='object' ? s.dismissed : {});
    } catch { setShopDismissed({}); }
  })(); }, [sg]);
  useEffect(() => { (async () => {   // #FLEET_SHOPREPORT_V1
    try { const s = await sg('fleet-shop-report'); if (s && typeof s==='object') setShopReport(s); } catch { /* nothing saved yet */ }
  })(); }, [sg]);
  // #FLEET_SHOP3P_V1 (2026-10-02) — Bill: "Units that are in 3rd party shops should be tracked here as well
  // and form should be editable." In Shop used to list ONLY units coded A-SHOP on Daily, so a unit out at a
  // vendor while still charged to a job (or parked A-IDLE) never showed — 9 of the 17 lines on Bob's sheet.
  //   • A unit can now be ADDED BY HAND from the Master ("+ Add unit"). It lives as its shop note
  //     (manual:true + dateIn) and stays listed until Complete or Remove — no Daily coding needed.
  //   • Every row is editable: Date In (blank = the automatic date from Daily), Diagnosis, Mechanic / shop,
  //     Est. Date Out, and an "at a 3rd-party shop" flag. Days Down follows Date In.
  // Still one store (fleet-shop-notes, keyed by unit) so a unit renumber carries these with it.
  // writeShopNote re-reads the stored notes before writing: two people saving different units a minute
  // apart can no longer drop each other's row (it matters now that a hand-added unit exists only as its note).
  const writeShopNote = async (k, rec) => {
    let base = shopNotes;
    try { const cur = await sg('fleet-shop-notes'); if (cur && cur.notes && typeof cur.notes==='object') base = cur.notes; } catch { /* use what is on screen */ }
    // #FLEET_SHOP_HOT_V1 — an edit of Diagnosis / Mechanic / dates must never drop Bob's 🔥 (only toggleShopHot
    // or ✓ Complete set rec.hot themselves).
    if (rec && rec.hot === undefined && base[k] && base[k].hot) {
      const h = base[k]; rec = { ...rec, hot:true, hotBy:h.hotBy||'', hotAt:h.hotAt||'' }; if (h.hotWhy) rec.hotWhy = h.hotWhy;
    }
    const next = { ...base }; if (rec) next[k] = rec; else delete next[k];
    await ss('fleet-shop-notes', { v:1, notes: next });
    setShopNotes(next);
  };
  // #FLEET_SHOP_HOT_V1 — set / clear the 🔥. Re-reads the stored note so it only touches the hot fields.
  const toggleShopHot = async (x) => {
    if (!x) return;
    const on = !x.hot;
    if (on ? !hotSetter : !hotClearer) return;
    let why = '';
    if (on) {
      const r = window.prompt(`🔥 Mark ${x.unit} HOT for the fleet team?\n\nOptional — why (e.g. "needed on Waco job Monday"):`, '');
      if (r === null) return;
      why = String(r).trim().slice(0, 120);
    } else if (!window.confirm(`Clear the 🔥 on ${x.unit}?${x.hotWhy ? `\n\n"${x.hotWhy}"` : ''}`)) return;
    const k = x.k || unitKey(x.unit);
    let base = shopNotes;
    try { const cur = await sg('fleet-shop-notes'); if (cur && cur.notes && typeof cur.notes==='object') base = cur.notes; } catch { /* use what is on screen */ }
    const rec = { ...(base[k] || {}) };
    delete rec.hotBy; delete rec.hotAt; delete rec.hotWhy;
    if (on) { rec.hot = true; rec.hotBy = upn || ''; rec.hotAt = new Date().toISOString(); if (why) rec.hotWhy = why; }
    else rec.hot = false;
    setBusy('…');
    try { await writeShopNote(k, rec); } catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy('');
  };
  const saveShopNote = async () => {
    if (!shopEdit) return;
    const k = unitKey(shopEdit.unit);
    if (!k) { alert('Enter a unit number.'); return; }
    const dateIn = shopIso(shopEdit.dateIn);
    let unit = shopEdit.unit, manual = !!shopEdit.manual;
    if (shopEdit._new) {
      const m = (active||[]).find(r => unitKey(r.unit) === k);
      if (!m) { alert(`${unitNorm(shopEdit.unit)} is not on the Equipment Master — pick a unit from the list.`); return; }
      if (shopRows.some(x => x.k === k)) { alert(`${m.unit} is already on the In Shop list — click its row to edit it.`); return; }
      unit = m.unit; manual = true;
    }
    if (manual && !dateIn) { alert('Date In is required for a unit added by hand.'); return; }
    if (dateIn && dateIn > iso(new Date())) { alert('Date In cannot be in the future.'); return; }
    const prev = shopNotes[k] || {};
    const rec = { diagnosis: shopEdit.diagnosis||'', mechanic: shopEdit.mechanic||'', estOut: shopEdit.estOut||'', updatedAt: new Date().toISOString() };
    if (dateIn) rec.dateIn = dateIn;
    if (shopEdit.thirdParty) rec.thirdParty = true;
    if (manual) { rec.manual = true; rec.unit = unit; rec.addedBy = prev.addedBy || upn || String(userRole||''); }
    setBusy('Saving…');
    try { await writeShopNote(k, rec); setShopEdit(null); } catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy('');
  };
  const clearShopNote = async (unit) => {
    const k = unitKey(unit);
    setBusy('…');
    try { await writeShopNote(k, null); setShopEdit(null); } catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy('');
  };
  // #FLEET_SHOPHISTORY_V1 — archive a finished shop visit. Manual click (not automatic on a location
  // change) so Diagnosis/Mechanic/Est-Out notes can be locked in first. Dismisses the row from In Shop
  // immediately (keyed by unit+dateIn, so a later fresh stay for the same unit isn't hidden) even
  // before the location digest itself catches up.
  const markShopComplete = async (x) => {
    if (!yardOpsEdit || !x) return;
    if (!window.confirm(`Mark ${x.unit} complete and move to Shop History?\n\nDate In ${fmtDate(x.dateIn)} · ${x.days} day${x.days===1?'':'s'} down`)) return;
    const rec = {
      unit: x.unit, desc: x.desc||'', dateIn: x.dateIn, dateOut: iso(new Date()),
      days: Number(x.days)||0, diagnosis: x.diagnosis||'', mechanic: x.mechanic||'', estOut: x.estOut||'',
      rate: num(x.rate), cost: (Number(x.days)||0) * num(x.rate),
      completedAt: new Date().toISOString(), completedBy: upn||'',
    };
    // #FLEET_SHOP3P_V1 — the history record remembers how the row got here so Undo can put it back exactly.
    if (x.thirdParty) rec.thirdParty = true;
    if (x.manual) rec.manual = true;
    else if (x.autoDateIn && x.autoDateIn !== x.dateIn) rec.autoDateIn = x.autoDateIn;
    const nextHist = [rec, ...shopHistory];
    // A Daily-fed stay is hidden by its AUTOMATIC date (a typed Date In must not change which stay is closed);
    // a hand-added unit has no Daily stay to hide — taking its note off the list is what closes it.
    const dismissKey = `${x.k}__${x.autoDateIn || x.dateIn}`;
    const nextDismissed = x.manual ? shopDismissed : { ...shopDismissed, [dismissKey]: rec.completedAt };
    setBusy('Saving…'); setShopHistory(nextHist); setShopDismissed(nextDismissed);
    try {
      await ss('fleet-shop-history', { v:1, records: nextHist });
      if (!x.manual) await ss('fleet-shop-dismissed', { v:1, dismissed: nextDismissed });
      const n = shopNotes[x.k];
      if (n && n.manual) await writeShopNote(x.k, null);
      else if (n && (n.dateIn || n.thirdParty || n.hot)) {   // a typed Date In / 3rd-party flag / 🔥 belongs to THIS stay, not the unit's next one
        const { dateIn: _dateIn, thirdParty: _thirdParty, hot: _hot, hotBy: _hotBy, hotAt: _hotAt, hotWhy: _hotWhy, ...rest } = n;
        await writeShopNote(x.k, { ...rest, hot:false });   // #FLEET_SHOP_HOT_V1
      }
    } catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy(''); setShopEdit(null);
  };
  // Undo, from the Shop History tab — pulls a record back out of history and clears its dismissal so
  // it reappears in In Shop (only if the unit's location still actually reads SHOP in the live digest).
  const undoShopComplete = async (rec) => {
    if (!yardOpsEdit || !rec) return;
    if (!window.confirm(`Undo — remove ${rec.unit}'s ${fmtDate(rec.dateOut)} completion from Shop History?`)) return;
    const nextHist = shopHistory.filter(r => r !== rec);
    const dismissKey = `${unitKey(rec.unit)}__${rec.autoDateIn || rec.dateIn}`;   // #FLEET_SHOP3P_V1
    const nextDismissed = { ...shopDismissed }; delete nextDismissed[dismissKey];
    setBusy('…'); setShopHistory(nextHist); setShopDismissed(nextDismissed);
    try {
      await ss('fleet-shop-history', { v:1, records: nextHist });
      await ss('fleet-shop-dismissed', { v:1, dismissed: nextDismissed });
      if (rec.manual || rec.autoDateIn || rec.thirdParty) {   // #FLEET_SHOP3P_V1 — put back what Complete took off the note
        const k = unitKey(rec.unit), n = shopNotes[k] || {};
        const back = { diagnosis: n.diagnosis || rec.diagnosis || '', mechanic: n.mechanic || rec.mechanic || '', estOut: n.estOut || rec.estOut || '', updatedAt: new Date().toISOString() };
        if (rec.manual || rec.autoDateIn) back.dateIn = rec.dateIn;
        if (rec.thirdParty) back.thirdParty = true;
        if (rec.manual) { back.manual = true; back.unit = rec.unit; }
        await writeShopNote(k, back);
      }
    } catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy('');
  };

  // ── load the visible week ──────────────────────────────────────────────────
  const loadWeek = async () => {
    firstLoadRef.current = true;   // #FLEET_AUTOSAVE_V1 — a fresh load must not be read as an edit to save
    pendingRemovalsRef.current = {};   // #FLEET_SAVEMERGE_V1 — a fresh pull replaces our whole picture of this week, so any not-yet-saved deletes from before are moot
    try { const w = await sg('fleet-daily-' + wkStart); setWeek(w && typeof w === 'object' ? w : {}); }
    catch { setWeek({}); }
    setDirty(false);
    setSaveErr('');
  };
  useEffect(() => { loadWeek(); }, [sg, wkStart]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #FLEET_WEEKROLLUP_REFRESH_V1 (2026-08-26) — Bill: "This section not Refreshing from updated
  // records. Can we either auto update or a refresh button." Root cause: `week` is only fetched once
  // per week-open (page load / switching weeks) — it doesn't pull in edits someone else saved to the
  // same week since (Jerry's Yard Walk, a PM's Daily Log, another open tab). A manual Refresh re-pulls
  // it; guarded with a confirm if there's an unsaved local edit, so a click here can't silently lose it.
  const [weekRefreshing, setWeekRefreshing] = useState(false);
  const refreshWeek = async () => {
    if (dirty && !window.confirm("You have an unsaved edit in this tab. Refresh will replace it with what's saved on the server. Continue?")) return;
    setWeekRefreshing(true);
    await loadWeek();
    setWeekRefreshing(false);
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const byKey = useMemo(() => {
    const m = {}; (active||[]).forEach(r => { const k = unitKey(r.unit); if (k) m[k] = r; }); return m;
  }, [active]);
  const cats = useMemo(() => [...new Set((active||[]).map(r => String(r.cat||'').trim()).filter(Boolean))].sort(), [active]);
  // #FLEET_RENTAL_V2 — rental/RPO units, now just Master rows flagged with rentalType. Feeds the
  // Rental/RPO tab, the Daily "R" badge, and the Owner Dash tile.
  const rentalRecords = useMemo(() => (active||[]).filter(r => String(r.rentalType||'').trim()), [active]);
  // Shape published into fleet-summary.rentalRpo for the Owner Dash tile — Bill: "Owners Dash will
  // see UNIT NUMBER - Description - Start Rental - Mo Rate - Weekly Rate".
  const rentalDigest = () => ({
    count: rentalRecords.length,
    items: rentalRecords.slice(0,20).map(r => ({ unit:r.unit, desc:r.desc||'', type:r.rentalType||'',
      rentalStart:r.rentalStart||'', monthlyRate:num(r.monthlyRate), weeklyRate:num(r.weeklyRate) })),
  });

  // ── #FLEET_WEEKLYHRS_V1 derived ───────────────────────────────────────────────
  const hrsWeeks = (hrsData && hrsData.weeks) || {};
  // #FLEET_GEOTAB_HOURS_V1 — device-by-VIN lookup off the sync payload. Standing rule: join on VIN,
  // never on the Geotab device name (see geotab-api-integration-notes.md) — device names are
  // hand-typed in MyGeotab and drift exactly the way the old COST extract's unit numbers did.
  const geotabByVin = useMemo(() => {
    const m = {};
    ((hrsGeotab && hrsGeotab.devices) || []).forEach(d => { const v = vinClean(d.vin); if (v) m[v] = d; });
    return m;
  }, [hrsGeotab]);
  const oemByVin = useMemo(() => oemIndex(hrsOem), [hrsOem]);   // #FLEET_OEM_HOURS_V1 — Serial / VIN → factory-feed machine
  // Every active unit, unit-bucketed, with this week's reading chained off last week's — this IS the
  // Weekly Inventory grid, one row per unit, in Bill's bucket order. RIGS are excluded from the
  // Geotab match entirely — Bill: "RIGs still manual as Vermeer/Ditchwitch not wired" — so a rig
  // never shows a false "no signal"/"no device" flag for a machine that was never going to report.
  // #FLEET_SVC_STATE_V1 (2026-10-05) — the most recent STORED reading per unit, up to and including the
  // week on screen. Service Due used to turn red only when THIS week's reading was typed in, so a rig
  // nobody had read yet this week stayed white even when last week's reading was already past due
  // (25-3996 on 10/5: due 1,295, last read 1,351). Bill: "flag units that are Overdue or hard to catch."
  const hrsLastKnown = useMemo(() => {
    const out = {};
    Object.keys(hrsWeeks).sort().forEach(mon => {
      if (mon > hrsWkStart) return;
      const wk = hrsWeeks[mon] || {};
      Object.keys(wk).forEach(uk => {
        const rec = wk[uk];
        if (rec && rec.reading != null && rec.reading !== '') out[uk] = { mon, reading: Number(rec.reading), source: rec.source || 'manual' };
      });
    });
    return out;
  }, [hrsWeeks, hrsWkStart]);   // eslint-disable-line react-hooks/exhaustive-deps
  const hrsRows = useMemo(() => {
    const prevMon = iso(addDays(new Date(hrsWkStart+'T12:00:00'), -7));
    const thisWk = hrsWeeks[hrsWkStart] || {};
    const prevWk = hrsWeeks[prevMon] || {};
    return (active || []).map(r => {
      const uk = unitKey(r.unit);
      const bucket = bucketOf(r.cat, hrsBucketMap);
      const metric = HRS_BUCKET_METRIC[bucket];
      const edit = hrsEdits[uk] || {};
      const saved = thisWk[uk];
      const savedReading = saved ? saved.reading : null;

      // What Geotab has for this unit, if anything — only consulted for buckets that get a metric
      // (not 'rigs', not 'other'/unset) and only when the VIN actually resolves to a device.
      const device = (bucket !== 'rigs' && metric) ? geotabByVin[vinClean(r.vin)] : null;
      const geotabValue = device ? (metric === 'mi' ? device.odometerMiles : device.engineHours) : null;
      const geotabAt = device ? (metric === 'mi' ? device.odometerAt : device.engineHoursAt) : null;
      let geotabStatus = null;   // null (n/a — rig/other/no-vin-metric) | 'auto' | 'no-signal' | 'no-device'
      if (bucket !== 'rigs' && metric) {
        geotabStatus = !device ? 'no-device' : (geotabValue == null ? 'no-signal' : 'auto');
      }
      // #FLEET_OEM_HOURS_V1 — the factory feed for this unit: every rig, plus any hour-metered unit whose
      // maker has a feed (Kobelco excavators). Only THIS week's own snapshot counts, never "today's"
      // number. A factory reading beats Geotab's engine hours, which run off on excavators and VACs.
      const oemDev = metric === 'hrs' ? oemForUnit(oemByVin, r.vin) : null;
      const oemValue = oemDev ? oemWeekHours(oemDev, hrsWkStart) : null;
      const oemAt = oemDev ? (oemWeekAt(oemDev, hrsWkStart) || oemDev.engineHoursAt || null) : null;
      const oemSilent = !!(oemDev && oemValue == null && (oemDev.stale || oemDev.engineHours == null));
      if (oemValue != null && geotabStatus !== 'auto') geotabStatus = null;   // the factory feed covers it — not a Geotab "needs a look"

      // Manual entry always wins over auto-fill: an explicit edit, or a reading already saved for
      // this week (whatever put it there — manual or a prior auto-fill), beats a fresh Geotab pull.
      const reading = edit.reading !== undefined ? edit.reading
        : (savedReading != null ? savedReading : (oemValue != null ? oemValue : (geotabStatus === 'auto' ? geotabValue : null)));
      const source = edit.reading !== undefined ? 'manual'
        : (savedReading != null) ? (saved.source || 'manual')   // a reading saved before this feature existed has no source tag — that was a human, not Geotab
        : (oemValue != null ? 'oem' : (geotabStatus === 'auto' && reading != null ? 'geotab' : null));
      // which maker's feed: the one stamped on the saved reading, else the one this unit matches now
      const oemBrand = (saved && saved.source === 'oem' && saved.feed) || (oemDev ? oemDev.provider : '');

      const start = (prevWk[uk] && prevWk[uk].reading != null) ? Number(prevWk[uk].reading) : null;
      const end = (reading != null && reading !== '') ? Number(reading) : null;
      const delta = (start != null && end != null) ? Math.round((end - start) * 100) / 100 : null;
      const assigned = edit.assignedOverride !== undefined ? edit.assignedOverride
        : (saved && saved.assignedOverride) ? saved.assignedOverride
        : (hrsFridayAssigned[uk] || '');
      const flagReason = edit.flagReason !== undefined ? edit.flagReason : ((saved && saved.flagReason) || '');

      // #FLEET_SERVICE_DUE_V1 — Serviced / DOS / Note read the persistent fleet-service blob the same
      // "in-progress edit wins" way everything else here does. Due is never stored — always recomputed
      // from serviced + the bucket/category's interval, so a bucket move or interval fix takes effect
      // immediately with no re-save needed.
      const svcSaved = (hrsService && hrsService.units && hrsService.units[uk]) || {};
      const serviceRule = serviceRuleFor(bucket, r.cat);
      const serviced = edit.serviced !== undefined ? edit.serviced : (svcSaved.serviced != null ? svcSaved.serviced : null);
      const dos = edit.dos !== undefined ? edit.dos : (svcSaved.dos || '');
      const note = edit.note !== undefined ? edit.note : (svcSaved.note || '');
      // #FLEET_TRAILER_RIG_V1 (2026-10-15) — trailers carry no hours/miles; that cell holds the rig the trailer runs with.
      // Persistent (fleet-service), so it carries week to week until someone changes it.
      const rig = edit.rig !== undefined ? edit.rig : (svcSaved.rig || '');
      const servicedNum = (serviced != null && serviced !== '') ? Number(serviced) : null;
      const due = (serviceRule && servicedNum != null) ? Math.round((servicedNum + serviceRule.interval) * 100) / 100 : null;
      // #FLEET_SVC_STATE_V1 — one shared rule (fleetService.js): this week's reading if there is one,
      // else the last one on file; red at/past due, amber inside the last 10%, plus the 6-month rule.
      const lastRec = hrsLastKnown[uk] || null;
      const known = end != null ? end : (lastRec ? lastRec.reading : null);
      const knownAt = end != null ? hrsWkStart : (lastRec ? lastRec.mon : null);
      const svc = serviceState({ rule: serviceRule, serviced: servicedNum, dos, reading: known });
      const dueFlag = svc.state === 'overdue';
      const dueSoon = svc.state === 'soon';

      return { r, uk, bucket, metric, start, end, delta, reading: end, source, geotabStatus, geotabAt, flagReason,
        oemBrand, oemProvider: oemDev ? oemDev.provider : '', oemAt, oemSilent, oemSince: oemDev ? (oemDev.engineHoursAt || null) : null,   // #FLEET_OEM_HOURS_V1
        oem: oemDev,   // #FLEET_RIG_BOARD_V1 — the whole feed record for this unit (idle, rods, service, fault codes) for the rig board
        assigned, assignedIsOverride: !!(saved && saved.assignedOverride) || edit.assignedOverride !== undefined,
        serviceRule, serviced: servicedNum, dos, note, rig, due, dueFlag, dueSoon, svc, known, knownAt };
    }).sort((a,b) => (HRS_BUCKET_ORDER.indexOf(a.bucket)-HRS_BUCKET_ORDER.indexOf(b.bucket))
      // #FLEET_MASTER_CATCOLLAPSE_V1 — rigs sort by R-class (size, smallest→largest) first, mirroring
      // the Revenue view's R-category breakdown; every other bucket just sorts by unit (year-first).
      || (a.bucket==='rigs' && b.bucket==='rigs' ? rigSort(a.r, b.r) : 0)   // #FLEET_RIG_MODEL_ORDER_V1
      || String(a.r.unit).localeCompare(String(b.r.unit)));
  }, [active, hrsWeeks, hrsWkStart, hrsBucketMap, hrsEdits, hrsFridayAssigned, geotabByVin, oemByVin, hrsService, hrsLastKnown]);
  // #FLEET_MAINT_V1 (2026-10-05) — the Maintenance tab's list: every SCHEDULED unit as of right now —
  // latest reading across every stored week (not the week Weekly Inventory happens to be paged to),
  // saved Serviced / DOS only (no in-progress edits), same serviceState() rule as the grid above.
  const svcBoard = useMemo(() => {
    const last = {};
    Object.keys(hrsWeeks).sort().forEach(mon => {
      const wk = hrsWeeks[mon] || {};
      Object.keys(wk).forEach(uk => {
        const rec = wk[uk];
        if (rec && rec.reading != null && rec.reading !== '') last[uk] = { mon, reading: Number(rec.reading), source: rec.source || 'manual' };
      });
    });
    return (active || []).map(r => {
      const uk = unitKey(r.unit);
      const bucket = bucketOf(r.cat, hrsBucketMap);
      const rule = serviceRuleFor(bucket, r.cat);
      if (!rule) return null;
      const sv = (hrsService && hrsService.units && hrsService.units[uk]) || {};
      const serviced = (sv.serviced != null && sv.serviced !== '') ? Number(sv.serviced) : null;
      const lr = last[uk] || null;
      const st = serviceState({ rule, serviced, dos: sv.dos || '', reading: lr ? lr.reading : null });
      return { uk, unit: r.unit, desc: r.desc || '', cat: r.cat || '', vin: r.vin || '', bucket, bucketLabel: HRS_BUCKET_LABEL[bucket] || bucket,
        rule, serviced, dos: sv.dos || '', note: sv.note || '',
        reading: lr ? lr.reading : null, readingAt: lr ? lr.mon : null, readingSource: lr ? lr.source : null, ...st };
    }).filter(Boolean);
  }, [active, hrsWeeks, hrsBucketMap, hrsService]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #PM_KIT_FLOW_V1 (2026-10-28) — Bill: "Overdue needs an alert when hours/miles are in excess for all to be alerted including PMs."
  // One small snapshot of every unit overdue / due soon, written when it changes, so the PM + SUP dashboards and the morning alert
  // job (api/pm-service-alerts.js) read the SAME answer Maintenance shows — without loading the master or every week of readings.
  const svcSnapRef = useRef('');
  useEffect(() => {
    if (!editable || !['Owner', 'MGMT', 'Yard', 'Fleet'].includes(String(userRole || '')) || !hrsService || !(active || []).length) return;
    const units = svcDueSnapshot(svcBoard); const sig = JSON.stringify(units);
    if (!units.length && !svcBoard.length) return;          // readings not loaded yet — don't blank the snapshot
    if (sig === svcSnapRef.current) return;
    const t = setTimeout(async () => {
      try { const prev = await sg(PM_SVC_DUE_KEY); if (prev && JSON.stringify(prev.units || []) === sig) { svcSnapRef.current = sig; return; } } catch { /* write it */ }
      try { await ss(PM_SVC_DUE_KEY, { v: 1, at: new Date().toISOString(), by: upn || '', units }); svcSnapRef.current = sig; } catch { /* next change retries */ }
    }, 4000);
    return () => clearTimeout(t);
  }, [svcBoard]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #PM_KIT_FLOW_V1 — PM kits a SUP closed from the field owe their Serviced reading (field logins can't write the service file):
  // any Fleet screen with write access posts them, on load and every 10 minutes, then re-reads Serviced / DOS.
  useEffect(() => {
    if (!editable || !['Owner', 'MGMT', 'Yard', 'Fleet'].includes(String(userRole || ''))) return undefined;
    let dead = false;
    const go = async () => {
      try {
        const n = await postPendingServiced(sg, ss, upn || '');
        if (n && !dead) { const s = await sg('fleet-service'); if (s && s.units) setHrsService(s); }
      } catch { /* next pass */ }
    };
    go(); const t = setInterval(go, 10 * 60 * 1000);
    return () => { dead = true; clearInterval(t); };
  }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #FLEET_VIN_TRIAGE_V1 (2026-09-22) — Bill: "need to outline the units that have VIN issues to
  // target them in the Geotab system." Two different problems on purpose: a blank VIN on the
  // Master is OUR data-hygiene gap (nothing to even match against); a VIN that IS on file but
  // still finds no Geotab device is a GEOTAB-side gap (wrong/missing VIN over there, or the unit
  // was never added) — different people fix each half. Skips rigs/trailers/no-metric buckets,
  // which were never going to report to Geotab in the first place.
  const hrsVinIssues = useMemo(() => {
    if (!hrsGeotabHistory) return [];
    const byVin = {};
    (hrsGeotabHistory.devices || []).forEach(d => { const v = vinClean(d.vin); if (v) byVin[v] = d; });
    const out = [];
    hrsRows.forEach(x => {
      if (x.bucket === 'rigs' || !x.metric) return;
      // Bill: units marked GPS (Geotab) = N/A on the Master were never going to report — that's not
      // a data-hygiene gap to chase, it's a real "no telematics on this unit" decision (e.g. 13-0300,
      // 12-0319, 12-0217). Leave everything else (blank/not-yet-checked, NW, NO GPS) on the list —
      // those are genuinely still open.
      if (String(x.r.gpsStatus || '').trim() === 'N/A') return;
      const vin = String(x.r.vin || '').trim();
      if (!vin) { out.push({ unit:x.r.unit, desc:x.r.desc||'', cat:x.r.cat||'', vin:'', reason:'no-vin' }); return; }
      if (!byVin[vinClean(vin)]) out.push({ unit:x.r.unit, desc:x.r.desc||'', cat:x.r.cat||'', vin, reason:'no-match' });
    });
    return out.sort((a,b) => String(a.unit).localeCompare(String(b.unit)));
  }, [hrsRows, hrsGeotabHistory]);
  const hrsDirty = Object.keys(hrsEdits).length > 0;
  const hrsFilteredRows = useMemo(() => {
    const q = hrsQ.trim().toLowerCase();
    return hrsRows.filter(x => {
      if (hrsBucketFilter && x.bucket !== hrsBucketFilter) return false;
      if (!q) return true;
      return [x.r.unit, x.r.desc, x.r.vin, x.assigned].some(v => String(v||'').toLowerCase().includes(q));
    });
  }, [hrsRows, hrsQ, hrsBucketFilter]);
  // MTD / QTD / YTD — every unit's weekly deltas (end-of-week reading minus the immediately prior
  // STORED reading, wherever that falls — a skipped week's whole gap lands on the week it was finally
  // read) bucketed by the later week's calendar month/quarter/year. For DOT: total miles/hours moved
  // this month, this quarter, this year, per unit.
  const hrsRollups = useMemo(() => {
    // #FLEET_YTD_BASELINE_V1 (2026-09-22) — a unit with a Baseline Reading/Date set on Equipment
    // Master (hrsBaseline/hrsBaselineDate) gets that baseline injected as the earliest point in its
    // delta chain below, so the first captured week's delta is (first reading − baseline) instead
    // of landing at 0. No baseline set = unchanged from before: YTD just starts counting from
    // whenever the first real weekly reading landed ("since tracking began"). Local date parser
    // (not the component's shared parseMDY) because this runs earlier in the file than that's
    // declared and a useMemo body executes immediately, before parseMDY would be initialized.
    const parseBaselineDate = (v) => {
      const s = String(v||'').trim(); if (!s) return null;
      let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (m) { let [,mo,da,yr] = m; yr = yr.length===2 ? (Number(yr)<70?'20':'19')+yr : yr;
        const d = new Date(Number(yr), Number(mo)-1, Number(da)); return isNaN(d) ? null : d; }
      m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) { const d = new Date(Number(m[1]), Number(m[2])-1, Number(m[3])); return isNaN(d) ? null : d; }
      return null;
    };
    const weekKeys = Object.keys(hrsWeeks).sort();
    const byUnit = {};   // unitKey -> [{mon, reading}] sorted
    (active || []).forEach(r => {
      const base = (r.hrsBaseline != null && String(r.hrsBaseline).trim() !== '') ? Number(r.hrsBaseline) : null;
      if (base == null || isNaN(base)) return;
      const bd = parseBaselineDate(r.hrsBaselineDate) || new Date(2026, 0, 1);
      (byUnit[unitKey(r.unit)] = byUnit[unitKey(r.unit)] || []).push({ mon: iso(bd), reading: base });
    });
    weekKeys.forEach(mon => {
      const wk = hrsWeeks[mon] || {};
      Object.keys(wk).forEach(uk => {
        const rec = wk[uk];
        if (rec && rec.reading != null && rec.reading !== '') {
          (byUnit[uk] = byUnit[uk] || []).push({ mon, reading:Number(rec.reading) });
        }
      });
    });
    const out = {};   // unitKey -> { mtd, qtd, ytd }
    const now = new Date();
    const curMon = monthKey(now), curQtr = quarterKey(now), curYr = yearKey(now);
    Object.keys(byUnit).forEach(uk => {
      const list = byUnit[uk].sort((a,b) => a.mon.localeCompare(b.mon));
      let mtd = 0, qtd = 0, ytd = 0;
      for (let i=1; i<list.length; i++) {
        const d = new Date(list[i].mon+'T12:00:00');
        const delta = list[i].reading - list[i-1].reading;
        if (!(delta > 0)) continue;   // a reset/odometer-rollover/typo shouldn't count against the totals
        if (monthKey(d) === curMon) mtd += delta;
        if (quarterKey(d) === curQtr) qtd += delta;
        if (yearKey(d) === curYr) ytd += delta;
      }
      out[uk] = { mtd: Math.round(mtd*100)/100, qtd: Math.round(qtd*100)/100, ytd: Math.round(ytd*100)/100 };
    });
    return out;
  }, [hrsWeeks, active]);
  const setHrsReading = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), reading: val } }));
  const setHrsAssignedOverride = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), assignedOverride: val } }));
  // #FLEET_GEOTAB_HOURS_V1 — Bill: "any ones that it is not [pulling] will either be a bad unit,
  // unplugged or not installed." Bob's own triage note for a no-signal/no-device unit, so a blank
  // cell reads as "already looked into it, here's why" instead of just looking unfinished.
  const setHrsFlagReason = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), flagReason: val } }));
  // #FLEET_SERVICE_DUE_V1 — same in-progress-edit pattern as the readings above; saveHrsWeek splits
  // these off into fleet-service (persistent) instead of fleet-weekly-hrs (this week only).
  const setHrsServiced = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), serviced: val } }));
  const setHrsDos      = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), dos: val } }));
  const setHrsNote     = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), note: val } }));
  const setHrsRig      = (uk, val) => setHrsEdits(e => ({ ...e, [uk]: { ...(e[uk]||{}), rig: val } }));   // #FLEET_TRAILER_RIG_V1
  // #FLEET_HRS_MERGE_WRITE_V1 (2026-10-01) — every write to fleet-weekly-hrs used to send the WHOLE blob from this
  // tab's memory, so any tab left open (Bill's, Bob's, a phone) silently wiped whatever anyone else had stored
  // since it loaded — caught when an open tab put back 14 weeks of frozen Geotab stamps a minute after they were
  // cleared. With ~100 hour-metered units now typed in by hand every week, that would eat real entries.
  // Now: re-read the stored copy, change ONLY the cells this action touched, write that back. Writes from this
  // tab run one at a time (hrsWriteQ) so two of them can't interleave.
  const hrsDataRef = useRef(null); hrsDataRef.current = hrsData;
  const hrsWriteQ = useRef(Promise.resolve());
  const writeHrsCells = (patch, onlyIfBlank) => {
    const run = async () => {
      let fresh = null;
      try { fresh = await sg('fleet-weekly-hrs'); } catch {}
      const base = (fresh && fresh.weeks && typeof fresh.weeks === 'object') ? fresh : (hrsDataRef.current || { v:1, weeks:{} });
      const nextData = hrsApplyCells(base, patch, onlyIfBlank);
      await ss('fleet-weekly-hrs', nextData);
      setHrsData(nextData);
      return nextData;
    };
    const pr = hrsWriteQ.current.then(run, run);
    hrsWriteQ.current = pr.catch(() => {});
    return pr;
  };
  // #FLEET_SVC_WRITE_V1 (2026-10-05) — fleet-service was still written WHOLE from this tab's memory, the
  // same failure #FLEET_HRS_MERGE_WRITE_V1 fixed for readings: an open tab would put back every unit's
  // old Serviced / DOS over whatever a mechanic had just saved from the Maintenance tab. Now: re-read the
  // stored copy, change only the units touched, write that back, one write at a time. patch =
  // { unitKey: { serviced?, dos?, note? } } — a null value removes that field. logEntries get appended
  // to fleet-service-log, the unit's service history ("completed shows what was done, mileage & date").
  const hrsServiceRef = useRef(null); hrsServiceRef.current = hrsService;
  const svcWriteQ = useRef(Promise.resolve());
  const writeServiceUnits = (patch, logEntries) => {
    const run = async () => {
      let fresh = null;
      try { fresh = await sg('fleet-service'); } catch {}
      const baseUnits = (fresh && fresh.units && typeof fresh.units === 'object') ? fresh.units
        : ((hrsServiceRef.current && hrsServiceRef.current.units) || {});
      const units = { ...baseUnits };
      Object.keys(patch || {}).forEach(uk => {
        const cur = { ...(units[uk] || {}) };
        const p = patch[uk] || {};
        Object.keys(p).forEach(f => { if (p[f] === null || p[f] === '') delete cur[f]; else cur[f] = p[f]; });
        cur.updatedAt = new Date().toISOString();
        cur.updatedBy = upn || '';
        units[uk] = cur;
      });
      const next = { v:1, units };
      await ss('fleet-service', next);
      setHrsService(next);
      if (logEntries && logEntries.length) {
        let lg = null;
        try { lg = await sg('fleet-service-log'); } catch {}
        const entries = (lg && Array.isArray(lg.entries)) ? lg.entries : [];
        await ss('fleet-service-log', { v:1, entries: [...entries, ...logEntries] });
      }
      return next;
    };
    const pr = svcWriteQ.current.then(run, run);
    svcWriteQ.current = pr.catch(() => {});
    return pr;
  };
  // ✓ in the Serviced cell — "serviced today": Serviced = the unit's current reading, DOS = today.
  // Lands as an ordinary edit and saves on its own (#FLEET_HRS_AUTOSAVE_V1).
  const markServicedNow = (x) => {
    if (x.known == null) { alert(`${x.r.unit}: type this week's reading first — Serviced is the reading on the unit at the time of service.`); return; }
    setHrsEdits(e => ({ ...e, [x.uk]: { ...(e[x.uk]||{}), serviced: String(x.known), dos: svcTodayMDY() } }));
  };
  // ✓ Serviced on the Maintenance tab — saves straight away (that screen has no "Save week").
  const serviceFromBoard = (x, f) => writeServiceUnits(
    { [x.uk]: { serviced: Number(f.serviced), dos: svcNormDos(f.dos) } },
    [svcLogEntry({ uk: x.uk, unit: x.unit, serviced: f.serviced, dos: f.dos, prevServiced: x.serviced, prevDos: x.dos,
      work: f.work, mechanic: f.mechanic, shop: f.shop, via: 'maint', by: upn || '' })]);
  // #FLEET_HRS_AUTOSAVE_V1 — one writer for Weekly Inventory edits, used by the Save button, the autosave timers and
  // the leave-the-week flush. Reads everything it needs through refs so a timer or a cleanup can call it safely.
  // An edit object already on its way to the server (hrsFlying) is never sent twice, so two triggers landing
  // together cannot log the same service event two times.
  hrsEditsRef.current = hrsEdits;
  const hrsRowsRef = useRef([]); hrsRowsRef.current = hrsRows;
  // #FLEET_TRAILER_RIG_V1 — the rig list for a trailer's Rig Assigned picker
  const hrsRigOpts = useMemo(() => hrsRows.filter(x => x.bucket === 'rigs').map(x => ({ unit: String(x.r.unit), desc: x.r.desc || '' })), [hrsRows]);
  const hrsFlying = useRef(new WeakSet());
  const hrsPersist = async (edits, wkKey) => {
    const todo = {};
    Object.keys(edits || {}).forEach(uk => { const e = edits[uk]; if (e && !hrsFlying.current.has(e)) { todo[uk] = e; hrsFlying.current.add(e); } });
    if (!Object.keys(todo).length) return todo;
    try {
      const wk = { ...((((hrsDataRef.current || {}).weeks) || {})[wkKey] || {}) };
      const cellPatch = {};   // #FLEET_HRS_MERGE_WRITE_V1 — only the cells edited here get written
      // #FLEET_SERVICE_DUE_V1 — Serviced / DOS / Note are persistent facts, not this week's reading,
      // so they land in fleet-service instead of this week's fleet-weekly-hrs record.
      const svcSavedUnits = (hrsServiceRef.current && hrsServiceRef.current.units) || {};
      const svcPatch = {};   // #FLEET_SVC_WRITE_V1 — only the units edited here get written
      const svcLog = [];
      Object.keys(todo).forEach(uk => {
        const e = todo[uk];
        const touchesWeek = e.reading !== undefined || e.assignedOverride !== undefined || e.flagReason !== undefined;
        if (touchesWeek) {
          const cur = { ...(wk[uk] || {}) };
          if (e.reading !== undefined) {
            if (e.reading === '' || e.reading == null) delete cur.reading;
            else { cur.reading = Number(e.reading); cur.source = 'manual'; }   // a human typed it — always wins over a later auto-fill
          }
          if (e.assignedOverride !== undefined) cur.assignedOverride = e.assignedOverride;
          if (e.flagReason !== undefined) { if (e.flagReason) cur.flagReason = e.flagReason; else delete cur.flagReason; }
          cur.enteredAt = new Date().toISOString();
          cur.enteredBy = upn || '';
          if (Object.keys(cur).length) { wk[uk] = cur; cellPatch[uk] = cur; } else { delete wk[uk]; cellPatch[uk] = null; }
        }
        if (e.serviced === undefined && e.dos === undefined && e.note === undefined && e.rig === undefined) return;
        const was = svcSavedUnits[uk] || {};
        const pch = {};
        if (e.serviced !== undefined) pch.serviced = (e.serviced === '' || e.serviced == null) ? null : Number(e.serviced);
        if (e.dos !== undefined) pch.dos = e.dos ? svcNormDos(e.dos) : null;   // always stored MM/DD/YY
        if (e.note !== undefined) pch.note = e.note ? e.note : null;
        if (e.rig !== undefined) pch.rig = e.rig ? e.rig : null;   // #FLEET_TRAILER_RIG_V1
        svcPatch[uk] = pch;
        // A changed Serviced reading or DOS is a service event — one line in the unit's history.
        const wasServiced = (was.serviced != null && was.serviced !== '') ? Number(was.serviced) : null;
        const newServiced = pch.serviced !== undefined ? pch.serviced : wasServiced;
        const newDos = pch.dos !== undefined ? (pch.dos || '') : (was.dos || '');
        const changed = (pch.serviced !== undefined && pch.serviced !== wasServiced)
          || (pch.dos !== undefined && svcNormDos(newDos) !== svcNormDos(was.dos || ''));
        if (changed && newServiced != null) {
          const row = (hrsRowsRef.current || []).find(x => x.uk === uk);
          svcLog.push(svcLogEntry({ uk, unit: row ? row.r.unit : uk, serviced: newServiced, dos: newDos,
            prevServiced: wasServiced, prevDos: was.dos || '', via: 'weekly', by: upn || '' }));
        }
      });
      if (Object.keys(cellPatch).length) {
        await writeHrsCells({ [wkKey]: cellPatch });   // #FLEET_HRS_MERGE_WRITE_V1
        // read it back — a save that did not stick must not be reported as saved
        let back = null; try { back = await sg('fleet-weekly-hrs'); } catch {}
        const bw = back && back.weeks ? (back.weeks[wkKey] || {}) : null;
        const miss = !bw ? Object.keys(cellPatch) : Object.keys(cellPatch).filter(uk => {
          const want = cellPatch[uk], got = bw[uk];
          return want == null ? got != null : (!got || got.reading !== want.reading || (got.assignedOverride || '') !== (want.assignedOverride || ''));
        });
        if (miss.length) throw new Error('the save did not go through — check the connection');
      }
      if (Object.keys(svcPatch).length) await writeServiceUnits(svcPatch, svcLog);
      return todo;
    } catch (err) {
      Object.keys(todo).forEach(uk => { try { hrsFlying.current.delete(todo[uk]); } catch {} });   // let a retry send them again
      throw err;
    }
  };
  hrsPersistRef.current = hrsPersist;
  const hrsSavingRef = useRef(false), hrsAgainRef = useRef(false);
  const hrsQuickT = useRef(null), hrsIdleT = useRef(null);
  const hrsSaveRef = useRef(null);
  const hrsKick = (ms) => {
    if (hrsQuickT.current) clearTimeout(hrsQuickT.current);
    hrsQuickT.current = setTimeout(() => { hrsQuickT.current = null; if (hrsSaveRef.current) hrsSaveRef.current({ auto: true }); }, ms);
  };
  // opts.auto = a timer called it (quiet; the row still being typed in waits). opts.idle = nothing has changed
  // for a few seconds, so that row goes too. No opts = the Save button: everything, now.
  const saveHrsWeek = async (opts) => {
    const auto = !!(opts && opts.auto), idle = !!(opts && opts.idle);
    const all = hrsEditsRef.current || {};
    if (!Object.keys(all).length) return;
    if (hrsSavingRef.current) { hrsAgainRef.current = true; return; }
    let typingUk = '';
    if (auto && !idle && typeof document !== 'undefined') {
      const el = document.activeElement;
      const tr = el && /^(INPUT|SELECT|TEXTAREA)$/.test(el.tagName || '') && el.closest ? el.closest('tr[data-uk]') : null;
      typingUk = tr ? (tr.getAttribute('data-uk') || '') : '';
    }
    const pick = {};
    Object.keys(all).forEach(uk => {
      if (uk === typingUk) return;
      const e = all[uk];
      if (auto && e.dos && !svcParseDate(e.dos)) return;   // a half-typed date waits for the rest of it
      pick[uk] = e;
    });
    if (!Object.keys(pick).length) return;
    hrsSavingRef.current = true;
    if (!auto) setHrsBusy('saving…');
    try {
      const done = await hrsPersist(pick, hrsWkStart);
      // only what was just saved leaves the list — anything typed while the save was in the air stays put
      setHrsEdits(cur => { const next = { ...cur }; Object.keys(done).forEach(uk => { if (cur[uk] === done[uk]) delete next[uk]; }); return next; });
      setHrsSavedAt(new Date().toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' }));
      setHrsAutoErr('');
    } catch (e) {
      const m = String((e && e.message) || e);
      if (auto) { setHrsAutoErr(m); hrsKick(8000); } else alert('Could not save: ' + m);
    }
    hrsSavingRef.current = false;
    if (!auto) setHrsBusy('');
    if (hrsAgainRef.current) { hrsAgainRef.current = false; hrsKick(500); }
  };
  hrsSaveRef.current = saveHrsWeek;
  const hrsRowBlur = () => { if (Object.keys(hrsEditsRef.current || {}).length) hrsKick(350); };
  useEffect(() => {
    if (!editable || !Object.keys(hrsEdits).length) return undefined;
    hrsKick(900);
    if (hrsIdleT.current) clearTimeout(hrsIdleT.current);
    hrsIdleT.current = setTimeout(() => { hrsIdleT.current = null; if (hrsSaveRef.current) hrsSaveRef.current({ auto: true, idle: true }); }, 6000);
    return () => { if (hrsIdleT.current) { clearTimeout(hrsIdleT.current); hrsIdleT.current = null; } };
  }, [hrsEdits]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (hrsQuickT.current) clearTimeout(hrsQuickT.current); if (hrsIdleT.current) clearTimeout(hrsIdleT.current); }, []);
  // closing the browser tab with something not yet saved (mid-typing, or a failed save) asks first
  useEffect(() => {
    const onBeforeUnload = (e) => { if (Object.keys(hrsEditsRef.current || {}).length) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
  // #FLEET_GEOTAB_HOURS_V1 — quietly writes this week's Geotab-sourced readings into
  // fleet-weekly-hrs the moment they're available, same as if Bob had typed them — Bob only has to
  // touch a unit by hand when Geotab genuinely has nothing for it. Never overwrites a reading that's
  // already stored for the week (manual OR a prior auto-fill) and never fights hrsEdits — an edit in
  // progress is left alone until it's saved or the week changes. Runs once per week per data-load
  // (hrsAutoFillRef), not on every render, so it can't loop against its own write.
  // #FLEET_GEOTAB_HISTORY_V1 (2026-09-22) — the live hrsGeotab feed only ever knows "right now," so
  // it's only trustworthy for the week that's ACTUALLY current. Bill caught this: opening a past
  // week that had never been auto-filled stamped it with whatever Geotab said on the day someone
  // happened to open it, so two different weeks kept landing on the same number (Start == End,
  // Weekly Δ stuck at 0). Fix: any week that ISN'T the current one pulls from the historical pull's
  // per-Monday snapshot (hrsGeotabHistory) instead of the live feed — a real dated value, or nothing
  // if the history pull hasn't covered that unit/week, never a guess.
  useEffect(() => {
    if (!active || !hrsData || !hrsGeotab || !hrsOem) return;   // #FLEET_OEM_HOURS_V1 — wait for the factory feeds too
    if (hrsAutoFillRef.current.wk === hrsWkStart && hrsAutoFillRef.current.done) return;
    const isCurrentWeek = hrsWkStart === iso(mondayOf(new Date()));
    // A past week's fill depends on the historical pull having loaded. If it hasn't yet, wait rather
    // than guess with "now" — and don't mark this week done yet either, otherwise the ref guard
    // above would permanently block a retry once hrsGeotabHistory actually finishes loading.
    if (!isCurrentWeek && !hrsGeotabHistory) return;
    const thisWk = hrsWeeks[hrsWkStart] || {};
    const additions = {};
    const histByVin = {};
    if (!isCurrentWeek) (hrsGeotabHistory.devices || []).forEach(d => { const v = vinClean(d.vin); if (v) histByVin[v] = d; });
    active.forEach(r => {
      const uk = unitKey(r.unit);
      if (thisWk[uk] && thisWk[uk].reading != null) return;   // something's already on file for this unit this week
      if (hrsEdits[uk] && hrsEdits[uk].reading !== undefined) return;   // don't clobber an in-progress edit
      const bucket = bucketOf(r.cat, hrsBucketMap);
      const metric = HRS_BUCKET_METRIC[bucket];
      if (!metric) return;
      // #FLEET_OEM_HOURS_V1 — a factory-feed reading for THIS week (rigs, Kobelco excavators) goes in first.
      const od = metric === 'hrs' ? oemForUnit(oemByVin, r.vin) : null;
      const ov = od ? oemWeekHours(od, hrsWkStart) : null;
      if (ov != null) { additions[uk] = { reading: ov, source:'oem', feed: od.provider, enteredAt: new Date().toISOString(), enteredBy:'oem-sync' }; return; }
      if (bucket === 'rigs') return;   // rigs never carry a Geotab tracker
      let val = null;
      if (isCurrentWeek) {
        const device = geotabByVin[vinClean(r.vin)];
        val = device ? (metric === 'mi' ? device.odometerMiles : device.engineHours) : null;
      } else {
        const hdev = histByVin[vinClean(r.vin)];
        val = hdev ? (metric === 'mi' ? (hdev.weeklyOdometerMiles || {})[hrsWkStart] : (hdev.weeklyEngineHours || {})[hrsWkStart]) : null;
      }
      if (val == null) return;
      // source stays 'geotab' either way (the Source column only knows 'geotab'/'manual') —
      // enteredBy carries which path actually filled it, for anyone digging through raw data later.
      additions[uk] = { reading: val, source:'geotab', enteredAt: new Date().toISOString(), enteredBy: isCurrentWeek ? 'geotab-sync' : 'geotab-history' };
    });
    hrsAutoFillRef.current = { wk: hrsWkStart, done: true };
    if (!Object.keys(additions).length) return;
    (async () => {
      try {
        const cells = {};
        Object.keys(additions).forEach(uk => { cells[uk] = { ...(thisWk[uk]||{}), ...additions[uk] }; });
        await writeHrsCells({ [hrsWkStart]: cells }, true);   // #FLEET_HRS_MERGE_WRITE_V1
      } catch { /* best-effort — Bob can still enter these by hand if the auto-fill write fails */ }
    })();
  }, [active, hrsData, hrsGeotab, hrsOem, hrsGeotabHistory, hrsWkStart, hrsBucketMap, geotabByVin, oemByVin]);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveHrsBucketMap = async (nextMap) => {
    setHrsBucketMap(nextMap);
    try { await ss('fleet-hrs-buckets', { v:1, map: nextMap }); }
    catch (e) { alert('Could not save bucket map: ' + (e.message||e)); }
  };
  useEffect(() => {
    const el = hrsHdrRef.current;
    if (!el) return;
    const measure = () => setHrsHdrH(el.offsetHeight || 33);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [view]);
  // Opens a print-ready window — same "write the HTML, let the browser's print dialog pick the
  // paper" pattern as the RIG Schedule print (App.js), sized for 11x17/Tabloid landscape. Repair
  // Cost is intentionally shown as "—" — Bill: that column is the PO-system pivot, "an entire few
  // sessions on its own," not wired yet.
  // #FLEET_HRSPRINT_VIEWER_V1 — always-current copy of what the sheet is built from. The click used to
  // capture whatever had loaded AT THAT MOMENT (and waited only on repair $), so a click before the
  // Master / weekly readings / Geotab / assignments landed printed blanks even though it "waited".
  const hrsPrintLive = useRef({});
  hrsPrintLive.current = { hrsRows, hrsWeeks, hrsWkStart,
    ready: active !== null && hrsData !== null && hrsGeotab !== null && hrsLoadFlags.current.buckets && hrsLoadFlags.current.fri };
  const printHrsSummary = async () => {
    // Viewer opens synchronously (pop-up safe). Desktop = its own window; phone/tablet or a blocked pop-up = in-app viewer.
    const viewer = (hrsViewerIsMobile() ? null : hrsViewerWindow('Fleet HRS Summary')) || hrsViewerOverlay('Fleet HRS Summary');
    // #FLEET_HRS_REPAIR_WAIT_V1 / #FLEET_HRS_REPAIR_LIFE_V1 — wait for PO repair $ (all years, read fresh) …
    let poRepair = {}, repairMissed = false;
    try { const pr = await loadPoRepair(); poRepair = pr.map || {}; repairMissed = !!pr.missed; } catch { repairMissed = true; }
    // … and for every other load the sheet reads (up to 20s), then build from the freshest rows.
    for (let i = 0; i < 100 && !hrsPrintLive.current.ready; i++) await new Promise(res => setTimeout(res, 200));
    const loadWarn = !hrsPrintLive.current.ready ? '⚠ Some fleet data was still loading — close and reopen in a moment.'
      : repairMissed ? '⚠ Some PO history did not load — repair $ may be low. Close and reopen.' : '';
    const { hrsRows, hrsWeeks, hrsWkStart } = hrsPrintLive.current;
    const esc = (s) => String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;');
    // #FLEET_HRSPRINT_3COL_V1 (2026-09-22) — Bill: "Lots of dead space... add another row making 3
    // sections with emphasis on RIGS and trucks... remove decimal in miles/hours... fix the $ position
    // so its clean and even." Whole-number readings, accounting-style $ ($ pinned left, digits right,
    // tabular figures), descriptions truncate with an ellipsis, last-known reading (grey italic *)
    // when this week's isn't in yet, true 17x11 page size.
    // #FLEET_HRSPRINT_FLOW_V1 (2026-09-22) — Bill: "We are old dude and need a bigger Font... Trucks
    // can start in 2nd row and roll over into 3rd row to allow 18 wheelers to land underneath so these
    // two groups share [the] 3 column[s]." Bigger type (11px, was 8.5px), and the sheet is now POURED
    // into columns by a row budget instead of fixed stacks:
    //   Page 1 — col 1: RIGS, Cleaners, Mixing · cols 2–3: Trucks flowing into 18 Wheeler
    //   Page 2 — VACs | Excavators | Forklifts + Other + Rentals/RPO (#FLEET_HRSPRINT_RENTALCOL_V1), Trailers last.
    // A group that runs out of room continues at the top of the next column with a "(cont.)" header;
    // anything that overflows page 1 carries onto the next page automatically, so the sheet never gets
    // clipped at the bottom as the fleet grows. ROW_PX / HEAD_PX / PAGE_PX match the CSS below — change
    // them together.
    // #FLEET_HRSPRINT_AUTOFIT_V1 (2026-09-22) — Bill: "We have a little more room on Page 1 to perhaps
    // increase the font a little and tighten up some dead space." Row height (and with it every font
    // size and column width, which are in em) is no longer fixed: it's the LARGEST size, in half-pixel
    // steps from 22px down, at which RIGS/Cleaners/Mixing fit column 1 and Trucks + 18 Wheeler fit
    // columns 2–3 of page 1. So page 1 always fills the sheet with the biggest type the real unit
    // counts allow — adding or retiring units re-fits itself, no more hand-tuning. The same size is
    // used on every page so the whole packet reads as one document.
    // #FLEET_HRSPRINT_RENTALCOL_V1 (2026-10-01) — Bill: "Could we squish RENTALS in the area to maximize
    // page and make it look cooler? Maybe ditch the TYP column." Rentals / RPO no longer gets a page of
    // its own: it pours into column 3 of page 2, under Forklifts + Other, on the same column grid as the
    // tables above it (Unit / Monthly / Weekly / Paid line up with Unit / Hours / Repair / Purchase).
    // Type column is gone (an RPO unit carries a small RPO tag instead); vendor + start date ride on a
    // second line under the description so nothing has to be truncated to fit a third of the sheet.
    // Rental rows are RENT_MUL rows tall — the pour engine budgets for that, so a long rental list
    // rolls to the next column / page like any other group.
    let ROW_PX = 16, HEAD_PX = 41;
    const PAGE_PX = 1000, TITLE_PX = 28, MIN_ROWS = 4;
    const headFor = (r) => Math.ceil((r + 4) + r + 4 + 1);   // group bar + column-header row + table gap (+1 slack)
    const isRentalRow = (x) => !!String(x.r.rentalType||'').trim();
    const EMPH = { rigs:true, trucks:true };
    const priorWeeks = Object.keys(hrsWeeks||{}).filter(k => k < hrsWkStart).sort().reverse();
    const lastKnown = (uk) => {
      for (const k of priorWeeks) { const c = hrsWeeks[k] && hrsWeeks[k][uk]; if (c && c.reading != null && c.reading !== '') return { v: Number(c.reading), manual: !isApiSrc(c.source) }; }
      return null;
    };
    // #FLEET_HRS_MANUAL_MARK_V1 (2026-10-01) — Bill: "Need to know which ones are API and ones that are Manual."
    // A reading typed in (or loaded from the weekly sheet) prints bold orange with an M pinned at the left of
    // the cell (same spot the $ sits, so the digits still line up, and it survives a black-and-white print);
    // an API (Geotab) reading stays plain black. Orange, not red — red means a problem everywhere else in DOPE.
    let usedPrior = false, usedManual = false;
    const mMark = '<span class="mk">M</span>';
    const readingCell = (x) => {
      if (x.reading != null && !isNaN(Number(x.reading))) {
        const man = !isApiSrc(x.source); if (man) usedManual = true;   // #FLEET_OEM_HOURS_V1 — a factory-feed reading is API, not manual
        return `<td class="r num${man ? ' man' : ''}">${man ? mMark : ''}${Math.round(Number(x.reading)).toLocaleString()}</td>`;
      }
      const lk = lastKnown(x.uk);
      if (lk && !isNaN(lk.v)) { usedPrior = true; if (lk.manual) usedManual = true; return `<td class="r num prior">${lk.manual ? mMark : ''}${Math.round(lk.v).toLocaleString()}*</td>`; }
      return `<td class="r dim">—</td>`;
    };
    const moneyCell = (v, cls) => { const n = Math.round(num(v)); return n ? `<td class="m num${cls ? ' ' + cls : ''}"><span>$</span>${n.toLocaleString()}</td>` : `<td class="r dim${cls ? ' ' + cls : ''}">—</td>`; };
    const shortMoney = (n) => n >= 1e6 ? '$' + (n/1e6).toFixed(1) + 'M' : n >= 1e3 ? '$' + Math.round(n/1e3).toLocaleString() + 'K' : '$' + Math.round(n);
    const byUnit = (a,b) => String(a.r.unit).localeCompare(String(b.r.unit));
    const groups = {};
    HRS_BUCKET_ORDER.forEach(bk => {
      const rows = hrsRows.filter(x => x.bucket === bk && !isRentalRow(x))
        .sort((a,b)=> (bk==='rigs' ? rigSort(a.r, b.r) : 0) || byUnit(a,b));   // #FLEET_RIG_MODEL_ORDER_V1
      if (rows.length) groups[bk] = { bk, label: HRS_BUCKET_LABEL[bk], metric: HRS_BUCKET_METRIC[bk], rows, noReading: bk === 'trailers' };
    });
    // Pour groups into columns of capPx height. Returns an array of columns, each a list of chunks
    // { g, rows, cont }. newCol: every group starts its own column (page 2 layout).
    const pour = (list, capFirst, capRest, newCol) => {
      const cols = [[]]; let used = 0;
      const cap = () => cols.length === 1 ? capFirst : capRest;
      const nextCol = () => { cols.push([]); used = 0; };
      list.filter(Boolean).forEach(g => {
        if (newCol && cols[cols.length-1].length) nextCol();
        const rowPx = ROW_PX * (g.rowMul || 1);   // #FLEET_HRSPRINT_RENTALCOL_V1 — rental rows are two lines tall
        let i = 0, cont = false;
        while (i < g.rows.length) {
          let fit = Math.floor((cap() - used - HEAD_PX) / rowPx);
          const left = g.rows.length - i;
          if (fit < Math.min(MIN_ROWS, left)) { if (cols[cols.length-1].length) { nextCol(); continue; } fit = MIN_ROWS; }
          const take = Math.min(fit, left);
          cols[cols.length-1].push({ g, rows: g.rows.slice(i, i + take), cont });
          used += HEAD_PX + take * rowPx; i += take; cont = true;
        }
      });
      return cols.filter(c => c.length);
    };
    const P1 = PAGE_PX - TITLE_PX;
    const LEFT1 = ['rigs','cleaners','mixing'].map(k => groups[k]), RIGHT1 = ['trucks','18wheeler'].map(k => groups[k]);
    for (let r = 22; r >= 14; r -= 0.5) {
      ROW_PX = r; HEAD_PX = headFor(r);
      if (pour(LEFT1, P1, P1).length <= 1 && pour(RIGHT1, P1, P1).length <= 2) break;
    }
    const FS = +(ROW_PX * 0.68).toFixed(2);   // body font px for the fitted row height
    const left  = pour(LEFT1, P1, PAGE_PX);
    const right = pour(RIGHT1, P1, P1);
    const page1 = [left[0], right[0], right[1]].filter(Boolean);
    // Everything that didn't fit on page 1 (plus page-2 buckets, plus any future bucket not named
    // here) flows onward, 3 columns per page.
    const named = new Set(['rigs','cleaners','mixing','forklifts','trucks','18wheeler','vacs','excavators','other','trailers']);
    const extra = Object.keys(groups).filter(k => !named.has(k)).map(k => groups[k]);
    const RENT_MUL = 1.8;
    const rentalRows = hrsRows.filter(isRentalRow).sort(byUnit);
    const rentalGroup = rentalRows.length ? { bk:'rentals', label:'Rentals / RPO', rows: rentalRows, rental:true, rowMul: RENT_MUL } : null;
    const onward = [
      ...left.slice(1), ...right.slice(2),
      ...pour([groups.vacs], PAGE_PX, PAGE_PX), ...pour([groups.excavators], PAGE_PX, PAGE_PX),
      ...pour([groups.forklifts, groups.other, ...extra, rentalGroup], PAGE_PX, PAGE_PX),
    ];
    const rentalTable = (c) => {
      const g = c.g;
      const mo = g.rows.reduce((s,x) => s + num(x.r.monthlyRate), 0), paidTot = g.rows.reduce((s,x) => s + num(rentalPaidToDate(x.r)), 0);
      return `
      <table class="rent">
        <colgroup><col><col style="width:5.8em"><col style="width:6.2em"><col style="width:6.4em"><col style="width:7.2em"></colgroup>
        <tr class="grp"><td colspan="5">${esc(g.label)}${c.cont ? ' <span class="n">(cont.)</span>' : ` <span class="n">${g.rows.length}</span><span class="tot">${(mo ? `${shortMoney(mo)} / mo` : '') + (mo && paidTot ? ' · ' : '') + (paidTot ? `Paid ${shortMoney(paidTot)}` : '')}</span>`}</td></tr>
        <tr class="hd"><th>Description · Vendor · Start</th><th>Unit</th><th class="r">Monthly</th><th class="r">Weekly</th><th class="r paid">Paid to Date</th></tr>
        ${c.rows.map(x => { const r = x.r, paid = rentalPaidToDate(r), start = fmtDate(r.rentalStart), rpo = /rpo/i.test(String(r.rentalType||''));
          return `<tr><td class="d2"><div class="l1">${esc(r.desc)}${rpo ? ' <span class="tag">RPO</span>' : ''}</div><div class="l2">${esc(String(r.rentalVendor||'').trim() || 'No vendor')} · ${start ? 'since ' + esc(start) : '<i>no start date</i>'}</div></td><td class="u">${esc(r.unit)}</td>${moneyCell(r.monthlyRate)}${moneyCell(r.weeklyRate)}${paid != null ? moneyCell(paid, 'paid') : '<td class="r dim paid">—</td>'}</tr>`; }).join('')}
      </table>`;
    };
    const chunkTable = (c) => {
      if (c.g.rental) return rentalTable(c);
      const g = c.g, withAssigned = g.bk === 'trucks', noRd = g.noReading;
      const total = g.rows.reduce((s,x) => s + num(x.r.purAmt), 0);
      const span = 4 + (withAssigned ? 1 : 0) + (noRd ? 0 : 1);
      return `
      <table class="${EMPH[g.bk] ? 'emph' : ''}">
        <colgroup><col><col style="width:5.8em">${withAssigned ? '<col style="width:7.4em">' : ''}${noRd ? '' : '<col style="width:6.2em">'}<col style="width:6.4em"><col style="width:7.2em"></colgroup>
        <tr class="grp"><td colspan="${span}">${esc(g.label)}${c.cont ? ' <span class="n">(cont.)</span>' : ` <span class="n">${g.rows.length}</span><span class="tot">${(() => { const rp = g.rows.reduce((s,x) => s + num(poRepair[unitKey(x.r.unit)]), 0); return (rp ? `Repair ${shortMoney(rp)} · ` : '') + (total ? shortMoney(total) : ''); })()}</span>`}</td></tr>
        <tr class="hd"><th>Description</th><th>Unit</th>${withAssigned ? '<th>Assigned</th>' : ''}${noRd ? '' : `<th class="r">${hrsMetricLabel(g.metric)}</th>`}<th class="r">Repair</th><th class="r pur">Purchase</th></tr>
        ${c.rows.map(x => `<tr><td class="d">${esc(x.r.desc)}</td><td class="u">${esc(x.r.unit)}</td>${withAssigned ? `<td class="d">${esc(x.assigned||'')}</td>` : ''}${noRd ? '' : readingCell(x)}${moneyCell(poRepair[unitKey(x.r.unit)])}${moneyCell(x.r.purAmt, 'pur')}</tr>`).join('')}
      </table>`;
    };
    const pageHtml = (cols) => `<div class="page"><div class="cols">${cols.map(col => `<div>${col.map(chunkTable).join('')}</div>`).join('')}${'<div></div>'.repeat(Math.max(0, 3 - cols.length))}</div></div>`;
    const pagesOf = (cols) => { const out = []; for (let i = 0; i < cols.length; i += 3) out.push(pageHtml(cols.slice(i, i + 3))); return out.join(''); };
    const page1Html = pageHtml(page1);
    const onwardHtml = pagesOf(onward);
    // Trailers: no reading, no schedule — poured 3-across, printed dead last.
    const trailerHtml = groups.trailers ? pagesOf(pour([groups.trailers], PAGE_PX, PAGE_PX)) : '';
    const html = `<html><head><title>Fleet HRS Summary — ${hrsWeekOf(hrsWkStart).label}</title><style>
      *{-webkit-print-color-adjust:exact;print-color-adjust:exact;box-sizing:border-box}
      body{font-family:Arial,Helvetica,sans-serif;margin:0;color:#111}
      .top{display:flex;align-items:baseline;gap:14px;height:${TITLE_PX}px}
      h1{font-size:19px;margin:0}
      .sub{font-size:12px;color:#555;white-space:nowrap;overflow:hidden}
      .page{break-after:page;page-break-after:always}
      .page:last-child{break-after:auto;page-break-after:auto}
      table{border-collapse:collapse;width:100%;table-layout:fixed;font-size:${FS}px;margin-bottom:4px}
      td,th{border:1px solid #999;padding:0 .4em;height:${ROW_PX}px;line-height:${ROW_PX-2}px}
      td.d{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      td.u{font-weight:700;white-space:nowrap}
      .num{font-variant-numeric:tabular-nums;white-space:nowrap}
      tr.grp td{background:#333;color:#fff;font-weight:800;font-size:${+(ROW_PX*0.8).toFixed(2)}px;height:${ROW_PX+4}px}
      .n{font-weight:400;opacity:.8;margin-left:6px}
      .tot{float:right;font-weight:700}
      tr.hd th{background:#e6e6e6;text-align:left;font-size:${+(ROW_PX*0.58).toFixed(2)}px;text-transform:uppercase;letter-spacing:.2px}
      tr:nth-child(even) td{background:#f4f4f4}
      td.r,th.r{text-align:right}
      td.m{text-align:right}
      td.m span{float:left;color:#666}
      /* #FLEET_HRS_PURCHASE_GREY_V1 — Bill: purchase price in a different, easy-to-read tone so Repair YTD stands out */
      td.pur{color:#4a4a4a;background:#ececec}
      td.pur span{color:#7a7a7a}
      th.pur{background:#dcdcdc}
      td.dim{color:#aaa}
      td.prior{color:#777;font-style:italic}
      td.man{font-weight:700;color:#C8651B}
      .mk{float:left;font-size:.8em;font-weight:800;font-style:normal}
      td.prior .mk{color:#999}
      table.emph{outline:2px solid #111;outline-offset:-1px}
      table.emph tr.grp td{background:#C8651B;font-size:${+(ROW_PX*0.9).toFixed(2)}px}
      .cols{display:flex;gap:8px;align-items:flex-start}
      .cols>div{flex:1;min-width:0}
      table.rent tr.grp td{background:#5A3E00}
      table.rent td{height:${+(ROW_PX*RENT_MUL).toFixed(2)}px}
      table.rent tr.grp td{height:${ROW_PX+4}px}
      table.rent td.d2{padding-top:0;padding-bottom:0;line-height:1;overflow:hidden}
      table.rent .l1,table.rent .l2{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      table.rent .l1{font-weight:700;line-height:${+(ROW_PX*0.92).toFixed(2)}px}
      table.rent .l2{font-size:${+(ROW_PX*0.62).toFixed(2)}px;line-height:${+(ROW_PX*0.8).toFixed(2)}px;color:#555;text-transform:uppercase;letter-spacing:.2px}
      table.rent .l2 i{color:#999;text-transform:none}
      table.rent .tag{font-size:.72em;font-weight:800;color:#fff;background:#5A3E00;border-radius:3px;padding:0 .35em;vertical-align:.1em}
      table.rent th{white-space:nowrap;overflow:hidden}
      table.rent td.paid{background:#F3E9D2;color:#3d2a00;font-weight:700}
      table.rent td.paid span{color:#8a6d2f;font-weight:400}
      table.rent td.paid.dim{color:#b9a77f;font-weight:400}
      table.rent th.paid{background:#E2D2AC}
      .vbar{position:sticky;top:0;z-index:9;display:flex;align-items:center;gap:10px;background:#111;color:#fff;font:700 14px Arial;padding:8px 12px;margin:-8px -8px 8px}
      @media print{@page{size:17in 11in;margin:5mm}.vbar{display:none!important}}
      @media screen{body{margin:8px}}
    </style></head><body>
      <div class="top"><h1>DAKOTA DIRECTIONAL DRILLING — Fleet HRS Summary</h1>
      <div class="sub">${hrsWeekOf(hrsWkStart).label} (read Mon ${hrsWeekOf(hrsWkStart).read}) · Repair = PO actuals 2021–${new Date().getFullYear()}, all PO users${usedManual ? ' · <b style="color:#C8651B">M</b> = entered by hand, plain = API (Geotab)' : ''}${usedPrior ? ' · <i>* = last recorded reading</i>' : ''}</div></div>
      ${page1Html}${onwardHtml}${trailerHtml}
    </body></html>`;
    try { viewer.show(html, loadWarn); } catch { /* the viewer was closed while it was loading */ }
  };

  // #FLEET_CAT_V1 — Bill: "lots more categories that will feed this sheet, near 26 total. only 6
  // displayed here." The master actually holds 63 DISTINCT category strings across 296 units, and
  // they are free text, so the list grew by typing: Trailer-VAC (21) and Trailer-Vac (1),
  // Truck 1T (35) and Truck-1T (8), Truck-VAC (12) and Truck-Vac (3) are the same category twice.
  //
  // Getting from 63 spellings down to Bill's ~26 real categories is a curation job only he and Bob
  // can do — I am not guessing which of "EXC" / "Excavator" / "EXC-Mini" are meant to merge. So this
  // gives him the tool instead of a guess: every category with its unit count, a rename, and a
  // merge-into. Case/punctuation collisions are called out because those are unambiguous.
  const catStats = useMemo(() => {
    const m = {};
    (active||[]).forEach(r => {
      const c = String(r.cat||'').trim(); if (!c) return;
      (m[c] = m[c] || { cat:c, n:0 }).n += 1;
    });
    const list = Object.values(m).sort((a,b) => b.n - a.n || a.cat.localeCompare(b.cat));
    const norm = (c) => c.toUpperCase().replace(/[^A-Z0-9]/g,'');
    const groups = {};
    list.forEach(x => { (groups[norm(x.cat)] = groups[norm(x.cat)] || []).push(x); });
    const collisions = Object.values(groups).filter(g => g.length > 1);
    return { list, collisions, blank: (active||[]).filter(r => !String(r.cat||'').trim()).length };
  }, [active]);

  // #FLEET_UNITDUPE_V1 — Bill: "this is repeated and unable to delete." Two active rows whose unit
  // numbers collapse to the same unitKey (hyphen/case/trailing-'*' stripped — "22-5090" and
  // "22-5090*" both key to 225090; a flat copy-paste duplicate keys the same too) are the same
  // physical machine typed twice. They already read/write the SAME day-cell (dayRec is keyed by
  // unitKey), so nothing is lost by collapsing them — Daily now shows one row per key
  // (#FLEET_UNITDUPE_V1 in dayRows below) and this panel is where the extra gets cleared out.
  // Retire is the only sanctioned removal (#FLEET_NODELETE_V1 — Bill: "I can't think of any reason
  // why we need to ever delete"), so the action here is Retire, not delete.
  const unitStats = useMemo(() => {
    const groups = {};
    (active||[]).forEach(r => {
      const k = unitKey(r.unit); if (!k) return;
      (groups[k] = groups[k] || []).push(r);
    });
    return { collisions: Object.values(groups).filter(g => g.length > 1) };
  }, [active]);

  // Rewrite one category onto another across every active row. Retired rows are left alone on
  // purpose — they are a closed record, and renaming history is how you lose the ability to
  // reconcile last year's numbers.
  const mergeCat = async (from, to) => {
    const n = (active||[]).filter(r => String(r.cat||'').trim() === from).length;
    if (!window.confirm(`Move ${n} unit${n===1?'':'s'} from "${from}" to "${to}"?\n\n"${from}" will no longer exist on the active master. Retired units keep their original category.`)) return;
    setBusy('Merging…');
    await persistMaster((active||[]).map(r => String(r.cat||'').trim() === from ? { ...r, cat:to } : r), retired);
    setBusy('');
  };

  // #FLEET_GATE_V1 — Bill's rule: a unit is on a job (CALLED IN or IN PROGRESS only), or Idle /
  // In repairs / Overhead. A COMPLETE job can still be charged, but only with an override, and past
  // a week only Bob or Bill can authorise it. So the index has to carry every status plus the
  // completion date, not just the live jobs.
  const jobIndex = useMemo(() => {
    const list = Array.isArray(jobs) ? jobs : [];
    const m = {};
    list.forEach(j => {
      const d = stripCP(j.displayDuc || j.duc || '');   // #FLEET_CPSTRIP_V1
      if (!d || m[d]) return;
      m[d] = { duc:d, cust:String(j.cust||j.customer||'').trim(), city:String(j.city||'').trim(),
               stat:String(j.stat||j.status||'').trim(), com:j.act_com, pm:String(j.pm||'').trim() };
    });
    return m;
  }, [jobs]);
  // Jobs offered in the picker — only the two statuses Bill allows outright.
  const liveJobs = useMemo(() =>
    Object.values(jobIndex)
      .filter(j => ['called in','in progress'].includes(j.stat.toLowerCase()))
      .sort((a,b)=>a.duc.localeCompare(b.duc)), [jobIndex]);
  // #FLEET_AUTOCOMPLETE_V1 — built once per liveJobs/sups change, reused by every Daily row's picker.
  const ducOptions = useMemo(() => [
    ...OVERHEAD.map(([c,l]) => ({ value:c, label:c, sub:l })),
    ...liveJobs.map(j => ({ value:j.duc, label:j.duc, sub:[j.cust,j.city].filter(Boolean).join(' · ') })),
  ], [liveJobs]);
  const supOptions = useMemo(() => sups.map(s => ({ value:s, label:s })), [sups]);

  const dayISO = iso(addDays(new Date(wkStart+'T12:00:00'), dayIx));
  const dayRec = useMemo(() => week[dayISO] || {}, [week, dayISO]);
  // #FLEET_LOCATIONS_V1 — Serial / VIN → where the unit is now, and where it was on the day being looked at.
  const locToday = iso(new Date());
  const locLive = useMemo(() => locIndex(locNow), [locNow]);
  const locDay = useMemo(() => locDayIndex(locDays, dayISO), [locDays, dayISO]);
  // #TILE_LOC_V1 (2026-10-13) — unit → where it is now, for the Idle / Down for repair tiles. Same join as the Daily GPS column
  // (Equipment Master VIN → latest fix). Units with no tracker (most trailers, reels) return null and show nothing.
  const _vinByUnit = useMemo(() => { const m = {}; (active || []).forEach(r => { const k = r && unitKey(r.unit); if (k && r.vin && !m[k]) m[k] = r.vin; }); return m; }, [active]);
  const tileLocOf = useMemo(() => (unit) => {
    const v = _vinByUnit[unitKey(unit)];
    return v ? locCell(v, locToday, locToday, locLive, null) : null;
  }, [_vinByUnit, locLive, locToday]);
  // #FLEET_DAILYTRUTH_V3 — re-read the Daily Log whenever the selected day changes (and on mount).
  // V1 read it once per mount, so a board left open overnight kept yesterday's answers: the foremen
  // would file their Dailies and the ✗/? marks would sit there stale, which is exactly how a check
  // mark loses its credibility. Changing day is also the natural moment the answer should move.
  useEffect(() => { let alive = true; (async () => {
    try { const dl = await sg('daily-logs'); if (alive) setDlogs(dl && typeof dl === 'object' && !Array.isArray(dl) ? dl : {}); }
    catch { if (alive) setDlogs({}); }
  })(); return () => { alive = false; }; }, [sg, dayISO]);

  const rate = (r) => num(r && r.ducDay);
  // #FLEET_RENTAL_V2 — Bill, 2026-08-21: "Monthly rate can auto calculate in rental template
  // (Monthly x 12 Months divided by 52 weeks divided by 5 days for daily rate)". Monthly Charge is
  // the one number Bill types; Weekly and the Daily rate (written into ducDay — the SAME field every
  // other cost consumer already reads: Daily's cost column, the weekly rollup, PM Dashboard's
  // equipment-cost roll-up) are derived here so they can never drift out of formula.
  const round2 = (n) => Math.round((Number(n)||0) * 100) / 100;
  const rentalRates = (monthly) => {
    const m = num(monthly);
    const weekly = round2((m * 12) / 52);
    const daily = round2(weekly / 5);
    return { monthly: m, weekly, daily };
  };
  // #FLEET_RENTAL_V3 — Bill, 2026-09-08: "would like to add total paid from start of rental."
  // Estimate, not an invoice total: Monthly Charge x months elapsed since Start (avg. 30.4368
  // days/month, same style as the rest of Fleet's date math). Returns null when there's no Start
  // date on file yet (several of today's placeholders don't have one) so the caller can show "—"
  // instead of a misleading $0.00. A future Start date pays $0 so far, not a negative number.
  const daysSinceStart = (v) => {
    const s0 = String(v == null ? '' : v).trim();
    if (!s0) return null;
    let d = null;
    let m = s0.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) d = new Date(+m[1], +m[2]-1, +m[3]);
    if (!d) { m = s0.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (m) { const y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; d = new Date(y, +m[1]-1, +m[2]); } }
    if (!d || isNaN(d.getTime())) return null;
    const t = new Date(); t.setHours(0,0,0,0); d.setHours(0,0,0,0);
    return Math.max(0, Math.round((t - d) / 86400000));
  };
  const rentalPaidToDate = (x) => {
    const days = daysSinceStart(x.rentalStart);
    if (days == null) return null;
    return round2((days / 30.4368) * num(x.monthlyRate));
  };

  // act_com is 114 clean ISO dates plus stragglers like "CXL", "HOLD", "03/26 CXL". Anything we
  // cannot read returns null, and null is treated as the STRICTEST tier — an unreadable completion
  // date must never be the loophole that lets stale cost through.
  const daysSinceComplete = (v) => {
    const s0 = String(v == null ? '' : v).trim();
    if (!s0) return null;
    let d = null;
    let m = s0.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) d = new Date(+m[1], +m[2]-1, +m[3]);
    if (!d) { m = s0.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
      if (m) { const y = +m[3] < 100 ? 2000 + +m[3] : +m[3]; d = new Date(y, +m[1]-1, +m[2]); } }
    if (!d) { m = s0.match(/^(\d{1,2})\/(\d{1,2})(?!\d)/);
      if (m) d = new Date(new Date().getFullYear(), +m[1]-1, +m[2]); }
    if (!d || isNaN(d.getTime())) return null;
    const t = new Date(); t.setHours(0,0,0,0); d.setHours(0,0,0,0);
    return Math.round((t - d) / 86400000);
  };

  // What a typed DUC resolves to.
  //   ok    — live job or an overhead bucket. No ceremony.
  //   warn  — Complete within a week. Anyone on Daily may override.
  //   lock  — Complete over a week ago, or completion date unreadable. Fleet / Owner only.
  //   bad   — not on the Job List, or a status Bill excludes (Approved, On Hold, Cancelled).
  const STALE_DAYS = 7;
  const gateFor = (duc, opsDay) => {   // #FLEET_OPSDATE_GATE_V1 — opsDay = the reporting date (defaults to the selected day)
    const d = stripCP(duc);   // #FLEET_CPSTRIP_V1
    if (!d) return { state:'empty' };
    if (OVERHEAD_SET.has(d)) return { state:'ok', kind:'bucket' };
    const j = jobIndex[d];
    if (!j) return { state:'bad', msg:'Not on the Job List' };
    const st = j.stat.toLowerCase();
    if (st === 'called in' || st === 'in progress') return { state:'ok', kind:'live', job:j };
    if (st === 'complete') {
      const n = daysSinceComplete(j.com);
      if (n == null) return { state:'lock', job:j, days:null, msg:'Complete — no readable completion date' };
      // #FLEET_OPSDATE_GATE_V1 — Bill: a job completed ON or AFTER the reporting date was live that day.
      // Reporting 09/21 for a job completed 09/29 is clean — no override. Only charge to a job AFTER it
      // completed needs one. (n <= daysSince(opsDay)  <=>  completion date >= ops date.)
      const opsN = daysSinceComplete(opsDay || dayISO);
      if (opsN != null && n <= opsN) return { state:'ok', kind:'live', job:j };
      // #FLEET_OPSDATE_GATE_V2 — Bill: the window counts from the REPORTING date, not today. Inventory
      // day past the completion date = flag for review: 1–7 days past → anyone may override, 8+ → Fleet/Owner.
      const past = opsN != null ? n - opsN : n;
      if (past <= STALE_DAYS) return { state:'warn', job:j, days:past, msg:`Complete ${past} day${past===1?'':'s'} before this date` };
      return { state:'lock', job:j, days:past, msg:`Complete ${past} days before this date` };
    }
    return { state:'bad', job:j, msg:`Job is ${j.stat}` };
  };
  const authorise = (unit, duc) => {
    const g = gateFor(duc);
    if (g.state === 'lock' && !masterEdit) { alert(`${duc} was completed ${g.days==null?'(date unreadable)':g.days+' days before this inventory date'}.\n\nOnly Bob or Bill can override a job this far past completion.`); return; }
    const who = g.state === 'lock' ? 'This is past a week — you are overriding as Fleet/Owner.' : 'Charging to a completed job needs an override.';
    if (!window.confirm(`${duc} — ${g.msg}.\n\n${who}\n\nAuthorise this allocation?`)) return;
    setCell(unit, { ovr: { by: upn || String(userRole||''), at: new Date().toISOString(), days: g.days } });
  };
  // #FLEET_DAILYTRUTH_V1 — index the Daily Log once: which DUCs were reported on each date, who
  // reported, and every date a DUC saw activity. Built from the raw store so it survives whatever
  // shape a job carries beyond `entries`. #FLEET_ALIAS_V1 — resolved through fork-alias so a job that
  // started as `custom-<ts>` and later got a real DUC shows up here as the real DUC, not the stale key.
  const dailyIdx = useMemo(() => {
    const byDate = {};   // 'YYYY-MM-DD' -> { DUC: [sup, ...] }
    const byDuc  = {};   // DUC -> sorted [dates]
    Object.entries(dlogs || {}).forEach(([duc, job]) => {
      const D = resolveLogDuc(duc); if (!D) return;
      const entries = (job && Array.isArray(job.entries)) ? job.entries : [];
      entries.forEach(e => {
        const d = String((e && e.date) || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
        (byDate[d] = byDate[d] || {});
        (byDate[d][D] = byDate[d][D] || []);
        const sup = String((e && e.sup) || '').trim();
        if (sup && !byDate[d][D].includes(sup)) byDate[d][D].push(sup);
        (byDuc[D] = byDuc[D] || []).push(d);
      });
    });
    Object.keys(byDuc).forEach(D => { byDuc[D] = [...new Set(byDuc[D])].sort(); });
    return { byDate, byDuc, loaded: dlogs != null };
  }, [dlogs, alias]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #FLEET_DAILYTRUTH_V1 — the verdict for one allocation. Deliberately three-state, not pass/fail:
  //   ok    the DUC has a Daily entry that day — corroborated
  //   none  the DUC is real and active but nobody filed a Daily that day (a REPORTING gap, which is
  //         common and innocent) — kept separate from drift so the mark stays worth reading
  //   miss  the DUC has no Daily activity at all — the actual drift signal
  // Overhead codes are exempt: A-IDLE/A-SHOP/A-OH never appear in a job's Daily Log.
  const dailyCheck = (duc, dateISO) => {
    const D = String(duc || '').trim().toUpperCase();
    if (!D || OVERHEAD_SET.has(D)) return null;
    if (!dailyIdx.loaded) return null;
    const sups = (dailyIdx.byDate[dateISO] || {})[D];
    if (sups) return { level:'ok', sups };
    const all = dailyIdx.byDuc[D];
    if (!all || !all.length) return { level:'miss' };
    return { level:'none', last: all[all.length - 1] };
  };

  // #FLEET_SUPAUTO_V1 — Bill 2026-08-19: "Jerry will assign Names Only and list will pull job ID
  // from prior days or matching days Daily." This is the reverse of dailyIdx above — indexed by
  // SUPERVISOR instead of DUC — so a typed name can be turned into a job. Built off the same
  // `daily-logs` blob, so there is no second source of truth to maintain or drift against.
  // #FLEET_ALIAS_V1 — resolved through fork-alias too, same reason as dailyIdx: a SUP whose Daily
  // entries were filed against a job while it was still `custom-<ts>` must not surface the stale
  // custom key here — the whole point is Jerry sees a real DUC he can act on, not a storage id.
  //   byDateSup[date][SUP] = Set(DUC)   — every job that SUP reported on that exact date
  //   bySup[SUP] = [{date,duc}...]      — that SUP's full history, sorted, for the prior-day fallback
  const supIdx = useMemo(() => {
    const byDateSup = {}, bySup = {};
    Object.entries(dlogs || {}).forEach(([duc, job]) => {
      const D = resolveLogDuc(duc); if (!D) return;
      const entries = (job && Array.isArray(job.entries)) ? job.entries : [];
      entries.forEach(e => {
        const d = String((e && e.date) || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return;
        const sup = String((e && e.sup) || '').trim().toUpperCase();
        if (!sup) return;
        (byDateSup[d] = byDateSup[d] || {});
        (byDateSup[d][sup] = byDateSup[d][sup] || new Set()).add(D);
        (bySup[sup] = bySup[sup] || []).push({ date: d, duc: D });
      });
    });
    Object.keys(bySup).forEach(s => bySup[s].sort((a, b) => a.date.localeCompare(b.date)));
    return { byDateSup, bySup, loaded: dlogs != null };
  }, [dlogs, alias]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #FLEET_SUPAUTO_V1 — the verdict for one SUP name on one date:
  //   ok       exactly one DUC reported by that SUP that day → the job. If nothing posted for that
  //            exact day yet (Daily comes out the FOLLOWING day), falls back to the SUP's most recent
  //            PRIOR day — silently, not flagged, because it self-corrects once the real day posts.
  //   conflict that SUP is on more than one Daily the same day — Jerry has to pick, can't guess.
  //   missing  no Daily activity for that SUP at all, ever — usually a spelling mismatch or a report
  //            that hasn't been filed. Either way, needs a human.
  const sup2duc = (supRaw, dateISO) => {
    const SN = String(supRaw || '').trim().toUpperCase();
    if (!SN) return { verdict: 'empty' };
    if (!supIdx.loaded) return { verdict: 'loading' };
    const todays = (supIdx.byDateSup[dateISO] || {})[SN];
    if (todays && todays.size === 1) return { verdict: 'ok', duc: [...todays][0], matchedDate: dateISO };
    if (todays && todays.size > 1) return { verdict: 'conflict', ducs: [...todays].sort(), matchedDate: dateISO };
    const hist = supIdx.bySup[SN] || [];
    for (let i = hist.length - 1; i >= 0; i--) {
      if (hist[i].date < dateISO) return { verdict: 'ok', duc: hist[i].duc, matchedDate: hist[i].date, fallback: true };
    }
    return { verdict: 'missing' };
  };

  // #FLEET_SUPAUTO_V1 — reconciles EVERY date already loaded in `week` (not just the open tab), so
  // opening Fleet > Daily this week re-derives Mon/Tue immediately with no button to remember. A row
  // is only touched when it is not pinned: an explicit A-IDLE/A-SHOP/A-OH (however it got there) or a
  // DUC a human typed straight into the DUC box (`ducAuto===false`) is left alone on purpose — see
  // Bill's call: "auto-fill skips explicit Idle/Shop/Overhead" even when the SUP has a job elsewhere.
  // A flagged row (conflict/missing) is written BLANK, per Bill: forces Jerry to look rather than
  // sitting under a flag nobody notices. Only touches local `week` state — still needs Save, same as
  // any other edit, so a re-derive is never silently written to the database.
  useEffect(() => {
    if (!editable || !supIdx.loaded || !(active || []).length) return;
    let changed = false;
    const next = {};
    Object.keys(week).forEach(dISO => {
      const day = { ...(week[dISO] || {}) };
      Object.entries(day).forEach(([k, cur]) => {
        const assignedName = String((cur || {}).assigned || '').trim();
        const curDucU = String((cur || {}).duc || '').trim().toUpperCase();
        if (!assignedName) {
          if (cur.ducAuto === true && curDucU) { day[k] = { ...cur, duc: '', ducAuto: undefined }; changed = true; }
          return;
        }
        const pinned = cur.ducAuto === false || OVERHEAD_SET.has(curDucU);
        if (pinned) return;
        const m = sup2duc(assignedName, dISO);
        const nextDuc = m.verdict === 'ok' ? m.duc : '';
        if (nextDuc !== curDucU || cur.ducAuto !== true) {
          day[k] = { ...cur, duc: nextDuc, ducAuto: true };
          changed = true;
        }
      });
      next[dISO] = day;
    });
    if (changed) { setWeek(w => ({ ...w, ...next })); setDirty(true); }
  }, [week, supIdx, active, editable]);   // eslint-disable-line react-hooks/exhaustive-deps

  const dayRows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    // #FLEET_SORT_V1 — Bill: "Ability to sort by fields in header."
    // Every sort resolves ties on unit number, so the order is stable and a re-sort never shuffles
    // rows that compare equal — the yard is reading down a list of 296 and needs it to sit still.
    // #FLEET_UNITDUPE_V1 — Bill: "this is repeated and unable to delete." A duplicate master row
    // (same unitKey — see unitStats above) reads the exact same day-cell as its twin, so showing
    // both here is pure noise, not a second unit. Keep the first one in sheet order; the Master tab's
    // "Duplicate unit numbers" panel is where Bill actually retires the extra.
    const seenUnit = new Set();
    const enriched = (active||[])
      .filter(r => { const k = unitKey(r.unit); if (!k || seenUnit.has(k)) return false; seenUnit.add(k); return true; })
      // #FLEET_BUCKETDRILL_V1 — 'job' means any real DUC, i.e. not one of the A-* overhead codes
      .filter(r => {
        if (!bucket) return true;
        const d = String((dayRec[unitKey(r.unit)]||{}).duc||'').trim().toUpperCase();
        return bucket === 'job' ? (!!d && !OVERHEAD_SET.has(d)) : d === bucket;
      })
      .map(r => {
        const k = unitKey(r.unit);
        const cur = dayRec[k] || {};
        const duc = stripCP(cur.duc||'');   // #FLEET_CPSTRIP_V1 — matches gateFor/dailyIdx/supIdx normalization
        const g = gateFor(duc);
        const j = g.job;
        const bucket = OVERHEAD_SET.has(duc) ? (OVERHEAD.find(o=>o[0]===duc)||[])[1] : '';
        const assignedName = String(cur.assigned||'').trim();
        const gpsCell = locCell(r.vin, dayISO, locToday, locLive, locDay);   // #FLEET_LOCATIONS_V1
        return { r, k, cur, duc, g, job:j, chk: dailyCheck(duc, dayISO),   // #FLEET_DAILYTRUTH_V1
          supChk: assignedName ? sup2duc(assignedName, dayISO) : null,    // #FLEET_SUPAUTO_V1
          unit:String(r.unit||''), assigned:String(cur.assigned||''), cost:rate(r),
          desc:String(r.desc||''), cat:String(r.cat||''), note:String(cur.note||''),
          gpsCell, gps: gpsCell ? gpsCell.text : '',   // #FLEET_LOCATIONS_V1
          cust: bucket || (j ? j.cust : ''), city: j ? j.city : '', pm: j ? j.pm : '',
          // #FLEET_JOBSTAT_V1 — Bill: "add STATUS. If completed Date Completed to support Override…
          // typically all IN PROGRESS but another layer to double check." The Job List's own status/
          // completion date, distinct from the OVR checkmark column — this is the WHY behind it.
          jstat: j ? (j.stat || '') : '', jcomp: (j && String(j.stat||'').toLowerCase()==='complete') ? String(j.com||'') : '',
          // #FLEET_RENTAL_V1 — Bill: small red circled "R" so Rental/RPO units can be spotted and
          // sorted on the Daily grid, same list feeding the Owner Dash tile.
          rentalRpo: !!String(r.rentalType||'').trim() };
      })
      // #FLEET_SEARCH_V1 — Bill: "need a search bar for units here." The box was already there
      // (placeholder "Filter units…") but only matched unit/category/description, computed before
      // the day's DUC/SUP/customer were even attached to the row — typing a job number, a customer
      // name, or a supervisor found nothing even though the box was sitting right there. Moved the
      // filter to run AFTER the row is fully enriched, so it now searches everything on the row:
      // unit #, category, description, DUC #, assigned SUP, customer, PM, and notes.
      .filter(x => !ql || `${x.unit} ${x.cat} ${x.desc} ${x.duc} ${x.assigned} ${x.cust} ${x.pm} ${x.note} ${x.gps}`.toLowerCase().includes(ql));
    const dir = sort.dir === 'desc' ? -1 : 1;
    const cmpU = (a,b) => a.unit.localeCompare(b.unit);
    const txt = (key) => (a,b) => (String(a[key]||'').localeCompare(String(b[key]||'')) * dir) || cmpU(a,b);
    // #FLEET_STATUS_V1 — sorting the Status column ranks by how much it wants attention: a bad DUC
    // first, then one that needs an override, then a SUP that couldn't auto-fill, then a Daily-Log
    // reporting gap (miss, then none), then everything clean/overridden/exempt last. Supersedes the
    // old #FLEET_DAILYTRUTH_V2 chk-only ranking now that Status consolidates all of those signals.
    const statusRank = (x) => {
      if (x.g.state === 'bad') return 0;
      if ((x.g.state==='warn'||x.g.state==='lock') && !x.cur.ovr) return 1;
      if (!x.duc && x.cur.ducAuto !== false && x.supChk && (x.supChk.verdict==='conflict'||x.supChk.verdict==='missing')) return 2;
      if (x.chk && x.chk.level === 'miss') return 3;
      if (x.chk && x.chk.level === 'none') return 4;
      return 5;
    };
    const cmp = sort.key === 'cost'   ? (a,b) => ((a.cost - b.cost) * dir) || cmpU(a,b)
              : sort.key === 'status' ? (a,b) => ((statusRank(a) - statusRank(b)) * dir) || cmpU(a,b)
              : sort.key === 'cat'    ? (a,b) => (a.cat.localeCompare(b.cat) * dir) || cmpU(a,b)
              // #FLEET_RENTAL_V1 — rental units first (or last, on reverse click)
              : sort.key === 'rentalRpo' ? (a,b) => (((b.rentalRpo?1:0) - (a.rentalRpo?1:0)) * dir) || cmpU(a,b)
              // #FLEET_LOCATIONS_V1 — by town; units with no GPS stamp always sit at the bottom
              : sort.key === 'gps' ? (a,b) => ((a.gps?0:1) - (b.gps?0:1)) || (a.gps.localeCompare(b.gps) * dir) || cmpU(a,b)
              : sort.key          ? txt(sort.key)
              // default: the sheet's own order — grouped by category, then unit
              : (a,b) => a.cat.localeCompare(b.cat) || cmpU(a,b);
    return enriched.sort(cmp);
  // #FLEET_BUCKETDRILL_V1 / #FLEET_DAILYTRUTH_V1 — tags live ABOVE the disable directive. Anything
  // written after the rule name on that line is parsed as another rule name and fails the build.
  }, [active, q, dayRec, jobIndex, sort, bucket, dailyIdx, dayISO, locLive, locDay, locToday]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #FLEET_EDITPIN_V1 — freeze the row order while a cell in this row is focused. dayRows itself
  // keeps re-sorting live (that's correct — Status/Assigned rank has to reflect what was just typed),
  // this just decouples the ON-SCREEN order from that live re-sort for as long as editingKey matches
  // the row. Order re-freezes the moment editingKey changes to a different row (or clears to null),
  // so switching rows or clicking away resumes normal sorting immediately.
  const displayRows = useMemo(() => {
    if (!editingKey) return dayRows;
    if (editOrderRef.current.key !== editingKey) {
      editOrderRef.current = { key: editingKey, order: dayRows.map(x => x.k) };
    }
    const order = editOrderRef.current.order;
    const pos = new Map(order.map((k,i) => [k,i]));
    return [...dayRows].sort((a,b) => (pos.has(a.k)?pos.get(a.k):1e9) - (pos.has(b.k)?pos.get(b.k):1e9));
  }, [dayRows, editingKey]);

  const dayFilled = useMemo(() => Object.keys(dayRec).length, [dayRec]);

  // #FLEET_SPLIT_V1 — Bill: "Fun feature to add, cost and % of Idle, OH & Repairs."
  // This is the number that justifies the whole module. Fleet cost is fixed whether a machine turns
  // or not; the only question is how much of it landed on a job. Split at render off the same master
  // rate the rows use, so the strip can never disagree with the table under it.
  const split = useMemo(() => {
    const b = { job:0, 'A-IDLE':0, 'A-SHOP':0, 'A-OH':0, blank:0 };
    const n = { job:0, 'A-IDLE':0, 'A-SHOP':0, 'A-OH':0, blank:0 };
    (active||[]).forEach(r => {
      const c = rate(r);
      const d = String((dayRec[unitKey(r.unit)]||{}).duc||'').trim().toUpperCase();
      const k = !d ? 'blank' : (OVERHEAD_SET.has(d) ? d : 'job');
      b[k] += c; n[k] += 1;
    });
    const allocated = b.job + b['A-IDLE'] + b['A-SHOP'] + b['A-OH'];
    const pct = (v) => allocated > 0 ? Math.round((v/allocated)*100) : 0;
    return { b, n, allocated, pct };
  }, [active, dayRec]);
  // Bill: "every day the entire list from Master needs be displayed here… he will need to assign a
  // unit each day." So the table is every active unit, blank, and the gap is counted out loud.
  const unassigned = useMemo(() =>
    (active||[]).filter(r => !String((dayRec[unitKey(r.unit)]||{}).duc||'').trim()).length, [active, dayRec]);

  // #PM_EQFLAG_DAYLIGHT_V1 (2026-08-24) — Bill: tested #PM_EQFLAG_CLEAR_V1 today, worked, wants a
  // warning light on the day-of row that ties back to the PM alert, and/or a link from Review straight
  // to that daily line. Building both: this lookup drives the warning light on the Daily row (below),
  // and goToFlagInDaily (below the flags list) is the Review → Daily jump.
  const openFlagsByUnit = useMemo(() => {
    const m = new Map();
    (pmFlags||[]).forEach(f => {
      if (f.status !== 'open' || f.wk !== wkStart || !Array.isArray(f.dates) || !f.dates.includes(dayISO)) return;
      const k = unitKey(f.unit); if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    });
    return m;
  }, [pmFlags, wkStart, dayISO]);
  // Review → Daily: jump straight to the flagged unit's line, on the week/day it was actually raised.
  const goToFlagInDaily = async (f) => {
    if (f.wk && f.wk !== wkStart) await switchWeek(f.wk);
    if (Array.isArray(f.dates) && f.dates.length && f.wk) {
      const mon = new Date(f.wk + 'T12:00:00');
      const target = new Date(f.dates[0] + 'T12:00:00');
      const diffDays = Math.round((target - mon) / 86400000);
      if (diffDays >= 0 && diffDays <= 4) setDayIx(diffDays);
    }
    setQ(f.unit);
    setView('daily');
  };

  // week roll-up by DUC — this is the "consolidated into weekly costing table" step
  // #FLEET_ROLLSORT_V1 — sortable week roll-up. Default cost desc: the table's job is "where did the
  // money go", and A-IDLE/A-SHOP sitting at the top on cost is exactly the signal Bill is hunting.
  const [rollSort, setRollSort] = useState({ k:'cost', dir:'desc' });
  const rollup = useMemo(() => {
    const acc = {};
    Object.entries(week).forEach(([d, units]) => {
      Object.entries(units||{}).forEach(([k, v]) => {
        const duc = String((v&&v.duc)||'').trim().toUpperCase();
        if (!duc) return;
        const r = byKey[k]; if (!r) return;
        if (!acc[duc]) acc[duc] = { duc, cost:0, days:0, units:new Set() };
        acc[duc].cost += rate(r); acc[duc].days += 1; acc[duc].units.add(k);
      });
    });
    return Object.values(acc).sort((a,b)=>b.cost-a.cost);
  }, [week, byKey]);
  const weekTotal = rollup.reduce((s,r)=>s+r.cost, 0);
  // #FLEET_ROLLSORT_V1 — sorted view. Customer sorts on the same label the cell renders (overhead name
  // or job customer), not the raw DUC, so "sort by customer" matches what the eye sees.
  const rollupSorted = useMemo(() => {
    const val = (r) => {
      if (rollSort.k === 'duc')   return r.duc;
      if (rollSort.k === 'cust')  return OVERHEAD_SET.has(r.duc)
        ? String((OVERHEAD.find(o=>o[0]===r.duc)||[])[1] || '')
        : String((jobIndex[r.duc] && jobIndex[r.duc].cust) || '');
      if (rollSort.k === 'units') return r.units.size;
      if (rollSort.k === 'days')  return r.days;
      return r.cost;
    };
    return [...rollup].sort((a,b) => {
      const av = val(a), bv = val(b);
      const c = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return rollSort.dir === 'asc' ? c : -c;
    });
  }, [rollup, rollSort, jobIndex]);

  // #FLEET_SUPROLL_V1 — Bill 2026-08-19: "pump in the Weekly Equipment cost here by assigned sup...
  // SUP - JOB - Units (Quantity for given Day) and cost each day if it fits." One line per SUP+JOB
  // pair for the week:
  //   maxUnits   — the PEAK daily unit count for that pair, not a sum. Bill's own example: 15 units
  //                Monday, 10 Tuesday → reads as 15 for the week, because it's mostly the same
  //                15 trucks sitting with the crew every day, not 25 distinct truck-days.
  //   weeklyCost — a running total that only grows on days the pair actually shows up (mirrors the
  //                per-unit `rate × days allocated` the DUC roll-up above already uses).
  //   daysLit    — which day-indices (0=Mon..5=Sat) contributed, so the UI can "light up" M T W T F S
  //                as the week fills in instead of showing a flat, opaque total.
  //   unitInfo   — every unit that ever carried this pair this week, and which days each was on it —
  //                what the collapsed row expands into. Read-only: editing still happens on the Daily
  //                tab itself, this is a rollup for reviewing where the week's equipment cost sat.
  const supRollup = useMemo(() => {
    const acc = {};
    DAYS.forEach((lbl, i) => {
      const d = iso(addDays(new Date(wkStart + 'T12:00:00'), i));
      const units = week[d] || {};
      const byGroup = {};   // this day only, to get the day's own unit count per pair
      Object.entries(units).forEach(([k, v]) => {
        const duc = String((v && v.duc) || '').trim().toUpperCase();
        if (!duc) return;
        const r = byKey[k]; if (!r) return;
        const sup = String((v && v.assigned) || '').trim().toUpperCase() || '(unassigned)';
        const key = sup + '|' + duc;
        (byGroup[key] = byGroup[key] || { sup, duc, unitKeys: [] }).unitKeys.push(k);
      });
      Object.values(byGroup).forEach(g => {
        const key = g.sup + '|' + g.duc;
        const rec = (acc[key] = acc[key] || { sup: g.sup, duc: g.duc, weeklyCost: 0, maxUnits: 0, daysLit: [], unitInfo: {} });
        let dayCost = 0;
        g.unitKeys.forEach(k => {
          const r = byKey[k]; if (!r) return;
          dayCost += rate(r);
          const info = (rec.unitInfo[k] = rec.unitInfo[k] || { unit: r.unit, desc: r.desc || '', days: [] });
          info.days.push(i);
        });
        rec.weeklyCost += dayCost;
        rec.maxUnits = Math.max(rec.maxUnits, g.unitKeys.length);
        rec.daysLit.push(i);
      });
    });
    return Object.values(acc).map(g => {
      const isOverhead = OVERHEAD_SET.has(g.duc);
      const j = jobIndex[g.duc];
      return { ...g, isOverhead,
        cust: isOverhead ? (OVERHEAD.find(o => o[0] === g.duc) || [])[1] : (j ? j.cust : ''),
        city: isOverhead ? '' : (j ? j.city : ''),
        daysLit: new Set(g.daysLit) };
    }).sort((a, b) => a.sup.localeCompare(b.sup) || b.weeklyCost - a.weeklyCost);
  }, [week, wkStart, byKey, jobIndex]);   // eslint-disable-line react-hooks/exhaustive-deps
  const supRollupTotal = supRollup.reduce((s, g) => s + g.weeklyCost, 0);
  const [supOpen, setSupOpen] = useState(new Set());   // expanded (sup|duc) keys
  const toggleSupOpen = (key) => setSupOpen(s => { const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n; });

  // #FLEET_WEEKTOT_V1 — Bill: "Need a way for Week to total but we can figure that out as we go."
  // Same four buckets as the day strip, summed Mon–Fri. Day totals sit alongside so a day that was
  // never filled in reads as a zero rather than quietly shrinking the week.
  const weekTot = useMemo(() => {
    const days = DAYS.map((lbl,i) => {
      const d = iso(addDays(new Date(wkStart+'T12:00:00'), i));
      const units = week[d] || {};
      const b = { job:0, 'A-IDLE':0, 'A-SHOP':0, 'A-OH':0 };
      Object.entries(units).forEach(([k,v]) => {
        const duc = String((v&&v.duc)||'').trim().toUpperCase(); if (!duc) return;
        const r = byKey[k]; if (!r) return;
        b[OVERHEAD_SET.has(duc) ? duc : 'job'] += rate(r);
      });
      const tot = b.job + b['A-IDLE'] + b['A-SHOP'] + b['A-OH'];
      return { lbl, d, b, tot, n: Object.keys(units).length };
    });
    const b = { job:0, 'A-IDLE':0, 'A-SHOP':0, 'A-OH':0 };
    days.forEach(x => Object.keys(b).forEach(kk => { b[kk] += x.b[kk]; }));
    const tot = b.job + b['A-IDLE'] + b['A-SHOP'] + b['A-OH'];
    return { days, b, tot, pct: (v) => tot > 0 ? Math.round((v/tot)*100) : 0 };
  }, [week, wkStart, byKey]);   // eslint-disable-line react-hooks/exhaustive-deps

  // ── daily edits ────────────────────────────────────────────────────────────
  const setCell = (unit, patch) => {
    const k = unitKey(unit); if (!k) return;
    setWeek(w => {
      const day = { ...(w[dayISO]||{}) };
      const cur = { ...(day[k]||{}) , ...patch };
      // An empty row is deleted rather than stored — but a note alone is a real reason to keep one
      // ("in shop, waiting on parts" before the DUC is known), so it counts as content.
      if (!String(cur.duc||'').trim() && !String(cur.assigned||'').trim() && !String(cur.note||'').trim()) {
        delete day[k];
        // #FLEET_SAVEMERGE_V1 — remember this as a real delete so persistWeek's merge-with-server
        // doesn't let a stale server copy of this same unit silently reappear on next save.
        const rem = pendingRemovalsRef.current[dayISO] || (pendingRemovalsRef.current[dayISO] = new Set());
        rem.add(k);
      } else {
        day[k] = cur;
        // Re-adding a unit that had a pending delete this tab cancels that delete.
        if (pendingRemovalsRef.current[dayISO]) pendingRemovalsRef.current[dayISO].delete(k);
      }
      return { ...w, [dayISO]: day };
    });
    setDirty(true);
  };
  // #FLEET_AUTOSAVE_V1 — Bill: "Seems we will need AUTO save here as these are not properly being
  // saved." Root cause: the week only ever reached the server on a manual Save click, and the guard
  // rail below used to block THAT ENTIRE WRITE (not just the cost digest) whenever any row needed
  // authorization — so a day with one unresolved allocation silently saved nothing, not even the 40
  // other rows that were fine. On top of that, switching weeks (Prev/Next/Today) reloaded `week` from
  // the server and clobbered whatever hadn't been saved yet. Fix: every edit now writes through on a
  // short debounce so nothing depends on remembering to click Save; the guard rail still keeps an
  // unauthorized allocation out of the weekly cost roll-up, it just no longer holds the raw data hostage.
  const unauthRows = (weekData) => {
    const list = [];
    Object.entries(weekData||{}).forEach(([d, units]) => Object.entries(units||{}).forEach(([k, v]) => {
      const g = gateFor(v && v.duc, d);   // #FLEET_OPSDATE_GATE_V1 — gate each row against its own day
      if ((g.state === 'warn' || g.state === 'lock') && !(v && v.ovr)) list.push(`${keyToDisplay(k)} → ${v.duc} (${d})`);
      if (g.state === 'bad') list.push(`${keyToDisplay(k)} → ${v.duc} (${d}) — ${g.msg}`);
    }));
    return list;
  };
  // #FLEET_SAVEMERGE_V1 (2026-09-04) — Bill/Jerry: Jerry tagged 72 units on Yard Walk (mobile) Fri
  // 09/04; the desktop Yard Walk screen, same day, same login, showed 0 — and the server record for
  // that week ended up with no Friday key at all. Root cause: this write always replaced the ENTIRE
  // week blob with whatever THIS tab/device happened to have sitting in memory (`week`, loaded once
  // when the screen opened or the week was switched to). Two tabs/devices open on the same week —
  // Jerry's phone doing a Yard Walk, a desktop Daily Inventory session, another PM's tab left open
  // from earlier — each autosave independently, and whichever save landed last won, silently
  // erasing everything the other one had added or changed, even on completely different days.
  // Fix: re-pull the freshest server copy right before every write and merge THIS save's changes
  // into it — day by day, unit by unit — instead of overwriting the whole blob blind. Two sessions
  // editing different days (or different units on the same day) now both survive; an explicit local
  // delete (tracked in pendingRemovalsRef since a deleted key just looks like "absent" and would
  // otherwise let a stale server copy of that same unit quietly reappear) always wins over whatever
  // the merge pulls from the server.
  const persistWeek = async (weekData, wk) => {
    let toSave = weekData;
    try {
      const server = await sg('fleet-daily-' + wk);
      if (server && typeof server === 'object') {
        const merged = {};
        new Set([...Object.keys(server), ...Object.keys(weekData)]).forEach(d => {
          const day = { ...(server[d]||{}), ...(weekData[d]||{}) };
          const removed = pendingRemovalsRef.current[d];
          if (removed && removed.size) removed.forEach(k => { delete day[k]; });
          if (Object.keys(day).length) merged[d] = day;
        });
        toSave = merged;
      }
    } catch { /* re-fetch failed — fall back to saving our own in-memory copy rather than blocking the save entirely */ }
    try {
      await ss('fleet-daily-' + wk, toSave);
      pendingRemovalsRef.current = {};
      setDirty(false); setSavedAt(new Date().toLocaleTimeString()); setSaveErr('');
      // #FLEET_INSHOP_LIVE_V1 (2026-08-26) — Bill/Jerry: an A-SHOP tag from Yard Walk should count
      // toward "In Shop" the same session, not only after tomorrow's report. rollSummary() already
      // rebuilds the fleet-summary digest on every clean save; the missing piece was re-reading it
      // back into `shopSum` here, so the tab/badge actually reflects it without a manual Refresh.
      // #FLEET_INSHOP_ROLL_V1 (2026-10-09) — Bill: "Yard Inventory not feeding In Shop summary — 16-2556 listed in
      // Shop … but not moving to IN SHOP Summary." The roll below used to run only when the WHOLE week had no row
      // waiting on an override. One job marked Complete with units still on it (any day that week) was enough to
      // stop it: the digest had not moved since Monday 10/5 while Daily showed 14 units A-SHOP against its 8.
      // Whether a job allocation still needs an override has nothing to do with which units are in the shop, so
      // the roll now follows every save.
      try { await rollSummary(); await loadShopSum(); } catch { /* digest is a bonus; never fail a save over it */ }
      return true;
    } catch (e) { setSaveErr('Autosave failed: ' + (e.message||e)); return false; }
  };
  // Debounced write-through: fires ~1.2s after the last change, so typing isn't interrupted but
  // nothing sits unsaved for long. Skipped right after a week loads (firstLoadRef) so opening a week
  // doesn't immediately re-save it.
  useEffect(() => {
    if (firstLoadRef.current) { firstLoadRef.current = false; return; }
    if (!dirty) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { persistWeek(week, wkStart); }, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week]);
  // Flush immediately before switching weeks so an edit made seconds ago can't lose the race against
  // the load effect overwriting `week` out from under it.
  const switchWeek = async (newStart) => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (dirty) await persistWeek(week, wkStart);
    setWkStart(newStart);
  };
  // Belt-and-suspenders: warn on tab-close/navigate-away if something hasn't been confirmed saved yet
  // (mid-debounce, or a failed autosave sitting there).
  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);
  const saveWeek = async () => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setBusy('Saving…');
    const ok = await persistWeek(week, wkStart);
    const unauth = ok ? unauthRows(week) : [];
    if (unauth.length) {
      alert(`Saved. ${unauth.length} allocation${unauth.length===1?'':'s'} still need${unauth.length===1?'s':''} authorization before they count toward the weekly cost roll-up:\n\n` +
        unauth.slice(0,12).join('\n') + (unauth.length>12 ? `\n…and ${unauth.length-12} more` : '') +
        `\n\nUse Override on the row, or pick a Called In / In Progress job.`);
    }
    setBusy('');
  };

  // #FLEET_SUMMARY_V2 — Bill: "could this auto populate each time the new day clicks, pulling from the
  // prior completed audit day?" Yes, and it is far cheaper than the 13-week scan V1 relied on.
  // We keep a per-unit { bucket, since } state in fleet-summary. On each save we compare today's
  // allocation to the stored bucket: unchanged → the run continues from `since`; changed → the run
  // restarts today. So durations accumulate one day at a time and never need history re-read.
  // The Idle-90 scan still seeds `since` with real history the first time; after that this maintains it.
  const rollSummary = async () => {
    const dISO = dayISO;
    const rec = week[dISO] || {};
    // Only roll a day that has actually been worked: rolling a blank day would reset every run to zero.
    if (!Object.keys(rec).length) return;
    let prev = null; try { prev = await sg('fleet-summary'); } catch {}
    const state = (prev && prev.state && typeof prev.state === 'object') ? prev.state : {};
    if (prev && prev.lastDay && prev.lastDay > dISO) return;   // never let an older day overwrite a newer roll
    const dayMs = 86400000;
    const daysBetween = (a,b) => Math.max(0, Math.round((new Date(b+'T00:00:00') - new Date(a+'T00:00:00'))/dayMs));
    const nextState = {}, idle = [], shop = [];
    (active||[]).forEach(r => {
      const k = unitKey(r.unit); if (!k) return;
      const duc = String((rec[k]||{}).duc||'').trim().toUpperCase();
      if (!duc) { const keep = state[k]; if (keep) nextState[k] = keep; return; }   // unallocated: leave the run alone
      const bucket = OVERHEAD_SET.has(duc) ? duc : 'JOB';
      const wasSame = state[k] && state[k].bucket === bucket;
      let since = wasSame ? state[k].since : dISO;
      // #FLEET_INSHOP_ROLL_V1 — the stored run is for a different bucket (or the digest sat un-rolled for days): walk
      // back through this week's sheet so a unit that went in the shop on Tuesday reads day 4 on Friday, not day 1.
      if (!wasSame) {
        const earlier = Object.keys(week).filter(d => /^\d{4}-\d{2}-\d{2}$/.test(d) && d < dISO).sort().reverse();
        for (const d of earlier) {
          const x = String(((week[d] || {})[k] || {}).duc || '').trim().toUpperCase();
          if (!x) continue;
          if ((OVERHEAD_SET.has(x) ? x : 'JOB') === bucket) since = d; else break;
        }
      }
      nextState[k] = { bucket, since };
      const days = daysBetween(since, dISO) + 1;   // inclusive: first day in the shop is day 1
      if (bucket === 'A-SHOP') shop.push({ unit:r.unit, desc:r.desc||'', days, rate:rate(r),
        yardCity: String((rec[k]||{}).city||'').trim(), yardNote: String((rec[k]||{}).note||'').trim() });   // #FLEET_INSHOP_ROLL_V1
      else if (bucket === 'A-IDLE' && days >= OWNER_IDLE_MIN) idle.push({ unit:r.unit, desc:r.desc||'', days, proven:true, rate:rate(r) });
    });
    idle.sort((a,b)=>b.days-a.days); shop.sort((a,b)=>b.days-a.days);
    const activeCount = (active||[]).length;
    await ss('fleet-summary', {
      at: new Date().toISOString(), lastDay: dISO, source: 'daily-roll',
      idleTarget: OWNER_IDLE_MIN, activeCount,
      uptimePct: activeCount ? Math.round(((activeCount - shop.length)/activeCount)*100) : null,
      idle: idle.slice(0,50), shop: shop.slice(0,50),
      idleCount: idle.length, shopCount: shop.length,
      idleBurn: idle.reduce((n,x)=>n+(x.rate||0),0), shopBurn: shop.reduce((n,x)=>n+(x.rate||0),0),
      // #FLEET_RENTAL_V2 — piped into Owner Dash in place of App Usage.
      rentalRpo: rentalDigest(),
      state: nextState,
    });
  };
  // #FLEET_INSHOP_ROLL_V1 — catch-up. If the digest is behind today's Daily sheet (an older day, or a different
  // number of A-SHOP units), roll it once when Fleet opens instead of waiting for someone to edit a Daily line.
  // Once per day per screen; only for people who can edit; only for today.
  const shopCatchUpRef = useRef('');
  useEffect(() => {
    if (!editable || !shopSum || !(active || []).length) return;
    const today = iso(new Date());
    if (dayISO !== today || shopCatchUpRef.current === today) return;
    const rec = week[dayISO] || {};
    if (!Object.keys(rec).length) return;
    const onSheet = Object.values(rec).filter(v => v && String(v.duc || '').trim().toUpperCase() === 'A-SHOP').length;
    const behind = String(shopSum.lastDay || '') < today || onSheet !== (Number(shopSum.shopCount) || 0);
    if (!behind) return;
    shopCatchUpRef.current = today;
    (async () => { try { await rollSummary(); await loadShopSum(); } catch { /* next save rolls it */ } })();
  }, [shopSum, week, dayISO, active, editable]);   // eslint-disable-line react-hooks/exhaustive-deps
  // The single biggest time saver: most units stay on the same job day to day. Bob fills
  // Monday, then carries it forward and only touches what moved.
  // #FLEET_COPYPREV_NAMESONLY_V1 (2026-08-25) — Bill: only the Assigned name should ride forward,
  // not the whole record. DUC keeps re-deriving fresh from today's Daily Log via the #FLEET_SUPAUTO_V1
  // effect below (untouched by this change), and A-IDLE/A-SHOP reset blank every day — Jerry
  // re-confirms each unit's physical location fresh on his Yard Walk, current day. Blocked on Monday
  // on purpose: a bad Friday should never ride into the new week (Bill: "reset each Monday...
  // sanitize any issues from the prior Friday").
  // #FLEET_COPYPREV_OH_V1 (2026-08-26) — Bill: "A-OH items could copy as these are static and rarely
  // change." Unlike A-IDLE/A-SHOP, an overhead unit is doing the same standing job tomorrow, so it
  // rides forward alongside the Assigned name — pinned (ducAuto:false) so #FLEET_SUPAUTO_V1 never
  // overwrites it.
  // #FLEET_COPYPREV_HOLIDAY_V1 (2026-08-27) — Bill/Jerry: "How to handle Monday Holidays as start of
  // the week, nothing to carry forward." When yesterday is a listed holiday (fleet-holidays) with
  // nothing stored — nobody worked it, there's genuinely nothing there to carry — reach back one more
  // day instead of coming up empty (into last week's Friday when the holiday sits on Monday; one day
  // earlier in the same week for a mid-week holiday). Bill's reasoning: units can't sit unallocated
  // through a paid holiday, they stay charged to whatever job they were on — so THIS bridging copy
  // carries the FULL prior record (DUC, Assigned, city, note), not just the Assigned name. An ordinary
  // day-to-day Copy Prev (no holiday involved) is unchanged from #FLEET_COPYPREV_NAMESONLY_V1/#_OH_V1.
  const copyPrev = async () => {
    if (dayIx === 0) { alert('Copy prev is off on Monday — the Assigned column starts blank each week.'); return; }
    const prevDate = addDays(new Date(dayISO+'T12:00:00'), -1);
    let prev = iso(prevDate);
    let src = week[prev];
    let bridged = false;
    const prevIsEmptyHoliday = holidays.includes(prev) && !(src && Object.keys(src).length);
    if (prevIsEmptyHoliday) {
      if (dayIx === 1) {
        // Monday was the holiday — Tuesday reaches back into last week's Friday.
        const lastMon = iso(addDays(new Date(wkStart+'T12:00:00'), -7));
        const lastFri = iso(addDays(new Date(lastMon+'T12:00:00'), 4));
        try { const wData = await sg('fleet-daily-' + lastMon); src = (wData||{})[lastFri] || {}; }
        catch { src = {}; }
        prev = lastFri; bridged = true;
      } else {
        // A holiday mid-week — step back one more day, still inside the loaded week.
        const before = iso(addDays(prevDate, -1));
        src = week[before]; prev = before; bridged = true;
      }
    }
    if (bridged) {
      const bridgeUnits = Object.entries(src || {}).filter(([, v]) => String((v||{}).duc||'').trim() || String((v||{}).assigned||'').trim());
      if (!bridgeUnits.length) { alert(`Nothing on ${prev} to copy either.`); return; }
      const today = week[dayISO] || {};
      const willOverwrite = bridgeUnits.filter(([k]) => String((today[k]||{}).assigned||'').trim() || String((today[k]||{}).duc||'').trim()).length;
      if (willOverwrite && !window.confirm(`A holiday falls in between — bridging from ${prev} instead.\n\nThis replaces ${willOverwrite} entr${willOverwrite===1?'y':'ies'} already entered today with ${prev}'s FULL record (DUC, Assigned, city, note) — every unit stays costed through the holiday. Continue?`)) return;
      setWeek(w => {
        const day = { ...(w[dayISO] || {}) };
        bridgeUnits.forEach(([k, v]) => { day[k] = { ...(day[k]||{}),
          duc: v.duc||'', assigned: v.assigned||'', city: v.city||'', note: v.note||'',
          ducAuto: OVERHEAD_SET.has(String(v.duc||'').trim().toUpperCase()) ? false : (day[k]||{}).ducAuto }; });
        return { ...w, [dayISO]: day };
      });
      setDirty(true);
      return;
    }
    const names = Object.entries(src || {}).filter(([, v]) => String((v || {}).assigned || '').trim());
    const ohUnits = Object.entries(src || {}).filter(([, v]) => String((v || {}).duc || '').trim().toUpperCase() === 'A-OH');
    if (!names.length && !ohUnits.length) { alert(`Nothing on ${prev} to copy.`); return; }
    const today = week[dayISO] || {};
    const willOverwrite = names.filter(([k]) => String((today[k] || {}).assigned || '').trim()).length
      + ohUnits.filter(([k]) => String((today[k] || {}).duc || '').trim()).length;
    if (willOverwrite && !window.confirm(`This replaces ${willOverwrite} entr${willOverwrite===1?'y':'ies'} already entered today with ${prev}'s.\n\nOnly Assigned names and A-OH units are copied — A-IDLE/A-SHOP and job DUCs are never carried forward. Continue?`)) return;
    setWeek(w => {
      const day = { ...(w[dayISO] || {}) };
      names.forEach(([k, v]) => { day[k] = { ...(day[k] || {}), assigned: v.assigned }; });
      ohUnits.forEach(([k, v]) => { day[k] = { ...(day[k] || {}), duc: 'A-OH', ducAuto: false,
        city: v.city || (day[k]||{}).city || '', note: v.note || (day[k]||{}).note || '' }; });
      return { ...w, [dayISO]: day };
    });
    setDirty(true);
  };
  // #FLEET_NOCLEARFILL_V1 — Bill: pulled "Clear" (wipes every allocation for the day) and "Fill idle"
  // (bulk-sets every open unit to A-IDLE) entirely. "Don't see the need really... more harm than good" —
  // a mis-click on either was a real risk with no real upside. Can come back if a real need shows up.

  // #FLEET_CSV_MAIL_V1 — Bill: "add the ability to Email CSV file that's formatted."
  // A browser cannot attach a file to a mail draft — that is a hard limit, not a shortcut taken.
  // So this does the only thing that actually works end to end: writes the CSV to disk AND opens a
  // draft already addressed and summarised, with the file sitting in Downloads ready to attach.
  // "Formatted" means the sheet's own column order, real $ amounts, and a TOTAL row that foots.
  const csvCell = (v) => { const s = String(v == null ? '' : v); return /[",\n]/.test(s) ? `"${s.replace(/"/g,'""')}"` : s; };
  const dayCsv = () => {
    const head = ['Unit','Assigned','Cost','Description','DUC #','Customer','City','PM','Notes','Status'];
    const lines = [head.join(',')];
    let tot = 0;
    dayRows.forEach(x => {
      if (x.duc) tot += x.cost;
      const flagged = !x.duc && x.cur.ducAuto !== false && x.supChk && (x.supChk.verdict==='conflict' || x.supChk.verdict==='missing');
      lines.push([ x.unit, x.assigned, x.cost ? x.cost.toFixed(2) : '',
        x.desc, x.duc, x.cust, x.city, x.pm, x.note,
        !x.duc ? (flagged ? (x.supChk.verdict==='conflict' ? 'SUP ON 2+ DAILIES — VERIFY' : 'NO DAILY FOR SUP — VERIFY') : 'NOT ALLOCATED')
               : (x.cur.ovr ? 'OVERRIDE' : (x.g.state==='bad' ? 'INVALID DUC' : '')) ].map(csvCell).join(','));
    });
    lines.push('');
    // footer rows use the same column count as the header so the CSV opens as one clean grid
    const foot = (label, amt, note) => lines.push([label,'',amt.toFixed(2),note,'','','','','',''].map(csvCell).join(','));
    foot('TOTAL', tot, `${dayFilled} of ${(active||[]).length} units allocated`);
    foot('1 Jobs',     split.b.job,       `${split.pct(split.b.job)}%`);
    foot('2 Idle',     split.b['A-IDLE'], `${split.pct(split.b['A-IDLE'])}%`);
    foot('3 Overhead', split.b['A-OH'],   `${split.pct(split.b['A-OH'])}%`);
    foot('4 Shop',     split.b['A-SHOP'], `${split.pct(split.b['A-SHOP'])}%`);
    return lines.join('\n');
  };
  const downloadCsv = () => {
    const name = `Daily Inventory ${dayISO}.csv`;   // #FLEET_RENAME_DAILYINV_V1
    const blob = new Blob(['﻿' + dayCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };
  // #FLEET_WEEKROLLUP_CSV_V1 — Bill: "prepare for Consolidated Weekly Report that will be uploaded
  // to job costing." CSV export only for now (his call — "CSV is fine for now, we can plumb later"
  // once a direct CostModule.js feed is worth building). One row per DUC for the week, same numbers
  // shown on-screen in "Week roll-up by DUC" (rollupSorted), plus a TOTAL row that foots.
  const weekRollupCsv = () => {
    const head = ['DUC','Customer','Units','Unit-days','Cost'];
    const lines = [head.join(',')];
    rollupSorted.forEach(r => {
      const oh = OVERHEAD_SET.has(r.duc); const j = jobIndex[r.duc];
      const cust = oh ? ((OVERHEAD.find(o=>o[0]===r.duc)||[])[1] || '') : (j ? j.cust : 'not a live job');
      lines.push([r.duc, cust, r.units.size, r.days, r.cost.toFixed(2)].map(csvCell).join(','));
    });
    lines.push(['TOTAL','','','', weekTotal.toFixed(2)].map(csvCell).join(','));
    return lines.join('\n');
  };
  const downloadWeekRollupCsv = () => {
    const name = `Consolidated Weekly Report ${wkStart}.csv`;
    const blob = new Blob(['﻿' + weekRollupCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };
  // #FLEET_WEEKROLLUP_SIMPLECSV_V1 — Bill 2026-08-24: "i also need a weekly roll up for equipment
  // cost so i can load in my weekly" — sent a screenshot of the pivot he builds by hand every week:
  // three columns, Work Date / UNIT / COST, one row per DUC (his "UNIT" = the DUC job code, same
  // rows as the roll-up above) plus the A-IDLE/A-OH/A-SHOP overhead rows, same Work Date repeated
  // on every row for the week. This mirrors that exact shape so it can be pasted straight into his
  // weekly workbook without reformatting. Work Date = the Monday this app keys the week off of
  // (wkStart) — flag to Bill if his pivot's date means something else (e.g. week-ending Sunday).
  const weekRollupSimpleCsv = () => {
    const [y, m, d] = wkStart.split('-');
    const workDate = `${m}/${d}/${y.slice(2)}`;
    const head = ['Work Date', 'UNIT', 'COST'];
    const lines = [head.join(',')];
    rollupSorted.forEach(r => {
      lines.push([workDate, r.duc, r.cost.toFixed(2)].map(csvCell).join(','));
    });
    return lines.join('\n');
  };
  const downloadWeekRollupSimpleCsv = () => {
    const name = `Weekly Equipment Cost ${wkStart}.csv`;
    const blob = new Blob(['﻿' + weekRollupSimpleCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };
  const emailCsv = () => {
    const name = downloadCsv();
    const body = [
      `DAKOTA DIRECTIONAL DRILLING — Daily Inventory`, `${dayISO}`, '',
      `${dayFilled} of ${(active||[]).length} units allocated${unassigned ? ` (${unassigned} still blank)` : ''}`, '',
      `Jobs         ${money2(split.b.job)}  (${split.pct(split.b.job)}%)`,
      `Idle         ${money2(split.b['A-IDLE'])}  (${split.pct(split.b['A-IDLE'])}%)`,
      `Overhead     ${money2(split.b['A-OH'])}  (${split.pct(split.b['A-OH'])}%)`,
      `Shop         ${money2(split.b['A-SHOP'])}  (${split.pct(split.b['A-SHOP'])}%)`,
      `─────────────────────────────`,
      `DAILY TOTAL  ${money2(split.allocated)}`, '',
      `CSV "${name}" has been saved to your Downloads folder — attach it to this message.`,
    ].join('\n');
    const to = dist.filter(Boolean).join(',');
    window.location.href = 'mailto:' + encodeURIComponent(to).replace(/%40/g,'@').replace(/%2C/g,',')
      + '?subject=' + encodeURIComponent(`Daily Inventory ${dayISO}`) + '&body=' + encodeURIComponent(body);
  };
  // The distribution list is stored, not retyped every morning. Kept deliberately dumb — a list of
  // addresses in one key — because the moment it needs groups or opt-outs it belongs in Outlook.
  const editDist = async () => {
    const raw = window.prompt(
      'Daily Inventory distribution list.\n\nOne address per line, or comma separated.\nThese are pre-filled on the To: line when you press Email CSV.',
      dist.join('\n'));
    if (raw == null) return;
    const next = [...new Set(raw.split(/[\s,;]+/).map(s=>s.trim()).filter(s => s.includes('@')))];
    setDist(next);
    try { await ss('fleet-dist-list', next); } catch (e) { alert('Could not save the list: ' + (e.message||e)); }
  };
  // #FLEET_HOLIDAY_V1 — same dumb-storage pattern as the dist list: Owner/Fleet keep this current by
  // hand, no deploy needed to add next year's dates.
  const editHolidays = async () => {
    const raw = window.prompt(
      'Yard holidays (dates Daily Inventory is not worked).\n\nOne date per line, YYYY-MM-DD.\nCopy Prev bridges past these instead of coming up empty.',
      holidays.join('\n'));
    if (raw == null) return;
    const next = [...new Set(raw.split(/[\s,;]+/).map(s=>s.trim()).filter(s => /^\d{4}-\d{2}-\d{2}$/.test(s)))].sort();
    setHolidays(next);
    try { await ss('fleet-holidays', next); } catch (e) { alert('Could not save the list: ' + (e.message||e)); }
  };

  // ── master writes ──────────────────────────────────────────────────────────
  // #FLEET_ADDGATE_V1 — needsFollowUp/addedAt/addedBy are new columns; merged onto whatever `cols`
  // already is (not just the fallback default) so they land even on an install that already has a
  // persisted column list from before this feature existed.
  // #FLEET_ASSETMGMT_V1 (2026-09-11) — Bill: GPS/Asset-tracker serial number, the two impac Fleet
  // telematics bundle costs (GPS/Camera vs. unpowered Asset Tracker — see cost email), and a single
  // start date per unit for the shared 3-year device life (placeholder 08/01/26 until real install
  // dates are tracked — see TELEM_DEFAULT_START below).
  const NEW_MASTER_COLS = ['needsFollowUp','addedAt','addedBy','docs','cameraStatus','gpsStatus',
    'gpsSerial','gpsCamCost','assetCost','telemStart',
    // #ASSETLEDGER_V1 (2026-09-16) - same new fields as the `need` backfill list above.
    'origin','insuredValue','cashLoan','addedToIns','addedToICRental','addedInQB','invoiceAttached',
    'paymentRecd','removedFromIns','removedFromICRental','removedFromQB','billOfSaleAttached','folderMoved',
    'arrival','decommission','decommissionPending','valueAdds',
    'hrsBaseline','hrsBaselineDate'];  // #FLEET_EQUIPFILES_V1 docs[] + #FLEET_CAMGPS_V1 camera/gps status, both added Sep 8; #FLEET_YTD_BASELINE_V1 baseline fields added Sep 22
  // #FLEET_EQUIPFILES_V1 (2026-09-08) — Bill: "need a place to add Invoices, records, inspections to
  // these Equipment files.. similar to emp files." Same private-bucket + signed-URL pattern as
  // HRModule's emp-files (#EMPFILE_V2) — a private 'equip-files' Supabase Storage bucket, RLS gated on
  // dope_tab_access('equipment') so only Owner/Fleet (masterEdit) can add/remove, anyone with Fleet
  // view access can open. Simpler than HR's named-slot tile system on purpose: equipment doesn't have
  // fixed required documents the way an employee file does — this is a flat, tagged attachment list
  // (Invoice / Spec / Inspection / Manual / Photo / Other) per unit, stored as a `docs[]` array on the
  // Master row itself (appended to the packed-row schema; older rows simply have no docs yet).
  const EQUIP_BUCKET = 'equip-files';
  const _equipHdr = async (extra) => {
    const tok = getToken ? await getToken() : null;
    if (!tok) throw new Error('Not signed in — please refresh and sign in again.');
    return { apikey: supaKey, Authorization: `Bearer ${tok}`, ...(extra || {}) };
  };
  // #FLEET_EQUIPFILES_V2 (2026-09-08) — Bill: "will it compress files?" Same recipe as HR's
  // emp-files (#EMPFILE_V3): downsize to a 1600px longest edge, re-encode as JPEG q0.82. Falls back
  // to the original file untouched if it isn't an image, if compression errors, or if the result
  // somehow isn't smaller (a already-small photo, e.g.).
  const compressImage = async (file) => {
    try {
      if (!file || !String(file.type || '').startsWith('image/')) return file;
      const bmp = await createImageBitmap(file);
      const MAX = 1600; let w = bmp.width, h = bmp.height;
      if (w > MAX || h > MAX) { const sc = Math.min(MAX / w, MAX / h); w = Math.round(w * sc); h = Math.round(h * sc); }
      const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
      cv.getContext('2d').drawImage(bmp, 0, 0, w, h);
      const blob = await new Promise(res => cv.toBlob(res, 'image/jpeg', 0.82));
      if (!blob || blob.size >= file.size) return file;
      return new File([blob], (file.name || 'photo').replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch { return file; }
  };
  // #FLEET_EQUIPFILES_V3 (2026-09-08) — MIME lookup by extension. Some OneDrive/network-synced
  // files reach the browser with file.type === '' (MIME sniff fails), which fell back to
  // 'application/octet-stream' — NOT in the bucket's allowed_mime_types list, so Storage rejected
  // the upload with a plain 400 ("Upload failed (400)."). Trust file.type only when it's a real,
  // non-generic value; otherwise look it up by extension.
  const EQUIP_MIME_BY_EXT = { jpg:'image/jpeg', jpeg:'image/jpeg', png:'image/png', webp:'image/webp',
    heic:'image/heic', gif:'image/gif', pdf:'application/pdf', doc:'application/msword',
    docx:'application/vnd.openxmlformats-officedocument.wordprocessingml.document', xls:'application/vnd.ms-excel',
    xlsx:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', txt:'text/plain' };
  // #FLEET_EQUIPFILES_V4 — filename -> tag hints for guessEquipDocKind (checked in order, first match
  // wins). Covers the titling paperwork Bill's actually attaching (trailers/trucks) plus the original
  // set. 'Manual' checked before 'Inspection' so "Inspection Manual" style names land as Manual.
  const EQUIP_DOC_KIND_HINTS = [
    [/bill\s*of\s*sale/i, 'Bill of sale'],
    [/registration/i, 'Registration'],
    [/title/i, 'Title'],
    [/plate/i, 'License plate'],
    [/manual/i, 'Manual'],
    [/inspect/i, 'Inspection'],
    [/invoice/i, 'Invoice'],
    [/spec/i, 'Spec sheet'],
  ];
  const uploadEquipFile = async (file, unit) => {
    if (!supaUrl || !supaKey) throw new Error('Storage not configured');
    if (file && file.size > 25 * 1024 * 1024) throw new Error('File is over the 25 MB limit.');
    const ext = (String(file.name || '').split('.').pop() || 'bin').toLowerCase().replace(/[^a-z0-9]/g, '');
    const contentType = (file.type && file.type !== 'application/octet-stream') ? file.type : (EQUIP_MIME_BY_EXT[ext] || file.type || 'application/octet-stream');
    const path = `${(unit || 'misc').toString().replace(/[^a-zA-Z0-9_-]/g, '')}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const res = await fetch(`${supaUrl}/storage/v1/object/${EQUIP_BUCKET}/${path}`, {
      method: 'POST',
      headers: await _equipHdr({ 'Content-Type': contentType, 'x-upsert': 'true' }),
      body: file,
    });
    if (!res.ok) {
      let detail = '';
      try { const j = await res.json(); detail = (j && (j.message || j.error)) ? ' — ' + (j.message || j.error) : ''; } catch {}
      const msg = res.status === 403 ? 'Upload blocked — your role does not have Fleet edit access.'
        : res.status === 413 ? 'File too large for the bucket limit (25 MB).'
        : 'Upload failed (' + res.status + ')' + detail + '.';
      throw new Error(msg);
    }
    return { path, name: file.name, type: contentType, size: file.size || 0, at: new Date().toISOString() };
  };
  const deleteEquipFile = async (path) => {
    if (!supaUrl || !supaKey || !path) return;
    try { await fetch(`${supaUrl}/storage/v1/object/${EQUIP_BUCKET}/${path}`, { method: 'DELETE', headers: await _equipHdr() }); } catch {}
  };
  const _equipSignCache = useRef({});
  const equipSignedUrl = async (path, secs) => {
    if (!path || !supaUrl) return '';
    const ttl = secs || 3600;
    const hit = _equipSignCache.current[path];
    if (hit && hit.exp > Date.now() + 60000) return hit.url;
    try {
      const res = await fetch(`${supaUrl}/storage/v1/object/sign/${EQUIP_BUCKET}/${path}`, {
        method: 'POST',
        headers: await _equipHdr({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ expiresIn: ttl }),
      });
      if (!res.ok) return '';
      const d = await res.json();
      const url = d && d.signedURL ? `${supaUrl}/storage/v1${d.signedURL}` : '';
      if (url) _equipSignCache.current[path] = { url, exp: Date.now() + ttl * 1000 };
      return url;
    } catch { return ''; }
  };
  // #FLEET_MASTER_LOG_V1 (2026-10-06) — Bill: Bob owns this file now and adds his own units, so "track changes
  // made". Every write of the Master goes through persistMaster, so the trail is taken there: what THIS tab had
  // before the save against what it is about to write. One line per unit touched — added / edited (field, from,
  // to) / retired / returned to active / renumbered / files added or removed — with who and when, kept in
  // fleet-master-log. A save that touches more than 40 units (an import, a category rename) is one summary line.
  // Nothing in the log is editable from the app.
  const MLOG_LABELS = { cat:'Category', desc:'Description', vin:'Serial / VIN', ducDay:'Daily rate', lifeMo:'Life (mo)', purDate:'Purchase date',
    purAmt:'Purchase $', finAmt:'Financed $', finCo:'Finance co', payment:'Payment', maturity:'Maturity', licPlate:'Plate', licState:'Plate state',
    tagId:'Toll tag', notes:'Notes', docs:'Files', cameraStatus:'Camera', gpsStatus:'GPS', gpsSerial:'GPS S/N', gpsCamCost:'GPS/Cam $/mo',
    assetCost:'Asset tracker $/mo', telemStart:'Telematics start', hrsBaseline:'Baseline reading', hrsBaselineDate:'Baseline date',
    saleDate:'Sold / retired date', saleAmt:'Sale $', saleTo:'Sold to', rentalType:'Rental type', rentalVendor:'Rental vendor', monthlyRate:'Monthly rate',
    weeklyRate:'Weekly rate', rentalStart:'Rental start', rentalDue:'Rental due', needsFollowUp:'Needs info flag', inspDone:'Inspection done',
    inspDue:'Inspection due', insuredValue:'Insured value', arrival:'Arrival checklist', decommission:'Decommission checklist', valueAdds:'Value adds' };
  const mlogText = (v) => { if (v == null || v === '' || v === false) return ''; if (typeof v === 'object') { try { return JSON.stringify(v); } catch { return ''; } } return String(v); };
  const masterChanges = (fields, prevA, prevR, nextA, nextR) => {
    const index = (act, ret) => {
      const m = {}, n = {};
      const put = (r, where) => { if (!r) return; const uk = unitKey(r.unit); if (!uk) return; n[uk] = (n[uk]||0) + 1; m[n[uk] > 1 ? `${uk}#${n[uk]}` : uk] = { r, where }; };
      (act||[]).forEach(r => put(r, 'active')); (ret||[]).forEach(r => put(r, 'retired'));
      return m;
    };
    const before = index(prevA, prevR), after = index(nextA, nextR);
    const diffRow = (a, b) => {
      const ch = [];
      fields.forEach(f => {
        if (f === 'unit' || f === 'status' || f === 'addedAt' || f === 'addedBy' || f === 'origin') return;
        const va = a ? a[f] : null, vb = b ? b[f] : null;
        if (mlogText(va) === mlogText(vb)) return;
        if (f === 'docs') {
          const nm = (v) => (Array.isArray(v) ? v : []).map(d => String((d && d.name) || '')).filter(Boolean);
          const A = nm(va), B = nm(vb); const add = B.filter(z => !A.includes(z)), rem = A.filter(z => !B.includes(z));
          ch.push({ f, a: rem.length ? `removed ${rem.join(', ')}`.slice(0, 140) : '', b: add.length ? `added ${add.join(', ')}`.slice(0, 140) : (rem.length ? '' : 'updated') });
          return;
        }
        if ((va != null && typeof va === 'object') || (vb != null && typeof vb === 'object')) { ch.push({ f, a:'', b:'updated' }); return; }
        ch.push({ f, a: mlogText(va).slice(0, 100), b: mlogText(vb).slice(0, 100) });
      });
      return ch;
    };
    const out = [];
    const gone = Object.keys(before).filter(k => !after[k]);
    const fresh = Object.keys(after).filter(k => !before[k]);
    const used = new Set();
    fresh.forEach(k => {
      const b = after[k]; const vin = String(b.r.vin||'').trim().toUpperCase();
      // a unit number changed: the old number disappears and a new one shows up carrying the same VIN (or it is the only row that moved)
      const g = (vin && gone.find(x => !used.has(x) && String(before[x].r.vin||'').trim().toUpperCase() === vin)) || (gone.length === 1 && fresh.length === 1 ? gone[0] : null);
      if (g) { used.add(g); out.push({ unit: b.r.unit, action:'renumbered', from: before[g].r.unit, changes: diffRow(before[g].r, b.r) }); }
      else out.push({ unit: b.r.unit, action: b.where === 'retired' ? 'added (retired)' : 'added', changes: ['cat','desc','vin'].filter(f => String(b.r[f]||'').trim()).map(f => ({ f, a:'', b: String(b.r[f]).slice(0, 100) })) });
    });
    gone.filter(k => !used.has(k)).forEach(k => out.push({ unit: before[k].r.unit, action:'removed', changes: [] }));
    Object.keys(after).forEach(k => {
      const a = before[k], b = after[k]; if (!a) return;
      const ch = diffRow(a.r, b.r);
      const moved = a.where !== b.where;
      if (!moved && !ch.length) { if (String(a.r.unit) !== String(b.r.unit)) out.push({ unit: b.r.unit, action:'renumbered', from: a.r.unit, changes: [] }); return; }
      out.push({ unit: b.r.unit, action: moved ? (b.where === 'retired' ? 'retired' : 'returned to active') : 'edited', changes: ch });
    });
    if (out.length > 40) {
      const n = {}; out.forEach(e => (e.changes||[]).forEach(c => { n[c.f] = (n[c.f]||0) + 1; }));
      return [{ unit: `${out.length} units`, action:'bulk change', units: out.slice(0, 60).map(e => e.unit),
        changes: Object.keys(n).sort((x, y) => n[y] - n[x]).slice(0, 8).map(f => ({ f, a:'', b: `${n[f]} unit${n[f]===1?'':'s'}` })) }];
    }
    return out;
  };
  const openMlog = async (unit) => {
    setMlogQ(unit || ''); setMlogOpen(true);
    try { const lg = await sg('fleet-master-log'); setMasterLog((lg && Array.isArray(lg.entries)) ? lg.entries : []); } catch { setMasterLog([]); }
  };
  const persistMaster = async (nextActive, nextRetired) => {
    const base = cols.length ? cols : ['unit','cat','desc','vin','ducDay','lifeMo','purDate','purAmt',
      'finAmt','finCo','payment','rate','maturity','saleAmt','saleDate','saleTo','notes','tagId','licState',
      'licPlate','inspDone','inspDue','rentalType','rentalVendor','monthlyRate','weeklyRate','rentalStart','rentalDue'];
    const c = [...base, ...NEW_MASTER_COLS.filter(x => !base.includes(x))];
    let mlog = [];   // #FLEET_MASTER_LOG_V1 — taken before the state moves
    try { mlog = masterChanges(c, active, retired, nextActive, nextRetired); } catch { mlog = []; }
    setActive(nextActive); setRetired(nextRetired); setCols(c);
    // #FLEET_TELEM_MERGE_V1 (2026-10-06) — this writes the WHOLE Master from this tab's memory. Now that the
    // Camera / GPS list is worked from the mechanic's dash, a Fleet tab left open since morning would put every
    // one of his updates back to what it was the moment anyone saved anything here. So: re-read first, and for
    // the three telematics fields keep the server's value wherever THIS tab did not change that field itself.
    // Rows are written merged; this tab's own rows are left as they are (the edit form finds its row by object).
    let outActive = nextActive;
    try {
      const fresh = await sg('fleet-equipment');
      if (fresh && Array.isArray(fresh.cols) && Array.isArray(fresh.active)) {
        const theirs = telemSnap(expand(fresh.cols, fresh.active));
        const seen = telemBaseRef.current || {};
        outActive = nextActive.map(r => {
          const uk = unitKey(r.unit); const t = theirs[uk]; const b = seen[uk];
          if (!t || !b) return r;
          let out = r;
          TELEM_FIELDS.forEach(f => { const mine = String(r[f]==null?'':r[f]); if (mine === b[f] && t[f] !== mine) { if (out === r) out = { ...r }; out[f] = t[f]; } });
          return out;
        });
      }
    } catch {}
    telemBaseRef.current = telemSnap(nextActive);
    const blob = { v:1, cols:c, active: packRows(c, outActive), retired: packRows(c, nextRetired) };
    await ss('fleet-equipment', blob);
    // #FLEET_MASTER_LOG_V1 — the Master is saved; the trail is best effort and must never fail the save.
    if (mlog.length) {
      try {
        const lg = await sg('fleet-master-log');
        const entries = (lg && Array.isArray(lg.entries)) ? lg.entries : [];
        const at = new Date().toISOString(); const by = upn || String(userRole||'');
        const nextLog = [...entries, ...mlog.map((e, i) => ({ id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2,6)}`, at, by, ...e }))].slice(-1500);
        await ss('fleet-master-log', { v:1, entries: nextLog });
        setMasterLog(nextLog);
      } catch {}
    }
  };
  // #FLEET_SHOPDASH_V1 — Camera / GPS status from the mechanic's dash. Not persistMaster: this re-reads the
  // Master, changes those fields on ONE unit and writes it back, so it never carries this screen's copy of the
  // other 300 rows. Open to the shop staff even at Fleet = View in the Access Matrix (ShopDash decides who is
  // assigned the list); every change lands in fleet-telem-log with who and when.
  const patchUnitTelem = async (unit, patch, who) => {
    const clean = {};
    TELEM_FIELDS.forEach(f => { if (patch && patch[f] !== undefined) clean[f] = String(patch[f]==null?'':patch[f]).trim(); });
    if (!Object.keys(clean).length) return;
    let fresh = null;
    try { fresh = await sg('fleet-equipment'); } catch { fresh = null; }
    if (!fresh || !Array.isArray(fresh.cols) || !Array.isArray(fresh.active)) throw new Error('could not read the Equipment Master just now — nothing was changed');
    const c = [...fresh.cols, ...NEW_MASTER_COLS.filter(x => !fresh.cols.includes(x))];
    const act = expand(fresh.cols, fresh.active);
    const uk = unitKey(unit);
    const cur = act.find(r => unitKey(r.unit) === uk);
    if (!cur) throw new Error(`${unit} is not on the Equipment Master`);
    const changes = TELEM_FIELDS.filter(f => f in clean && String(cur[f]==null?'':cur[f]) !== clean[f]).map(f => ({ field:f, from:String(cur[f]==null?'':cur[f]), to:clean[f] }));
    if (changes.length) {
      const nextAct = act.map(r => r === cur ? { ...r, ...clean } : r);
      await ss('fleet-equipment', { v:1, cols:c, active: packRows(c, nextAct), retired: packRows(c, expand(fresh.cols, fresh.retired)) });
      try {
        const lg = await sg('fleet-telem-log');
        const entries = (lg && Array.isArray(lg.entries)) ? lg.entries : [];
        const at = new Date().toISOString();
        await ss('fleet-telem-log', { v:1, entries: [...entries, ...changes.map(ch => ({ unit: cur.unit, ...ch, by: (who && who.by) || upn || '', byName: (who && who.byName) || '', at }))].slice(-3000) });
      } catch {}
      // #FLEET_SHOPDASH_SCOPE_V1 — the same change on the Equipment Master's own change log (fleet-master-log, the entry
      // shape masterChanges writes), so a Camera / GPS edit made from a mechanic's dash shows with every other edit to the file.
      try {
        const ml = await sg('fleet-master-log');
        const mentries = (ml && Array.isArray(ml.entries)) ? ml.entries : [];
        await ss('fleet-master-log', { v:1, entries: [...mentries, { id: `${Date.now()}-t-${Math.random().toString(36).slice(2,6)}`, at: new Date().toISOString(),
          by: (who && who.by) || upn || '', unit: cur.unit, action:'edited', via:'shop dash', changes: changes.map(ch => ({ f: ch.field, a: ch.from, b: ch.to })) }].slice(-1500) });
      } catch {}
    }
    telemBaseRef.current = { ...(telemBaseRef.current||{}), [uk]: { ...((telemBaseRef.current||{})[uk]||{}), ...clean } };
    setActive(list => (list||[]).map(r => unitKey(r.unit) === uk ? { ...r, ...clean } : r));
  };
  // #FLEET_GEOTAB_HISTORY_V1 (2026-09-22) — one-time backfill: fills Baseline Reading/Date
  // (hrsBaseline/hrsBaselineDate, #FLEET_YTD_BASELINE_V1) from the historical Geotab pull, for any
  // Geotab-covered unit that doesn't already have one. NEVER overwrites a value already there —
  // hand-typed or from an earlier run of this same backfill — so it's safe to click more than
  // once (e.g. after re-running the history pull with a wider window). RIGS/no-metric buckets are
  // skipped the same way the weekly auto-fill already skips them.
  const backfillBaselines = async () => {
    if (!active || !hrsGeotabHistory) return;
    const byVin = {};
    (hrsGeotabHistory.devices || []).forEach(d => { const v = vinClean(d.vin); if (v) byVin[v] = d; });
    let filled = 0, skippedHasBaseline = 0, noMatch = 0, noValue = 0;
    const next = active.map(r => {
      if (String(r.hrsBaseline || '').trim() !== '') { skippedHasBaseline++; return r; }
      const bucket = bucketOf(r.cat, hrsBucketMap);
      const metric = HRS_BUCKET_METRIC[bucket];
      if (bucket === 'rigs' || !metric) return r;
      const device = byVin[vinClean(r.vin)];
      if (!device) { noMatch++; return r; }
      const val = metric === 'mi' ? device.baselineOdometerMiles : device.baselineEngineHours;
      const at = metric === 'mi' ? device.baselineOdometerAt : device.baselineEngineHoursAt;
      if (val == null || !at) { noValue++; return r; }
      filled++;
      return { ...r, hrsBaseline: String(val), hrsBaselineDate: fmtDate(at) };
    });
    if (!filled) {
      setHrsBackfillMsg(`Nothing to fill — ${skippedHasBaseline} already had a baseline, ${noMatch} had no Geotab match, ${noValue} had no historical value yet.`);
      setTimeout(() => setHrsBackfillMsg(''), 6000);
      return;
    }
    setHrsBusy('Backfilling baselines…');
    await persistMaster(next, retired);
    setHrsBusy('');
    setHrsBackfillMsg(`Filled baseline for ${filled} unit${filled===1?'':'s'} — ${skippedHasBaseline} already set, ${noMatch} no Geotab match, ${noValue} no historical value yet.`);
    setTimeout(() => setHrsBackfillMsg(''), 8000);
  };
  // #FLEET_GEOTAB_HISTORY_V1 (2026-09-22) — bulk version of the baseline backfill above: instead of
  // filling just ONE anchor point per unit, this walks every Monday the historical pull actually has
  // a reading for and writes it into that week's Weekly Inventory row — real dated "period in time"
  // data, the same as if Bob had typed each week by hand, so MTD/QTD/YTD see real weekly deltas
  // instead of one big jump landing entirely on whichever week happens to be open. NEVER overwrites
  // a reading already on file for a unit/week — hand-typed, live auto-fill, or an earlier run of
  // this same backfill — so it's always safe to re-run after a wider history pull. Skips a cell on
  // the currently-open week if Bob has an unsaved edit sitting in it, same guard the live auto-fill
  // uses, so it can't clobber something he's mid-typing.
  const backfillWeeklyReadings = async () => {
    if (!active || !hrsGeotabHistory) return;
    const byVin = {};
    (hrsGeotabHistory.devices || []).forEach(d => { const v = vinClean(d.vin); if (v) byVin[v] = d; });
    const nextWeeks = { ...hrsWeeks };
    const cellPatch = {};   // #FLEET_HRS_MERGE_WRITE_V1
    let filled = 0, noMatch = 0;
    const weeksTouched = new Set();
    const matchedUnits = new Set();
    active.forEach(r => {
      const bucket = bucketOf(r.cat, hrsBucketMap);
      const metric = HRS_BUCKET_METRIC[bucket];
      if (bucket === 'rigs' || !metric) return;
      const device = byVin[vinClean(r.vin)];
      if (!device) { noMatch++; return; }
      const seriesObj = metric === 'mi' ? device.weeklyOdometerMiles : device.weeklyEngineHours;
      if (!seriesObj) return;
      const uk = unitKey(r.unit);
      matchedUnits.add(uk);
      Object.keys(seriesObj).forEach(mon => {
        const val = seriesObj[mon];
        if (val == null) return;
        if (mon === hrsWkStart && hrsEdits[uk] && hrsEdits[uk].reading !== undefined) return;   // don't clobber an in-progress edit
        const wk = nextWeeks[mon] || {};
        if (wk[uk] && wk[uk].reading != null) return;   // never overwrite what's already there
        nextWeeks[mon] = { ...wk, [uk]: { reading: val, source:'geotab', enteredAt: new Date().toISOString(), enteredBy:'geotab-history-bulk' } };
        (cellPatch[mon] = cellPatch[mon] || {})[uk] = nextWeeks[mon][uk];
        filled++;
        weeksTouched.add(mon);
      });
    });
    if (!filled) {
      setHrsBackfillMsg(`Nothing to fill — ${matchedUnits.size} matched unit${matchedUnits.size===1?'':'s'} already had every available week on file, ${noMatch} no Geotab match.`);
      setTimeout(() => setHrsBackfillMsg(''), 6000);
      return;
    }
    setHrsBusy('Backfilling weekly history…');
    try {
      await writeHrsCells(cellPatch, true);   // #FLEET_HRS_MERGE_WRITE_V1
      setHrsBackfillMsg(`Filled ${filled} weekly reading${filled===1?'':'s'} across ${weeksTouched.size} week${weeksTouched.size===1?'':'s'} for ${matchedUnits.size} unit${matchedUnits.size===1?'':'s'} — ${noMatch} no Geotab match.`);
    } catch (e) {
      setHrsBackfillMsg('Could not save: ' + (e.message||e));
    }
    setHrsBusy('');
    setTimeout(() => setHrsBackfillMsg(''), 8000);
  };
  // #FLEET_VIN_TRIAGE_V1 — tab-separated so it pastes straight into Excel or an email to Geotab/impac.
  const copyVinIssues = async () => {
    const header = 'Unit\tDescription\tCategory\tVIN on file\tIssue';
    const rows = hrsVinIssues.map(v => `${v.unit}\t${v.desc}\t${v.cat}\t${v.vin}\t${v.reason==='no-vin' ? 'No VIN on Master' : 'VIN on file, no Geotab match'}`);
    try {
      await navigator.clipboard.writeText([header, ...rows].join('\n'));
      setHrsBackfillMsg(`Copied ${hrsVinIssues.length} unit${hrsVinIssues.length===1?'':'s'} to clipboard.`);
    } catch (e) { alert('Could not copy: ' + (e.message||e)); }
    setTimeout(() => setHrsBackfillMsg(''), 4000);
  };
  // #FLEET_UNIT_RENAME_V1 (2026-10-02) — Bill: "the Master should handle [it] moving forward." Nothing in
  // Fleet has a hidden ID — Daily, weekly hours, the board, service, shop, toll/inspection and the PO logs
  // all find a unit by its NUMBER — so correcting a number here used to change this row and strand the
  // rest (25-0028 → 24-0028: blank on Daily, hours and board tile gone, PO repair cost "—"). Changing the
  // number on an EXISTING row now carries its history with it (unitRename.js has the per-key rules).
  //   unitMentionKeys — one server-side search for every stored key that mentions the unit, so the move
  //     only opens what it needs (and can name anything it found but does not own). null = search not
  //     available → fall back to the known list.
  //   carryUnitRename — 'none' (number unchanged, or nothing filed under it: save as usual), 'cancel'
  //     (stop, nothing written) or 'moved' (history carried: caller saves the Master row, then reloads so
  //     this tab cannot write the old number back from memory).
  // History moves FIRST and the Master row LAST: if it stops part-way the Master still shows the old
  // number, and pressing Save again simply finishes the job (what already moved is skipped).
  const unitMentionKeys = async (unit) => {
    const k = unitKey(unit), d = unitNorm(unit);
    if (!supaUrl || !supaKey || !/^[0-9A-Z]{4,}$/.test(k) || !/^[0-9A-Z-]{4,}$/.test(d)) return null;
    // the key form is always a whole JSON string ("250028"); the display form may sit inside typed text
    const pats = [`like.*${encodeURIComponent('"' + k + '"')}*`];
    pats.push(`ilike.*${encodeURIComponent(d)}*`);
    try {
      const tok = getToken ? await getToken() : null;
      const lists = await Promise.all(pats.map(async (pat) => {
        const res = await fetch(`${supaUrl}/rest/v1/app_storage?select=key&value=${pat}`, { headers: { apikey: supaKey, Authorization: `Bearer ${tok || supaKey}` } });
        if (!res.ok) throw new Error('search ' + res.status);
        const rows = await res.json();
        if (!Array.isArray(rows)) throw new Error('search shape');
        return rows.map(r => r && r.key).filter(Boolean);
      }));
      return [...new Set([].concat(...lists))];
    } catch { return null; }
  };
  const carryUnitRename = async (orig, u) => {
    const from = orig ? unitNorm(orig.unit) : '';
    if (!from || !unitKey(from) || !unitKey(u) || unitKey(from) === unitKey(u)) return 'none';
    const taken = [ ...(active||[]), ...(retired||[]) ].find(r => r !== orig && unitKey(r.unit) === unitKey(u));
    if (taken) {
      alert(`${u} is already on the master${taken.status === 'retired' ? ' (retired)' : ''}.\n\n${from} cannot be renumbered onto a number that is in use — its history would land on top of the other unit's.`);
      return 'cancel';
    }
    setBusy(`Checking what is filed under ${from}…`);
    // Settle any Daily edit still waiting to autosave so it is not written back under the old number.
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    if (dirty) await persistWeek(week, wkStart);
    const keys = (await unitMentionKeys(from)) || unitRenameFallbackKeys();
    let plan;
    try { plan = await renameUnitEverywhere({ sg, ss, from, to: u, keys, dryRun: true }); }
    catch (e) { setBusy(''); alert(`Could not read ${from}'s history — nothing was changed.\n\n${e.message||e}`); return 'cancel'; }
    setBusy('');
    const lines = plan.areas.map(a => `   •  ${a.label} — ${a.n} ${a.noun}${a.n === 1 ? '' : 's'}`);
    const msg = `Renumber ${from} → ${u}?\n\n`
      + (plan.changed.length ? `Its history moves to the new number:\n${lines.join('\n')}\n` : `Nothing else is filed under ${from} yet — only the Master row changes.\n`)
      + (plan.clash ? `\n${plan.clash} spot${plan.clash === 1 ? '' : 's'} already hold${plan.clash === 1 ? 's' : ''} an entry under ${u} and will be left as ${plan.clash === 1 ? 'it is' : 'they are'}.\n` : '')
      + (plan.leftAlone.length ? `\nAlso mentions ${from}, left as written: ${plan.leftAlone.join(', ')}\n` : '')
      + (plan.changed.length ? `\nThe page reloads when it is done. Anyone else with Fleet open should refresh.` : '');
    if (!window.confirm(msg)) return 'cancel';
    if (!plan.changed.length) return 'none';
    setBusy(`Moving ${from} → ${u}…`);
    try {
      const done = await renameUnitEverywhere({ sg, ss, from, to: u, keys });
      try {   // who renumbered what, when — best effort, never blocks the move
        const log = await sg('fleet-unit-renames');
        const items = (log && Array.isArray(log.items)) ? log.items : [];
        await ss('fleet-unit-renames', { v:1, items: [ ...items, { from, to: u, at: new Date().toISOString(), by: upn || String(userRole||''), areas: done.areas, keys: done.changed } ] });
      } catch { /* the log is a record, not a dependency */ }
    } catch (e) {
      setBusy('');
      alert(`The move stopped part-way: ${e.message||e}\n\nThe Master still says ${from}. Press Save again to finish — what already moved stays moved.`);
      return 'cancel';
    }
    return 'moved';
  };
  const saveUnit = async () => {
    if (!edit) return;
    const u = unitNorm(edit.unit);
    if (!u) { alert('Unit number is required.'); return; }
    const moving = await carryUnitRename(edit._orig, u);   // #FLEET_UNIT_RENAME_V1
    if (moving === 'cancel') return;
    const dupe = (active||[]).some(r => unitKey(r.unit) === unitKey(u) && r !== edit._orig);
    if (dupe && !window.confirm(`${u} already exists on the master.\n\nTwo rows with one unit number means cost and hours split across both. Save anyway?`)) return;
    const rec = { ...edit }; delete rec._orig;
    rec.unit = u;
    // #FLEET_ADDGATE_V1 — a brand-new row (no _orig) gets stamped as needing a follow-up pass
    // (Ashlie's fast-add is expected to be missing VIN/plate/etc. at first). Editing an existing
    // flagged row auto-clears the flag once both VIN and Lic Plate are filled in; "Mark info
    // complete" below covers units that will legitimately never get one of those.
    if (!edit._orig) {
      rec.needsFollowUp = true;
      rec.addedAt = iso(new Date());
      // #FLEET_ADDGATE_V2 — an add from anyone but the Owner role is named, and marked so the Owner Dash asset
      // ledger lists it under "Needs paperwork" until a Purchase entry exists (same path as a Yard-flagged unit).
      const _own = String(userRole||'') === 'Owner';
      rec.addedBy = _own ? 'Owner' : (upn ? String(upn).split('@')[0] : String(userRole||''));
      if (!_own) rec.origin = 'fleet-added';
    } else if (rec.needsFollowUp && String(rec.vin||'').trim() && String(rec.licPlate||'').trim()) {
      rec.needsFollowUp = false;
    }
    const next = edit._orig ? (active||[]).map(r => r === edit._orig ? rec : r) : [ ...(active||[]), rec ];
    setBusy('Saving…'); await persistMaster(next, retired); setBusy(''); setEdit(null);
    if (moving === 'moved') window.location.reload();   // #FLEET_UNIT_RENAME_V1 — drop this tab's copies still keyed to the old number
  };
  // #FLEET_EQUIPFILES_V1 — add/remove touch ONLY the docs[] field on the last-SAVED row
  // (edit._orig), never the in-progress draft in the rest of the edit form. That way dropping a file
  // on a record you're mid-editing can't accidentally commit an unrelated unsaved field change; it
  // persists immediately (no separate Save click) the same way HR's document uploads do, and the
  // draft's other fields are untouched.
  // #FLEET_EQUIPFILES_V2 — takes a FileList/array so a multi-select (photos especially) uploads as
  // one batch: each file compresses/uploads in turn (so "Uploading 2 of 5…" is meaningful) but they
  // all land in a SINGLE persistMaster call at the end, not one save per file — cheaper, and avoids
  // N racing writes to the same row if someone fires off a big multi-select.
  // #FLEET_EQUIPFILES_V4 (2026-09-08) — Bill dropped 6 real titling docs (Bill of Sale, Inspection,
  // License Plate, Registration, Title Application, Title) in one batch and noted he didn't bother
  // changing the tag dropdown per file since "it's in the name of file" — the one dropdown value was
  // applying to the whole batch, so all 6 landed tagged "Invoice." Guess the tag from the filename
  // first; the dropdown is now the FALLBACK for a file whose name doesn't say what it is, not
  // something you have to reset before every drop.
  const guessEquipDocKind = (filename, fallback) => {
    const n = String(filename || '');
    for (const [re, kind] of EQUIP_DOC_KIND_HINTS) { if (re.test(n)) return kind; }
    return fallback;
  };
  const addEquipDoc = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!edit || !edit._orig || !files.length) return;
    const added = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setEquipDocMsg(files.length > 1 ? `Uploading ${i + 1} of ${files.length}…` : 'Uploading…');
        const compressed = await compressImage(files[i]);
        const uploaded = await uploadEquipFile(compressed, edit._orig.unit);
        added.push({ ...uploaded, kind: guessEquipDocKind(files[i].name, equipDocKind) });
      }
      const nextDocs = [...(Array.isArray(edit._orig.docs) ? edit._orig.docs : []), ...added];
      const nextOrig = { ...edit._orig, docs: nextDocs };
      const nextActive = (active||[]).map(r => r === edit._orig ? nextOrig : r);
      await persistMaster(nextActive, retired);
      setEdit(d => (d ? { ...d, docs: nextDocs, _orig: nextOrig } : d));
      setEquipDocMsg(added.length > 1 ? `${added.length} files added.` : '');
      if (added.length > 1) setTimeout(() => setEquipDocMsg(''), 2500);
    } catch (e) {
      // Whatever uploaded before the failure is still real — save it rather than losing it, then report.
      if (added.length) {
        const nextDocs = [...(Array.isArray(edit._orig.docs) ? edit._orig.docs : []), ...added];
        const nextOrig = { ...edit._orig, docs: nextDocs };
        const nextActive = (active||[]).map(r => r === edit._orig ? nextOrig : r);
        try { await persistMaster(nextActive, retired); setEdit(d => (d ? { ...d, docs: nextDocs, _orig: nextOrig } : d)); } catch {}
      }
      setEquipDocMsg((e.message || 'Upload failed.') + (added.length ? ` (${added.length} of ${files.length} saved before the error.)` : ''));
    }
  };
  const removeEquipDoc = async (docToRemove) => {
    if (!edit || !edit._orig || !docToRemove) return;
    if (!window.confirm(`Remove "${docToRemove.name}" from ${edit._orig.unit}? This deletes the file — it can't be undone.`)) return;
    setEquipDocMsg('Removing…');
    try {
      await deleteEquipFile(docToRemove.path);
      const nextDocs = (Array.isArray(edit._orig.docs) ? edit._orig.docs : []).filter(d => d.path !== docToRemove.path);
      const nextOrig = { ...edit._orig, docs: nextDocs };
      const nextActive = (active||[]).map(r => r === edit._orig ? nextOrig : r);
      await persistMaster(nextActive, retired);
      setEdit(d => (d ? { ...d, docs: nextDocs, _orig: nextOrig } : d));
      setEquipDocMsg('');
    } catch (e) { setEquipDocMsg(e.message || 'Remove failed.'); }
  };
  const openEquipDoc = async (doc) => {
    let url = equipDocUrls[doc.path];
    if (!url) { url = await equipSignedUrl(doc.path); if (url) setEquipDocUrls(prev => ({ ...prev, [doc.path]: url })); }
    if (url) window.open(url, '_blank'); else setEquipDocMsg('Could not open that file.');
  };
  // #FLEET_RELATEDRECS_V1 — jump straight into the matching Toll/Inspection record (edit mode if one
  // exists, a pre-filled Add form if it doesn't) instead of sending Bill to the tab to search by hand.
  const jumpToToll = (unit) => {
    const hit = tollByKey[unitKey(unit)];
    setView('toll');
    setQToll(unit);
    setTollEdit(hit ? { ...hit, _orig: hit } : { unit, tagId:'', plate:'', vehInfo: (edit && edit.desc) || '', dateIssued:'', status:'Active' });
  };
  const jumpToInsp = (unit) => {
    const hit = inspByKey[unitKey(unit)];
    setView('inspec');
    setQInsp(unit);
    setInspEdit(hit ? { ...hit, _orig: hit } : { unit, type:'', model:'', use:'', sup:'', lastDate:'', nextDate:'', state:'', license:'' });
  };
  // Retire → the record MOVES to the retired list, mirroring hr-employees → hr-departed.
  // A status flag would mean every downstream view has to remember to filter; a separate
  // list means retired equipment simply isn't there, and the retired list is a real dataset.
  // #FLEET_ARCHIVE_WHERE_V1 — Bill: archiving (sold/traded) needs to capture WHERE too, not just
  // date + amount — who/where it went (buyer, dealer, trade-in against a new unit, etc.).
  // #ASSETLEDGER_V1 (2026-09-16) - if this unit came through Ashlie's Owner Dash disposal entry,
  // saleDate/saleAmt/saleTo are already set and decommissionPending gates the retire until Fleet's
  // checklist (below) is fully checked off and saved. Otherwise this is the original manual path,
  // unchanged, for a unit retired straight out of Fleet without going through her ledger first.
  const retireUnit = async (r) => {
    if (r.decommissionPending && !decomComplete(r.decommission)) {
      alert(`${r.unit} was flagged sold/disposed by Ashlie but the Decommission checklist isn't complete yet.\n\nOpen ${r.unit}, check off every item removed (GPS, Drag Up Tank, Toll Tag, etc.), Save, then Retire.`);
      return;
    }
    let when = r.saleDate, amt = r.saleAmt, to = r.saleTo;
    if (!String(when||'').trim()) {
      when = window.prompt(`Retire ${r.unit}.\n\nDate sold / retired (YYYY-MM-DD):`, iso(new Date()));
      if (when == null) return;
    }
    if (amt === '' || amt == null) {
      const a = window.prompt(`Sale amount for ${r.unit}?  (blank if none)`, '');
      if (a == null) return;
      amt = a.trim() ? num(a) : '';
    }
    if (!String(to||'').trim()) {
      const t = window.prompt(`Sold / traded to — who or where?  (blank if none)`, '');
      if (t == null) return;
      to = t.trim();
    }
    const rec = { ...r, status:'retired', saleDate: String(when).trim(), saleAmt: amt, saleTo: to, decommissionPending:false };
    setBusy('Retiring…');
    await persistMaster((active||[]).filter(x => x !== r), [ rec, ...retired ]);
    setBusy(''); setEdit(null);
  };
  // #ASSETLEDGER_V1 - Jerry's add-only path: a minimal placeholder row, not the full Owner-gated
  // Add. origin:'yard-flagged' marks it so the Owner Dash asset ledger can surface it as a "needs
  // paperwork" reminder for Ashlie once it's sat a while with no matching Purchase entry, and so
  // whoever reviews it can tell at a glance this one didn't come through her ledger first -
  // including the case where it turns out to be a rental (renumbered with the R-prefix by
  // Owner/Fleet afterward, never touches her ledger).
  const saveFlaggedUnit = async () => {
    if (!flagEdit) return;
    const u = unitNorm(flagEdit.unit);
    if (!u) { alert('Unit number is required.'); return; }
    const dupe = (active||[]).some(r => unitKey(r.unit) === unitKey(u));
    if (dupe) { alert(`${u} already exists on the master.`); return; }
    const rec = { unit:u, cat:flagEdit.cat||'', desc:flagEdit.desc||'', vin:flagEdit.vin||'', status:'active',
      origin:'yard-flagged', needsFollowUp:true, addedAt: iso(new Date()), addedBy: String(userRole||'') };
    setBusy('Saving…');
    await persistMaster([ ...(active||[]), rec ], retired);
    setBusy(''); setFlagEdit(null);
  };
  const unretire = async (r) => {
    if (!window.confirm(`Return ${r.unit} to the active master?`)) return;
    const rec = { ...r, status:'active' }; delete rec.saleDate; delete rec.saleAmt; delete rec.saleTo;
    setBusy('…'); await persistMaster([ rec, ...(active||[]) ], retired.filter(x => x !== r)); setBusy('');
  };
  // #FLEET_ADDGATE_V1 — "Mark info complete" dismisses the needs-info flag without requiring
  // VIN/Lic Plate, for units that legitimately will never get one (referenced by the button but
  // never defined — build-breaking no-undef until this landed).
  const markInfoComplete = async (orig) => {
    if (!orig) return;
    const next = (active||[]).map(r => r === orig ? { ...r, needsFollowUp:false } : r);
    setBusy('Saving…'); await persistMaster(next, retired); setBusy(''); setEdit(null);
  };

  // #FLEET_RENTAL_V2 — Bill, 2026-08-21: rentals write straight into the Equipment Master now, so a
  // saved rental is automatically the same record Daily assigns and every cost view reads — no
  // second list to keep in sync. `rentalEdit._orig` (when editing an existing row) is the live Master
  // record itself, same pattern as `edit._orig` on the ordinary unit editor.
  // #FLEET_RENTAL_LIVE_V1 (2026-08-27) — Bill: "Rental Table from Fleet should pump into Owners Dash
  // update Start rental date." Root cause matched #FLEET_INSHOP_LIVE_V1 exactly: fleet-summary.rentalRpo
  // only got republished by an unrelated Daily save or an Idle-90 scan, so an edit here (Start date,
  // vendor, rate…) sat invisible on Owner Dash until one of those happened to run. Every rental write
  // now republishes the digest itself, immediately — merged onto whatever's already in fleet-summary so
  // the idle/shop counts from the last scan aren't clobbered.
  const publishRentalDigest = async () => {
    try { const cur = (await sg('fleet-summary')) || {}; await ss('fleet-summary', { ...cur, rentalRpo: rentalDigest() }); }
    catch { /* best effort — Owner Dash tile catches up on the next scan/save either way */ }
  };
  const saveRentalItem = async () => {
    if (!rentalEdit) return;
    const u = unitNorm(rentalEdit.unit);
    if (!u) { alert('Unit number is required.'); return; }
    const moving = await carryUnitRename(rentalEdit._orig, u);   // #FLEET_UNIT_RENAME_V1 — a placeholder rental number becoming the real one keeps its history
    if (moving === 'cancel') return;
    const dupe = (active||[]).some(r => unitKey(r.unit) === unitKey(u) && r !== rentalEdit._orig);
    if (dupe && !window.confirm(`${u} already exists on the master.\n\nTwo rows with one unit number means cost and hours split across both. Save anyway?`)) return;
    const { monthly, weekly, daily } = rentalRates(rentalEdit.monthlyRate);
    const base = rentalEdit._orig || {};
    const rec = { ...base,
      unit: u,
      cat: String(base.cat||'').trim() || 'Rental/RPO',
      desc: rentalEdit.desc || '',
      vin: String(rentalEdit.vin || '').trim().toUpperCase(),   // #FLEET_RENTAL_VIN_V1
      rentalType: rentalEdit.rentalType || 'Rental',
      rentalVendor: rentalEdit.rentalVendor || '',
      monthlyRate: monthly, weeklyRate: weekly, ducDay: daily,
      rentalStart: rentalEdit.rentalStart || '',
      rentalDue: rentalEdit.rentalDue || '',
      notes: rentalEdit.notes || '',
      status: 'active',
    };
    const next = rentalEdit._orig ? (active||[]).map(r => r === rentalEdit._orig ? rec : r) : [ ...(active||[]), rec ];
    setBusy('Saving…'); await persistMaster(next, retired); await publishRentalDigest(); setBusy(''); setRentalEdit(null);
    if (moving === 'moved') window.location.reload();   // #FLEET_UNIT_RENAME_V1
  };
  // Returned to vendor — moves to Retired, same mechanism as an owned unit's Retire (#FLEET_NODELETE_V1
  // still applies: a rental that's been on jobs has real cost history behind it now, so it doesn't
  // vanish, it closes out). "Sold/traded to" is repurposed to record who it went back to.
  const returnRentalItem = async (r) => {
    const when = window.prompt(`Mark ${r.unit} returned to vendor.\n\nReturn date (YYYY-MM-DD):`, iso(new Date()));
    if (when == null) return;
    const rec = { ...r, status:'retired', saleDate: String(when).trim(), saleAmt:'', saleTo: r.rentalVendor || '' };
    setBusy('…');
    await persistMaster((active||[]).filter(x => x !== r), [ rec, ...retired ]);
    await publishRentalDigest();
    setBusy(''); setRentalEdit(null);
  };
  // RPO matured — it's already a real Master record (that's the whole point of #FLEET_RENTAL_V2), so
  // "moving to Master" now just means clearing the rental flags: the unit stops showing the red "R"
  // and drops off the Rental/RPO tab and Owner Dash tile, but keeps its current daily rate and all its
  // Daily/cost history untouched. Bill: "RPO Items move to purchase after period has matured."
  const transitionRentalToMaster = async (x) => {
    if (!window.confirm(`Mark ${x.unit} as matured — move it from Rental/RPO into ordinary owned equipment?\n\nIt keeps its current daily rate. Fill in purchase details (VIN, purchase price, financing) from the Master tab when you have them.`)) return;
    const rec = { ...x, rentalType:'', rentalVendor:'', monthlyRate:'', weeklyRate:'', rentalStart:'', rentalDue:'' };
    setBusy('Moving…');
    await persistMaster((active||[]).map(r => r === x ? rec : r), retired);
    await publishRentalDigest();
    setBusy(''); setRentalEdit(null);
  };
  // #EQUIP_LIST_EXPORT_V1 (2026-10-27) — Bill: "pull a CSV or DAKOTA letterhead for Equipment we have with UNIT Number,
  //   Category, Description maybe VIN. Estimating asking for submittal and could be good to have for insurance later."
  //   Uses whatever the Master is filtered to (category / search); all active units when unfiltered. No $ on it.
  const equipListRows = () => {
    const ql = String(qM || '').trim().toLowerCase();
    return (active || []).filter(r => (catF === 'all' || String(r.cat || '') === catF) && (!ql || `${r.unit} ${r.cat} ${r.desc} ${r.vin || ''}`.toLowerCase().includes(ql)))
      .slice().sort((a, b) => String(a.cat || '').localeCompare(String(b.cat || '')) || String(a.unit || '').localeCompare(String(b.unit || '')));
  };
  const downloadEquipCsv = () => {
    const rows = equipListRows();
    const lines = [['Unit #', 'Category', 'Description', 'Serial / VIN', 'Plate'].join(',')];
    rows.forEach(r => lines.push([r.unit, r.cat || '', r.desc || '', r.vin || '', r.licPlate || ''].map(csvCell).join(',')));
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `Dakota Equipment List${catF !== 'all' ? ' - ' + catF : ''} ${iso(new Date())}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };
  const printEquipList = (withVin) => {
    const rows = equipListRows();
    const pv = openPrintView({ title: 'Equipment List', status: 'Building equipment list…' });
    const E = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;');
    const groups = {}; rows.forEach(r => { const c = String(r.cat || '').trim() || 'Other'; (groups[c] = groups[c] || []).push(r); });
    const today = new Date().toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
    const body = Object.keys(groups).sort().map(c => `<tr class="cat"><td colspan="${withVin ? 3 : 2}">${E(c)} <span>(${groups[c].length})</span></td></tr>`
      + groups[c].map(r => `<tr><td class="u">${E(r.unit)}</td><td>${E(r.desc)}</td>${withVin ? `<td class="v">${E(r.vin)}</td>` : ''}</tr>`).join('')).join('');
    const summary = Object.keys(groups).sort().map(c => `<span>${E(c)} <b>${groups[c].length}</b></span>`).join('');
    const html = '<!doctype html><html><head><meta charset="utf-8"><title>Dakota Equipment List</title><style>'
      + '@page{margin:0.45in;} body{font-family:Arial,Helvetica,sans-serif;color:#111;font-size:11.5px;margin:0;-webkit-print-color-adjust:exact;print-color-adjust:exact;}'
      + '.hdr{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #3a2a05;padding-bottom:10px;margin-bottom:12px;} .co{text-align:right;font-size:11px;color:#333;line-height:1.5;}'
      + 'h1{font-size:19px;margin:0 0 2px;} .sub{color:#555;font-size:11px;margin-bottom:8px;} .sum{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:10.5px;color:#333;margin:0 0 10px;padding:6px 8px;background:#fbf5e6;border:1px solid #e3d3a8;border-radius:4px;}'
      + 'table{width:100%;border-collapse:collapse;} th,td{border:1px solid #bbb;padding:4px 7px;text-align:left;vertical-align:top;} thead th{background:#efe4c6;color:#3a2a05;font-size:10px;text-transform:uppercase;border-color:#3a2a05;}'
      + 'thead{display:table-header-group;} tr{page-break-inside:avoid;} .cat td{background:#3a2a05;color:#fff;font-weight:bold;font-size:11px;letter-spacing:.3px;} .cat span{font-weight:normal;color:#e8d9b0;}'
      + '.u{font-weight:bold;white-space:nowrap;width:80px;} .v{font-family:Consolas,monospace;font-size:10.5px;white-space:nowrap;} .foot{text-align:center;font-style:italic;font-size:10px;color:#555;margin-top:12px;}'
      + '</style></head><body>'
      + '<div class="hdr"><img src="/duc-logo.png" alt="Dakota Utility Contractors" style="height:62px">'
      + '<div class="co"><b>Dakota Utility Contractors</b><br>2501 S. Oak Grove Rd.<br>Ennis, TX 75119<br>Office: (972) 875-6344<br>Fax: (972) 875-6342</div></div>'
      + `<h1>Equipment List${catF !== 'all' ? ' — ' + E(catF) : ''}</h1><div class="sub">${rows.length} units · as of ${E(today)}</div>`
      + `<div class="sum">${summary}</div>`
      + `<table><thead><tr><th>Unit #</th><th>Description</th>${withVin ? '<th>Serial / VIN</th>' : ''}</tr></thead><tbody>${body}</tbody></table>`
      + '<div class="foot">Dakota Utility Contractors · Equipment owned / operated · #WEAREDAKOTA</div></body></html>';
    pv.setHtml(html, 'Dakota Equipment List');
  };

  // #FLEET_RENTAL_V1 — Bill: "Rental / RPO needs ability to print or list items i[n] a report."
  const rentalCsv = () => {
    const head = ['Unit','Type','Vendor/Source','Description','Monthly Charge','Weekly Rate','Daily Rate','Total Paid','Start','Matures/Return','Notes'];
    const lines = [head.join(',')];
    rentalRecords.forEach(x => lines.push([x.unit, x.rentalType||'', x.rentalVendor||'', x.desc||'', x.monthlyRate||'', x.weeklyRate||'', x.ducDay||'', rentalPaidToDate(x) ?? '', x.rentalStart||'', x.rentalDue||'', x.notes||''].map(csvCell).join(',')));
    return lines.join('\n');
  };
  const downloadRentalCsv = () => {
    const name = `Rental-RPO List ${iso(new Date())}.csv`;
    const blob = new Blob(['﻿' + rentalCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };

  // #FLEET_INSHOP_V1 — shop[] (from the fleet-summary digest) joined with the diagnosis/mechanic/
  // est-out notes Bob keeps. dateIn is derived from `days` the same way Bob's spreadsheet's "Date In"
  // and Fleet's own Idle-90 scan both work — today minus the down-run length — so nobody has to type it.
  // #FLEET_INSHOP_DESC_LIVE_V1 (2026-09-18) — Bill: In Shop's Description column was showing
  // whatever text was baked into the shop digest snapshot from whenever the unit went down, so an
  // Equipment Master correction never showed up here — same staleness #FLEET_TOLL_DESC_V1 already
  // fixed for Toll Tags. Now overridden with a live Equipment Master lookup by unit when a match
  // exists, falling back to the digest's own desc only for a unit that's since dropped off Master.
  const shopRows = useMemo(() => {
    const rows = (shopSum && Array.isArray(shopSum.shop)) ? shopSum.shop : [];
    const today = new Date(); today.setHours(0,0,0,0);
    const liveDesc = {}, liveRow = {};
    (active||[]).forEach(r => { const k = unitKey(r.unit); if (k && !liveDesc[k]) { liveDesc[k] = r.desc || ''; liveRow[k] = r; } });
    // #FLEET_SHOP3P_V1 — days down from a typed Date In: calendar days, the day it went in counts as day 1
    // (same count the Daily-fed rows already show).
    const daysSince = (d) => { const t = new Date(d + 'T00:00:00'); return isNaN(t.getTime()) ? 0 : Math.max(1, Math.round((today - t) / 86400000) + 1); };
    const fromDaily = {};
    const auto = rows.map(x => {
      const k = unitKey(x.unit); fromDaily[k] = true;
      const n = shopNotes[k] || {};
      const autoDateIn = iso(addDays(today, -(Number(x.days)||0) + 1));
      const dateInSet = shopIso(n.dateIn);            // typed override, '' = automatic
      const dateIn = dateInSet || autoDateIn;
      const days = dateInSet ? daysSince(dateInSet) : x.days;
      const desc = (liveDesc[k] != null && liveDesc[k] !== '') ? liveDesc[k] : (x.desc || '');
      return { ...x, k, autoDateIn, dateInSet, dateIn, days, desc, diagnosis:n.diagnosis||'', mechanic:n.mechanic||'', estOut:n.estOut||'', thirdParty:!!n.thirdParty, manual:false,
        hot:!!n.hot, hotBy:n.hotBy||'', hotAt:n.hotAt||'', hotWhy:n.hotWhy||'' };   // #FLEET_SHOP_HOT_V1
    }).filter(x => !shopDismissed[`${x.k}__${x.autoDateIn}`]);   // #FLEET_SHOPHISTORY_V1 — Marked-Complete rows drop off here even if the digest hasn't refreshed yet
    // Hand-added units (3rd-party shops, or anything Daily has on a job / idle). Skipped once Daily itself
    // reports the unit A-SHOP — the Daily row then carries the same note, typed Date In included.
    const byHand = Object.keys(shopNotes).filter(k => shopNotes[k] && shopNotes[k].manual && !fromDaily[k] && shopIso(shopNotes[k].dateIn)).map(k => {
      const n = shopNotes[k], m = liveRow[k];
      const dateIn = shopIso(n.dateIn);
      return { unit: m ? m.unit : (n.unit || k), k, autoDateIn:'', dateInSet:dateIn, dateIn, days: daysSince(dateIn), desc: m ? (m.desc||'') : '', rate: m ? num(m.ducDay) : 0,
        diagnosis:n.diagnosis||'', mechanic:n.mechanic||'', estOut:n.estOut||'', thirdParty:!!n.thirdParty, manual:true,
        hot:!!n.hot, hotBy:n.hotBy||'', hotAt:n.hotAt||'', hotWhy:n.hotWhy||'' };
    });
    const all = !byHand.length ? auto
      : [...auto, ...byHand].sort((a,b) => ((Number(b.days)||0) - (Number(a.days)||0)) || String(a.unit).localeCompare(String(b.unit)));
    // #FLEET_SHOP_HOT_V1 — Bob's 🔥 units lead every list that reads shopRows (In Shop, print, dash tiles, shop staff)
    return all.some(x => x.hot) ? [...all.filter(x => x.hot), ...all.filter(x => !x.hot)] : all;
  }, [shopSum, shopNotes, shopDismissed, active]);
  // #FLEET_SHOPREPORT_TELEM_V1 (2026-10-02) — Bill: "Camera & GPS count should also come from Equipment Master
  // file so there is no drift here and consistent." Bob typed these two lines on his sheet every week; they
  // are now COUNTED from the Master's own Camera / GPS columns (X = has one, working · NW = has one, not
  // working · NO CAM / NO GPS = needs one · N/A = not required · blank = not yet checked) and print as the
  // first lines under Discussion Questions. Installed = X + NW. Trailers = any category containing "trailer".
  const telemLines = useMemo(() => {
    const c = { cam:{ ok:0, nw:0, need:0 }, gps:{ ok:0, nw:0, need:0 }, tr:{ n:0, ok:0, nw:0, need:0 }, unchecked:0 };
    (active||[]).forEach(r => {
      const g = String(r.gpsStatus||'').trim().toUpperCase(), m = String(r.cameraStatus||'').trim().toUpperCase();
      const trailer = /trailer/i.test(String(r.cat||''));
      if (m === 'X') c.cam.ok++; else if (m === 'NW') c.cam.nw++; else if (m === 'NO CAM') c.cam.need++;
      if (g === 'X') c.gps.ok++; else if (g === 'NW') c.gps.nw++; else if (g === 'NO GPS') c.gps.need++;
      if (trailer) { c.tr.n++; if (g === 'X') c.tr.ok++; else if (g === 'NW') c.tr.nw++; else if (g === 'NO GPS') c.tr.need++; }
      if (!g || !m) c.unchecked++;
    });
    const one = (label, x) => `${label} — ${x.ok + x.nw} INSTALLED (${x.ok} working, ${x.nw} not working) · ${x.need} still need one`;
    return {
      lines: [ one('CAMERAS', c.cam), one('GPS', c.gps), `GPS ON TRAILERS — ${c.tr.ok + c.tr.nw} of ${c.tr.n} INSTALLED · ${c.tr.need} still need one` ],
      foot: `Counted from the Equipment Master${c.unchecked ? ` · ${c.unchecked} unit${c.unchecked===1?'':'s'} not yet checked` : ''}`,
      counts: c,   // #FLEET_SHOPRPT_COLOR_V1 — raw counts for the print's working / down / missing bars
    };
  }, [active]);
  // #FLEET_SHOP_SLIP_V1 (2026-10-23) — Bill: "have Est Done self calculate and show the slip when this occurs."
  // Est. Done is still typed by Bob; DOPE now works out where it stands against today on its own:
  //   late  = date passed and the unit is still on the In Shop list → shows +Nd slip
  //   today = due today · ok = still ahead · bad = Est. Done is BEFORE Date In (a typo — flagged CHECK)
  // Units that are actually done come off with ✓ Complete; a slip on a finished unit is a talking point.
  const shopSlip = (x) => {
    const toIso = (v) => { const t = String(v||'').trim(); let m = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
      if (m) return `${m[1]}-${pad2(+m[2])}-${pad2(+m[3])}`;
      m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
      if (m) return `${m[3].length === 2 ? '20' + m[3] : m[3]}-${pad2(+m[1])}-${pad2(+m[2])}`;
      return ''; };
    const est = toIso(x && x.estOut); if (!est) return null;
    const today = iso(new Date()), din = toIso(x.dateIn);
    const dd = (a, b) => Math.round((new Date(a + 'T00:00:00') - new Date(b + 'T00:00:00')) / 86400000);
    if (din && est < din) return { state:'bad', days:0 };
    if (est < today) return { state:'late', days:dd(today, est) };
    if (est === today) return { state:'today', days:0 };
    return { state:'ok', days:dd(est, today) };
  };
  // #FLEET_SHOP3P_V1 — what the edit panel opens with for a row. dateIn here is the TYPED date ('' = automatic).
  const shopEditOf = (x) => ({ unit:x.unit, desc:x.desc, autoDateIn:x.autoDateIn||'', dateIn:x.dateInSet||'', days:x.days,
    diagnosis:x.diagnosis, mechanic:x.mechanic, estOut:x.estOut, thirdParty:!!x.thirdParty, manual:!!x.manual });
  // #FLEET_SHOPHISTORY_V1 — per-unit rollup for the Equipment Master badge: how many times, how many
  // total days down, all-time. Chronic-offender visibility without opening Shop History.
  const shopHistoryByUnit = useMemo(() => {
    const m = {};
    (shopHistory||[]).forEach(r => {
      const k = unitKey(r.unit);
      if (!m[k]) m[k] = { count:0, days:0 };
      m[k].count += 1; m[k].days += Number(r.days)||0;
    });
    return m;
  }, [shopHistory]);
  // #OWNERDASH_INSHOP_JUMP_V1 — Bill: click a unit on Owner Dash's "In Shop" tile → jump straight
  // into this In Shop tab (the notes-for-Bob sheet already existed, #FLEET_INSHOP_V1 — this is just
  // the missing link from Owner Dash into it), scrolled to that unit's row with its notes panel open
  // for anyone who can edit. Waits for the shop digest to actually load before deciding a unit isn't
  // there — the digest is fetched async on mount, same race the App.js KMZ/DUC deep-links guard against.
  useEffect(() => {
    if (!pendingUnit || !shopSum) return;
    const k = unitKey(pendingUnit);
    const match = shopRows.find(x => x.k === k);
    if (!match) { onPendingConsumed?.(); return; }
    setView('inshop');
    if (yardOpsEdit) setShopEdit(shopEditOf(match));
    setTimeout(() => {
      const el = document.querySelector(`[data-shop-unit="${match.unit}"]`);
      if (el) el.scrollIntoView({ behavior:'smooth', block:'center' });
    }, 100);
    onPendingConsumed?.();
  }, [pendingUnit, shopSum, shopRows]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #OWNERDASH_FUEL_JUMP_V1 (2026-09-02) — Bill: Owner Dash's compact Fuel tile shouldn't try to
  // cram in a diesel-vs-unleaded breakdown — make it clickable instead, straight into this page's
  // full detail. Same pendingUnit/onPendingConsumed shape as the In Shop jump above, just landing on
  // a view instead of a scrolled-to row.
  useEffect(() => {
    if (!pendingView) return;
    setView(pendingView);
    onPendingViewConsumed?.();
  }, [pendingView]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #FLEET_INSHOP_V1 — Bill: "a report to combined Rental & units down to be developed for weekly
  // review inside of Fleet." One CSV, two sections, exactly Bob's Friday-meeting sheet shape.
  const inShopCsv = () => {
    const lines = [`In Shop / Down Equipment — as of ${iso(new Date())}`];
    lines.push(['Unit','Description','Date In','Days Down','Diagnosis','Est. Date Out','Mechanic','Daily Rate','Where'].join(','));
    shopRows.forEach(x => lines.push([x.unit, x.desc||'', fmtDate(x.dateIn)||'', x.days||'', x.diagnosis||'', fmtDate(x.estOut)||'', x.mechanic||'', x.rate||'', x.thirdParty ? '3rd party' : 'Shop'].map(csvCell).join(',')));   // Where: #FLEET_SHOP3P_V1
    lines.push('');
    lines.push('Rental / RPO');
    lines.push(['Unit','Type','Vendor/Source','Description','Monthly Charge','Weekly Rate','Daily Rate','Total Paid','Start','Matures/Return','Notes'].join(','));
    rentalRecords.forEach(x => lines.push([x.unit, x.rentalType||'', x.rentalVendor||'', x.desc||'', x.monthlyRate||'', x.weeklyRate||'', x.ducDay||'', rentalPaidToDate(x) ?? '', x.rentalStart||'', x.rentalDue||'', x.notes||''].map(csvCell).join(',')));
    return lines.join('\n');
  };
  const downloadInShopCsv = () => {
    const name = `In Shop and Rental Report ${iso(new Date())}.csv`;
    const blob = new Blob(['﻿' + inShopCsv()], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = name; a.click();
    setTimeout(()=>URL.revokeObjectURL(a.href), 4000);
    return name;
  };

  // #FLEET_SHOPREPORT_V1 (2026-10-02) — Bill (Bob's emailed "Fleet Maintenance Report.xlsx" attached):
  // "Could we make the DOPE In Shop produce a report to resemble this… trying to get away from separate
  // sheets." One letter-landscape page laid out like Bob's sheet: Weekending / Submitted By top right,
  // numbered grid (# / Unit # / Description / Diagnosis / Date In / Days Down / Estimated Date Out /
  // Mechanic) padded with blank numbered rows for write-ins, then Discussion Questions on the left and
  // PO Spending + the Rental list on the right. The grid and the rental list are live data; PO spending
  // and the discussion items are the only typed pieces (fleet-shop-report), edited at the bottom of
  // the In Shop tab. Daily Rate stays on screen and in the CSV — it is not a column on Bob's sheet.
  const saveShopReport = async () => {
    if (!shopReportDraft) return;
    const next = { v:1, po: String(shopReportDraft.po||'').trim(), notes: String(shopReportDraft.notes||'').replace(/\s+$/,''),
      by: upn || String(userRole||''), updatedAt: new Date().toISOString() };
    setBusy('Saving…');
    try { await ss('fleet-shop-report', next); setShopReport(next); setShopReportDraft(null); }
    catch (e) { alert('Could not save: ' + (e.message||e)); }
    setBusy('');
  };
  const printShopReport = async () => {
    const title = 'Fleet Maintenance Report';
    // Viewer opens synchronously (pop-up safe) — same preview-with-Print/Close-bar the 11x17 uses.
    const viewer = (hrsViewerIsMobile() ? null : hrsViewerWindow(title, '🖨 Print · Letter landscape')) || hrsViewerOverlay(title, 1010);
    let rep = shopReport;   // re-read so notes saved from another screen a minute ago are on the sheet
    try { const s = await sg('fleet-shop-report'); if (s && typeof s==='object') { rep = s; setShopReport(s); } } catch { /* print what is on screen */ }
    const esc = (v) => String(v==null?'':v).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const who = (e) => String(e||'').split('@')[0].replace(/[._]+/g,' ').trim().toUpperCase();
    const wkEnd = addDays(mondayOf(new Date()), 4);   // Friday of this week — Bob's "Weekending"
    const MIN_ROWS = 15;   // blank numbered rows for write-ins; 18 keeps the counted Camera/GPS lines + notes on page 1
    const outN = shopRows.filter(x => x.thirdParty).length;   // #FLEET_SHOP3P_V1 — ◆ marks a unit at a 3rd-party shop
    // #FLEET_SHOPRPT_V2 (2026-10-15) — Bill: drop the grey lines on the numbered column, "Days Down" → DAYS, "Est. Date Out"
    // → Est. DONE, tighten those columns, and pipe in the unit's GPS location (latest Geotab / factory-feed fix, matched by
    // VIN — same source as the Daily sheet's GPS column). A fix older than today carries its age.
    let locIx = {};
    try { const lb = await sg(LOC_KEY); locIx = locIndex(lb); } catch { /* print without locations */ }
    const vinOf = {}; (active||[]).forEach(r => { const k = unitKey(r.unit); if (k && r.vin && !vinOf[k]) vinOf[k] = r.vin; });
    const todayIso = iso(new Date());
    const locTxt = (x) => { const u = locForVin(locIx, vinOf[x.k || unitKey(x.unit)]); if (!u) return '';
      const pl = locPlace(u); if (!pl) return '';
      const d = u.at ? new Date(u.at) : null; const old = d && !isNaN(d.getTime()) ? Math.floor((Date.now() - d.getTime()) / 86400000) : 0;
      return pl + (old >= 1 && iso(d) < todayIso ? ` (${old}d)` : ''); };
    // #FLEET_SHOPRPT_COLOR_V1 (2026-10-23) — Bill: "add some color… printing for a meeting and would like to look
    // fantastic." Days badge (red 30+, amber 14–29, green under 14), Est. Done slip from shopSlip, stale GPS tinted.
    const ageCls = (d) => { const n = Number(d)||0; return n >= 30 ? 'a-red' : n >= 14 ? 'a-amb' : 'a-grn'; };
    const estCell = (x) => { if (!x.estOut) return '<td class="c"></td>';
      const d = esc(fmtDate(x.estOut)), sl = shopSlip(x);
      if (sl && sl.state === 'late') return `<td class="c est-late" title="Slipped ${sl.days} day${sl.days===1?'':'s'}">${d}<span class="slip">+${sl.days}d</span></td>`;
      if (sl && sl.state === 'bad') return `<td class="c est-late" title="Est. Done is before Date In">${d}<span class="slip">CHECK</span></td>`;
      if (sl && sl.state === 'today') return `<td class="c est-today">${d}</td>`;
      return `<td class="c">${d}</td>`; };
    const lateN = shopRows.filter(x => { const sl = shopSlip(x); return sl && sl.state === 'late'; }).length;
    const rows = shopRows.map((x,i) => { const lt = locTxt(x);
      return `<tr${x.hot ? ' class="hot"' : ''}><td class="n">${i+1}</td><td class="u">${x.hot ? '🔥 ' : ''}${esc(x.unit)}${x.thirdParty ? ' <span class="tp">◆</span>' : ''}</td><td>${esc(x.desc)}${x.hot && x.hotWhy ? `<div class="hw">${esc(x.hotWhy)}</div>` : ''}</td>`
      + `<td>${esc(x.diagnosis)}</td><td class="c">${esc(fmtDate(x.dateIn))}</td><td class="c"><span class="age ${ageCls(x.days)}">${esc(x.days)}</span></td>`
      + estCell(x) + `<td class="mech">${esc(x.mechanic)}</td><td class="loc${/\(\d+d\)$/.test(lt) ? ' stale' : ''}">${esc(lt)}</td></tr>`; });
    for (let i = rows.length; i < MIN_ROWS; i++) rows.push(`<tr><td class="n">${i+1}</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>`);
    const po = String(rep.po||'').trim();
    // #FLEET_SHOPRPT_V2 — PO SPENDING = MO / QTR / YTD from Fleet's own POs (Bob's BM log + his old-number history for this
    // year), invoiced dollars only — the same Actual AP's BM card adds up. An old-number history line that the new PO was also
    // written under is counted once.
    let poSpend = null;
    try {
      const yr = new Date().getFullYear();
      const live = await sg('po-log-bm'); const hist = await sg(`po-hist-bm-${yr}`);
      const L = Array.isArray(live) ? live : [], H = Array.isArray(hist) ? hist : [];
      const olds = new Set(L.map(r => String(r.oldPo || '').toUpperCase()).filter(Boolean));
      const actOf = (r) => { const inv = Array.isArray(r.invoices) ? r.invoices.filter(x => x && x.amount != null && x.amount !== '' && !isNaN(Number(x.amount))) : [];
        if (inv.length) return inv.reduce((t, x) => t + Number(x.amount), 0); return (r.actual != null && r.actual !== '' && !isNaN(Number(r.actual))) ? Number(r.actual) : 0; };
      const pad = (n) => String(n).padStart(2, '0'); const now = new Date();
      const moS = `${yr}-${pad(now.getMonth()+1)}-01`, qS = `${yr}-${pad(Math.floor(now.getMonth()/3)*3+1)}-01`, yS = `${yr}-01-01`;
      const t = { mo:0, qtr:0, ytd:0 };
      [...L, ...H.filter(r => !olds.has(String(r.po || '').toUpperCase()))].forEach(r => {
        if (!r || !(r.supplier || r.description)) return; const d = String(r.date || ''); if (d < yS || d.slice(0,4) !== String(yr)) return;
        const a = actOf(r); t.ytd += a; if (d >= qS) t.qtr += a; if (d >= moS) t.mo += a; });
      poSpend = t;
    } catch { /* fall back to the typed figure */ }
    const poTile = (k, v, cls) => `<div class="${cls||''}"><div class="k">${k}</div><div class="v">${esc(v)}</div></div>`;   // #FLEET_SHOPRPT_COLOR_V1
    const poTxt = poSpend
      ? `<div class="spend">${poTile('Month', money(poSpend.mo))}${poTile('Quarter', money(poSpend.qtr))}${poTile('YTD', money(poSpend.ytd), 'ytd')}</div>${po ? `<div class="pt">noted: ${esc(/^[\d,.\s]+$/.test(po) ? '$' + po : po)}</div>` : ''}`
      : `<div class="spend1">${!po ? '<span class="blank"></span>' : esc(/^[\d,.\s]+$/.test(po) ? '$' + po : po)}</div>`;
    const notes = String(rep.notes||'').trim();
    // #FLEET_SHOPRPT_COLOR_V1 — same Equipment Master counts (#FLEET_SHOPREPORT_TELEM_V1), drawn as bars:
    // green = installed & working · red = installed, not working · grey = still needs one.
    const tc = telemLines.counts;
    const tBar = (label, ok, nw, total, txt) => { const pc = (v) => total ? (100 * v / total).toFixed(1) : 0;
      return `<div class="metric"><div class="lab">${label}</div><div class="bar"><div class="w" style="width:${pc(ok)}%"></div><div class="x" style="width:${pc(nw)}%"></div></div><div class="num">${esc(txt)}</div></div>`; };
    const telemHtml = tc
      ? `<div class="telem">${tBar('CAMERAS', tc.cam.ok, tc.cam.nw, tc.cam.ok + tc.cam.nw + tc.cam.need, `${tc.cam.ok + tc.cam.nw} of ${tc.cam.ok + tc.cam.nw + tc.cam.need} · ${tc.cam.nw} down`)}`
        + `${tBar('GPS', tc.gps.ok, tc.gps.nw, tc.gps.ok + tc.gps.nw + tc.gps.need, `${tc.gps.ok + tc.gps.nw} of ${tc.gps.ok + tc.gps.nw + tc.gps.need} · ${tc.gps.nw} down`)}`
        + `${tBar('GPS TRAILERS', tc.tr.ok, tc.tr.nw, tc.tr.n, `${tc.tr.ok + tc.tr.nw} of ${tc.tr.n} installed`)}`
        + `<div class="tf">Green = installed &amp; working · Red = installed, not working · Grey = still needs one. ${esc(telemLines.foot)}</div></div>`
      : `<div class="telem">${telemLines.lines.map(l => `<div>${esc(l)}</div>`).join('')}<div class="tf">${esc(telemLines.foot)}</div></div>`;
    const notesHtml = telemHtml + (notes ? `<div class="notes">${esc(notes)}</div>` : '<div class="rule"></div>'.repeat(3));
    const rentHtml = rentalRecords.length
      ? rentalRecords.map(x => `<tr><td class="u">${esc(x.unit)}</td><td>${esc([x.rentalVendor, x.desc].filter(Boolean).join(' — '))}`
          + `${String(x.rentalType||'').toUpperCase()==='RPO' ? ' <b>(RPO)</b>' : ''}</td><td class="r">${num(x.monthlyRate) ? esc(money(num(x.monthlyRate))) + '/mo' : ''}</td></tr>`).join('')
      : '<tr><td colspan="3">None on the Rental/RPO list.</td></tr>';
    const rentTot = rentalRecords.reduce((t, x) => t + num(x.monthlyRate), 0);   // #FLEET_SHOPRPT_COLOR_V1
    const rentFoot = rentalRecords.length ? `<tr class="tot"><td>${rentalRecords.length} RENTAL${rentalRecords.length===1?'':'S'}</td><td></td><td class="r">${esc(money(rentTot))}/mo</td></tr>` : '';
    const asOf = (shopSum && shopSum.at) ? new Date(shopSum.at).toLocaleString([], { month:'numeric', day:'numeric', year:'2-digit', hour:'numeric', minute:'2-digit' }) : '';
    const logoUrl = ((typeof window !== 'undefined' && window.location) ? window.location.origin : '') + '/ddd-logo-black.png';   // #FLEET_LOGO_BW_V1
    // #FLEET_SHOPRPT_COLOR_V1 — Submitted By is the Fleet Manager: it's Bob's report whoever hits Print.
    const submitter = 'BOB MACY';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} ${iso(new Date())}</title><style>
      *{box-sizing:border-box} html,body{margin:0;padding:0}
      body{font-family:"Segoe UI",Calibri,Arial,Helvetica,sans-serif;color:#1b2330;font-size:12.5px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      .sheet{width:10.2in;margin:0 auto}
      .top{display:flex;align-items:center;gap:16px;border-bottom:3px solid #0f2a47;padding-bottom:7px;margin-bottom:8px}
      .top img{height:50px;padding-right:16px;border-right:1px solid #cfd6df}
      .top h1{font-size:23px;margin:0;letter-spacing:.6px;color:#0f2a47;font-weight:800}
      .top .sub{font-size:11px;color:#566273;margin-top:2px}
      .pills{margin-top:5px;display:flex;gap:6px;flex-wrap:wrap}
      .pill{padding:2px 9px;border-radius:11px;font-weight:700;font-size:10.5px}
      .p-down{background:#fde3e1;color:#a8231a} .p-3rd{background:#e4ecf8;color:#1f4f8a} .p-late{background:#fff1d6;color:#8a5a00}
      .top .sp{flex:1}
      .sig{border-collapse:separate;border-spacing:0 4px;font-size:12px}
      .sig td{padding:0 8px;color:#566273}
      .sig td.v{background:#0f2a47;color:#fff;font-weight:800;min-width:1.25in;text-align:center;padding:4px 12px;border-radius:3px;font-size:13px}
      table.g{width:100%;border-collapse:collapse;table-layout:fixed}
      table.g th,table.g td{border:1px solid #d3dae3;padding:2px 5px;height:21px;vertical-align:middle;overflow-wrap:anywhere}
      table.g th{font-size:10.5px;font-weight:700;text-align:left;background:#1f4f8a;color:#fff;border-color:#1f4f8a;text-transform:uppercase;letter-spacing:.4px;white-space:nowrap}
      table.g thead{display:table-header-group}
      table.g tr{break-inside:avoid}
      table.g tbody tr:nth-child(even) td{background:#f4f7fb}
      table.g tbody tr td.n{background:none!important}
      table.g td.n{text-align:right;color:#8a95a5;border:0;font-size:11px}
      table.g th.n{border:0;background:none}
      table.g td.u{font-weight:800;white-space:nowrap}
      table.g td.mech{font-weight:600}
      table.g .c{text-align:center;white-space:nowrap}
      table.g .tp{font-size:.75em;vertical-align:.12em;color:#1f4f8a}
      table.g tr.hot td{background:#FFF1C9!important;-webkit-print-color-adjust:exact;print-color-adjust:exact}
      table.g tr.hot td.n{background:none!important}
      table.g .hw{font-size:10.5px;font-weight:700;color:#8a5a00}
      table.g td.loc{font-size:11.5px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
      table.g td.loc.stale{color:#8a5a00}
      .age{display:inline-block;min-width:28px;text-align:center;padding:1px 6px;border-radius:10px;font-weight:800;color:#fff}
      .a-red{background:#d93a2b} .a-amb{background:#f2a91c} .a-grn{background:#2e9d5b}
      .est-late{color:#c0281b;font-weight:700} .est-today{color:#b07000;font-weight:700}
      .slip{display:inline-block;margin-left:4px;padding:0 5px;border-radius:8px;background:#d93a2b;color:#fff;font-size:9.5px;font-weight:800}
      .legend{display:flex;gap:12px;margin-top:5px;font-size:10px;color:#566273;align-items:center;flex-wrap:wrap}
      .legend .dot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:3px;vertical-align:-1px}
      .foot{display:flex;gap:18px;margin-top:9px;break-inside:avoid}
      .foot .fl{flex:1.05;min-width:0} .foot .fr{flex:1;min-width:0}
      .card{border:1px solid #d3dae3;border-radius:5px;overflow:hidden}
      .card h3{margin:0;background:#0f2a47;color:#fff;font-size:11px;letter-spacing:.8px;padding:4px 10px;text-transform:uppercase}
      .card .body{padding:7px 10px}
      .notes{white-space:pre-wrap;overflow-wrap:anywhere;line-height:1.3;margin-top:4px}
      .rule{border-bottom:1px solid #b9c1cc;height:19px}
      .telem{margin-bottom:4px}
      .metric{display:grid;grid-template-columns:96px 1fr 104px;align-items:center;gap:8px;margin-bottom:5px}
      .metric .lab{font-weight:800;color:#0f2a47;font-size:11.5px}
      .bar{height:12px;background:#e8ecf1;border-radius:6px;overflow:hidden;display:flex}
      .bar .w{background:#2e9d5b} .bar .x{background:#d93a2b}
      .metric .num{font-size:10.5px;color:#3b4656;text-align:right}
      .telem .tf{font-size:9.5px;color:#8a95a5;font-style:italic}
      .spend{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:5px}
      .spend>div{background:#eef3fa;border-left:4px solid #1f4f8a;padding:3px 8px}
      .spend .k{font-size:9.5px;color:#566273;text-transform:uppercase;letter-spacing:.5px}
      .spend .v{font-size:15px;font-weight:800;color:#0f2a47}
      .spend .ytd{border-left-color:#2e9d5b}
      .spend1{font-weight:800;margin-bottom:5px}
      .pt{font-size:10.5px;color:#555;margin-bottom:4px}
      .blank{display:inline-block;width:1.5in;border-bottom:1px solid #000}
      table.rt{width:100%;border-collapse:collapse;font-size:11.5px}
      table.rt td{padding:2px 6px 2px 0;vertical-align:top;border-bottom:1px solid #e3e8ee}
      table.rt td.u{font-weight:800;white-space:nowrap;color:#1f4f8a}
      table.rt td.r{text-align:right;white-space:nowrap;padding-right:0;font-weight:700}
      table.rt tr.tot td{border-top:2px solid #0f2a47;border-bottom:none;font-weight:800;color:#0f2a47}
      .vbar{position:sticky;top:0;z-index:9;display:flex;align-items:center;gap:10px;background:#111;color:#fff;font:700 14px Arial;padding:8px 12px;margin:-8px -8px 8px}
      @media print{@page{size:11in 8.5in;margin:0.35in}.vbar{display:none!important}}
      @media screen{body{margin:8px}}
    </style></head><body><div class="sheet">
      <div class="top"><img src="${logoUrl}" alt="Dakota Directional Drilling">
        <div><h1>FLEET MAINTENANCE REPORT</h1>
        <div class="sub">In Shop / down equipment${asOf ? ' · Daily Inventory as of ' + esc(asOf) : ''}</div>
        <div class="pills"><span class="pill p-down">${shopRows.length} unit${shopRows.length===1?'':'s'} down</span>${outN ? `<span class="pill p-3rd">◆ ${outN} at 3rd-party shop</span>` : ''}${lateN ? `<span class="pill p-late">${lateN} past est. done</span>` : ''}</div></div>
        <div class="sp"></div>
        <table class="sig"><tr><td class="v">${esc(fmtDate(iso(wkEnd)))}</td><td>Weekending</td></tr>
        <tr><td class="v">${esc(submitter)}</td><td>Submitted By</td></tr></table>
      </div>
      <table class="g"><colgroup><col style="width:3%"><col style="width:7.5%"><col style="width:16%"><col style="width:26.5%"><col style="width:7.5%"><col style="width:5%"><col style="width:9%"><col style="width:11%"><col style="width:14.5%"></colgroup>
        <thead><tr><th class="n"></th><th>Unit #</th><th>Description</th><th>Diagnosis</th><th class="c">Date In</th><th class="c">Days</th><th class="c">Est. Done</th><th>Mechanic</th><th>GPS Location</th></tr></thead>
        <tbody>${rows.join('')}</tbody></table>
      <div class="legend"><b>Days down:</b><span><span class="dot" style="background:#d93a2b"></span>30+</span><span><span class="dot" style="background:#f2a91c"></span>14–29</span><span><span class="dot" style="background:#2e9d5b"></span>under 14</span>
        <span><b class="est-late">Red Est. Done +Nd</b> = slipped N days</span><span><b class="est-today">Amber</b> = due today</span><span><b style="color:#8a5a00">(Nd)</b> = GPS last ping age</span><span><b style="color:#1f4f8a">◆</b> = 3rd-party shop</span></div>
      <div class="foot">
        <div class="fl card"><h3>Discussion Questions</h3><div class="body">${notesHtml}</div></div>
        <div class="fr card"><h3>PO Spending &amp; Rentals</h3><div class="body">${poTxt}<table class="rt">${rentHtml}${rentFoot}</table></div></div>
      </div>
    </div></body></html>`;
    try { viewer.show(html, ''); } catch { /* the viewer was closed while it was loading */ }
  };

  // #FLEET_IDLE90_V1 — Bill: "track Idle equipment that's been IDLE for 90 days to determine if we
  // need it." This is the disposal question, and it is the one thing a daily sheet can answer that
  // no other system can: a unit that has not touched a job in a quarter is either about to, or it is
  // rent, insurance and a payment book with nothing on the other side.
  //
  // Honesty rule baked in: the answer is only as good as the history behind it. If the stored weeks
  // cover 21 days, a unit CANNOT be proven idle for 90 — so the view reports its own coverage and
  // never dresses "no data" up as "idle". Loaded on demand: 14 weekly keys is a real fetch.
  const IDLE_TARGET = 90;
  // #FLEET_OWNERIDLE30_V1 — Bill: Owner Dash's Idle Equipment tile should qualify at 30+ days, not 90.
  // Kept separate from IDLE_TARGET on purpose — Bob's own "Idle 90" report/tab in Fleet stays at 90;
  // only what gets published for Owner Dash (fleet-summary) uses the lower bar.
  const OWNER_IDLE_MIN = 30;
  const [idleScan, setIdleScan] = useState(null);   // null = not scanned yet
  const scanIdle = async () => {
    setBusy('Reading history…');
    const weeks = [];
    const m0 = mondayOf(new Date());
    for (let i = 0; i < 14; i++) weeks.push(iso(addDays(m0, -7*i)));
    const found = {};   // dateISO -> units
    for (const wk of weeks) {
      try {
        const w = await sg('fleet-daily-' + wk);
        if (w && typeof w === 'object') Object.assign(found, w);
      } catch { /* a missing week is simply no history, not an error */ }
    }
    const all = Object.keys(found).sort();
    setIdleScan({ days: found, earliest: all[0] || null, at: new Date().toLocaleTimeString() });
    // #FLEET_SUMMARY_V1 — publish a small digest for the Owner Dash. The dashboard cannot compute this
    // itself without re-reading 13 week-keys on every load for every owner, so the scan that already
    // has the data in hand writes the answer down. It carries its own `at` stamp: a number that admits
    // how old it is beats a fast one that quietly lies.
    try {
      const dates = all.slice();
      const today = new Date(); today.setHours(0,0,0,0);
      const dayMs = 86400000;
      const cover = dates.length ? Math.round((today - new Date(dates[0]+'T00:00:00')) / dayMs) : 0;
      const idle = [], shop = [];
      (active||[]).forEach(r => {
        const k = unitKey(r.unit); if (!k) return;
        let lastJob = '';
        for (const d of dates) {
          const duc = String(((found[d]||{})[k]||{}).duc||'').trim().toUpperCase();
          if (duc && !OVERHEAD_SET.has(duc) && d > lastJob) lastJob = d;
        }
        // consecutive trailing days in a bucket — "how long has it been like this", walking backwards
        const runOf = (code) => { let n = 0;
          for (let i = dates.length - 1; i >= 0; i--) {
            const duc = String(((found[dates[i]]||{})[k]||{}).duc||'').trim().toUpperCase();
            if (duc === code) n++; else if (duc) break; else break;
          } return n; };
        const shopRun = runOf('A-SHOP');
        if (shopRun > 0) shop.push({ unit:r.unit, desc:r.desc||'', days:shopRun, rate:rate(r) });
        const sinceJob = lastJob ? Math.round((today - new Date(lastJob+'T00:00:00')) / dayMs) : null;
        const daysIdle = sinceJob == null ? cover : sinceJob;
        const proven = sinceJob != null;
        if (daysIdle >= OWNER_IDLE_MIN && shopRun === 0) idle.push({ unit:r.unit, desc:r.desc||'', days:daysIdle, proven, rate:rate(r) });
      });
      idle.sort((a,b)=>b.days-a.days); shop.sort((a,b)=>b.days-a.days);
      // #FLEET_SUMMARY_V2 — seed the per-unit run state from real history so the daily roll continues
      // an existing run instead of restarting it at day 1 the next time someone saves.
      const seedState = {}; const lastDay = dates[dates.length-1] || null;
      if (lastDay) (active||[]).forEach(r => {
        const k = unitKey(r.unit); if (!k) return;
        const bucketOn = (d) => { const x = String(((found[d]||{})[k]||{}).duc||'').trim().toUpperCase();
          return x ? (OVERHEAD_SET.has(x) ? x : 'JOB') : null; };
        const cur = bucketOn(lastDay); if (!cur) return;
        let since = lastDay;
        for (let i = dates.length-1; i >= 0; i--) { const b2 = bucketOn(dates[i]);
          if (b2 === cur) since = dates[i]; else if (b2) break; }
        seedState[k] = { bucket: cur, since };
      });
      // #FLEET_UPTIME_V1 — usable assets = active fleet minus whatever is sitting in the shop.
      // Idle units are NOT subtracted: they work, they are simply not deployed. Conflating the two
      // would make uptime a utilisation number and it would stop meaning "can we put it to work".
      const activeCount = (active||[]).length;
      const uptimePct = activeCount ? Math.round(((activeCount - shop.length) / activeCount) * 100) : null;
      await ss('fleet-summary', {
        at: new Date().toISOString(), coverageDays: cover, idleTarget: OWNER_IDLE_MIN,
        activeCount, uptimePct,
        idle: idle.slice(0,50), shop: shop.slice(0,50),
        idleCount: idle.length, shopCount: shop.length,
        idleBurn: idle.reduce((n,x)=>n+(x.rate||0),0), shopBurn: shop.reduce((n,x)=>n+(x.rate||0),0),
        // #FLEET_RENTAL_V2 — piped into Owner Dash in place of App Usage.
        rentalRpo: rentalDigest(),
        state: seedState, lastDay, source: 'scan',   // #FLEET_SUMMARY_V2
      });
    } catch (e) { /* the scan itself still succeeded; the digest is a bonus */ }
    setBusy('');
  };
  const idleReport = useMemo(() => {
    if (!idleScan) return null;
    const dates = Object.keys(idleScan.days).sort();
    const today = new Date(); today.setHours(0,0,0,0);
    const coverage = idleScan.earliest
      ? Math.round((today - new Date(idleScan.earliest+'T00:00:00')) / 86400000) : 0;
    const rows = (active||[]).map(r => {
      const k = unitKey(r.unit);
      let lastJob = '', nJob = 0, nIdle = 0, nShop = 0, nOh = 0;
      dates.forEach(d => {
        const duc = String(((idleScan.days[d]||{})[k]||{}).duc||'').trim().toUpperCase();
        if (!duc) return;
        if (duc === 'A-IDLE') nIdle++;
        else if (duc === 'A-SHOP') nShop++;
        else if (duc === 'A-OH') nOh++;
        else { nJob++; if (d > lastJob) lastJob = d; }
      });
      const since = lastJob ? Math.round((today - new Date(lastJob+'T00:00:00')) / 86400000) : null;
      // With no job in the window, all we can honestly say is "at least the coverage".
      const daysIdle = since == null ? coverage : since;
      const proven = since != null || coverage >= IDLE_TARGET;
      return { r, unit:r.unit, desc:r.desc, cat:r.cat, rate:rate(r), lastJob, since, daysIdle, proven,
               nJob, nIdle, nShop, nOh, seen: nJob+nIdle+nShop+nOh,
               burn: rate(r) * (nIdle + nShop + nOh) };
    }).filter(x => x.seen > 0 || x.rate > 0);
    const flagged = rows.filter(x => x.nJob === 0 || (x.since != null && x.since >= IDLE_TARGET))
                        .sort((a,b) => b.daysIdle - a.daysIdle || b.rate - a.rate);
    return { rows, flagged, coverage, dates: dates.length,
             burn: flagged.reduce((s,x)=>s+x.rate,0) };
  }, [idleScan, active]);

  // ── data-quality flags ─────────────────────────────────────────────────────
  const flags = useMemo(() => {
    const out = [];
    const seen = {};
    (active||[]).forEach(r => {
      const k = unitKey(r.unit);
      if (k) { seen[k] = (seen[k]||0) + 1; }
      if (!unitOk(r.unit)) out.push({ sev:'warn', unit:r.unit, kind:'Shape', msg:'Not in NN-XXXX form' });
      const v = unitVsVin(r.unit, r.vin);
      if (v.level === 'bad') {
        const should = unitFromVin(r.vin, '20'+String(r.unit||'').slice(0,2));
        out.push({ sev:'bad', unit:r.unit, kind:'Unit vs VIN', msg:v.msg + (should ? ` — implies ${should}` : '') });
      }
    });
    Object.entries(seen).forEach(([k,n]) => { if (n>1) out.push({ sev:'bad', unit:keyToDisplay(k), kind:'Duplicate unit', msg:`${n} rows share this unit number` }); });
    const vinMap = {};
    (active||[]).forEach(r => { if (!isVinPlaceholder(r.vin)) { const v=String(r.vin).toUpperCase().trim(); (vinMap[v]=vinMap[v]||[]).push(r.unit); } });
    Object.entries(vinMap).forEach(([v,us]) => { if (us.length>1) out.push({ sev:'bad', unit:us.join(' / '), kind:'Duplicate VIN', msg:`${us.length} units share VIN ${v}` }); });
    (active||[]).forEach(r => { if (isVinPlaceholder(r.vin)) out.push({ sev:'info', unit:r.unit, kind:'No VIN', msg:String(r.vin||'(blank)').slice(0,40) }); });
    const order = { bad:0, warn:1, info:2 };
    return out.sort((a,b) => order[a.sev]-order[b.sev] || String(a.unit).localeCompare(String(b.unit)));
  }, [active]);
  const badCount = flags.filter(f=>f.sev==='bad').length;

  // ── import ─────────────────────────────────────────────────────────────────
  const [imp, setImp] = useState('');
  const [impMsg, setImpMsg] = useState('');
  const runImport = async () => {
    const txt = imp.trim();
    if (!txt) return;
    try {
      if (txt.startsWith('{')) {
        const j = JSON.parse(txt);
        if (!j.cols || !j.active) throw new Error('JSON is missing cols/active');
        setBusy('Importing…');
        await persistMaster(expand(j.cols, j.active), expand(j.cols, j.retired||[]));
        setBusy(''); setImpMsg(`Imported ${(j.active||[]).length} active and ${(j.retired||[]).length} retired units.`);
        setImp('');
        return;
      }
      // TSV / CSV pasted straight out of Excel — first row is the header
      const sep = txt.includes('\t') ? '\t' : ',';
      const lines = txt.split(/\r?\n/).filter(l => l.trim());
      const hdr = lines[0].split(sep).map(h => h.trim());
      const map = { 'DUC Unit':'unit','UNIT':'unit','Unit':'unit','CAT DESCRIPTION':'cat','UNIT DESCRIPTION':'desc',
        'SERIAL / VIN':'vin','DUC Day':'ducDay','Pur Date':'purDate','Pur Amnt':'purAmt','Notes':'notes' };
      const keys = hdr.map(h => map[h] || h.replace(/\s+/g,'').replace(/[^0-9A-Za-z]/g,'').replace(/^./,c=>c.toLowerCase()));
      const recs = lines.slice(1).map(l => {
        const cells = l.split(sep); const o = {};
        keys.forEach((k,i) => { const v=(cells[i]||'').trim(); if (v) o[k]=v; });
        return o;
      }).filter(o => String(o.unit||'').trim());
      if (!recs.length) throw new Error('No rows with a unit number were found');
      if (!window.confirm(`Import ${recs.length} rows?\n\nThis REPLACES the active master (${(active||[]).length} units). The retired list is untouched.`)) return;
      setBusy('Importing…');
      await persistMaster(recs, retired); setBusy('');
      setImpMsg(`Imported ${recs.length} units.`); setImp('');
    } catch (e) { setImpMsg('Could not read that: ' + (e.message||e)); }
  };

  // ══════════════════════════════════════════════════════════════════════════
  // #FLEET_YARDWALK_V1 / #FLEET_INSPEC_V1 / #FLEET_TOLL_V1 (2026-08-25) — Bill/Jerry: a phone-first
  // walk-the-yard tool for marking A-IDLE/A-SHOP with a location + note. It writes straight onto
  // today's Daily Inventory record via the same setCell every other Daily edit uses — no separate
  // storage, no second sync step, autosave already covers it. Plus two reference lists Jerry keeps by
  // hand today: Inspections (with due/overdue) and Toll Tags. Seeded from the two sample sheets Bill
  // sent 2026-08-25 — add more through the app from here on (masterEdit: Owner/Fleet, same as the rest
  // of the Fleet master).
  // ══════════════════════════════════════════════════════════════════════════

  // ── Yard Walk — search a unit, tag it IDLE/SHOP with a location + note ──────
  const [qWalk, setQWalk]           = useState('');
  const [walkUnit, setWalkUnit]     = useState(null);   // the active-master row being tagged, or null
  const [walkBucket, setWalkBucket] = useState('A-IDLE');
  const [walkCity, setWalkCity]     = useState('');
  const [walkNote, setWalkNote]     = useState('');
  // Remembers the last location typed so a whole walk through one yard doesn't re-type it every unit.
  const [lastWalkCity, setLastWalkCity] = useState('Ennis');

  // #FLEET_YARDWALK_WEEKLOCK_V1 (2026-08-26) — see the banner text in the Yard Walk view for the why.
  const walkWeeksBack = Math.round((mondayOf(new Date()) - new Date(wkStart+'T12:00:00')) / (7*86400000));
  const walkEditable = editable && walkWeeksBack <= 1;

  // #FLEET_YARDWALK_FINDINGS_V1 (2026-08-26) — day-keyed running notes, own storage key.
  const [walkFindings, setWalkFindings] = useState({});
  const [findingsSavedAt, setFindingsSavedAt] = useState('');
  useEffect(() => { (async () => {
    try { const s = await sg('fleet-yard-findings'); setWalkFindings((s && typeof s === 'object') ? s : {}); }
    catch { setWalkFindings({}); }
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveFindings = async (text) => {
    const next = { ...walkFindings, [dayISO]: text };
    setWalkFindings(next);
    try { await ss('fleet-yard-findings', next); setFindingsSavedAt(new Date().toLocaleTimeString()); } catch { /* best effort */ }
  };

  // #FLEET_YARDWALK_LOG_V1 (2026-09-08) — Bill: "Need a scratch pad or ability to enter items found
  // that are not on any list... has to be resolved quickly." Distinct from the free-text Findings note
  // above (a per-day blob) — this is a tracked LIST, one row per item, with who logged it, when, and
  // an explicit open/resolved state, because these are action items, not just notes. Two email legs:
  // (1) instant confirmation back to whoever logged it, fire-and-forget, same pattern as PMDashboard's
  // #SMS_ATTENDANCE_ALERT_V1 — never blocks the save; (2) one end-of-day digest to the distro list
  // (`yard-findings-distro` in app_storage, defaults to Bob + Bill) of everything still OPEN, sent
  // server-side by api/yard-findings-digest.js on a weekday 6pm Central Vercel Cron — so it nags until
  // closed, not just the day it was found.
  const [walkLog, setWalkLog] = useState([]);
  const [walkLogText, setWalkLogText] = useState('');
  const [walkLogBusy, setWalkLogBusy] = useState(false);
  useEffect(() => {
    let dead = false;
    const load = async (first) => {
      try { const s = await sg('fleet-yard-findings-log'); if (!dead) setWalkLog(Array.isArray(s) ? s : []); }
      catch { if (!dead && first) setWalkLog([]); }
    };
    load(true);
    // #FLEET_GPS_ASSIGN_V1 — PMs now add rows from the GPS map ("NAME FIX"), so re-read every 5 minutes to pick them up
    const t = setInterval(() => load(false), 5 * 60 * 1000);
    return () => { dead = true; clearInterval(t); };
  }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #FLEET_GPS_ASSIGN_V1 — every write reads the list fresh first and applies the change to that, so a row a PM
  // flagged from the GPS map after this screen loaded is never written over by this screen's older copy.
  const walkLogSave = async (fn) => {
    let base = walkLog;
    try { const s = await sg('fleet-yard-findings-log'); if (Array.isArray(s)) base = s; } catch { /* use what is on screen */ }
    const next = fn(base);
    setWalkLog(next);
    try { await ss('fleet-yard-findings-log', next); } catch { /* best effort */ }
    return next;
  };
  const shortWho = (e) => { const n = String(e||'').split('@')[0].split('.')[0]; return n ? n[0].toUpperCase()+n.slice(1) : '—'; };
  // #YARD_UNIT_FOUND_V1 (2026-10-06) — Bill: Jerry logged 26-2101 here on 09/22 and 10/01, a unit in the yard
  // that never came through the front door onto the Equipment Master. It sat as an open row, six "1 item still
  // open" digests went out, and it was even marked Resolved once while still off the Master. So every item is
  // now read for unit numbers and checked against the Master: one that isn't on it is tagged NOT ON MASTER,
  // can't be resolved without a confirm, and the daily email goes out as "UNIT # FOUND — MUST RESOLVE"
  // (api/yard-findings-digest.js does the same check server-side).
  const walkUnitsOf = (text) => [...new Set(String(text||'').toUpperCase().match(/\bR?\d{2}-[0-9A-Z]{4}R?\b/g) || [])];
  const walkMasterState = (u) => active == null ? '' : (active||[]).some(r => unitKey(r.unit) === unitKey(u)) ? 'active' : (retired||[]).some(r => unitKey(r.unit) === unitKey(u)) ? 'retired' : 'missing';
  const walkOffMaster = (text) => walkUnitsOf(text).filter(u => { const st = walkMasterState(u); return st === 'missing' || st === 'retired'; });
  // #YARD_UNIT_AUTOCLOSE_V1 (2026-10-06) — Bill, after seeing the 10/05 email: "Maybe we should not allow Jerry
  // to Clear but get cleared when added to Equipment Master... when added it should resolve it the day it was
  // added." So a found-unit row is no longer closed by a click from Yard. It closes itself the moment every
  // unit it names is active on the Master, dated to that day. Only rows that were OFF the Master when logged
  // auto-close (offMaster is stamped at Add; rows from before the stamp count when the text is nothing but
  // unit numbers) — "25-1221 won't tag" about a unit already on the Master stays a normal open item.
  const walkWasOff = (it) => Array.isArray(it && it.offMaster) ? it.offMaster.length > 0
    : (walkUnitsOf(it && it.text).length > 0 && !String((it && it.text)||'').toUpperCase().replace(/\bR?\d{2}-[0-9A-Z]{4}R?\b/g, '').replace(/[^0-9A-Z]/g, ''));
  useEffect(() => {
    if (!editable || active == null || !walkLog.length) return;
    const today = iso(new Date());
    const patch = {};
    walkLog.forEach(x => {
      if (!x || x.resolved || !walkWasOff(x)) return;
      const us = walkUnitsOf(x.text);
      if (!us.length || !us.every(u => walkMasterState(u) === 'active')) return;
      // the day it went on the Master, when the Master row carries one; otherwise today
      const added = us.map(u => { const r = (active||[]).find(m => unitKey(m.unit) === unitKey(u)); const d = String((r && r.addedAt)||'').slice(0,10); return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : ''; }).filter(Boolean).sort().pop();
      const day = (added && added >= String(x.at||'').slice(0,10) && added <= today) ? added : today;
      patch[x.id] = { resolved:true, resolvedAt: day === today ? new Date().toISOString() : `${day}T17:00:00.000Z`, resolvedBy:'Equipment Master', autoResolved:true };
    });
    if (!Object.keys(patch).length) return;
    walkLogSave(base => base.map(x => (x && patch[x.id] && !x.resolved) ? { ...x, ...patch[x.id] } : x)).catch(() => {});   // #FLEET_GPS_ASSIGN_V1 — fresh read first
  }, [active, walkLog]);   // eslint-disable-line react-hooks/exhaustive-deps
  const addWalkLogItem = async () => {
    const text = walkLogText.trim();
    if (!text || walkLogBusy) return;
    setWalkLogBusy(true);
    const entry = { id: `${Date.now()}-${Math.random().toString(36).slice(2,7)}`, at: new Date().toISOString(),
      day: dayISO, by: upn || '', text, resolved: false, ...(active == null ? {} : { offMaster: walkOffMaster(text) }) };   // #YARD_UNIT_AUTOCLOSE_V1
    setWalkLogText('');
    await walkLogSave(base => [entry, ...base]);   // #FLEET_GPS_ASSIGN_V1 — fresh read first
    setWalkLogBusy(false);
    if (upn) {
      fetch('/api/send-email', { method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ to: upn, subject: (() => { const off = walkOffMaster(text); return off.length ? `UNIT${off.length===1?'':'S'} ${off.join(', ')} FOUND — MUST RESOLVE (logged ${fmtMD(new Date(dayISO+'T12:00:00'))})` : `Yard Walk item logged — ${fmtMD(new Date(dayISO+'T12:00:00'))}`; })(),   // #YARD_UNIT_FOUND_V1
          html: `<div style="font-family:Calibri,Arial,sans-serif;font-size:14px;color:#222"><p>Logged and on the list — tracked until resolved:</p>`
            + `<p style="font-size:15px;padding:10px;background:#f4f4f4;border-radius:6px">${text.replace(/</g,'&lt;')}</p>`
            + `<p style="color:#888;font-size:12px">Dakota Ops · Fleet · Yard Walk</p></div>` })
      }).catch(()=>{});
    }
  };
  const resolveWalkLogItem = async (id, resolved) => {
    // #YARD_UNIT_FOUND_V1 — resolving stops the daily email, so a unit still off the Master takes a deliberate yes.
    const cur = walkLog.find(x => x.id === id);
    const off = resolved && cur ? walkOffMaster(cur.text) : [];
    // #YARD_UNIT_AUTOCLOSE_V1 — it closes itself when the unit goes on the Master. Yard can't click it away;
    // Owner / MGMT can dismiss one that should never be on the Master (not a Dakota unit, logged in error).
    if (off.length && !masterEdit) { alert(`${off.join(', ')} ${off.length===1?'is':'are'} not on the Equipment Master.\n\nThis closes by itself the day the unit is added to the Master.`); return; }
    if (off.length && !window.confirm(`${off.join(', ')} ${off.length===1?'is':'are'} still NOT on the Equipment Master.\n\nDismissing stops the daily "UNIT FOUND — MUST RESOLVE" email.\nOnly dismiss if this is not a Dakota unit or it was logged in error. Otherwise add it to the Master and it closes by itself.\n\nDismiss anyway?`)) return;
    await walkLogSave(base => base.map(x => x.id === id   // #FLEET_GPS_ASSIGN_V1 — fresh read first
      ? { ...x, resolved, resolvedAt: resolved ? new Date().toISOString() : null, resolvedBy: resolved ? (upn||'') : null, autoResolved: false, dismissed: false, ...(off.length ? { resolvedOffMaster: off, dismissed: true } : {}) }
      : x));
  };

  // #FLEET_YARD_BOARD_V2 (2026-08-27) — Bill: the "digital White board" is a tile mirror of the
  // actual magnetic dispatch board at the shop (see the photos he sent), not a notes field. Every
  // active unit is an auto-generated tile, colored by category; Jerry taps a tile then taps a lane to
  // place it — mirroring what he physically sees, on his phone. The point isn't automation: hand-set
  // position is what surfaces drift between the real board and whatever the system otherwise assumes.
  // Lands on Jerry's Yard hub; Bob/Owner can open and use it too, from their own dashboard, when
  // they need to (Bill: "Bob or owner to access if/when needed").
  const [board, setBoard] = useState(null);           // { v, supervisors:[], staff:[], positions:{tileKey:laneId}, labels:{tileKey:note}, laneOrder:[], updatedAt, updatedBy }
  const [boardSel, setBoardSel] = useState('');        // unit/staff key currently selected for tap-to-place
  const [boardDark, setBoardDark] = useState(true);    // local toggle — FleetModule itself has no app-wide light mode yet
  const boardDefault = () => ({ v:1, supervisors:[], staff:[], positions:{}, labels:{}, laneOrder:[], catColors:{}, tileOrder:{} });
  const [boardLoadFailed, setBoardLoadFailed] = useState(false);
  const [boardColorsOpen, setBoardColorsOpen] = useState(false); // #FLEET_BOARD_V4 (2026-09-18) — unit-type color legend toggle
  // #FLEET_BOARD_LOADGUARD_V1 (2026-08-27) — root-cause fix for a real data-loss bug: every board
  // mutator (place a tile, add/rename a supervisor, Load Monday's Daily, etc.) used `board ||
  // boardDefault()` as its starting point, then saved that straight back to storage. If a mutator ran
  // before this load finished — someone tapping the board within the first second it opens, or a
  // transient fetch hiccup — `board` was still null, so the mutator silently built on an EMPTY board
  // and persisted that as the new truth, wiping every real placement, supervisor, and note that had
  // been saved before. Fix: a failed load no longer fakes an empty board — it leaves `board` null — and
  // the render below refuses to show any board controls at all while `board` is null, so there is no
  // button left to click that could save over real data before it's actually loaded.
  useEffect(() => { (async () => {
    try { const b = await sg('fleet-yard-board'); setBoard((b && typeof b==='object' && !Array.isArray(b)) ? { ...boardDefault(), ...b } : boardDefault()); }
    catch { setBoardLoadFailed(true); }
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #FLEET_BOARD_RIGTYPE_V1 — live weekly data for the Rig Fleet by Type panel: Monday's Daily
  // Assigned/DUC per unit, corrected by Tuesday's entry if PMs have updated it by then (Bill: "Updated
  // Tue once Daily reports are updated by PMs"). Read-only display, not the tap-to-place board above —
  // nothing here is ever saved back, so there's no load-order risk like #FLEET_BOARD_LOADGUARD_V1.
  const [rigWeek, setRigWeek] = useState(null);
  useEffect(() => { (async () => {
    const monISO = iso(mondayOf(new Date()));
    const tueISO = iso(addDays(new Date(monISO+'T12:00:00'), 1));
    let wk = null;
    try { wk = await sg('fleet-daily-' + monISO); } catch { wk = null; }
    const monRec = (wk && typeof wk === 'object' && wk[monISO]) || {};
    const tueRec = (wk && typeof wk === 'object' && wk[tueISO]) || {};
    setRigWeek({ monISO, tueISO, monRec, tueRec });
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  // #FLEET_BOARD_SUPJOB_V6 (2026-09-18) — Bill: "these guys can sometimes move between RIGS." A
  // rig's own Daily entry only tells you who's on THAT unit right now; if a crew swapped iron
  // mid-week, keying job info off the unit can point at stale/wrong info. This NAME-keyed index scans
  // every entry in this week's Daily (Monday, Tuesday-corrected) so whichever unit a person is
  // actually logged against — this rig or a different one — their job follows them, not their old
  // rig. Hoisted out of rigSections in V7 so it also drives the Name Tag job readout on the
  // tap-to-place board below (Bill: "good to see who is where, what RIG they are on and what
  // equipment they have" — one lookup, two places).
  const supJobByName = useMemo(() => {
    if (!rigWeek) return {};
    const { monRec, tueRec } = rigWeek;
    const map = {};
    const scanDaily = (rec, rank) => {
      Object.keys(rec||{}).forEach(k => {
        const e = rec[k]; const name = normPersonName(e && e.assigned);
        const duc = String((e && e.duc)||'').trim();
        if (!name || !duc || duc.startsWith('A-')) return;
        // Tuesday's entries (rank 2) win over Monday's (rank 1) for the same person, same
        // Mon-then-Tue-corrects pattern as everywhere else on this panel.
        if (!map[name] || rank >= map[name].rank) map[name] = { duc, rank };
      });
    };
    scanDaily(monRec, 1); scanDaily(tueRec, 2);
    return map;
  }, [rigWeek]);
  const rigSections = useMemo(() => {
    if (!rigWeek) return null;
    const { monRec, tueRec } = rigWeek;
    const rigs = (active||[]).filter(r => /^Drill-R\d/.test(String(r.cat||'')));
    const byType = {};
    const unclassified = [];
    rigs.forEach(r => {
      const type = classifyRigType(r.desc);
      const k = unitKey(r.unit);
      const tue = tueRec[k]; const mon = monRec[k];
      const unitDuc = String((tue && tue.duc) || (mon && mon.duc) || '').trim();
      const assigned = String((tue && tue.assigned) || (mon && mon.assigned) || '').trim();
      const byName = assigned ? supJobByName[normPersonName(assigned)] : null;
      const duc = (byName && byName.duc) || unitDuc;
      const job = (duc && !duc.startsWith('A-')) ? jobIndex[stripCP(duc)] : null;
      const entry = { unit:r.unit, tkey:k, duc, assigned, job };
      if (type) { (byType[type] = byType[type] || []).push(entry); } else { unclassified.push(entry); }
    });
    const sections = RIG_TYPE_ORDER.filter(t => byType[t]).map(t => ({ type:t, units:byType[t] }));
    if (unclassified.length) sections.push({ type:'Unclassified', units:unclassified });
    return sections;
  }, [active, rigWeek, jobIndex, supJobByName]);
  const saveBoardData = async (next) => {
    setBoard(next);
    try { await ss('fleet-yard-board', { ...next, updatedAt:new Date().toLocaleString(), updatedBy:String(userRole||'') }); }
    catch { /* best effort */ }
  };
  const placeTile = (uKey, dest) => {
    const base = board || boardDefault();
    saveBoardData({ ...base, positions:{ ...(base.positions||{}), [uKey]:dest } });
    setBoardSel('');
  };
  // #FLEET_BOARD_V3 (2026-08-27) — Bill's first-use feedback: rename a supervisor in place, reorder
  // lanes (both the fixed yard-status ones and supervisor ones) to mimic the physical board's actual
  // layout, a short note that shows under a tile's unit number, and Staff tiles (named people, placed
  // just like equipment). All still tap-based, not drag — same reasoning as tile placement: reliable
  // on Jerry's phone.
  const addSupervisor = () => {
    const name = window.prompt('Supervisor name to add:');
    if (!name || !name.trim()) return;
    const base = board || boardDefault();
    saveBoardData({ ...base, supervisors:[...(base.supervisors||[]), name.trim()] });
  };
  const renameSupervisor = (oldName) => {
    const name = window.prompt('Rename supervisor:', oldName);
    if (!name || !name.trim() || name.trim() === oldName) return;
    const base = board || boardDefault();
    const positions = { ...(base.positions||{}) };
    Object.keys(positions).forEach(k => { if (positions[k] === `sup:${oldName}`) positions[k] = `sup:${name.trim()}`; });
    const laneOrder = (base.laneOrder||[]).map(id => id === `sup:${oldName}` ? `sup:${name.trim()}` : id);
    saveBoardData({ ...base, supervisors:(base.supervisors||[]).map(s=>s===oldName?name.trim():s), positions, laneOrder });
  };
  const removeSupervisor = (name) => {
    if (!window.confirm(`Remove ${name} from the board? Any tiles placed with them move back to Unplaced.`)) return;
    const base = board || boardDefault();
    const positions = { ...(base.positions||{}) };
    Object.keys(positions).forEach(k => { if (positions[k] === `sup:${name}`) delete positions[k]; });
    saveBoardData({ ...base, supervisors:(base.supervisors||[]).filter(s=>s!==name), positions,
      laneOrder:(base.laneOrder||[]).filter(id=>id!==`sup:${name}`) });
  };
  // #FLEET_BOARD_NAMETAG_V5 (2026-09-18) — Bill, after seeing v4 live: "Want to be able to MOVE NAME
  // Tags and equipment under each supervisor to mimic how the board looks today." Renamed from the
  // never-used "staff" tile (same `staff` storage array, same `staff:Name` tkey — no data-shape
  // change, so nothing live is migrated) into a bolder, paper-tag-styled tile that drops into ANY
  // lane (including the 47 hand-built supervisor lanes already live) and reorders with the same ▲ ▼
  // arrows equipment uses — so a lane can read name tag -> their gear -> next name tag -> their gear,
  // matching the physical board's crew groupings, without touching any existing lane/placement.
  const addNameTag = () => {
    const name = window.prompt('Name for the new tag:');
    if (!name || !name.trim()) return;
    const base = board || boardDefault();
    saveBoardData({ ...base, staff:[...(base.staff||[]), name.trim()] });
  };
  const removeNameTag = (name) => {
    if (!window.confirm(`Remove ${name}'s name tag from the board?`)) return;
    const base = board || boardDefault();
    const positions = { ...(base.positions||{}) }; delete positions[`staff:${name}`];
    const labels = { ...(base.labels||{}) }; delete labels[`staff:${name}`];
    saveBoardData({ ...base, staff:(base.staff||[]).filter(s=>s!==name), positions, labels });
  };
  // #FLEET_BOARD_DAILYSYNC_V1 (2026-08-27) — Bill, after seeing the board built fully by hand: "load
  // the Sup names and auto pull from Daily to keep this as the truth and he could zero in on what
  // needs updating... we are placing units there from Daily landing of Monday Assigned. This is only
  // a Monday Check point or weekly flash." Daily's Assigned name (whoever currently has a unit out,
  // per that week's `fleet-daily-<mondayISO>` record) becomes the supervisor lane and the unit's
  // starting position — an explicit button, not silent/continuous, since it's meant as a deliberate
  // weekly reset: load Monday's paperwork, then Jerry corrects drift against the physical board by
  // hand for the rest of the week, same tap-to-place as always. Only touches units that actually have
  // a Monday Assigned name — anything blank (typically sitting in Shop/Idle/OOS) is left exactly where
  // it's been manually placed.
  const syncFromDaily = async () => {
    const mon = iso(mondayOf(new Date()));
    let wkData;
    try { wkData = await sg('fleet-daily-' + mon); } catch { wkData = null; }
    const mondayRec = (wkData && typeof wkData === 'object' && wkData[mon]) || {};
    const norm = (s) => String(s||'').trim().toUpperCase().replace(/\s+/g,' ');
    const base = board || boardDefault();
    const existingSupByNorm = {};
    (base.supervisors||[]).forEach(nm => { existingSupByNorm[norm(nm)] = nm; });
    const matches = [];
    (active||[]).forEach(r => {
      const k = unitKey(r.unit); if (!k) return;
      const name = String((mondayRec[k]||{}).assigned||'').trim();
      if (name) matches.push({ tkey:k, name });
    });
    if (!matches.length) { alert(`No Assigned names found in ${mon}'s Daily yet — nothing to load.`); return; }
    const newSupNames = [];
    matches.forEach(m => { if (!existingSupByNorm[norm(m.name)] && !newSupNames.some(n=>norm(n)===norm(m.name))) newSupNames.push(m.name); });
    if (!window.confirm(`Load ${mon}'s Daily Assigned into the board? This places ${matches.length} unit(s) into supervisor lanes (overwriting their current spot) and adds ${newSupNames.length} new supervisor lane(s)${newSupNames.length?': '+newSupNames.join(', '):''}.`)) return;
    const positions = { ...(base.positions||{}) };
    matches.forEach(m => { positions[m.tkey] = `sup:${existingSupByNorm[norm(m.name)] || m.name}`; });
    saveBoardData({ ...base, supervisors:[...(base.supervisors||[]), ...newSupNames], positions });
  };
  const setTileNote = (tKey, current) => {
    const note = window.prompt('Short note for this tile (blank to clear):', current || '');
    if (note == null) return;
    const base = board || boardDefault();
    const labels = { ...(base.labels||{}) };
    if (note.trim()) labels[tKey] = note.trim().slice(0, 24); else delete labels[tKey];
    saveBoardData({ ...base, labels });
  };
  // Every fixed group + every current supervisor, in whatever order Bill's last save left them —
  // anything never manually ordered yet (a brand-new supervisor, or first load ever) just lands at
  // the end rather than getting lost.
  const boardLaneIds = (sup) => [...BOARD_GROUPS.map(([id])=>`grp:${id}`), ...sup.map(name=>`sup:${name}`)];
  const orderedLaneIds = (base) => {
    const all = boardLaneIds(base.supervisors||[]);
    const saved = (base.laneOrder||[]).filter(id => all.includes(id));
    const missing = all.filter(id => !saved.includes(id));
    return [...saved, ...missing];
  };
  const moveLane = (laneId, dir) => {
    const base = board || boardDefault();
    const order = orderedLaneIds(base);
    const i = order.indexOf(laneId); const j = i + dir;
    if (i < 0 || j < 0 || j >= order.length) return;
    const next = order.slice(); const tmp = next[i]; next[i] = next[j]; next[j] = tmp;
    saveBoardData({ ...base, laneOrder: next });
  };

  const walkResults = useMemo(() => {
    const ql = qWalk.trim().toLowerCase();
    if (!ql) return [];
    const seen = new Set();
    return (active||[])
      .filter(r => { const k = unitKey(r.unit); if (!k || seen.has(k)) return false; seen.add(k); return true; })
      .filter(r => `${r.unit} ${r.cat||''} ${r.desc||''}`.toLowerCase().includes(ql))
      .slice(0, 25);
  }, [qWalk, active]);

  // #FLEET_YARDWALK_PREFILL_V1 (2026-08-27) — Bill: Copy Prev stays names-only; Yard Walk is what
  // fills City/Notes for the day (see #FLEET_COPYPREV_HOLIDAY_V1 note above). Before this, City
  // defaulted to whatever was last TYPED this session (not this unit's own history) and Note had no
  // default at all — so a unit sitting in the same shop bay for two weeks made Jerry retype "Ennis"
  // every walk. Now it looks back at THIS unit's most recent stored day this week and offers that as
  // the default — still just a default; Jerry edits or clears it before Save like always.
  const lastWalkFor = (k) => {
    for (let i = dayIx - 1; i >= 0; i--) {
      const d = week[iso(addDays(new Date(wkStart+'T12:00:00'), i))];
      const v = d && d[k];
      if (v && (String(v.city||'').trim() || String(v.note||'').trim() || String(v.assigned||'').trim())) return v;
    }
    return null;
  };
  const [walkAssignedName, setWalkAssignedName] = useState('');   // read-only context while tagging
  const openWalkUnit = (r) => {
    const k = unitKey(r.unit);
    const cur = (week[dayISO]||{})[k] || {};
    const curDuc = String(cur.duc||'').trim().toUpperCase();
    const prior = lastWalkFor(k);
    setWalkUnit(r);
    setWalkBucket(curDuc === 'A-SHOP' ? 'A-SHOP' : 'A-IDLE');
    setWalkCity(cur.city || (prior && prior.city) || lastWalkCity);
    setWalkNote(cur.note || (prior && prior.note) || '');
    setWalkAssignedName(cur.assigned || (prior && prior.assigned) || '');
    setQWalk('');
  };
  const saveWalkEntry = () => {
    if (!walkUnit) return;
    setCell(walkUnit.unit, { duc: walkBucket, city: walkCity.trim(), note: walkNote });
    if (walkCity.trim()) setLastWalkCity(walkCity.trim());
    setWalkUnit(null); setWalkCity(''); setWalkNote('');
  };
  // Clears just what Yard Walk itself set (duc + city) — leaves Assigned/notes untouched, same
  // "never silently disturb a field this tool didn't own" rule as the rest of Daily Inventory.
  const removeWalkEntry = (unit) => { setCell(unit, { duc:'', city:'' }); };

  // Today's walked units — read straight off today's Daily Inventory record, so this list and the
  // grid on Daily Inventory can never disagree; there's only one copy of the data.
  const walkedToday = useMemo(() => {
    const day = week[dayISO] || {};
    const seen = new Set();
    return (active||[])
      .filter(r => { const k = unitKey(r.unit); if (!k || seen.has(k)) return false; seen.add(k); return true; })
      .map(r => ({ r, k: unitKey(r.unit), cur: day[unitKey(r.unit)] || {} }))
      .filter(x => x.cur.duc === 'A-IDLE' || x.cur.duc === 'A-SHOP');
  }, [week, dayISO, active]);

  const walkCityOptions = useMemo(() => {
    const s = new Set(['Ennis']);
    Object.values(week[dayISO]||{}).forEach(v => { if (v && v.city) s.add(String(v.city).trim()); });
    return [...s].sort((a,b)=> a==='Ennis'?-1: b==='Ennis'?1: a.localeCompare(b));
  }, [week, dayISO]);

  // ── Inspections list (Jerry's INSPEC tab) — unit, type/model/use, supervisor, dates, license ──
  const [insp, setInsp]         = useState(null);   // null = still loading
  const [qInsp, setQInsp]       = useState('');
  const [inspEdit, setInspEdit] = useState(null);
  const INSP_SEED = [
    { unit:'03-6744', type:'WHL', model:'Peterbilt',     use:'Haul Trk',  sup:'HAUL',    lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1L28270' },
    { unit:'05-4745', type:'WHL', model:'Freightliner',  use:'Water Trk', sup:'MATT B',  lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1L28084' },
    { unit:'07-7327', type:'WHL', model:'Freightliner',  use:'Water Trk', sup:'EXTRA',   lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1M43179' },
    { unit:'07-9221', type:'WHL', model:'Freightliner',  use:'Water Trk', sup:'MATT D',  lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1M08215' },
    { unit:'14-3869', type:'WHL', model:'W-STAR 4700',   use:'Water Trk', sup:'GABRIEL', lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1L28229' },
    { unit:'15-1046', type:'WHL', model:'W-STAR 4900FA', use:'VAC Trk',   sup:'CODY',    lastDate:'05/31/26', nextDate:'05/31/27', state:'TX', license:'1L28243' },
    { unit:'15-6517', type:'WHL', model:'KENWORTH',      use:'VAC Trk',   sup:'EXTRA',   lastDate:'02/28/26', nextDate:'02/28/27', state:'TX', license:'1L22448' },
    { unit:'15-6518', type:'WHL', model:'KENWORTH',      use:'VAC Trk',   sup:'EXTRA',   lastDate:'02/28/26', nextDate:'02/28/27', state:'TX', license:'1M10763' },
  ];
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-inspections');
      if (s && Array.isArray(s.items)) { setInsp(s.items); return; }
      // First run — seed from Bill's sample sheet, then persist so everyone sees the same starting list.
      setInsp(INSP_SEED);
      try { await ss('fleet-inspections', { v:1, items: INSP_SEED }); } catch { /* seed is a convenience, not critical */ }
    } catch { setInsp([]); }
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveInspList = async (list) => {
    setInsp(list);
    try { await ss('fleet-inspections', { v:1, items: list }); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const saveInspItem = () => {
    if (!inspEdit || !String(inspEdit.unit||'').trim()) { alert('Unit # is required.'); return; }
    const rec = { ...inspEdit }; delete rec._orig;
    const list = inspEdit._orig
      ? (insp||[]).map(x => x === inspEdit._orig ? rec : x)
      : [...(insp||[]), rec];
    saveInspList(list);
    setInspEdit(null);
  };
  const deleteInspItem = (x) => {
    if (!window.confirm(`Remove the inspection record for ${x.unit}?`)) return;
    saveInspList((insp||[]).filter(y => y !== x));
    setInspEdit(null);
  };
  // Parses MM/DD/YY, MM/DD/YYYY, or ISO — anything else is left unparseable, same as fmtDate's own
  // rule (show it verbatim rather than guess). Returns null, never NaN, so callers can just check truthy.
  const parseMDY = (v) => {
    const s = String(v||'').trim(); if (!s) return null;
    let m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) { let [,mo,da,yr] = m; yr = yr.length===2 ? (Number(yr)<70?'20':'19')+yr : yr;
      const d = new Date(Number(yr), Number(mo)-1, Number(da)); return isNaN(d) ? null : d; }
    m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) { const d = new Date(Number(m[1]), Number(m[2])-1, Number(m[3])); return isNaN(d) ? null : d; }
    return null;
  };
  // #FLEET_ASSETMGMT_V1 — renewal date for a telematics device: start date (or the 08/01/26
  // placeholder, if this unit hasn't had a real one entered yet) + the fixed 3-year life.
  const telemRenewal = (startVal) => {
    const d = parseMDY(startVal) || parseMDY(TELEM_DEFAULT_START);
    if (!d) return '';
    const nd = new Date(d.getFullYear(), d.getMonth() + TELEM_LIFE_MONTHS, d.getDate());
    return `${pad2(nd.getMonth()+1)}/${pad2(nd.getDate())}/${String(nd.getFullYear()).slice(2)}`;
  };
  const inspStatus = (nextDate) => {
    const d = parseMDY(nextDate);
    if (!d) return { label:'—', color:F.muted };
    const days = Math.round((d - new Date(new Date().toDateString())) / 86400000);
    if (days < 0)   return { label:`OVERDUE ${Math.abs(days)}d`, color:F.red };
    if (days <= 30) return { label:`DUE ${days}d`, color:F.amber };
    return { label:'OK', color:F.green };
  };
  // #FLEET_INSPEC_SORT_V1 (2026-08-26) — Bill: "ability to sort columns." Clicking a header sorts by
  // it; a third click clears back to the original overdue-first default.
  const [inspSort, setInspSort] = useState({ key:'', dir:'asc' });
  const inspRows = useMemo(() => {
    const ql = qInsp.trim().toLowerCase();
    const rows = (insp||[])
      .filter(x => !ql || `${x.unit} ${x.type} ${x.model} ${x.use} ${x.sup} ${x.state} ${x.license}`.toLowerCase().includes(ql))
      .map(x => ({ x, st: inspStatus(x.nextDate) }));
    const cmpUnit = (a,b) => String(a.x.unit).localeCompare(String(b.x.unit));
    const statusRank = (r) => r.st.color===F.red ? 0 : r.st.color===F.amber ? 1 : r.st.color===F.green ? 2 : 3;
    if (!inspSort.key) return rows.sort((a,b) => (statusRank(a)-statusRank(b)) || cmpUnit(a,b));
    const dir = inspSort.dir === 'desc' ? -1 : 1;
    if (inspSort.key === 'status') return rows.sort((a,b) => ((statusRank(a)-statusRank(b))*dir) || cmpUnit(a,b));
    if (inspSort.key === 'lastDate' || inspSort.key === 'nextDate') {
      return rows.sort((a,b) => {
        const da = parseMDY(a.x[inspSort.key]), db = parseMDY(b.x[inspSort.key]);
        if (!da && !db) return cmpUnit(a,b);
        if (!da) return 1; if (!db) return -1;
        return ((da - db) * dir) || cmpUnit(a,b);
      });
    }
    return rows.sort((a,b) => (String(a.x[inspSort.key]||'').localeCompare(String(b.x[inspSort.key]||'')) * dir) || cmpUnit(a,b));
  }, [insp, qInsp, inspSort]);

  // #FLEET_INSPEC_MONTHWARN_V1 (2026-08-26) — Bill: "provide warning at 1st day of each month for
  // inspections needed that month." Shown for the whole current calendar month, not just day 1 — a
  // warning only visible if someone happens to open the tab on the 1st isn't much of a warning.
  const inspDueThisMonth = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear(), m = now.getMonth();
    return (insp||[]).filter(x => { const d = parseMDY(x.nextDate); return d && d.getFullYear()===y && d.getMonth()===m; });
  }, [insp]);   // eslint-disable-line react-hooks/exhaustive-deps

  // #FLEET_DATEDISPLAY_DMY_V1 (2026-08-26, reverted same day) — Bill first asked for DD/MM/YY
  // display here, then: "No additional date needed, we will leave inspection date as the truth of
  // dates for this report." Dates stay exactly as entered/stored (MM/DD/YY) everywhere on this tab.

  // ── Toll Tags list (Jerry's TOLL tab) — unit ↔ tag ↔ plate ──────────────────
  const [toll, setToll]         = useState(null);   // null = still loading
  const [qToll, setQToll]       = useState('');
  const [tollSort, setTollSort] = useState({ key:'', dir:'asc' });   // #FLEET_TOLL_SORT_V1 (2026-09-17) — Jerry asked for the Toll Tags list to be sortable
  const [tollEdit, setTollEdit] = useState(null);
  const TOLL_SEED = [
    { unit:'16-2556', tagId:'DNT.08035540', plate:'TX - 962497J', vehInfo:'2016 DITCHWITCH 16-2556', unitNum:'162556', status:'Active' },
    { unit:'17-2049', tagId:'DNT.08035547', plate:'TX - 962559J', vehInfo:'2017 C5 17-2049',         unitNum:'172049', status:'Active' },
    { unit:'19-0362', tagId:'DNT.09868039', plate:'TX - 973739J', vehInfo:'2019 FELLING 19-0362',    unitNum:'190362', status:'Active' },
  ];
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-tolltags');
      if (s && Array.isArray(s.items)) { setToll(s.items); return; }
      setToll(TOLL_SEED);
      try { await ss('fleet-tolltags', { v:1, items: TOLL_SEED }); } catch { /* seed is a convenience, not critical */ }
    } catch { setToll([]); }
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveTollList = async (list) => {
    setToll(list);
    try { await ss('fleet-tolltags', { v:1, items: list }); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const _tollToday = () => { const d = new Date(); const p2 = (x) => String(x).padStart(2,'0'); return `${p2(d.getMonth()+1)}/${p2(d.getDate())}/${d.getFullYear()}`; };   // #TOLL_DEACT_DATE_V1 — same MM/DD/YYYY the issued dates use
  const saveTollItem = () => {
    if (!tollEdit || !String(tollEdit.unit||'').trim() || !String(tollEdit.tagId||'').trim()) { alert('Unit and TollTag ID are required.'); return; }
    const rec = { ...tollEdit }; delete rec._orig;
    // #TOLL_DEACT_DATE_V1 (2026-10-13) — Jerry: keep the history. Going inactive stamps a Date Deactivated (today,
    // editable) instead of anyone typing over Date Issued. Re-activating a tag files the old issued/deactivated pair
    // into rec.history and starts a fresh active stint, so nothing is ever overwritten.
    const _isAct = (v) => /^\s*active\s*$/i.test(String(v||''));
    const _was = tollEdit._orig;
    if (!_isAct(rec.status) && !String(rec.dateDeactivated||'').trim() && (!_was || _isAct(_was.status))) rec.dateDeactivated = _tollToday();
    if (_was && !_isAct(_was.status) && _isAct(rec.status) && String(_was.dateDeactivated||'').trim()) {
      rec.history = [ ...(Array.isArray(_was.history) ? _was.history : []), { tagId:_was.tagId, dateIssued:_was.dateIssued||'', dateDeactivated:_was.dateDeactivated, status:_was.status } ];
      rec.dateDeactivated = '';
      if (String(rec.dateIssued||'') === String(_was.dateIssued||'')) rec.dateIssued = _tollToday();
    }
    const list = tollEdit._orig
      ? (toll||[]).map(x => x === tollEdit._orig ? rec : x)
      : [...(toll||[]), rec];
    saveTollList(list);
    setTollEdit(null);
  };
  // #FLEET_TOLL_DELCONFIRM_V1 — per the original TOLL-tab spec, delete needs an explicit
  // confirm-the-tag step (type the tag ID back), not a generic "are you sure" — a toll tag removed
  // by accident on the wrong row is a billing problem, not just a data nuisance.
  const deleteTollItem = (x) => {
    if (window.prompt(`Type the TollTag ID to confirm removing it:\n${x.tagId}`, '') !== x.tagId) return;
    saveTollList((toll||[]).filter(y => y !== x));
    setTollEdit(null);
  };
  // #FLEET_TOLL_DESC_V1 (2026-08-26) — Bill: "Unit # listed twice — change second one to
  // Description." The second column just re-stated the unit number in a different format; swapped
  // it for the unit's real description off the Equipment Master (live lookup, not stored on the toll
  // record — always current, even if the master description changes later).
  const unitDescByKey = useMemo(() => {
    const m = {};
    (active||[]).forEach(r => { const k = unitKey(r.unit); if (k && !m[k]) m[k] = r.desc || ''; });
    return m;
  }, [active]);
  const tollRows = useMemo(() => {
    const ql = qToll.trim().toLowerCase();
    const filtered = (toll||[]).filter(x => !ql || `${x.unit} ${x.tagId} ${x.plate} ${x.vehInfo} ${x.unitNum} ${x.status} ${x.dateIssued||''}`.toLowerCase().includes(ql));
    if (!tollSort.key) return filtered;
    // #FLEET_TOLL_SORT_V1 — click-to-sort, same three-state cycle (asc → desc → sheet order) as
    // Equip Master / Shop History. Description isn't stored on the toll record itself (it's the live
    // Equipment Master lookup below), so it sorts off `unitDescByKey` like the cell already renders.
    const dir = tollSort.dir === 'desc' ? -1 : 1;
    const val = (x) => tollSort.key === 'desc' ? (unitDescByKey[unitKey(x.unit)] || x.vehInfo || '') : (x[tollSort.key] || '');
    return [...filtered].sort((a,b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric:true, sensitivity:'base' }) * dir);
  }, [toll, qToll, tollSort, unitDescByKey]);
  // #FLEET_RELATEDRECS_V1 (2026-09-08) — Bill: "Can the Yard Toll Tag & Yard inspection merge into
  // Master with its corresponding data?" A literal merge would break Toll's own delete-confirm flow
  // and Inspection's overdue/due-soon tracking, both of which are real, separate workflows Jerry runs
  // off their own tabs — so this keeps Toll/Inspection as their own datasets (same join-by-unit-key
  // pattern Daily/COST/the Master itself already use) and instead surfaces a live summary + one-click
  // jump on the Master edit panel, so a "1 by 1" pass through Master doesn't need a second trip
  // through two more tabs to see what's on file for that unit.
  const fuelCardIdx = useFuelCardIndex(sg);   // #FLEET_FUEL_CARDS_V1 — unit → active Voyager card(s), for the Master's Fuel Card column
  // #FLEET_TRACKER_IMEI_V1 (2026-10-23) — Bill: "Where can we match these IMEI numbers?" Trackers in the shop's BAD box
  // only show an IMEI on the label; the Master and Geotab only know the serial. 'fleet-tracker-ids' holds impac's own
  // serial ↔ IMEI list (127 asset trackers, from their 10/06/26 export, plus BAD-box flags from 10/23) — loaded here so
  // the Master shows IMEI under GPS S/N and the Tracker lookup box finds a unit from the last 4 of an IMEI or a serial.
  const [trackerIds, setTrackerIds] = useState(null);
  useEffect(() => { let dead = false;
    (async () => { try { const t = await sg('fleet-tracker-ids'); if (!dead) setTrackerIds(t && Array.isArray(t.trackers) ? t : { trackers:[] }); }
      catch { if (!dead) setTrackerIds({ trackers:[] }); } })();
    return () => { dead = true; }; }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const imeiBySerial = useMemo(() => { const m = {};
    ((trackerIds && trackerIds.trackers) || []).forEach(t => { if (t && t.serial) m[String(t.serial).trim().toUpperCase()] = t; });
    return m; }, [trackerIds]);
  const [trkQ, setTrkQ] = useState('');
  const tollByKey = useMemo(() => {
    const m = {};
    (toll||[]).forEach(x => { const k = unitKey(x.unit); if (k && !m[k]) m[k] = x; });
    return m;
  }, [toll]);
  const inspByKey = useMemo(() => {
    const m = {};
    (insp||[]).forEach(x => { const k = unitKey(x.unit); if (k && !m[k]) m[k] = x; });
    return m;
  }, [insp]);

  // #FLEET_TOLLINSP_CANDIDATES_V1 (2026-09-17) — Bill: "Master New or Inducted should auto populate
  // into Toll tag & Inspection list to assume these items may need to be included on these list but
  // Jerry has the ability to remove if not required but not reappear." Every active Master unit that
  // has no record on a tab is surfaced there as a "Suggested from Master" candidate; Jerry can add it
  // (jumps straight into that tab's own Add form, unit prefilled) or mark it "not required," which is
  // recorded here — a dismissal persists across sessions/devices and the unit never resurfaces as a
  // candidate again, unless someone explicitly un-dismisses it (masterEdit only, from the "Dismissed"
  // list each tab's Suggested panel can expand to). Kept as its own small store rather than a field on
  // Master/Toll/Insp — same reasoning as #FLEET_RELATEDRECS_V1: it's Jerry's own workflow decision,
  // not equipment data, and a unit's dismissal shouldn't disappear just because its Master row or a
  // toll/insp record gets edited for something unrelated.
  const [tollInspDismissed, setTollInspDismissed] = useState(null);   // null = still loading
  useEffect(() => { (async () => {
    try {
      const s = await sg('fleet-tollinsp-dismissed');
      setTollInspDismissed((s && s.toll && s.insp) ? s : { v:1, toll:{}, insp:{} });
    } catch { setTollInspDismissed({ v:1, toll:{}, insp:{} }); }
  })(); }, [sg]);   // eslint-disable-line react-hooks/exhaustive-deps
  const dismissCandidate = async (kind, unit) => {
    const uk = unitKey(unit); if (!uk) return;
    const cur = tollInspDismissed || { v:1, toll:{}, insp:{} };
    const next = { v:1, toll:{ ...cur.toll }, insp:{ ...cur.insp } };
    next[kind][uk] = { at: new Date().toISOString(), by: upn || '' };
    setTollInspDismissed(next);
    try { await ss('fleet-tollinsp-dismissed', next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const undismissCandidate = async (kind, uk) => {
    const cur = tollInspDismissed || { v:1, toll:{}, insp:{} };
    const next = { v:1, toll:{ ...cur.toll }, insp:{ ...cur.insp } };
    delete next[kind][uk];
    setTollInspDismissed(next);
    try { await ss('fleet-tollinsp-dismissed', next); } catch (e) { alert('Could not save: ' + (e.message||e)); }
  };
  const activeByKey = useMemo(() => {
    const m = {}; (active||[]).forEach(r => { const k = unitKey(r.unit); if (k) m[k] = r; }); return m;
  }, [active]);
  const tollCandidates = useMemo(() => {
    if (!tollInspDismissed) return [];
    return (active||[]).filter(r => { const k = unitKey(r.unit); return k && !tollByKey[k] && !tollInspDismissed.toll[k]; })
      .sort((a,b) => String(a.unit).localeCompare(String(b.unit)));
  }, [active, tollByKey, tollInspDismissed]);
  const inspCandidates = useMemo(() => {
    if (!tollInspDismissed) return [];
    return (active||[]).filter(r => { const k = unitKey(r.unit); return k && !inspByKey[k] && !tollInspDismissed.insp[k]; })
      .sort((a,b) => String(a.unit).localeCompare(String(b.unit)));
  }, [active, inspByKey, tollInspDismissed]);
  const [tollDismissedOpen, setTollDismissedOpen] = useState(false);
  const [inspDismissedOpen, setInspDismissedOpen] = useState(false);

  // ── shared bits ────────────────────────────────────────────────────────────
  const btn = { background:F.cardHi, border:`1px solid ${F.line}`, color:F.text, borderRadius:8,
    padding:'7px 13px', fontSize:12.5, fontWeight:800, cursor:'pointer' };
  const inp = { background:F.bg, border:`1px solid ${F.line}`, borderRadius:7, padding:'6px 9px',
    fontSize:12.5, color:F.text, outline:'none', width:'100%', boxSizing:'border-box' };
  const th = { padding:'8px 10px', fontSize:10, fontWeight:800, textTransform:'uppercase',
    letterSpacing:.4, color:F.muted, textAlign:'left', whiteSpace:'nowrap',
    position:'sticky', top:0, background:F.cardHi, zIndex:1 };
  const td = { padding:'6px 10px', fontSize:12.5, color:F.text, verticalAlign:'middle' };
  // Compact variant for the single Daily toolbar — same shape, less air, so week nav + day tabs +
  // filter + six actions + Save all fit one row without wrapping on a normal laptop.
  const btnS = { ...btn, padding:'6px 10px', fontSize:11.5 };
  // The one money cell used everywhere: $ pinned to the left edge, figure right, two decimals.
  // A column of these lines up on the sign AND the decimal point, which is the whole point.
  const Money = ({ v, color, dim }) => (Number(v)||0) === 0
    ? <span style={{ display:'block', textAlign:'right', color:F.muted }}>—</span>
    : (
      /* #FLEET_MONEYALIGN_V1 — was justifyContent:'space-between', which pinned the $ to the far left
         of a wide COST column and the digits to the far right, leaving a canyon between them. The $
         now sits against its number and the whole group right-aligns; tabular figures keep the decimal
         points stacked down the column, which is the alignment that actually matters. */
      <span style={{ display:'flex', justifyContent:'flex-end', fontWeight:800,
        fontVariantNumeric:'tabular-nums', color: color || (dim ? F.muted : F.text) }}>
        <span style={{ display:'inline-flex', gap:3 }}>
          <span style={{ color:F.muted, fontWeight:700 }}>$</span><span>{cents(v)}</span>
        </span>
      </span>
    );

  // #YARD_HERO_HUBSTATS_V1 (2026-09-24) — Bill: "summary numbers on each [button] that are
  // useful... Chemicals: $XXX.XX On Hand. Fuel - Weekly $XXX.XX WK 2." Chemicals/Fuel each keep
  // their own state inside a separate component (ChemicalsYardDashboard / YardFuelWeekly below),
  // so — same "duplicate the read, no shared scope" pattern already used for Staff Calendar/
  // Celebrations — this is its own small self-fetch just for the two hub tile subtitles.
  const [chemOnHand, setChemOnHand] = useState(null);
  const [fuelSnap, setFuelSnap] = useState(null);
  const [csgnSnap, setCsgnSnap] = useState(null);   // #YARD_CONSIGN_V1 — hub tile subtitle
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const invSnap = await sg('fleet-chem-inventory');
        if (alive && Array.isArray(invSnap)) {
          const val = invSnap.filter(r => { const c = String(r.code||'').trim(); return c !== 'V CSGN' && c !== 'D CSGN'; }).reduce((s,r) => s + (Number(r.qty)||0) * (Number(r.cost)||0), 0);
          setChemOnHand(val);
        }
      } catch {}
      try {
        const cs = await sg(CONSIGN_KEY);
        if (alive && cs && cs.items) {
          const lists = Object.values(cs.items).filter(l => Array.isArray(l) && l.length);
          setCsgnSnap({ total: lists.reduce((t,l) => t + consignTotal(l), 0), oh: lists.reduce((t,l) => t + consignOnHandValue(l), 0), n: lists.length });   // #YARD_CONSIGN_V2
        }
      } catch {}
      try {
        const f = await sg('fleet-fuel-weekly');
        if (alive && f && typeof f === 'object' && !Array.isArray(f)) {
          const years = Object.keys(f).filter(k => /^\d+$/.test(k)).map(Number).sort((a,b) => b-a);
          const y = years[0];
          const wks = (y && f[y]) || {};
          const filled = Object.keys(wks).map(Number).filter(w => Number((wks[w]||{}).TOTAL) > 0).sort((a,b) => b-a);
          if (filled.length) setFuelSnap({ wk: filled[0], total: Number((wks[filled[0]]||{}).TOTAL) || 0 });
        }
      } catch {}
    })();
    return () => { alive = false; };
  }, [sg]);

  if (active === null) return (
    <div style={{ background:F.bg, minHeight:'60vh', display:'flex', alignItems:'center', justifyContent:'center', color:F.accent, fontWeight:800 }}>Loading Fleet…</div>
  );

  const empty = !active.length;

  // #FLEET_YARD_SPLIT_V1 / #FLEET_YARD_TABS_V1 (2026-08-27) — full tab set; which ones render depends
  // on role. Yard (Jerry) gets a narrower field-ops slice; Fleet/Owner (Bob) keep everything, plus the
  // new Fleet Manager rollup. Kept as one array/filter (not a second copy of the tab strip) so the two
  // roles can never drift out of sync with each other.
  const ALL_TABS = [
    ['home','Yard'],
    ['daily','Daily Inventory'],['yard','Yard Walk'],
    ['weeklyinv','Weekly Inventory'],   // #FLEET_WEEKLYHRS_V1 — Bob's replacement for the WK ## INV workbook
    ['master','Equipment Master'],['rental','Rental/RPO'],
    ['maint','Maintenance'],   // #FLEET_MAINT_V1 — service due / at Ennis / fault codes / DVIR
    ['telem','Cam / GPS'],     // #FLEET_TELEM_HOT_V1 — cameras / GPS not reporting
    ['inshop','In Shop'],['shophistory','Shop History'],['idle','Idle 90'],['flags','Review'],
    ['inspec','Inspections'],['toll','Toll Tags'],
    ['chem','Chemicals'],['consign','Consignment'],['tooling','Tooling'],['board','Digital Board'],
    ['timeclock','Time Clock'],
    ['fuel','Fuel · Weekly'],
    ['library','Fleet Library'],   // #FLEET_LIBRARY_V1
    ['fleetdash','Fleet Manager'],
    ['mydash','My Dash'],   // #FLEET_SHOPDASH_V1
    ['import','Import'],
  ];
  const YARD_VISIBLE = new Set(['home','daily','yard','flags','inspec','maint','inshop','shophistory','toll','chem','consign','tooling','board','timeclock','fuel','master','weeklyinv']);   // #FLEET_YARD_SPLIT_V2 — Bill: Jerry owns Review too; #FLEET_TIMECLOCK_V1 adds his weekly clock in/out; #FLEET_SHOPHISTORY_V1 adds his completed-shop-visit log; #ASSETLEDGER_V1 (2026-09-16) — 'master' added so Jerry can reach Equipment Master to use the new add-only Flag button (view-only otherwise, same as before); #FLEET_WEEKLYHRS_V1 (2026-09-17) — 'weeklyinv' added so whoever actually walks the yard reading hour meters (Jerry, same as the physical yard check) can key readings in too, not just Bob
  const tabDefs = ALL_TABS.filter(([k]) => {
    if (k === 'home') return yardScoped;          // Bob/Owner land on Daily as always — the hub is Jerry's front door
    if (k === 'mydash') return false;             // #FLEET_SHOPDASH_V1 — reached from the gold "← My Dash" button / the Shop Staff strip
    if (isShopStaff) return SHOP_VIEW_SET.has(k); // #FLEET_SHOPDASH_SCOPE_V1 — shop staff: only the screens their dash links to (#MECH_DASH_TIDY_V1: no Shop History)
    if (k === 'fleetdash') return masterEdit && !restrictToYard;  // Bob's own rollup — lives on the Fleet side only per Bill: "these should live on the Fleet Dash" — hidden on the Yard portal even for Owner/Fleet checking on him
    if (yardScoped) return YARD_VISIBLE.has(k);
    return true;
  }).map(t => (isShopStaff && t[0] === 'maint') ? ['maint', 'MTX', ...t.slice(2)] : t);   // #MECH_DASH_TIDY_V1 — staff see MTX

  // #FLEET_YARD_PORTAL_V1 / #FLEET_TEAM_ROW_V1 — computed here (component level) so the top-row YARD pill can carry the count. What the YARD button and its drawer show (red number = needs attention)
  const tollDue = (toll || []).filter(x => { const st = String(x.status||'').trim(); return st && st !== 'Active'; }).length;
  const walkOpen = (walkLog || []).filter(x => !x.resolved).length;
  const yardItems = [
    // [view, label, sub, alert count (needs attention), alert text]
    ['yard', 'Yard Walk', `${walkedToday.length}/${active.length} tagged today`, walkOpen, `${walkOpen} item${walkOpen===1?'':'s'} to resolve`],
    ['daily', 'Daily Inventory', `${dayFilled}/${active.length} allocated`, unassigned, `${unassigned} not allocated today`],
    ['inspec', 'Inspections', inspDueThisMonth.length ? 'due this month' : 'up to date', inspDueThisMonth.length, `${inspDueThisMonth.length} due this month`],
    ['toll', 'Toll Tags', `${(toll||[]).length} tags`, tollDue, `${tollDue} not active`],
    ['consign', 'Consignment', csgnSnap ? `${money(csgnSnap.oh || 0)} on hand` : 'Vermeer · DW · Filters & Tires · MSC', openReqN, `${openReqN} parts request${openReqN===1?'':'s'} waiting`],
    ['flags', 'Review', 'data issues', badCount, `${badCount} to review`],
    ['chem', 'Chemicals', chemOnHand==null ? 'inventory' : `${money(chemOnHand)} on hand`, 0, ''],
    ['tooling', 'Tooling', 'inventory', 0, ''],
    ['fuel', 'Fuel · Weekly', fuelSnap ? `${money(fuelSnap.total)} · WK${fuelSnap.wk}` : 'gallons & $ by week', 0, ''],
    ['shophistory', 'Shop History', `${shopHistory.length} record${shopHistory.length===1?'':'s'}`, 0, ''],
    ['board', 'Digital Board', 'place units', 0, ''],
    ['timeclock', 'Time Clock', 'clock in / out', 0, ''],
    ['import', 'Import', 'load files', 0, ''],
  ].filter(([k]) => ALL_TABS.some(([tk]) => tk === k));
  const yardAlerts = yardItems.reduce((t, x) => t + (x[0] === 'daily' ? 0 : (Number(x[3]) || 0)), 0);   // allocation is a daily to-do (amber), not an alert — left out of the red total
  // #FLEET_BOB_TOP_V1 (2026-10-16) — Bob's shortcut pills, built once: top header on desktop, under the Welcome banner on phones.
  const renderFleetPills = () => {
          const idleN = (shopSum && shopSum.idleCount) || 0;
          const P = [
            { k:'weeklyinv', label:'WKLY INV' },   // #FLEET_DASH_PILLS_SHORT_V1 (2026-10-12) — Bill: shorter labels on the top row
            ...(poPanel ? [{ k:'__po', label:'PO Log' }] : []),
            // #FLEET_MAINT_NAV_V1 (2026-10-05) — the Maintenance tab shipped with no way in from Bob's dash
            // (this row and the row-2 pills are hand-listed; the tab strip is hidden here). Count = overdue.
            { k:'maint', label:'MTX', n: svcBoard.filter(x => x.state === 'overdue').length, hot: svcBoard.some(x => x.state === 'overdue') },
            { k:'inshop', label: shopRows.some(x => x.hot) ? `In Shop 🔥${shopRows.filter(x => x.hot).length}` : 'In Shop', n: shopRows.length, hot: shopRows.length > 0 },   // #FLEET_SHOP_HOT_V1
            { k:'idle', label:'Idle', n: idleN },
            { k:'rental', label:'Rental/RPO', n: rentalRecords.length },
            { k:'master', label:'EQUIP MASTER', n: active.length, gold:true },
            // #FLEET_TEAM_ROW_V1 (2026-10-14) — Bill: "Yard tile to be on top between Equip Master & Asset Sum." Red count = needs attention.
            { k:'__yard', label:'YARD', n: yardAlerts, hot: yardAlerts > 0, red:true, tip:'Yard Walk, Daily Inventory, Inspections, Toll Tags, Consignment, Chemicals, Tooling, Fuel, Shop History, Digital Board, Time Clock, Review, Import' },
            { k:'__print', label:'🖨 ASSET SUM', print:true, tip:'Print HRS/COST · 11x17' },
          ];
          const go = (k) => {
            if (k === '__po') { const el = document.getElementById('fleet-dash-po'); if (el) el.scrollIntoView({ behavior:'smooth', block:'start' }); return; }
            if (k === '__print') { printHrsSummary(); return; }
            if (k === '__yard') { setYardOpen(o => !o); return; }
            setView(k);
          };
          return (
            <div style={{ display:'flex', flexWrap:'wrap', gap: phone ? 5 : 7, alignItems:'center' }}>
              {P.map(p => {
                const bg = p.gold ? F.gold : p.print ? 'transparent' : '#22252A';
                const bd = p.gold ? F.gold : p.print ? '#FF7A1A' : '#3A3D42';
                const fg = p.gold ? '#1A1300' : p.print ? '#FFB020' : '#EDEEF0';
                return (
                  <button key={p.k} onClick={()=>go(p.k)} title={p.tip || undefined} style={{ cursor:'pointer', background:bg, border:`1px solid ${bd}`,
                    borderRadius:20, padding: phone ? '5px 10px' : '6px 13px', color:fg, fontSize: phone ? 10.5 : 11, fontWeight:900, textTransform:'uppercase',
                    letterSpacing:.3, whiteSpace:'nowrap', display:'inline-flex', alignItems:'center', gap:6,
                    ...(p.gold ? { boxShadow:`0 0 10px ${F.gold}55` } : {}) }}>
                    {p.label}
                    {p.n != null && (
                      <span style={{ fontSize:10.5, fontWeight:900, borderRadius:10, padding:'1px 7px',
                        background: p.gold ? '#1A130022' : p.hot ? (p.red ? '#F87171' : '#FF7A1A') : '#3A3D42',
                        color: p.gold ? '#1A1300' : p.hot ? (p.red ? '#fff' : '#1A1300') : '#EDEEF0' }}>{p.n}</span>
                    )}
                  </button>
                );
              })}
            </div>
          );
  };
  return (
    <div className="dope-shell" style={{ background: yardScoped ? STEEL_BG : F.bg, minHeight:'100vh', color:F.text, fontFamily:'Calibri, Arial, sans-serif', borderRadius:14, padding:'6px 16px 14px' }}>
      {/* #FLEET_MASTER_UNITLIST_V1 (2026-08-27) — Bill: new Inspection/Toll records should be
          drop-down-picked off the Master, not free-typed, so they can't drift onto a unit that
          doesn't exist. Rendered once, referenced by list= on both forms. */}
      <datalist id="fleet-master-unit-list">
        {(active||[]).map(r => <option key={r.unit} value={r.unit} />)}
      </datalist>

      {/* #FLEET_BRAND_V2 — Bill: Fleet trades as DAKOTA DIRECTIONAL DRILLING, a different company
          (and logo) from Dakota Utility Contractors. v1 gave the wordmark its own full-width band and
          Bill was right that it wasted the space — the brand and the module header are now ONE row:
          real logo left, live counts in the middle, view buttons right. The typographic wordmark is
          kept only as the fallback if the image is missing, so the row never collapses. */}
      <div style={{ display:'flex', alignItems:'center', gap: phone ? 7 : 14, marginBottom: phone ? 8 : 12, paddingBottom: phone ? 8 : 10,
        borderBottom:`2px solid ${F.gold}44`, flexWrap:'wrap', position:'relative',
        ...(yardScoped ? { paddingTop:8 } : {}) }}>
        {yardScoped && <RivetCorners header />}
        {/* #FLEET_LOGO_BW_V1 (2026-10-02) — Bill: Fleet's logo is BLACK on a white background or WHITE on a
            black one, anywhere in Fleet. Screen (dark) = /ddd-logo-white.png; every print / email = /ddd-logo-black.png. */}
        <img src="/ddd-logo-white.png" alt="Dakota Directional Drilling" style={{ height: phone ? 30 : 42, flexShrink:0 }}
          onError={e=>{ e.currentTarget.style.display='none';
            const t = e.currentTarget.nextElementSibling; if (t) t.style.display='block'; }} />
        <div style={{ display:'none', fontSize:17, fontWeight:900, letterSpacing:2, color:'#fff',
          fontFamily:'"Arial Narrow", Calibri, Arial, sans-serif' }}>DAKOTA DIRECTIONAL DRILLING</div>

        <div style={{ borderLeft:`1px solid ${F.line}`, paddingLeft: phone ? 8 : 14, display: (phone && view === 'fleetdash') ? 'none' : undefined }}>   {/* #FLEET_MOBILE_HDR_V1 — phone dash: count rides on the Welcome line */}
          <div style={{ fontSize: phone ? 14 : 17, fontWeight:900, letterSpacing:.4, lineHeight:1.15 }}>
            FLEET <span style={{ color:F.accent }}>· {active.length} active</span>
          </div>
          <div style={{ fontSize:10.5, color:F.muted }}>
            {retired.length > 0 && <>{retired.length} retired</>}
            {view === 'daily' && <> · {dayFilled} of {active.length} allocated {fmtMD(new Date(dayISO+'T12:00:00'))}</>}
          </div>
        </div>

        {badCount > 0 && (
          <span onClick={()=>setView('flags')} title="Data issues that need review" style={{ cursor:'pointer', fontSize:11, fontWeight:900, color:'#fff', background:F.red, borderRadius:6, padding:'3px 9px' }}>
            {badCount} to review
          </span>
        )}
        {/* #FLEET_DASH_V3_TOPPILLS (2026-09-24) — Bill: top row, in this order: Weekly Inventory,
            PO Log (new), In Shop (count), Idle (count), Rental/RPO (count), Equipment Master (count,
            GOLD), Print HRS/COST 11x17. Everything else drops to row 2 inside the dash, right-aligned
            next to "Welcome, Bob". PO Log scrolls to the PO section; Print fires the same 11x17 print
            that used to sit inside the Weekly Inventory widget (removed there so it isn't twice). */}
        {view === 'fleetdash' && !phone && renderFleetPills()}   {/* #FLEET_BOB_TOP_V1 — phone: pills render under Bob's banner instead */}
        {!(phone && view === 'home') && <div style={{ flex:1 }} />}   {/* #MECH_DASH_TIDY_V1 — staff dash: SAFETY NAT sits in the right corner */}   {/* #FLEET_MOBILE_HDR_V1 — phone: SAFETY NAT flows in with the pills */}
        {busy && <span style={{ fontSize:11.5, color:F.gold, fontWeight:700 }}>{busy}</span>}
        {/* #FLEET_DASH_SAFETYNAT_V1 (2026-10-06) — Bill: SAFETY NAT button on Bob's dash, top right of this row, to report
            from within. Opens the same quick-file overlay as the PM board; he stays on his dash after Submit. */}
        {/* #SAFETY_NAT_ALLDASH_V1 (2026-10-06) — Bill: "add SAFETY NAT button for all dashboards moving forward including
            YARD". Same button on the shop staff dash and Jerry's Yard hub. */}
        {((view === 'fleetdash' && masterEdit) || view === 'mydash' || view === 'home') && (
          <button onClick={() => requestNewSafetyReport()} title="File a Damage, Vehicle, Injury, Investigation, Audit or Near-Miss report"
            style={{ background:'#FF6A00', color:'#0A0E1A', border:'1px solid #FF6A00', borderRadius:20, padding: phone ? '5px 10px' : '6px 14px', fontSize: phone ? 10.5 : 11, fontWeight:900,
              textTransform:'uppercase', letterSpacing:.3, cursor:'pointer', whiteSpace:'nowrap', boxShadow:'0 0 20px -5px #FF6A00' }}>
            + SAFETY NAT<sup style={{ fontSize:8, fontWeight:900, verticalAlign:'super', marginLeft:1 }}>®</sup>
          </button>
        )}
        {/* #FLEET_DASH_BUTTONS_NOTBAR_V1 (2026-08-27) — Bill: Jerry's Yard dash and Bob's Fleet
            Manager dash should each be "his own dash with Buttons (Not a boxed bar)" — this bordered
            tab strip duplicated the button-grid launcher already shown on those two landing views, so
            it's hidden there; every other screen keeps it for lateral navigation between sections. */}
        {view !== 'home' && view !== 'fleetdash' && view !== 'mydash' && (
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, alignItems:'center' }}>
          {/* #FLEET_SHOPDASH_V1 — same gold "go back" button Jerry has, for the shop staff. */}
          {isShopStaff && (
            <button onClick={()=>{ setEdit(null); setRentalEdit(null); setView('mydash'); }} style={{
              background:F.gold, color:'#1A1300', border:`1px solid ${F.gold}`, borderRadius:20,
              padding:'7px 16px', fontSize:12.5, fontWeight:900, cursor:'pointer', ...glow(F.gold) }}>
              ← My Dash
            </button>
          )}
          {/* #FLEET_BACKHOME_V1 (2026-09-01) — Bill: "a way for him to go back to his Dashboard once
              in a path" — the 'home'/"Yard" pill used to sit inline with every other tab, easy to miss
              in a row of otherwise-identical buttons. Pulled out as its own gold, glowing button so it
              reads as "go back" rather than "go to yet another section." */}
          {/* #FLEET_BACKDASH_V1 (2026-10-08) — Bill: "when choosing a tile, no way to go back to dashboard. Currently I
              have to choose Fleet again from top ribbon." Jerry and the shop staff had a gold back button; Bob/Owner didn't. */}
          {!yardScoped && !isShopStaff && tabDefs.some(([k]) => k === 'fleetdash') && (
            <button onClick={()=>{ setEdit(null); setRentalEdit(null); setView('fleetdash'); }} style={{
              background:F.gold, color:'#1A1300', border:`1px solid ${F.gold}`, borderRadius:20,
              padding:'7px 16px', fontSize:12.5, fontWeight:900, cursor:'pointer', ...glow(F.gold) }}>
              ← Fleet Dash
            </button>
          )}
          {yardScoped && (
            <button onClick={()=>{ setEdit(null); setRentalEdit(null); setView('home'); }} style={{
              background:F.gold, color:'#1A1300', border:`1px solid ${F.gold}`, borderRadius:20,
              padding:'7px 16px', fontSize:12.5, fontWeight:900, cursor:'pointer', ...glow(F.gold) }}>
              ← Dashboard
            </button>
          )}
          {/* #FLEET_PHONE_NAV_V1 (2026-10-22) — Bill: on a phone the wall of section buttons sat between you and the data. Inside a
              screen, phone shows only the gold back button + this screen's name; the dash (Fleet Dash / Yard / My Dash) is the menu.
              Anyone with no dash to go back to keeps the buttons so they're never stranded. Desktop unchanged. */}
          {phone && (isShopStaff || yardScoped || tabDefs.some(([k]) => k === 'fleetdash')) && (() => { const t = ALL_TABS.find(([k]) => k === view);
            return t ? <span style={{ fontSize:14, fontWeight:900, color:F.accent, letterSpacing:.3 }}>{(isShopStaff && t[0] === 'maint') ? 'MTX' : t[1]}</span> : null; })()}
          {!(phone && (isShopStaff || yardScoped || tabDefs.some(([k]) => k === 'fleetdash'))) && tabDefs.filter(([k]) => k !== 'home').map(([k,l]) => (
            <button key={k} onClick={()=>{ if (k !== 'master') setEdit(null); if (k !== 'rental') setRentalEdit(null); setView(k); }} style={{
              background: view===k ? F.accent : F.cardHi, color: view===k ? '#04121C' : F.text,
              border: `1px solid ${view===k ? F.accent : F.line}`, borderRadius:20,
              padding:'7px 16px', fontSize:12.5, fontWeight:800, cursor:'pointer',
              ...(view===k ? glow(F.accent) : {}) }}>{l}
              {/* #PM_EQFLAG_V1 — badge so Fleet notices a PM flag without opening the tab */}
              {k==='flags' && pmFlags.filter(f=>f.status==='open').length > 0 &&
                <span style={{ marginLeft:5, background:F.red, color:'#fff', borderRadius:8, padding:'0 5px', fontSize:10 }}>{pmFlags.filter(f=>f.status==='open').length}</span>}
              {/* #FLEET_INSHOP_V1 — badge so the down-equipment count is visible without opening the tab */}
              {k==='inshop' && shopRows.length > 0 &&
                <span style={{ marginLeft:5, background:F.amber, color:'#04121C', borderRadius:8, padding:'0 5px', fontSize:10 }}>{shopRows.length}</span>}
              {/* #FLEET_INSPEC_V1 — badge counts inspections that are overdue right now (not "due soon") */}
              {k==='inspec' && inspRows.filter(r=>r.st.color===F.red).length > 0 &&
                <span style={{ marginLeft:5, background:F.red, color:'#fff', borderRadius:8, padding:'0 5px', fontSize:10 }}>{inspRows.filter(r=>r.st.color===F.red).length}</span>}
            </button>
          ))}
        </div>
        )}
      </div>

      {empty && view !== 'import' && (
        <div style={{ background:F.card, border:`1px solid ${F.gold}55`, borderRadius:12, padding:'16px 18px', marginBottom:14 }}>
          <div style={{ fontWeight:900, color:F.gold, marginBottom:4 }}>No equipment loaded yet</div>
          <div style={{ fontSize:12.5, color:F.muted }}>Open <b style={{ color:F.text }}>Import</b> and drop in the master file, or paste the EQP tab straight out of Excel.</div>
        </div>
      )}

      {/* ═══════════════ DAILY ═══════════════ */}
      {view === 'daily' && (
        <Fragment>
          {/* #FLEET_TOOLBAR_V2 — Bill: "consolidate this to one row… the space between SAT & SAVE
              could we place the rest of these buttons, and search bar? makes it clean."
              One row: week nav · Mon–Fri · filter · actions · Save. The gap between the day tabs and
              Save was dead space on every screen width, and the actions all belong to the day the
              tabs just picked — putting them anywhere else made you look in two places. */}
          <div style={{ display:'flex', gap:7, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),-7)))} title="Previous week" style={{ ...btnS, padding:'6px 10px' }}>‹</button>
            <span style={{ fontSize:12.5, fontWeight:800, minWidth:96, textAlign:'center', whiteSpace:'nowrap' }}>
              Wk {fmtMD(new Date(wkStart+'T12:00:00'))}
            </span>
            <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),7)))} title="Next week" style={{ ...btnS, padding:'6px 10px' }}>›</button>
            <button onClick={()=>{ switchWeek(iso(mondayOf(new Date()))); setDayIx(Math.min(4,(new Date().getDay()+6)%7)); }} style={{ ...btnS, color:F.accent }}>Today</button>

            <div style={{ display:'inline-flex', border:`1px solid ${F.line}`, borderRadius:9, overflow:'hidden', marginLeft:3 }}>
              {DAYS.map((d,i) => {
                const di = iso(addDays(new Date(wkStart+'T12:00:00'), i));
                const n = Object.keys(week[di]||{}).length;
                const on = i===dayIx;
                return (
                  <button key={d} onClick={()=>setDayIx(i)} title={`${di} · ${n} logged`}
                    style={{ background:on?F.accent:'transparent', color:on?'#04121C':F.text, border:'none',
                      borderLeft:i?`1px solid ${F.line}`:'none', padding:'6px 10px', fontSize:11.5, fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>
                    {d} <span style={{ opacity:.75, fontSize:9.5 }}>{fmtMD(addDays(new Date(wkStart+'T12:00:00'), i))}</span>
                    {n>0 && <span style={{ marginLeft:4, fontSize:9, background:on?'#04121C':F.line, color:on?F.accent:F.muted, borderRadius:9, padding:'0 4px' }}>{n}</span>}
                  </button>
                );
              })}
            </div>

            {/* everything that acts on the day just picked, in the space that used to be empty.
                #FLEET_SEARCH_V1 — widened + relabeled so it reads as the search bar it now is
                (unit / DUC / SUP / customer / notes), not just a unit-number filter. */}
            <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search unit, DUC #, SUP, customer…"
              title="Searches unit #, category, description, DUC #, assigned SUP, customer, PM, and notes"
              style={{ ...inp, width:210, padding:'6px 9px', fontSize:11.5, marginLeft:3 }} />
            {q && <button onClick={()=>setQ('')} title="Clear search" style={{ ...btnS, padding:'6px 9px', color:F.muted }}>✕</button>}
            {/* #FLEET_COPYPREV_NAMESONLY_V1 — Copy prev now only carries the Assigned name forward,
                one day back (never past that week's Monday). DUC still re-derives fresh from today's
                Daily Log via #FLEET_SUPAUTO_V1 below; A-IDLE/A-SHOP/A-OH are untouched and reset blank
                every day. Disabled on Monday so a bad Friday can't ride into the new week. */}
            {editable && <button onClick={copyPrev} disabled={dayIx===0}
              title={dayIx===0 ? "Off on Monday — Assigned starts blank each week." : "Carries the Assigned name forward, plus A-OH units (static, rarely change). DUC keeps re-deriving from today's Daily Log; A-IDLE/A-SHOP are untouched — Jerry re-confirms those fresh on his Yard Walk. If yesterday was a listed holiday, bridges to the last work day and carries everything."}
              style={{ ...btnS, color:F.accent, borderColor:`${F.accent}66`, opacity: dayIx===0 ? 0.45 : 1, cursor: dayIx===0 ? 'not-allowed' : 'pointer' }}>Copy prev</button>}
            <button onClick={downloadCsv} title="Download this day as a formatted CSV" style={btnS}>CSV</button>
            <button onClick={emailCsv} title={dist.length ? `Save the CSV and open a draft to: ${dist.join(', ')}` : 'Save the CSV and open a mail draft — set a distribution list to pre-fill the To: line'}
              style={{ ...btnS, color:F.gold, borderColor:`${F.gold}55` }}>✉ Email{dist.length ? ` (${dist.length})` : ''}</button>
            <button onClick={editDist} title="Who the Daily Inventory goes to" style={{ ...btnS, color:F.muted }}>Dist…</button>
            {masterEdit && <button onClick={editHolidays} title="Dates the yard is closed — Copy Prev bridges past these instead of coming up empty" style={{ ...btnS, color:F.muted }}>Holidays…</button>}

            <div style={{ flex:1 }} />
            <span style={{ fontSize:11.5, color: unassigned ? F.amber : F.green, fontWeight:800, whiteSpace:'nowrap' }}>
              {unassigned ? `${unassigned} blank` : 'all allocated'}
            </span>
            {saveErr && <span title={saveErr} style={{ fontSize:11.5, color:F.red, fontWeight:800, cursor:'default' }}>⚠ autosave failed</span>}
            {!saveErr && dirty && <span style={{ fontSize:11.5, color:F.amber, fontWeight:800 }}>saving…</span>}
            {!saveErr && !dirty && savedAt && <span style={{ fontSize:11.5, color:F.green, fontWeight:700 }}>saved {savedAt}</span>}
            {editable && <button onClick={saveWeek} disabled={!dirty && !saveErr} title="Autosave already writes every change through ~1.2s after you stop typing — this forces it right now" style={{ ...btnS, padding:'6px 16px', background: (dirty||saveErr)?F.green:F.cardHi, color: (dirty||saveErr)?'#04121C':F.muted, cursor: (dirty||saveErr)?'pointer':'default' }}>Save now</button>}
          </div>

          {/* #FLEET_SPLIT_V1 — the five buckets, in Bill's order: Jobs, Idle, OH, Shop, then the
              day total made of all four. Fleet cost is fixed whether a machine turns or not; the
              only question this board answers is how much of it landed on a job. */}
          <div style={{ display:'flex', gap:10, marginBottom:10, flexWrap:'wrap' }}>
            {[['1 · Jobs', split.b.job, split.n.job, F.green, 'job'],
              ['2 · Idle', split.b['A-IDLE'], split.n['A-IDLE'], F.muted, 'A-IDLE'],
              ['3 · Overhead', split.b['A-OH'], split.n['A-OH'], F.violet, 'A-OH'],
              ['4 · Shop', split.b['A-SHOP'], split.n['A-SHOP'], F.orange, 'A-SHOP']].map(([lbl,amt,cnt,clr,bkey]) => (
              <div key={lbl} onClick={() => setBucket(b => b === bkey ? null : bkey)}
                title={`Show only the ${cnt} unit(s) in ${lbl.replace(/^\d+ · /,'')}`}
                style={{ flex:'1 1 165px', background:F.card, border:`1px solid ${bucket===bkey?clr:F.line}`,
                borderLeft:`3px solid ${clr}`, borderRadius:10, padding:'9px 13px', cursor:'pointer',
                boxShadow: bucket===bkey ? `0 0 18px -6px ${clr}` : 'none' }}>
                <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>{lbl}</div>
                <div style={{ display:'flex', alignItems:'baseline', gap:8, marginTop:2 }}>
                  <span style={{ fontSize:17, fontWeight:900, color:clr, fontVariantNumeric:'tabular-nums' }}>{money2(amt)}</span>
                  <span style={{ fontSize:12, fontWeight:900, color:F.text }}>{split.pct(amt)}%</span>
                </div>
                <div style={{ fontSize:10.5, color:F.muted }}>{cnt} unit{cnt===1?'':'s'}</div>
              </div>
            ))}
            <div style={{ flex:'1 1 175px', background:F.cardHi, border:`1px solid ${F.gold}66`,
              borderLeft:`3px solid ${F.gold}`, borderRadius:10, padding:'9px 13px' }}>
              <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.gold }}>5 · Daily total</div>
              <div style={{ fontSize:20, fontWeight:900, color:F.gold, marginTop:1, fontVariantNumeric:'tabular-nums' }}>{money2(split.allocated)}</div>
              <div style={{ fontSize:10.5, color: split.n.blank ? F.amber : F.muted }}>
                {split.n.blank ? `${split.n.blank} unit${split.n.blank===1?'':'s'} not allocated · ${money2(split.b.blank)} unaccounted` : 'every unit accounted for'}
              </div>
            </div>
          </div>

          {/* fleet-duc-list / fleet-sup-list native datalists removed — #FLEET_AUTOCOMPLETE_V1
              replaced both inputs with AutoCompleteInput (ducOptions/supOptions above), which also
              works on mobile. City stays a native datalist below — not flagged as a mobile problem. */}
          {/* #FLEET_OHCITY_V1 — Bill: A-IDLE/A-SHOP need a City too, so idle/shop equipment still shows
              a location (nothing to derive it from — there's no job). Suggestions from today's real job
              cities; free text also saves. */}
          <datalist id="fleet-city-list">
            {[...new Set(liveJobs.map(j => j.city).filter(Boolean))].sort().map(c => <option key={c} value={c} />)}
          </datalist>

          <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'calc(100vh - 356px)' }}>
            <RivetCorners tone="steel" kind="bolt" />
            <table style={{ width:'100%', borderCollapse:'collapse', tableLayout:'fixed' }}>
              {/* #FLEET_COLW_V1 — widths are Bill's: Assigned narrow and centred (longest real name is
                  NORBERTO), DUC narrow, Customer wide, City added. tableLayout:'fixed' is what makes
                  a colgroup binding — without it the browser re-flows off content and ignores it. */}
              {/* #FLEET_COLW_V2 — Bill: "pull this in and leave the last part for notes to tighten
                  this up." Every data column now has a real width so they sit shoulder to shoulder;
                  Notes takes the slack at the right, which is the only column that genuinely wants
                  whatever room is left over. */}
              {/* #FLEET_COLW_V3 — Bill: "make the DUC number box larger to see the entire custom
                  number and ability to edit." A real DUC is 8 chars but an unresolved `custom-<ts>`
                  placeholder (see #FLEET_ALIAS_V1 above) can run to 20 — 128px was clipping those
                  mid-edit. Pulled the extra mostly from Description/Customer so the table doesn't
                  grow much wider overall.
                  #FLEET_STATUS_V1 — Bill: "Override crashing into number... reserve a space next to
                  Job Number for check mark, X or override note or whatever to appear there." The
                  OVERRIDE button, the bad/warn/lock messages, the SUP flag and the old far-right
                  "Daily" cross-check mark ALL move into one Status column right after DUC # — the DUC
                  box itself goes back to being just the value, full width, nothing crowding it. */}
              <colgroup>
                <col style={{ width:74 }} />{/* Unit */}
                <col style={{ width:26 }} />{/* Rental/RPO flag — #FLEET_RENTAL_V1 */}
                <col style={{ width:94 }} />{/* Assigned — NORBERTO is the longest real name */}
                <col style={{ width:82 }} />{/* Cost — $ pinned left, figure right */}
                <col style={{ width:160 }} />{/* Description */}
                <col style={{ width:176 }} />{/* DUC # — #FLEET_COLW_V3 */}
                <col style={{ width:132 }} />{/* Status — #FLEET_STATUS_V1 */}
                <col style={{ width:150 }} />{/* Customer */}
                <col style={{ width:108 }} />{/* City */}
                <col style={{ width:58 }} />{/* PM */}
                <col style={{ width:96 }} />{/* Job Status — #FLEET_JOBSTAT_V1 */}
                <col style={{ width:88 }} />{/* Completed — #FLEET_JOBSTAT_V1 */}
                <col />{/* Notes — takes the slack */}
                <col style={{ width:128 }} />{/* GPS — #FLEET_LOCATIONS_V1, far right so Notes keeps its room */}
                <col style={{ width:48 }} />{/* row actions */}
              </colgroup>
              {/* column order mirrors Bill's Daily sheet so the yard doesn't have to relearn it */}
              <thead><tr>
                {[['unit','Unit','center'], ['rentalRpo','R','center'], ['assigned','Assigned','center'], ['cost','Cost','right'],
                  ['desc','Description','left'], ['duc','DUC #','left'], ['status','Status','center'],
                  ['cust','Customer','left'], ['city','City','left'], ['pm','PM','left'],
                  ['jstat','Job Status','left'], ['jcomp','Completed','left'],
                  ['note','Notes','left'], ['gps','GPS','left']].map(([key,label,align]) => (
                  <th key={key} onClick={()=>setSort(s => s.key===key ? (s.dir==='asc' ? {key,dir:'desc'} : {key:'',dir:'asc'}) : {key,dir:'asc'})}
                    title={sort.key===key ? 'Click again to reverse, once more for sheet order' : `Sort by ${label}`}
                    style={{ ...th, textAlign:align, cursor:'pointer', userSelect:'none',
                      color: sort.key===key ? F.accent : F.muted }}>
                    {label}<span style={{ marginLeft:4, fontSize:8.5, opacity: sort.key===key ? 1 : .35 }}>
                      {sort.key===key ? (sort.dir==='asc' ? '▲' : '▼') : '↕'}</span>
                  </th>
                ))}
                <th style={{ ...th, textAlign:'center' }}>&nbsp;</th>
              </tr></thead>
              <tbody>
                {displayRows.map(x => {
                  const r = x.r, k = x.k, cur = x.cur, duc = x.duc, g = x.g;
                  const legacy = unitLegacy(r.unit);
                  const authed = !!cur.ovr;
                  const needsOvr = (g.state==='warn' || g.state==='lock') && !authed;
                  const bc = g.state==='ok' ? (g.kind==='bucket' ? F.violet : F.green)
                           : g.state==='warn' ? (authed ? F.green : F.amber)
                           : g.state==='lock' ? (authed ? F.green : F.red)
                           : g.state==='bad'  ? F.red : F.line;
                  const pool = new Set(Object.keys(jobIndex).map(d => d.replace(/[^0-9A-Z]/g,'')));
                  const sugg = g.state==='bad' ? transposeCandidates(duc, pool) : [];
                  const job = g.job;
                  // #FLEET_SUPAUTO_V1 — same test as the flag message in the DUC cell; shared here so
                  // the Assigned box itself carries the flag color too (the message is easy to miss
                  // scrolling a list of 296, the red border on the name they just typed is not).
                  const supFlagged = !duc && cur.ducAuto !== false && x.supChk && (x.supChk.verdict==='conflict' || x.supChk.verdict==='missing');
                  // #PM_EQFLAG_DAYLIGHT_V1 — PM flagged this exact unit on this exact day; clicking jumps to Review.
                  const pmFlagsHere = openFlagsByUnit.get(k);
                  return (
                    <tr key={k} style={{ borderTop:`1px solid ${F.line}`,
                      background: !duc ? 'rgba(255,255,255,.03)' : needsOvr ? `${F.amber}12` : 'transparent' }}>
                      <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap', textAlign:'center' }} title={legacy ? 'Legacy unit number — cost history is tied to it' : r.unit}>
                        {r.unit}{legacy && <span style={{ marginLeft:4, fontSize:8.5, fontWeight:900, color:F.muted, border:`1px solid ${F.line}`, borderRadius:8, padding:'0 4px' }}>OLD</span>}
                         <TelemFlag f={telemFlagFor(tflags, r.unit)} style={{ display:'flex', justifyContent:'center', flexWrap:'wrap', marginTop:2 }} />
                        {pmFlagsHere && pmFlagsHere.length > 0 && (
                          <span onClick={()=>setView('flags')}
                            title={`PM flag — ${pmFlagsHere.map(f=>`${f.pmName||f.pm}: "${f.note||'flagged'}"`).join(' · ')} — click to open Review`}
                            style={{ marginLeft:4, cursor:'pointer', fontSize:12 }}>⚠️</span>
                        )}
                      </td>
                      {/* #FLEET_RENTAL_V2 — small red circled "R" so Rental/RPO units are visible and sortable */}
                      <td style={{ ...td, textAlign:'center', padding:'4px 2px' }}>
                        {x.rentalRpo && <span title={`Rental/RPO — ${r.rentalType||'Rental/RPO'}`}
                          style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:16, height:16,
                            borderRadius:'50%', border:`1.5px solid ${F.red}`, color:F.red, fontSize:9.5, fontWeight:900 }}>R</span>}
                      </td>
                      <td style={{ ...td, padding:'4px 4px' }}>
                        <AutoCompleteInput options={supOptions} value={cur.assigned||''} disabled={!editable}
                          onChange={v=>setCell(r.unit, { assigned: v.toUpperCase() })}
                          onFocus={()=>setEditingKey(k)} onBlur={()=>setEditingKey(p=>p===k?null:p)}
                          title={supFlagged ? (x.supChk.verdict==='conflict' ? `${cur.assigned} is on ${x.supChk.ducs.length} Dailies today — pick a job` : `No Daily filed for ${cur.assigned}`) : undefined}
                          placeholder="—" style={{ ...inp, textAlign:'center', fontWeight:700, padding:'6px 2px', fontSize:12,
                            border: supFlagged ? `1.5px solid ${F.red}` : inp.border }} />
                      </td>
                      {/* $ pinned left, figure right — the column reads as one straight edge on both sides */}
                      <td style={{ ...td, whiteSpace:'nowrap', color: x.cost?F.text:F.muted, paddingRight:12 }}>
                        {x.cost ? (
                          <span style={{ display:'flex', justifyContent:'flex-end', fontWeight:800, fontVariantNumeric:'tabular-nums' }}>{/* #FLEET_MONEYALIGN_V1 */}
                            <span style={{ display:'inline-flex', gap:3 }}>
                              <span style={{ color:F.muted }}>$</span><span>{cents(x.cost)}</span>
                            </span>
                          </span>
                        ) : <span style={{ display:'block', textAlign:'right' }}>—</span>}
                      </td>
                      <td style={{ ...td, fontSize:11.5, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={`${r.desc||''}${r.cat?` · ${r.cat}`:''}`}>{r.desc}</td>
                      <td style={{ ...td, padding:'4px 5px' }}>
                        <AutoCompleteInput options={ducOptions} value={cur.duc||''} disabled={!editable}
                          onChange={v=>setCell(r.unit, { duc: v.toUpperCase(), ovr: undefined, ducAuto:false })}
                          onFocus={()=>setEditingKey(k)} onBlur={()=>setEditingKey(p=>p===k?null:p)}
                          placeholder="DUC / A-IDLE"
                          style={{ ...inp, border:`1.5px solid ${bc}`, fontWeight:700, padding:'6px 6px', width:'100%', textAlign:'center' }} />
                      </td>
                      {/* #FLEET_STATUS_V1 — Bill: "Override crashing into number... reserve a space
                          next to Job Number for check mark, X or override note or whatever to appear
                          there." One column, one thing at a time — a filled DUC is either bad, needs
                          an override, is overridden, or clean (the Daily Log cross-check mark takes
                          that slot); an EMPTY DUC either carries the SUP flag or shows nothing. These
                          never overlap: every gate state and the cross-check require a filled DUC, the
                          SUP flag only ever shows when the box is blank (#FLEET_SUPAUTO_V1). */}
                      <td style={{ ...td, textAlign:'center', padding:'4px 4px', fontSize:9.5 }}>
                        {g.state==='bad' ? (
                          <div style={{ color:F.red, fontWeight:800 }}>
                            <div>✗ {g.msg}</div>
                            {sugg.length > 0 && <div>did you mean <b style={{ color:F.gold, cursor:'pointer' }} onClick={()=>setCell(r.unit,{duc:keyToDisplay(sugg[0]), ovr:undefined})}>{keyToDisplay(sugg[0])}</b>?</div>}
                          </div>
                        ) : needsOvr ? (
                          editable ? (
                            <button onClick={()=>authorise(r.unit, duc)}
                              title={g.state==='lock' ? `${g.msg} — over a week past completion, Fleet / Owner only` : `${g.msg} — needs an override`}
                              style={{ background:'transparent', border:`1px solid ${g.state==='lock'?F.red:F.amber}`,
                                color: g.state==='lock'?F.red:F.amber, borderRadius:6, padding:'4px 7px',
                                fontSize:9.5, fontWeight:900, cursor:'pointer', whiteSpace:'nowrap' }}>OVERRIDE</button>
                          ) : <span style={{ color:g.state==='lock'?F.red:F.amber, fontWeight:800 }}>{g.msg}</span>
                        ) : authed ? (
                          <span title={`Overridden by ${cur.ovr.by||'—'}`} style={{ fontWeight:900, color:F.green, border:`1px solid ${F.green}66`, borderRadius:9, padding:'1px 6px', whiteSpace:'nowrap' }}>✓ OVR</span>
                        ) : x.chk ? (
                          <span title={
                            x.chk.level === 'ok'   ? `Confirmed — ${duc} has a Daily Log entry for ${dayISO}${x.chk.sups && x.chk.sups.length ? ` (reported by ${x.chk.sups.join(', ')})` : ''}.`
                            : x.chk.level === 'none' ? `No Daily Log filed for ${duc} on ${dayISO}. The job is real — last Daily entry was ${x.chk.last}. Usually a late report rather than a wrong DUC.`
                            : `${duc} has NO Daily Log activity at any time. Check the job number — this is the drift signal.`}
                            style={{ fontSize:14, fontWeight:900, cursor:'help',
                              color: x.chk.level === 'ok' ? F.green : x.chk.level === 'none' ? F.amber : F.red }}>
                            {x.chk.level === 'ok' ? '\u2713' : x.chk.level === 'none' ? '?' : '\u2717'}
                          </span>
                        ) : supFlagged ? (
                          <div style={{ color:F.red, fontWeight:800, display:'flex', flexDirection:'column', alignItems:'center', gap:1 }}>
                            <span style={{ fontSize:13 }}>🚩</span>
                            <span>{x.supChk.verdict==='conflict'
                              ? `${cur.assigned} on ${x.supChk.ducs.length} Dailies — pick one`
                              : `No Daily for ${cur.assigned}`}</span>
                          </div>
                        ) : <span style={{ color:F.line }}>–</span>}
                      </td>
                      <td style={{ ...td, color: job?F.text:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                          title={job ? job.cust : ''}>
                        {OVERHEAD_SET.has(duc) ? <span style={{ color:F.violet, fontWeight:700 }}>{(OVERHEAD.find(o=>o[0]===duc)||[])[1]}</span> : (job ? job.cust || '—' : '')}
                      </td>
                      <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', padding: (duc==='A-IDLE'||duc==='A-SHOP') ? '4px 5px' : td.padding }} title={job ? job.city : ''}>
                        {(duc==='A-IDLE'||duc==='A-SHOP') ? (
                          <input list="fleet-city-list" value={cur.city||''} disabled={!editable}
                            onChange={e=>setCell(r.unit, { city: e.target.value })}
                            placeholder="City" style={{ ...inp, padding:'5px 6px', width:'100%' }} />
                        ) : (job ? job.city || '' : '')}
                      </td>
                      <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{job ? job.pm || '' : ''}</td>
                      {/* #FLEET_JOBSTAT_V1 — Bill: "add STATUS. If completed Date Completed to support
                          Override… typically all IN PROGRESS but another layer to double check." Lands
                          right after PM. Complete shows gold (it's the state that needs the OVR to its
                          left explained) — everything else (Called In / In Progress) reads as routine. */}
                      <td style={{ ...td, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                          color: job && String(job.stat||'').toLowerCase()==='complete' ? F.gold : F.muted,
                          fontWeight: job && String(job.stat||'').toLowerCase()==='complete' ? 800 : 400 }}>
                        {job ? job.stat || '' : ''}
                      </td>
                      <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {job && String(job.stat||'').toLowerCase()==='complete' ? (job.com || '—') : ''}
                      </td>
                      {/* Notes — free text, saved on the day's record alongside duc/assigned. This is
                          where "swapped mid-day", "waiting on parts", "hauled to Sherman" lives, and
                          it rides along into the CSV so the note reaches whoever gets the email. */}
                      <td style={{ ...td, padding:'4px 5px' }}>
                        <input value={cur.note||''} disabled={!editable}
                          onChange={e=>setCell(r.unit, { note: e.target.value })}
                          onFocus={()=>setEditingKey(k)} onBlur={()=>setEditingKey(p=>p===k?null:p)}
                          placeholder="—" style={{ ...inp, fontSize:11.5, padding:'6px 7px', minWidth:0,
                            border:`1px solid ${cur.note ? F.line : 'transparent'}`,
                            background: cur.note ? F.bg : 'transparent' }} />
                      </td>
                      {/* #FLEET_LOCATIONS_V1 (2026-10-09) — Bill: "time stamp location of unit on this … inventory sheet
                          to just list city off to the far right allowing room for notes." Where the unit's tracker last
                          reported on the day being looked at: today is live (re-read every 5 minutes; click opens the
                          spot in Google Maps), an earlier day shows where it was at the end of that day, a day still to
                          come is blank. Blank also = no tracker matched to this unit by Serial / VIN. Read-only. */}
                      <td style={{ ...td, padding:'3px 8px', lineHeight:1.2, overflow:'hidden' }}
                        title={x.gpsCell ? x.gpsCell.title : 'No GPS position for this unit on this day'}>
                        {x.gpsCell && (() => {
                          const gp = x.gpsCell;
                          const body = (
                            <Fragment>
                              <div style={{ fontSize:11.5, fontWeight:700, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                                color: gp.old ? F.muted : gp.moving ? F.green : gp.yard ? F.muted : F.text }}>{gp.moving ? '▸ ' : ''}{gp.text}</div>
                              <div style={{ fontSize:9.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                                color: gp.old ? F.amber : F.muted }}>{gp.old ? 'last heard ' : ''}{gp.when}</div>
                            </Fragment>
                          );
                          return gp.url
                            ? <a href={gp.url} target="_blank" rel="noopener noreferrer" style={{ display:'block', color:'inherit', textDecoration:'none' }}>{body}</a>
                            : body;
                        })()}
                      </td>
                      {/* #FLEET_ROWACT_V2 — Bill: "Daily should not be able to edit Master list."
                          So the jump-to-master pencil is gone. The only action on a Daily row is
                          clearing TODAY's allocation — the unit stays listed and blank, because
                          every unit must show every day. Master edits happen on the Master tab only.
                          #FLEET_ROWACT_V3 — that control was a bare × at the right edge of the row,
                          which reads as "delete this row" in every app ever built. It never deleted
                          anything, but it was alarming enough that Bill thought units could vanish
                          off the daily inventory. Identical behaviour, labelled so it can't be misread. */}
                      <td style={{ ...td, padding:'4px 6px', whiteSpace:'nowrap', textAlign:'center' }}>
                        {editable && (duc || cur.assigned) && (
                          <button onClick={()=>setCell(r.unit, { duc:'', assigned:'', ovr:undefined })}
                            title={`Clear ${r.unit}'s DUC and operator for this day only.\nThe unit stays on the daily inventory — units leave this list only by being retired on the Equipment Master.`}
                            style={{ background:'transparent', border:`1px solid ${F.line}`, color:F.muted, fontSize:9.5, fontWeight:800, letterSpacing:0.2, textTransform:'uppercase', cursor:'pointer', padding:'2px 6px', borderRadius:5, lineHeight:1.4, whiteSpace:'nowrap' }}
                            onMouseEnter={e=>{ e.currentTarget.style.borderColor=F.gold; e.currentTarget.style.color=F.gold; }}
                            onMouseLeave={e=>{ e.currentTarget.style.borderColor=F.line; e.currentTarget.style.color=F.muted; }}>Clear</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* #FLEET_WEEKSIDE_V1 — the day-by-day totals and the DUC roll-up answer the same question from
              two angles, so reading them together beats scrolling between them. Side by side above
              1200px, stacked below it (the roll-up's five columns need the room on a laptop). */}
          <div className="dope-weekside" style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(560px, 100%), 1fr))', gap:12, alignItems:'start' }}>
          {/* #FLEET_WEEKTOT_V1 — the week, day by day, in the same four buckets as the top strip */}
          <div style={{ marginTop:12, background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px' }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:13, fontWeight:900 }}>Week of {fmtMD(new Date(wkStart+'T12:00:00'))}</div>
              <div style={{ fontSize:11.5, color:F.muted }}>Mon–Fri · click a day to open it</div>
              <div style={{ flex:1 }} />
              <div style={{ fontSize:18, fontWeight:900, color:F.gold, fontVariantNumeric:'tabular-nums' }}>{money2(weekTot.tot)}</div>
            </div>
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
                <thead><tr>
                  <th style={th}>&nbsp;</th>
                  {weekTot.days.map((x,i) => (
                    <th key={x.d} onClick={()=>setDayIx(i)} style={{ ...th, textAlign:'right', cursor:'pointer',
                      color: i===dayIx ? F.accent : F.muted }}>{x.lbl} {fmtMD(new Date(x.d+'T12:00:00'))}</th>
                  ))}
                  <th style={{ ...th, textAlign:'right', color:F.gold }}>Week</th>
                </tr></thead>
                <tbody>
                  {[['Jobs','job',F.green], ['Idle','A-IDLE',F.muted], ['Overhead','A-OH',F.violet], ['Shop','A-SHOP',F.orange]].map(([lbl,key,clr]) => (
                    <tr key={key} style={{ borderTop:`1px solid ${F.line}` }}>
                      <td style={{ ...td, fontWeight:800, color:clr, whiteSpace:'nowrap' }}>{lbl}</td>
                      {weekTot.days.map(x => (
                        <td key={x.d} style={{ ...td, textAlign:'right', color: x.b[key] ? F.text : F.muted, fontVariantNumeric:'tabular-nums' }}>
                          {x.b[key] ? money2(x.b[key]) : '—'}
                        </td>
                      ))}
                      <td style={{ ...td, textAlign:'right', fontWeight:900, color:clr, fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>
                        {money2(weekTot.b[key])} <span style={{ fontSize:10.5, color:F.muted }}>{weekTot.pct(weekTot.b[key])}%</span>
                      </td>
                    </tr>
                  ))}
                  <tr style={{ borderTop:`2px solid ${F.line}` }}>
                    <td style={{ ...td, fontWeight:900, color:F.gold }}>TOTAL</td>
                    {weekTot.days.map(x => (
                      <td key={x.d} style={{ ...td, textAlign:'right', fontWeight:900, color: x.tot?F.text:F.muted, fontVariantNumeric:'tabular-nums' }}>{x.tot?money2(x.tot):'—'}</td>
                    ))}
                    <td style={{ ...td, textAlign:'right', fontWeight:900, color:F.gold, fontVariantNumeric:'tabular-nums' }}>{money2(weekTot.tot)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* weekly roll-up — the "consolidated into weekly costing table" step */}
          <div style={{ marginTop:12, background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px' }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:13, fontWeight:900 }}>Week roll-up by DUC</div>
              <div style={{ fontSize:11.5, color:F.muted }}>Mon–Fri, cost = master daily rate × days allocated</div>
              {/* #FLEET_WEEKROLLUP_WKLABEL_V1 — Bill: "add Week ending Date, WK # and have it toggle
                  as weeks go by for reference if/when needed." This panel already follows whichever
                  week is loaded (rollup is built off `week`, which only ever holds the open week) —
                  this just labels which week that is, right on the panel, with its own Prev/Next so
                  reviewing an old week doesn't mean scrolling back up to the main week nav. */}
              <div style={{ display:'inline-flex', alignItems:'center', gap:6, border:`1px solid ${F.line}`, borderRadius:8, padding:'3px 4px' }}>
                <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),-7)))} title="Previous week" style={{ ...btnS, padding:'4px 8px' }}>‹</button>
                <span style={{ fontSize:11.5, fontWeight:800, whiteSpace:'nowrap' }}>
                  WK {isoWeekNum(new Date(wkStart+'T12:00:00'))} · Week ending {fmtMD(addDays(new Date(wkStart+'T12:00:00'), 4))}
                </span>
                <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),7)))} title="Next week" style={{ ...btnS, padding:'4px 8px' }}>›</button>
              </div>
              <div style={{ flex:1 }} />
              <button onClick={refreshWeek} disabled={weekRefreshing} title="Pull in changes saved by others (Yard Walk, Daily Log, another tab) since this page opened" style={btnS}>{weekRefreshing ? 'Refreshing…' : '⟳ Refresh'}</button>
              <button onClick={downloadWeekRollupCsv} title="Download the Consolidated Weekly Report CSV — one row per DUC, ready to hand to job costing" style={btnS}>CSV</button>
              <button onClick={downloadWeekRollupSimpleCsv} title="Download Work Date / UNIT / COST — matches your weekly pivot, ready to paste into your weekly" style={btnS}>CSV (weekly)</button>
              <div style={{ fontSize:16, fontWeight:900, color:F.green }}>{money(weekTotal)}</div>
            </div>
            {!rollup.length ? <div style={{ fontSize:12.5, color:F.muted }}>Nothing allocated this week yet.</div> : (
              <div style={{ maxHeight:260, overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  {/* #FLEET_ROLLSORT_V1 — click to sort. Cost desc is the default because "where did the
                      money go this week" is the question this table exists to answer. */}
                  <thead><tr>{[['duc','DUC','left'],['cust','Customer','left'],['units','Units','right'],['days','Unit-days','right'],['cost','Cost','right']].map(([k,lab,al]) => {
                    const on = rollSort.k === k;
                    return <th key={k} onClick={() => setRollSort(v => ({ k, dir: v.k===k && v.dir==='asc' ? 'desc' : 'asc' }))}
                      style={{ ...th, textAlign:al, cursor:'pointer', userSelect:'none', color: on ? F.gold : undefined }}>
                      {lab}{on ? (rollSort.dir==='asc' ? ' \u25B2' : ' \u25BC') : ' \u21C5'}</th>;
                  })}</tr></thead>
                  <tbody>
                    {rollupSorted.map(r => {
                      const oh = OVERHEAD_SET.has(r.duc); const j = jobIndex[r.duc];
                      return (
                        <tr key={r.duc} style={{ borderTop:`1px solid ${F.line}` }}>
                          <td style={{ ...td, fontWeight:800, color: oh?F.violet:F.text }}>{r.duc}</td>
                          <td style={{ ...td, color:F.muted }}>{oh ? (OVERHEAD.find(o=>o[0]===r.duc)||[])[1] : (j ? j.cust : <span style={{ color:F.red }}>not a live job</span>)}</td>
                          <td style={{ ...td, textAlign:'right', color:F.muted }}>{r.units.size}</td>
                          <td style={{ ...td, textAlign:'right', color:F.muted }}>{r.days}</td>
                          <td style={{ ...td, textAlign:'right', fontWeight:800 }}>{money(r.cost)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* #FLEET_SUPROLL_V1 — weekly equipment by supervisor. Read-only rollup: collapsed one line
              per SUP+JOB, expands to the units that made it up and which days each was on it. */}
          <div style={{ marginTop:12, background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px' }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8, flexWrap:'wrap' }}>
              <div style={{ fontSize:13, fontWeight:900 }}>Weekly equipment by supervisor</div>
              <div style={{ fontSize:11.5, color:F.muted }}>Mon–Fri · units at peak day, cost running · click a row to expand</div>
              <div style={{ flex:1 }} />
              <button onClick={refreshWeek} disabled={weekRefreshing} title="Pull in changes saved by others (Yard Walk, Daily Log, another tab) since this page opened" style={btnS}>{weekRefreshing ? 'Refreshing…' : '⟳ Refresh'}</button>
              <div style={{ fontSize:18, fontWeight:900, color:F.gold, fontVariantNumeric:'tabular-nums' }}>{money2(supRollupTotal)}</div>
            </div>
            {!supRollup.length ? <div style={{ fontSize:12.5, color:F.muted }}>Nothing allocated this week yet.</div> : (
              <div style={{ maxHeight:340, overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    {[['','',20],['SUP','left'],['Job','left'],['City','left'],['Customer','left'],['Units','right'],['Weekly cost','right']].map(([lab,al],i) => (
                      <th key={i} style={{ ...th, textAlign:al||'left', width: i===0?20:undefined }}>{lab}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {supRollup.map(g => {
                      const key = g.sup + '|' + g.duc;
                      const open = supOpen.has(key);
                      const unitRows = Object.values(g.unitInfo).sort((a,b)=>a.unit.localeCompare(b.unit));
                      return (
                        <Fragment key={key}>
                          <tr onClick={()=>toggleSupOpen(key)} style={{ borderTop:`1px solid ${F.line}`, cursor:'pointer' }}
                            onMouseEnter={e=>e.currentTarget.style.background=F.cardHi} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <td style={{ ...td, color:F.muted, fontSize:10 }}>{open ? '▾' : '▸'}</td>
                            <td style={{ ...td, fontWeight:800 }}>{g.sup}</td>
                            <td style={{ ...td, fontWeight:700, color: g.isOverhead?F.violet:F.text }}>{g.duc}</td>
                            <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:110 }} title={g.city}>{g.city}</td>
                            <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:130 }} title={g.cust}>{g.cust}</td>
                            <td style={{ ...td, textAlign:'right', fontWeight:800 }}>{g.maxUnits}</td>
                            <td style={{ ...td, textAlign:'right' }}>
                              {/* #FLEET_SUPROLL_V1 — Bill: "MTWTF slowly light up above total... $1,000
                                  small M above, then $2000 to have small MT above." Day letters that
                                  contributed to the running total light gold; the rest stay dim. */}
                              <div style={{ display:'flex', justifyContent:'flex-end', gap:2, marginBottom:1 }}>
                                {DAYS.map((lbl,i) => (
                                  <span key={i} style={{ fontSize:8, fontWeight:900, width:9, textAlign:'center',
                                    color: g.daysLit.has(i) ? F.gold : F.line }}>{lbl[0]}</span>
                                ))}
                              </div>
                              <div style={{ fontWeight:900, fontVariantNumeric:'tabular-nums' }}>{money2(g.weeklyCost)}</div>
                            </td>
                          </tr>
                          {open && unitRows.map(u => (
                            <tr key={u.unit} style={{ borderTop:`1px solid ${F.line}`, background:'rgba(255,255,255,.02)' }}>
                              <td style={td}></td>
                              <td style={{ ...td, fontSize:11, color:F.muted }} colSpan={2}>{u.unit} <span style={{ opacity:.7 }}>{u.desc}</span></td>
                              <td style={{ ...td, fontSize:10, color:F.muted }} colSpan={2}>
                                {DAYS.map((lbl,i) => (
                                  <span key={i} style={{ marginRight:5, fontWeight: u.days.includes(i)?900:400,
                                    color: u.days.includes(i)?F.accent:F.line }}>{lbl}</span>
                                ))}
                              </td>
                              <td style={td}></td>
                              <td style={td}></td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>{/* #FLEET_WEEKSIDE_V1 — end side-by-side grid */}
        </Fragment>
      )}

      {/* ═══════════════ MASTER ═══════════════ */}
      {view === 'master' && (
        <Fragment>
          {/* #FLEET_ASSETMGMT_V1 — Bill: "Top of page needs total Value of all assets listed from
              Purchase Dollar amount entered (Subject to review and updating)." Sum of Purchase $ across
              every active unit on the master — not just whatever's currently filtered/searched, since
              this is meant to read as the fleet's total asset value at a glance. */}
          <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:10, flexWrap:'wrap' }}>
            <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>Total asset value</span>
            <span style={{ fontSize:19, fontWeight:900, color:F.green }}>{money(active.reduce((s,r)=>s+num(r.purAmt),0))}</span>
            <span style={{ fontSize:11, color:F.muted }}>across {active.length} active units · from Purchase $, subject to review</span>
            {/* #FLEET_GPSCAM_IMPORT_V1 — Bill, 2026-09-15: split into two independent progress readouts
                (was one combined "both columns" stat — Bill asked for separate GPS and Camera buckets
                since a unit can need tracking on one and not the other). Each is its own column non-blank
                / active.length — X, NW, NO GPS/NO CAM, or N/A all count as "checked"; only a genuinely
                blank cell counts as pending. */}
            {(() => {
              const total = active.length;
              const gpsDone = active.filter(r => String(r.gpsStatus||'').trim()).length;
              const camDone = active.filter(r => String(r.cameraStatus||'').trim()).length;
              const gpsPct = total ? Math.round((gpsDone/total)*100) : 0;
              const camPct = total ? Math.round((camDone/total)*100) : 0;
              const pctColor = pct => pct>=100?F.green : pct>=60?F.accent : F.amber;
              return (
                <span style={{ display:'inline-flex', alignItems:'baseline', gap:18, marginLeft:18, paddingLeft:18, borderLeft:`1px solid ${F.line}` }}>
                  <span style={{ display:'inline-flex', alignItems:'baseline', gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>GPS checked</span>
                    <span style={{ fontSize:19, fontWeight:900, color:pctColor(gpsPct) }}>{gpsPct}%</span>
                    <span style={{ fontSize:11, color:F.muted }}>{gpsDone} of {total}</span>
                  </span>
                  <span style={{ display:'inline-flex', alignItems:'baseline', gap:8 }}>
                    <span style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>Camera checked</span>
                    <span style={{ fontSize:19, fontWeight:900, color:pctColor(camPct) }}>{camPct}%</span>
                    <span style={{ fontSize:11, color:F.muted }}>{camDone} of {total}</span>
                  </span>
                </span>
              );
            })()}
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <input value={qM} onChange={e=>setQM(e.target.value)} placeholder="Search unit, VIN, description…" style={{ ...inp, width:300 }} />
            {/* #FLEET_TRACKER_IMEI_V1 — tracker lookup: last 4 of an IMEI (what's on the label) or any part of a serial */}
            {(() => {
              const q = trkQ.trim().toUpperCase().replace(/\s+/g, '');
              const bySer = {}; (active || []).forEach(r => { const sn = String(r.gpsSerial || '').trim().toUpperCase(); if (sn && !bySer[sn]) bySer[sn] = r; });
              let hits = null;
              if (q.length >= 4) {
                hits = ((trackerIds && trackerIds.trackers) || []).filter(t => String(t.imei || '').includes(q) || String(t.serial || '').toUpperCase().includes(q))
                  .map(t => ({ t, r: bySer[String(t.serial || '').toUpperCase()] || null }));
                const seen = new Set(hits.map(h => String(h.t.serial).toUpperCase()));
                Object.keys(bySer).filter(sn => sn.includes(q) && !seen.has(sn)).forEach(sn => hits.push({ t: { serial: sn, imei: '' }, r: bySer[sn] }));
                hits = hits.slice(0, 12);
              }
              const chip = (txt, c) => <span style={{ fontSize:9.5, fontWeight:800, color:c, border:`1px solid ${c}66`, borderRadius:4, padding:'0 5px', marginLeft:5, whiteSpace:'nowrap' }}>{txt}</span>;
              const gc = (g) => g === 'X' ? F.green : g === 'NW' ? F.amber : g === 'NO GPS' ? F.red : F.muted;
              return (
                <div style={{ position:'relative' }}>
                  <input value={trkQ} onChange={e => setTrkQ(e.target.value)} placeholder="Tracker lookup — IMEI last 4 or S/N"
                    title="Type the last 4 of the IMEI on a tracker's label (or part of its serial) to see which unit it belongs to"
                    style={{ ...inp, width:240, ...(trkQ ? { borderColor:F.accent } : {}) }} />
                  {hits && (
                    <div style={{ position:'absolute', zIndex:40, top:'100%', left:0, marginTop:4, width:520, background:F.card, border:`1px solid ${F.line}`, borderRadius:8, boxShadow:'0 12px 32px rgba(0,0,0,.45)', padding:6, maxHeight:360, overflowY:'auto' }}>
                      {!hits.length && <div style={{ fontSize:12, color:F.muted, padding:6 }}>No tracker matches “{trkQ}” — not on impac's list or the Master.</div>}
                      {hits.map(({ t, r }) => {
                        const im = String(t.imei || '');
                        return (
                          <div key={t.serial} onClick={() => { if (r) { setQM(r.unit); setTrkQ(''); } }}
                            style={{ padding:'6px 8px', borderBottom:`1px solid ${F.line}`, cursor: r ? 'pointer' : 'default', fontSize:12 }}>
                            <div style={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:2 }}>
                              <b style={{ fontFamily:'Consolas,monospace' }}>{t.serial}</b>
                              {im && <span style={{ marginLeft:8, color:F.muted, fontFamily:'Consolas,monospace' }}>IMEI {im.slice(0, -4)}<b style={{ color:F.text }}>{im.slice(-4)}</b></span>}
                              {t.badBox === true && chip('IN BAD BOX', F.red)}
                            </div>
                            <div style={{ marginTop:2, color:F.muted }}>
                              {r ? <span>On Master: <b style={{ color:F.text }}>{r.unit}</b> {r.desc ? `· ${r.desc}` : ''} · GPS <b style={{ color:gc(r.gpsStatus) }}>{r.gpsStatus || 'not checked'}</b></span>
                                 : <span>Not on any Master unit{t.unit ? ` · impac had it on ${t.unit}` : ''}</span>}
                              {t.impacStatus ? ` · impac: ${t.impacStatus}` : ''}
                            </div>
                            {t.note && <div style={{ marginTop:2, fontSize:11, color:F.amber }}>{t.note}</div>}
                          </div>);
                      })}
                      {trackerIds && trackerIds.loadedAt && <div style={{ fontSize:10, color:F.muted, padding:'5px 6px 1px', fontStyle:'italic' }}>impac list loaded {fmtDate(String(trackerIds.loadedAt).slice(0,10))} · click a unit to jump to it</div>}
                    </div>)}
                </div>);
            })()}
            <select value={catF} onChange={e=>setCatF(e.target.value)} style={{ ...inp, width:190 }}>
              <option value="all">All categories</option>
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {/* #FLEET_CAMGPS_V1 — working-list filter for the "1 by 1" pass: only units still
                pending on Camera OR GPS (blank on either — Installed and Void both count as done). */}
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11.5, color:F.text, cursor:'pointer', userSelect:'none' }}>
              <input type="checkbox" checked={camGpsPendingOnly} onChange={e=>setCamGpsPendingOnly(e.target.checked)} />
              Camera/GPS pending only
            </label>
            <div style={{ flex:1 }} />
            <button onClick={()=>setCatPanel(v=>!v)} style={{ ...btn, color: catPanel?F.accent:F.text,
              borderColor: catStats.collisions.length ? `${F.amber}66` : F.line }}>
              Categories · {catStats.list.length}
              {catStats.collisions.length > 0 && <span style={{ marginLeft:6, color:F.amber, fontWeight:900 }}>⚠ {catStats.collisions.length}</span>}
            </button>
            {!masterEdit && <span style={{ fontSize:11.5, color:F.muted, border:`1px solid ${F.line}`, borderRadius:8, padding:'6px 10px' }}>View only — the master is Owner / Fleet Manager</span>}
            {/* #EQUIP_LIST_EXPORT_V1 — equipment list for submittals / insurance (follows the category + search filter) */}
            <button onClick={downloadEquipCsv} title="Unit #, Category, Description, Serial/VIN, Plate — what's filtered here, or every active unit" style={btn}>⬇ Equip CSV</button>
            <button onClick={() => printEquipList(true)} title="Dakota letterhead equipment list, grouped by category, with Serial/VIN" style={btn}>🖨 Letterhead</button>
            <button onClick={() => printEquipList(false)} title="Same letterhead list without VINs (for customer submittals)" style={{ ...btn, color:F.muted }}>no VIN</button>
            {masterEdit && <button onClick={()=> mlogOpen ? setMlogOpen(false) : openMlog('')} title="Who changed what on the Master, and when" style={{ ...btn, color: mlogOpen?F.accent:F.text }}>Change log</button>}
            {canAddMaster && <button onClick={()=>setEdit({ unit:'', cat:'', desc:'', vin:'', ducDay:'', status:'active' })} style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add unit</button>}
            {canFlagUnit && <button onClick={()=>setFlagEdit({ unit:'', cat:'', desc:'', vin:'' })} style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Flag new unit</button>}
          </div>

          <datalist id="fleet-cat-list">{cats.map(c => <option key={c} value={c} />)}</datalist>

          {/* #FLEET_MASTER_LOG_V1 — who changed what on the Master, and when. Read only. */}
          {mlogOpen && masterEdit && (() => {
            const s = mlogQ.trim().toLowerCase();
            const all = (masterLog || []).slice().reverse();
            const rows = all.filter(e => !s || [e.unit, e.from, e.by, e.action, ...(e.units||[]), ...(e.changes||[]).map(c => `${MLOG_LABELS[c.f]||c.f} ${c.a} ${c.b}`)].some(v => String(v||'').toLowerCase().includes(s)));
            const when = (v) => { const d = new Date(v); if (isNaN(d)) return ''; let h = d.getHours(); const ap = h >= 12 ? 'p' : 'a'; h = h % 12 || 12; return `${pad2(d.getMonth()+1)}/${pad2(d.getDate())}/${String(d.getFullYear()).slice(2)} ${h}:${pad2(d.getMinutes())}${ap}`; };
            const clr = (a) => /^added/.test(a) ? F.green : a === 'retired' ? F.amber : a === 'removed' ? F.red : (a === 'renumbered' || a === 'bulk change') ? F.accent : F.text;
            const tc = { ...td, textAlign:'center' };
            return (
              <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8, flexWrap:'wrap' }}>
                  <div style={{ fontSize:13, fontWeight:900, color:F.accent }}>Equipment Master · Change Log</div>
                  <div style={{ fontSize:11.5, color:F.muted }}>{masterLog == null ? 'loading…' : `${rows.length} of ${all.length} entries · newest first`}</div>
                  <input value={mlogQ} onChange={e=>setMlogQ(e.target.value)} placeholder="Search unit, person, field…" style={{ ...inp, width:220, padding:'5px 9px', fontSize:11.5 }} />
                  {mlogQ && <button onClick={()=>setMlogQ('')} style={{ ...btnS, color:F.muted }}>Show all</button>}
                  <div style={{ flex:1 }} />
                  <button onClick={()=>setMlogOpen(false)} style={{ ...btnS }}>Close</button>
                </div>
                <div style={{ fontSize:11, color:F.muted, marginBottom:8 }}>Every add, edit, retire and file change saved on the Master from 10/06/26 on — who and when. Changes made before that date were not recorded. Read only.</div>
                <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:380, border:`1px solid ${F.line}`, borderRadius:10 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>{['When','Who','Unit','Action','What changed'].map(l => <th key={l} style={{ ...th, textAlign:'center', position:'sticky', top:0, background:F.cardHi }}>{l}</th>)}</tr></thead>
                    <tbody>
                      {rows.slice(0, 300).map((e, i) => (
                        <tr key={e.id || i} style={{ borderTop:`1px solid ${F.line}` }}>
                          <td style={{ ...tc, fontSize:11.5, whiteSpace:'nowrap' }}>{when(e.at)}</td>
                          <td style={{ ...tc, fontSize:11.5, whiteSpace:'nowrap' }}>{String(e.by||'').split('@')[0] || '—'}</td>
                          <td style={{ ...tc, fontWeight:900, whiteSpace:'nowrap' }}>{e.unit}</td>
                          <td style={{ ...tc, fontSize:11, fontWeight:900, textTransform:'uppercase', whiteSpace:'nowrap', color:clr(e.action) }}>{e.action}{e.from ? <div style={{ fontSize:10, fontWeight:700, color:F.muted, textTransform:'none' }}>was {e.from}</div> : null}</td>
                          <td style={{ ...td, fontSize:11.5 }}>
                            {(e.changes||[]).length ? (e.changes||[]).map((c, j) => (
                              <span key={j} style={{ display:'inline-block', marginRight:12 }}><b>{MLOG_LABELS[c.f] || c.f}:</b> {c.a !== '' && c.b !== '' && c.f !== 'docs' ? <><span style={{ color:F.muted }}>{c.a}</span> → {c.b}</> : (c.f === 'docs' ? [c.b, c.a].filter(Boolean).join(' · ') : (c.b !== '' ? <><span style={{ color:F.muted }}>blank</span> → {c.b}</> : <><span style={{ color:F.muted }}>{c.a}</span> → blank</>))}</span>
                            )) : <span style={{ color:F.muted }}>—</span>}
                            {e.units && e.units.length ? <div style={{ fontSize:10.5, color:F.muted, marginTop:2 }}>{e.units.join(', ')}{e.units.length >= 60 ? ' …' : ''}</div> : null}
                          </td>
                        </tr>
                      ))}
                      {masterLog != null && !rows.length && <tr><td colSpan={5} style={{ ...td, textAlign:'center', color:F.muted, padding:20 }}>{all.length ? 'Nothing matches that search.' : 'No changes recorded yet. The first save on the Master starts the log.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
                {rows.length > 300 && <div style={{ fontSize:11, color:F.muted, marginTop:6 }}>Showing the newest 300 — search a unit number to narrow it.</div>}
              </div>
            );
          })()}
          {catPanel && (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8, flexWrap:'wrap' }}>
                <div style={{ fontSize:13, fontWeight:900 }}>Categories · {catStats.list.length} distinct</div>
                <div style={{ fontSize:11.5, color:F.muted }}>
                  across {active.length} active units{catStats.blank ? ` · ${catStats.blank} with no category` : ''}
                </div>
                <div style={{ flex:1 }} />
                <button onClick={()=>setCatPanel(false)} style={{ ...btn, fontSize:11.5 }}>Close</button>
              </div>

              {catStats.collisions.length > 0 && (
                <div style={{ background:`${F.amber}12`, border:`1px solid ${F.amber}55`, borderRadius:9, padding:'10px 12px', marginBottom:10 }}>
                  <div style={{ fontSize:12, fontWeight:900, color:F.amber, marginBottom:6 }}>
                    Same category, {catStats.collisions.length === 1 ? 'two spellings' : 'more than one spelling'}
                  </div>
                  <div style={{ fontSize:11.5, color:F.text, marginBottom:8 }}>
                    These differ only by case or punctuation, so they are the same category twice. Merging the smaller into the larger is safe.
                  </div>
                  {catStats.collisions.map((g,i) => {
                    const big = g[0], rest = g.slice(1);
                    return (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:5 }}>
                        <span style={{ fontSize:12, fontWeight:800 }}>{big.cat} <span style={{ color:F.muted, fontWeight:600 }}>({big.n})</span></span>
                        {rest.map(x => (
                          <span key={x.cat} style={{ fontSize:12 }}>
                            <span style={{ color:F.muted }}>vs</span> <b>{x.cat}</b> <span style={{ color:F.muted }}>({x.n})</span>
                            {masterEdit && (
                              <button onClick={()=>mergeCat(x.cat, big.cat)} style={{ ...btn, marginLeft:6, padding:'3px 9px', fontSize:10.5, color:F.amber, borderColor:`${F.amber}66` }}>
                                merge into {big.cat}
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}

              <div style={{ fontSize:11.5, color:F.muted, marginBottom:8 }}>
                Click a category to filter the list below it. Merge moves every active unit onto the target spelling — retired units keep theirs.
              </div>
              <div style={{ maxHeight:280, overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={th}>Category</th>
                    <th style={{ ...th, textAlign:'right' }}>Units</th>
                    <th style={{ ...th, textAlign:'right' }}>Daily rate</th>
                    {masterEdit && <th style={th}>Merge into</th>}
                  </tr></thead>
                  <tbody>
                    {catStats.list.map(x => {
                      const sum = active.filter(r => String(r.cat||'').trim() === x.cat).reduce((a,r)=>a+rate(r), 0);
                      return (
                        <tr key={x.cat} style={{ borderTop:`1px solid ${F.line}`, background: catF===x.cat ? `${F.accent}12` : 'transparent' }}>
                          <td style={{ ...td, fontWeight:800, cursor:'pointer' }} onClick={()=>setCatF(catF===x.cat ? 'all' : x.cat)}>{x.cat}</td>
                          <td style={{ ...td, textAlign:'right', color:F.muted }}>{x.n}</td>
                          <td style={{ ...td, paddingRight:12 }}><Money v={sum} dim /></td>
                          {masterEdit && (
                            <td style={{ ...td, padding:'4px 8px' }}>
                              <select value="" onChange={e=>{ if (e.target.value) mergeCat(x.cat, e.target.value); }}
                                style={{ ...inp, fontSize:11.5, padding:'5px 7px' }}>
                                <option value="">—</option>
                                {catStats.list.filter(y => y.cat !== x.cat).map(y => <option key={y.cat} value={y.cat}>{y.cat} ({y.n})</option>)}
                              </select>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* #FLEET_UNITDUPE_V1 — Bill: "this is repeated and unable to delete." Same shape as the
              category-collision panel above, for unit numbers instead: rows that key to the same
              unit (hyphen/case/trailing-'*' stripped) are one machine typed twice. Daily already
              collapses these to a single row on its own; this is where the extra actually gets
              cleared off the master. Retire, not delete — #FLEET_NODELETE_V1 is still the rule. */}
          {unitStats.collisions.length > 0 && (
            <div style={{ background:`${F.red}0d`, border:`1px solid ${F.red}55`, borderRadius:12, padding:'12px 14px', marginBottom:12 }}>
              <div style={{ fontSize:12.5, fontWeight:900, color:F.red, marginBottom:6 }}>
                Duplicate unit number{unitStats.collisions.length===1?'':'s'} · {unitStats.collisions.length}
              </div>
              <div style={{ fontSize:11.5, color:F.text, marginBottom:8 }}>
                These rows key to the same unit once punctuation is stripped — Daily is already showing
                only one of them, but the extra is still sitting on the active master. Retire whichever
                one isn't the real record (a $0-history row or a description like "increase value
                of…" is usually the mistake).
              </div>
              {unitStats.collisions.map((g,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6,
                  paddingBottom:6, borderBottom: i<unitStats.collisions.length-1 ? `1px solid ${F.line}` : 'none' }}>
                  {g.map((r,j) => (
                    <span key={j} style={{ fontSize:12, display:'inline-flex', alignItems:'center', gap:6,
                      border:`1px solid ${F.line}`, borderRadius:8, padding:'4px 8px' }}>
                      <b>{r.unit}</b>
                      <span style={{ color:F.muted }} title={r.desc}>{r.desc || '—'}</span>
                      {masterEdit && (
                        <button onClick={()=>retireUnit(r)} style={{ ...btn, padding:'2px 8px', fontSize:10.5, color:F.amber, borderColor:`${F.amber}66` }}>
                          Retire this one
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          )}

          {edit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:4 }}>{edit._orig ? `Edit ${edit._orig.unit}` : 'Add unit'}</div>
              {/* #FLEET_MASTER_TOPBAR_V1 (2026-10-26) — Bill: Save / Close without saving / Retire / Change history were at the very bottom,
                  under the whole work history. Moved to the top and pinned, so they stay in reach while scrolling the form. */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center', position:'sticky', top:0, zIndex:6, background:F.card, padding:'8px 0 10px', marginBottom:10, borderBottom:`1px solid ${F.line}` }}>
                <button onClick={saveUnit} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
                <button onClick={()=>setEdit(null)} style={{ ...btn }}>Close without saving</button>
                {/* #FLEET_NODELETE_V1 — Bill: "Delete row makes me nervous… should only be active or
                    retired once entered. I can't think of any reason why we need to ever delete."
                    Right call: a unit that existed has cost and hours behind it, and a hard delete
                    orphans them silently. Retire is the ONLY exit — it moves the row to the retired
                    list, which is itself a dataset. There is deliberately no delete button here. */}
                {edit._orig && (!edit._orig.decommissionPending || decomComplete(edit._orig.decommission)) && <button onClick={()=>retireUnit(edit._orig)} style={{ ...btn, color:F.amber, borderColor:`${F.amber}66` }}>Retire / sold…</button>}
                {edit._orig && edit._orig.decommissionPending && !decomComplete(edit._orig.decommission) && <span style={{ fontSize:11.5, color:F.muted, border:`1px solid ${F.line}`, borderRadius:8, padding:'6px 10px' }}>Check off the Decommission checklist above, Save, then Retire</span>}
                {edit._orig && edit._orig.needsFollowUp && masterEdit && <button onClick={()=>markInfoComplete(edit._orig)} style={{ ...btn }} title="Dismiss the needs-info flag without filling in VIN/Lic Plate — for units that legitimately won't get one">Mark info complete</button>}
                {edit._orig && masterEdit && <button onClick={()=>openMlog(edit._orig.unit)} style={{ ...btn }} title="Who changed this unit, and when">Change history</button>}
                <div style={{ marginLeft:'auto' }} />
                <button onClick={()=>{ setEdit(null); setView('daily'); }} style={{ ...btn, color:F.accent, borderColor:`${F.accent}55` }}>← Back to Daily</button>
              </div>
              {/* #FLEET_RENTAL_V2 — a rental/RPO record's daily rate is DERIVED from Monthly Charge, not
                  hand-typed, so it stays out of sync if edited here. Point to the Rental/RPO tab instead. */}
              {edit._orig && String(edit._orig.rentalType||'').trim() && (
                <div style={{ background:`${F.gold}12`, border:`1px solid ${F.gold}55`, borderRadius:9, padding:'8px 12px', marginBottom:10, fontSize:11.5, color:F.text }}>
                  This is a Rental/RPO unit — edit its Monthly Charge, Weekly/Daily rate, vendor and dates from{' '}
                  <b onClick={()=>{ setEdit(null); setView('rental'); }} style={{ cursor:'pointer', color:F.gold, textDecoration:'underline' }}>Fleet → Rental/RPO</b>. Unit #, category, VIN and everything else here still applies.
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
                {[['unit','Unit #'],['cat','Category'],['desc','Description'],['vin','Serial / VIN'],
                  // #FLEET_YTD_BASELINE_V1 (2026-09-22) — one-time anchor per unit so YTD in Weekly
                  // Inventory can be (current reading − baseline) instead of only counting from whenever
                  // weekly tracking went live. Baseline Date defaults to 1/1/26 when blank; for a unit
                  // that arrived later in the year, put its in-service date + reading here instead.
                  ['hrsBaseline','Baseline reading (mi/hrs)'],['hrsBaselineDate','Baseline date  MM/DD/YY'],
                  ['ducDay','Daily rate ($)'],['lifeMo','Life (months)'],['purDate','Purchase date  MM/DD/YY'],['purAmt','Purchase $'],
                  ['finCo','Finance co'],['payment','Payment'],['maturity','Maturity  MM/DD/YY'],['licPlate','Plate'],
                  ['cameraStatus','Camera (Geotab)'],['gpsStatus','GPS (Geotab)'],
                  ['gpsSerial','GPS/Asset Tracker S/N'],['gpsCamCost','GPS/Camera Bundle $/mo'],
                  ['assetCost','Asset Tracker $/mo'],['telemStart','Telematics start  MM/DD/YY'],
                  ['notes','Notes']].map(([k,l]) => {
                  const rentalLocked = k==='ducDay' && edit._orig && String(edit._orig.rentalType||'').trim();
                  // #FLEET_GPSCAM_IMPORT_V1 (2026-09-15) — vocabulary switched from the original
                  // Pending/Installed/Void tri-state to Jerry Wadekin's own KEY off his GPS CAMERA LIST:
                  // X = has & working, NW = has one but not working, NO GPS/NO CAM = missing, needs one,
                  // N/A = not required (matches what 200 of 201 units were bulk-imported with on
                  // 2026-09-15, cross-checked against impac Fleet's Geotab device + camera reports).
                  // Fixed dropdown, not free text — same reason as before: no drift on what's basically
                  // a data-hygiene field.
                  const isStatusField = k==='cameraStatus' || k==='gpsStatus';
                  // #FLEET_ASSETMGMT_V1 — a unit is on ONE telematics bundle, never both: GPS/Camera
                  // ($18.80/mo GPS-only or $43.00/mo with camera — impac Fleet, Sep 2026) for powered
                  // units, Asset Tracker ($11.31/mo) for unpowered ones like trailers. Shown as a hint,
                  // not enforced, since Bill types the actual rate per unit.
                  const isCostField = k==='gpsCamCost' || k==='assetCost';
                  return (
                  <div key={k}>
                    <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}{rentalLocked ? ' (from Monthly Charge)' : ''}</div>
                    {isCostField ? (
                      <Fragment>
                        <input value={edit[k]==null?'':edit[k]} onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={inp}
                          placeholder={k==='gpsCamCost' ? `${TELEM_RATES.gps.toFixed(2)} GPS only / ${TELEM_RATES.cam.toFixed(2)} w/ camera` : TELEM_RATES.asset.toFixed(2)} />
                        {k==='assetCost' && (
                          <div style={{ fontSize:9.5, color:F.muted, marginTop:3 }}>
                            3-yr life · renews {telemRenewal(edit.telemStart)}{!String(edit.telemStart||'').trim() ? ` (placeholder start ${TELEM_DEFAULT_START})` : ''}
                          </div>
                        )}
                      </Fragment>
                    ) : k==='telemStart' ? (
                      <input value={edit[k]==null?'':edit[k]} onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} placeholder={TELEM_DEFAULT_START} />
                    ) : isStatusField ? (
                      <select value={edit[k]||''} onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={inp}>
                        <option value="">Not yet checked</option>
                        <option value="X">X — has one, working</option>
                        <option value="NW">NW — has one, not working</option>
                        <option value={k==='cameraStatus' ? 'NO CAM' : 'NO GPS'}>{k==='cameraStatus' ? 'NO CAM' : 'NO GPS'} — missing, needs one</option>
                        <option value="N/A">N/A — not required</option>
                      </select>
                    ) : (
                      <Fragment>
                        {/* Category is a datalist off the existing values — that is what stops a 64th
                            spelling being invented every time someone adds a unit. */}
                        <input value={edit[k]==null?'':edit[k]} list={k==='cat' ? 'fleet-cat-list' : undefined} readOnly={!!rentalLocked}
                          onChange={e=>setEdit(d=>({ ...d, [k]:e.target.value }))} style={{ ...inp, ...(rentalLocked ? { color:F.muted, cursor:'not-allowed' } : {}) }} />
                        {k==='cat' && edit.cat && !cats.includes(String(edit.cat).trim()) && (
                          <div style={{ fontSize:9.5, fontWeight:800, color:F.amber, marginTop:3 }}>New category — check it isn't an existing one spelled differently</div>
                        )}
                        {k==='gpsSerial' && String(edit.gpsSerial||'').trim() && (() => {   // #FLEET_TRACKER_IMEI_V1
                          const t = imeiBySerial[String(edit.gpsSerial).trim().toUpperCase()];
                          if (!t) return /^HE/i.test(String(edit.gpsSerial).trim())
                            ? <div style={{ fontSize:9.5, color:F.amber, marginTop:3 }}>Not on impac's tracker list — no IMEI on file</div> : null;
                          return <div style={{ fontSize:10, color:F.muted, marginTop:3 }}>IMEI <b style={{ color:F.text, fontFamily:'Consolas,monospace' }}>{t.imei}</b>
                            {t.impacStatus ? ` · impac: ${t.impacStatus}` : ''}{t.badBox === true ? <b style={{ color:F.red }}> · in BAD box</b> : ''}</div>;
                        })()}
                      </Fragment>
                    )}
                  </div>
                  );
                })}
              </div>
              {(() => {
                const v = unitVsVin(edit.unit, edit.vin);
                const should = unitFromVin(edit.vin, '20' + String(edit.unit||'').slice(0,2));
                const c = v.level==='ok'?F.green:v.level==='bad'?F.red:v.level==='warn'?F.amber:F.muted;
                return (
                  <div style={{ marginTop:10, fontSize:11.5, fontWeight:700, color:c }}>
                    Unit # rule (year + last 4 of VIN): {v.msg}
                    {v.level==='bad' && should && (
                      <> · <span onClick={()=>setEdit(d=>({ ...d, unit:should }))} style={{ cursor:'pointer', color:F.gold, fontWeight:900, textDecoration:'underline' }}>use {should}</span></>
                    )}
                    {/* #FLEET_UNIT_RENAME_V1 — say up front that a changed number takes the unit's history with it */}
                    {edit._orig && unitKey(edit._orig.unit) && unitKey(edit.unit) && unitKey(edit._orig.unit) !== unitKey(edit.unit) && (
                      <div style={{ marginTop:4, color:F.amber, fontWeight:800 }}>
                        Renumbering {unitNorm(edit._orig.unit)} → {unitNorm(edit.unit)} — Save moves its Daily, hours, board, service, shop, toll/inspection and PO history to the new number. You'll see the list before anything changes.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* #FLEET_RELATEDRECS_V1 — live summary of this unit's Toll Tag + Inspection, pulled from
                  their own datasets (not merged — see the note at tollByKey above), one click to open
                  or start one. Only once the unit's actually saved, same reasoning as Attachments. */}
              {edit._orig && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${F.line}`, display:'flex', gap:16, flexWrap:'wrap' }}>
                  {(() => {
                    const t = tollByKey[unitKey(edit._orig.unit)];
                    return (
                      <div style={{ fontSize:11.5 }}>
                        <span style={{ fontWeight:800, color:F.muted, textTransform:'uppercase', letterSpacing:.4, fontSize:9.5 }}>Toll tag </span>
                        {t ? <span style={{ color:F.text }}>{t.tagId} · {t.status||'—'}</span> : <span style={{ color:F.muted }}>none on file</span>}
                        {' '}<span onClick={()=>jumpToToll(edit._orig.unit)} style={{ color:F.gold, cursor:'pointer', textDecoration:'underline', fontWeight:700 }}>{t ? 'Open' : '+ Add'}</span>
                      </div>
                    );
                  })()}
                  {(() => {
                    const x = inspByKey[unitKey(edit._orig.unit)];
                    const st = x ? inspStatus(x.nextDate) : null;
                    return (
                      <div style={{ fontSize:11.5 }}>
                        <span style={{ fontWeight:800, color:F.muted, textTransform:'uppercase', letterSpacing:.4, fontSize:9.5 }}>Inspection </span>
                        {x ? <span style={{ color:st.color, fontWeight:700 }}>{st.label} ({x.nextDate||'—'})</span> : <span style={{ color:F.muted }}>none on file</span>}
                        {' '}<span onClick={()=>jumpToInsp(edit._orig.unit)} style={{ color:F.gold, cursor:'pointer', textDecoration:'underline', fontWeight:700 }}>{x ? 'Open' : '+ Add'}</span>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* #ASSETLEDGER_V1 (2026-09-16) — Bill: value-adds (items added after purchase that
                  raise a unit's worth, e.g. the 22-5090 vac-tank addition) are logged on Ashlie's
                  Asset Ledger, not here — this is read-only context so Fleet can see what's already
                  been logged while working the unit. */}
              {edit._orig && Array.isArray(edit._orig.valueAdds) && edit._orig.valueAdds.length > 0 && (
                <div style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${F.line}` }}>
                  <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:6 }}>
                    Value-adds logged (Ashlie's Asset Ledger) — {money(edit._orig.valueAdds.reduce((s,v)=>s+(Number(v.amount)||0),0))} total
                  </div>
                  {edit._orig.valueAdds.map((v,i) => (
                    <div key={i} style={{ fontSize:11.5, color:F.text, padding:'3px 0' }}>
                      {v.date || '—'} — {v.desc || 'value-add'} — <b>{money(Number(v.amount)||0)}</b>
                    </div>
                  ))}
                </div>
              )}

              {/* #FLEET_EQUIPFILES_V1 — Bill: "need a place to add Invoices, records, inspections to
                  these Equipment files.. similar to emp files." Requires the row to already exist
                  (uploads key off the saved unit #), so a brand-new not-yet-saved row shows a note
                  instead of the uploader. */}
              <div
                onDragOver={masterEdit && edit._orig ? (e=>{ e.preventDefault(); e.currentTarget.style.outline = `2px dashed ${F.accent}`; e.currentTarget.style.outlineOffset='2px'; }) : undefined}
                onDragLeave={masterEdit && edit._orig ? (e=>{ e.currentTarget.style.outline='none'; }) : undefined}
                onDrop={masterEdit && edit._orig ? (e=>{ e.preventDefault(); e.currentTarget.style.outline='none'; const fl = e.dataTransfer.files; if (fl && fl.length) addEquipDoc(fl); }) : undefined}
                style={{ marginTop:12, paddingTop:12, borderTop:`1px solid ${F.line}`, borderRadius:8 }}>
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:6 }}>
                  Attachments — invoices, specs, inspections, records{masterEdit && edit._orig ? ' (drag & drop files here too)' : ''}
                </div>
                {!edit._orig ? (
                  <div style={{ fontSize:11.5, color:F.muted }}>Save this unit first, then attach files.</div>
                ) : (
                  <Fragment>
                    {(Array.isArray(edit.docs) ? edit.docs : []).length === 0 && (
                      <div style={{ fontSize:11.5, color:F.muted, marginBottom:8 }}>No files attached yet.</div>
                    )}
                    {(Array.isArray(edit.docs) ? edit.docs : []).map((d, i) => (
                      <div key={d.path || i} style={{ display:'flex', alignItems:'center', gap:8, padding:'5px 0', borderBottom: i < edit.docs.length-1 ? `1px solid ${F.line}` : 'none' }}>
                        <span style={{ fontSize:10, fontWeight:800, color:F.accent, border:`1px solid ${F.accent}55`, borderRadius:6, padding:'2px 6px', flexShrink:0 }}>{d.kind || 'Other'}</span>
                        <span onClick={()=>openEquipDoc(d)} style={{ fontSize:12, color:F.text, cursor:'pointer', textDecoration:'underline', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }} title={d.name}>{d.name}</span>
                        <span style={{ fontSize:10.5, color:F.muted, flexShrink:0 }}>{d.at ? fmtDate(d.at) : ''}</span>
                        {masterEdit && <span onClick={()=>removeEquipDoc(d)} style={{ fontSize:11, color:F.red, cursor:'pointer', flexShrink:0 }}>Remove</span>}
                      </div>
                    ))}
                    {masterEdit && (
                      <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:8, flexWrap:'wrap' }}>
                        <select value={equipDocKind} onChange={e=>setEquipDocKind(e.target.value)} style={{ ...inp, width:'auto' }}>
                          {['Invoice','Spec sheet','Inspection','Manual','Bill of sale','Registration','Title','License plate','Photo','Other'].map(k => <option key={k} value={k}>{k}</option>)}
                        </select>
                        <input type="file" multiple onChange={e => { const fl = e.target.files; e.target.value=''; if (fl && fl.length) addEquipDoc(fl); }} style={{ fontSize:11.5 }} />
                        {equipDocMsg && <span style={{ fontSize:11, color:F.muted }}>{equipDocMsg}</span>}
                      </div>
                    )}
                  </Fragment>
                )}
              </div>

              {edit._orig && masterEdit && !arrivalComplete(edit.arrival) && (
                <div style={{ background:`${F.accent}14`, border:`1px solid ${F.accent}55`, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:900, color:F.accent, marginBottom:6 }}>Fleet Arrival checklist — items installed/confirmed when {edit._orig.unit} came in</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:6 }}>
                    {ARR_ITEMS.map(([k,l]) => (
                      <label key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <input type="checkbox" checked={!!(edit.arrival && edit.arrival[k])}
                          onChange={e=>setEdit(d=>({ ...d, arrival:{ ...d.arrival, [k]:e.target.checked } }))} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {edit._orig && edit._orig.decommissionPending && masterEdit && (
                <div style={{ background:`${F.amber}14`, border:`1px solid ${F.amber}55`, borderRadius:10, padding:'10px 12px', marginBottom:12 }}>
                  <div style={{ fontSize:12, fontWeight:900, color:F.amber, marginBottom:6 }}>Decommission checklist — Ashlie flagged {edit._orig.unit} sold/disposed{edit._orig.saleTo?` to ${edit._orig.saleTo}`:''}. Confirm each item removed before retiring.</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:6 }}>
                    {DEC_ITEMS.map(([k,l]) => (
                      <label key={k} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                        <input type="checkbox" checked={!!(edit.decommission && edit.decommission[k])}
                          onChange={e=>setEdit(d=>({ ...d, decommission:{ ...d.decommission, [k]:e.target.checked } }))} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {/* #FLEET_WO_TABLES_V1 — the unit's work history (WOs, POs, service, shop visits, DVIRs), newest first */}
              {edit._orig && edit._orig.unit && <UnitWorkHistory sg={sg} unit={edit._orig.unit} theme={{ text:F.text, muted:F.muted, line:F.line, accent:F.accent, gold:F.gold, good:F.green, bad:F.red, violet:F.violet }} />}
            </div>
          )}

          {flagEdit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:6 }}>Flag new unit — arrived, not yet on Ashlie's Purchased list</div>
              <div style={{ fontSize:11.5, color:F.muted, marginBottom:10 }}>
                Minimal record only — Unit #, description, VIN, category. This doesn't retire or edit anything else, and it shows up as a "needs paperwork" reminder on the Owner Dash until it's matched to a Purchase entry or reclassified (e.g. turns out to be a rental).
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
                {[['unit','Unit #'],['desc','Description'],['vin','VIN #'],['cat','Category']].map(([k,l]) => (
                  <div key={k}>
                    <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}</div>
                    <input value={flagEdit[k]==null?'':flagEdit[k]} onChange={e=>setFlagEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} list={k==='cat' ? 'fleet-cat-list' : undefined} />
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                <button onClick={saveFlaggedUnit} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save flag</button>
                <button onClick={()=>setFlagEdit(null)} style={{ ...btn }}>Close without saving</button>
              </div>
            </div>
          )}

          {(() => {
            const ql = qM.trim().toLowerCase();
            // #FLEET_RATE_BUDGET_V1 — per-unit daily cost split, computed (never stored on the master row)
            const _owned = (active||[]).filter(r => !String(r.rentalType||'').trim());
            const _days = num(rateCfg.days) || 260;
            const _insDay = _owned.length ? num(rateCfg.autoPremium) / _owned.length / _days : 0;
            const _rateSum = _owned.reduce((t,r) => t + rate(r), 0);
            const _mtxPerRate = _rateSum ? num(rateCfg.mtxBudget) / _days / _rateSum : 0;
            const rcBy = {}; _owned.forEach(r => { const rt = rate(r); const plated = !!String(r.licPlate||'').trim();
              const o = { insDay:_insDay, terpDay: rt * num(rateCfg.terpPct) / 100, taxDay: plated ? 0 : rt * num(rateCfg.taxPct) / 100, mtxDay: rt * _mtxPerRate };
              o.costDay = o.insDay + o.terpDay + o.taxDay + o.mtxDay; rcBy[unitKey(r.unit)] = o; });
            const rcOf = (r) => rcBy[unitKey(r.unit)] || {};
            const RC_KEYS = ['insDay','terpDay','taxDay','mtxDay','costDay'];
            const rcTot = (k) => Object.values(rcBy).reduce((t,o) => t + (o[k]||0), 0);
            const filtered = active.filter(r =>
              (catF==='all' || String(r.cat||'')===catF) &&
              (!camGpsPendingOnly || !String(r.cameraStatus||'').trim() || !String(r.gpsStatus||'').trim()) &&
              (!ql || `${r.unit} ${r.cat} ${r.desc} ${r.vin||''}`.toLowerCase().includes(ql)));
            // #FLEET_MSORT_V1 — Bill, 2026-08-21: "Equip Master needs to be sortable." Same click-to-sort
            // / click-again-to-reverse / click-once-more-for-sheet-order pattern as Daily Inventory's
            // header (#FLEET_SORT_V1 above), applied to the Master table.
            const dir = msort.dir === 'desc' ? -1 : 1;
            const cmpU = (a,b) => String(a.unit||'').localeCompare(String(b.unit||''));
            const txt = (key) => (a,b) => (String(a[key]||'').localeCompare(String(b[key]||'')) * dir) || cmpU(a,b);
            const num_ = (key) => (a,b) => ((num(a[key]) - num(b[key])) * dir) || cmpU(a,b);
            // #FLEET_ASSETMGMT_SORT_V1 — Bill, 2026-09-17: "Unable to sort Master in the last 3
            // categories Inspection, Toll Tag & VIN rule." These are derived off other tabs (not
            // stored on the row) so they need their own rank functions rather than a plain field
            // read — same red/amber/green/muted priority as the colored dot each column already
            // shows, with "not on that tab at all" ranked worst (0) so gaps sort to the top.
            const rankInsp = (r) => { const iRow = inspByKey[unitKey(r.unit)]; if (!iRow) return 0;
              const st = inspStatus(iRow.nextDate); return st.color===F.red?1 : st.color===F.amber?2 : 3; };
            const rankToll = (r) => { const tRow = tollByKey[unitKey(r.unit)]; if (!tRow) return 0;
              const s = String(tRow.status||'').trim(); return (s && s!=='Active') ? 1 : 2; };
            const rankVin = (r) => { const v = unitVsVin(r.unit, r.vin);
              return v.level==='bad'?0 : v.level==='warn'?1 : v.level==='ok'?2 : 3; };
            const cmp = RC_KEYS.includes(msort.key) ? (a,b) => ((num(rcOf(a)[msort.key]) - num(rcOf(b)[msort.key])) * dir) || cmpU(a,b)   // #FLEET_RATE_BUDGET_V1
                      : msort.key === 'rate'   ? (a,b) => ((rate(a) - rate(b)) * dir) || cmpU(a,b)
                      : msort.key === 'rentalRpo' ? (a,b) => (((!!b.rentalType?1:0) - (!!a.rentalType?1:0)) * dir) || cmpU(a,b)
                      : msort.key === 'purAmt' || msort.key === 'purDate' || msort.key === 'maturity'
                        || msort.key === 'gpsCamCost' || msort.key === 'assetCost'
                        || msort.key === 'insDay' || msort.key === 'terpDay' || msort.key === 'taxDay' || msort.key === 'mtxDay' ? num_(msort.key)   // #FLEET_RATE_SPLIT_COLS_V1
                      : msort.key === 'fuelCard' ? (a,b) => (String((fuelCardIdx[unitKey(a.unit)]||['~']).join()).localeCompare(String((fuelCardIdx[unitKey(b.unit)]||['~']).join())) * dir) || cmpU(a,b)   // #FLEET_FUEL_CARDS_V1
                      : msort.key === 'insp'    ? (a,b) => ((rankInsp(a) - rankInsp(b)) * dir) || cmpU(a,b)
                      : msort.key === 'toll'    ? (a,b) => ((rankToll(a) - rankToll(b)) * dir) || cmpU(a,b)
                      : msort.key === 'vinrule' ? (a,b) => ((rankVin(a) - rankVin(b)) * dir) || cmpU(a,b)
                      : msort.key          ? txt(msort.key)
                      : cmpU;   // #FLEET_MSORT_V1 — default (no column clicked) now sorts by Unit, not Category
            const list = [...filtered].sort(cmp);
            const heads = [['unit','Unit','center'],['rentalRpo','R','center'],['cat','Category','left'],
              ['desc','Description','left'],['vin','Serial / VIN','left'],['rate','Rate','right'],
              // #FLEET_RATE_SPLIT_COLS_V1 (2026-10-16) — Bill: INS / TERP / TAX / MTX behind Rate, per DUC Day.
              // Values load later (insDay / terpDay / taxDay / mtxDay on the master row); hover shows week (x5) and month (x5x52/12).
              ['insDay','INS','right'],['terpDay','TERP','right'],['taxDay','TAX','right'],['mtxDay','MTX','right'],['costDay','TOTAL','right'],
              ['purAmt','Purchase','right'],['purDate','Purchased','center'],['maturity','Maturity','center'],
              // #FLEET_GPSCAM_IMPORT_V1 — GPS/Camera check columns Bill asked for, straight off the
              // cameraStatus/gpsStatus fields (bulk-imported 2026-09-15 from Jerry's GPS CAMERA LIST +
              // impac Fleet's Geotab device/camera reports) — sortable like everything else here.
              ['gpsStatus','GPS','center'],['cameraStatus','Camera','center'],
              // #FLEET_ASSETMGMT_V1 — asset-management columns Bill asked for: plate + GPS S/N are
              // sortable text, the two bundle costs sortable numeric (handled in `cmp` above).
              ['licPlate','Plate','left'],['gpsSerial','GPS S/N','left'],['fuelCard','Fuel Card','left'],   // #FLEET_FUEL_CARDS_V1
              ['gpsCamCost','GPS/Cam $/mo','right'],['assetCost','Asset $/mo','right'],
              // #FLEET_ASSETMGMT_SORT_V1 — now sortable like everything else (rank fns above)
              ['insp','Inspection','center'],['toll','Toll Tag','center'],['vinrule','VIN rule','center']];
            // #FLEET_MASTER_FIT_V1 (2026-10-16) — Bill: Description unreadable. The table was 100% wide with ~1,600px of fixed
            // columns, so Description (the only flexible one) got squeezed to nothing on a normal screen. Now VIN / GPS S/N /
            // Fuel Card size to their longest value, and the table keeps at least 320px for Description (scrolls sideways if needed).
            const _fitW = (vals, label, min, max) => Math.round(Math.min(max, Math.max(min, Math.max(String(label).length + 2, ...vals.map(v => String(v||'').length)) * 7.1 + 18)));
            const wVin  = _fitW(list.map(r => r.vin), 'Serial / VIN', 70, 170);
            const wGps  = _fitW(list.map(r => r.gpsSerial), 'GPS S/N', 60, 130);
            const wFuel = _fitW(list.map(r => (fuelCardIdx[unitKey(r.unit)]||[]).join(', ')), 'Fuel Card', 60, 140);
            const wRateCol = 58;
            const fixedW = 82+26+96 + wVin + 96 + wRateCol*4 + 64 + 118+92+92 + 60+64 + 100+wGps+wFuel + 96+90 + 70+64+70;
            const DESC_MIN = 320;
            const rsplit = (v, label) => { const d = num(v); if (!d) return <span style={{ color:F.line }}>—</span>;
              return <span title={`${label}: $${d.toFixed(2)}/day · $${(d*5).toFixed(2)}/wk · $${(d*5*52/12).toFixed(2)}/mo`}><Money v={d} dim /></span>; };
            return (
              <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'calc(100vh - 300px)' }}>
                <div style={{ padding:'7px 12px', fontSize:11.5, color:F.muted, borderBottom:`1px solid ${F.line}` }}>{list.length} of {active.length} units{masterEdit ? ' · click a row to edit' : ''}</div>
                {/* #FLEET_RATE_BUDGET_V1 — fleet totals on top: per DUC Day and per year (x260) */}
                {(() => { const rt = _rateSum, c = rcTot('costDay'); const $d = v => '$' + Math.round(v).toLocaleString(); const $y = v => '$' + Math.round(v * _days).toLocaleString();
                  const cells = [['Rate', rt, F.text], ['INS', rcTot('insDay'), F.muted], ['TERP', rcTot('terpDay'), F.muted], ['TAX', rcTot('taxDay'), F.muted], ['MTX', rcTot('mtxDay'), F.muted], ['TOTAL cost', c, c > rt ? F.red : F.amber], ['Rate − cost', rt - c, rt - c >= 0 ? F.green : F.red]];
                  const cfgIn = (k, label) => (
                    <label style={{ display:'flex', alignItems:'center', gap:5, fontSize:10.5, color:F.muted, fontWeight:700 }}>{label}
                      <input key={rateCfg[k]} defaultValue={Math.round(num(rateCfg[k])).toLocaleString()} disabled={!masterEdit}
                        onBlur={e => { const v = num(String(e.target.value).replace(/[^0-9.]/g,'')); if (v && v !== num(rateCfg[k])) saveRateCfg({ [k]: v }); }}
                        style={{ width:96, background:F.bg, color:F.text, border:`1px solid ${F.line}`, borderRadius:6, padding:'3px 6px', fontSize:11.5, textAlign:'right' }} /></label>);
                  return (
                    <div style={{ padding:'8px 12px', borderBottom:`1px solid ${F.line}`, display:'flex', flexWrap:'wrap', gap:14, alignItems:'center', position:'sticky', left:0 }}>
                      {cells.map(([l, v, clr]) => (
                        <div key={l} style={{ minWidth:92 }}>
                          <div style={{ fontSize:9.5, fontWeight:800, letterSpacing:.4, textTransform:'uppercase', color:F.muted }}>{l}</div>
                          <div style={{ fontSize:14, fontWeight:900, color:clr, fontVariantNumeric:'tabular-nums' }}>{$d(v)}<span style={{ fontSize:10, color:F.muted, fontWeight:600 }}>/day</span></div>
                          <div style={{ fontSize:10, color:F.muted, fontVariantNumeric:'tabular-nums' }}>{$y(v)}/yr</div>
                        </div>))}
                      <div style={{ display:'flex', flexDirection:'column', gap:4, marginLeft:'auto' }}>
                        {cfgIn('autoPremium', 'Auto liability $/yr')}
                        {cfgIn('mtxBudget', 'MTX budget $/yr')}
                        <div style={{ fontSize:9.5, color:F.muted }}>{_owned.length} owned units · {_days} DUC Days/yr · TERP {rateCfg.terpPct}% · TAX {rateCfg.taxPct}% (no plate)</div>
                      </div>
                    </div>); })()}
                <table style={{ width:'100%', minWidth: fixedW + DESC_MIN, borderCollapse:'collapse', tableLayout:'fixed' }}>
                  <colgroup>
                    {/* #FLEET_GPSCAM_IMPORT_V1 — Bill, 2026-09-15: Category narrowed (he's cleaning the
                        bucket names down to short labels) to hand the freed width to Description, which
                        was truncating on longer entries. */}
                    <col style={{ width:82 }} /><col style={{ width:26 }} /><col style={{ width:96 }} /><col />
                    <col style={{ width:wVin }} /><col style={{ width:96 }} />
                    <col style={{ width:wRateCol }} /><col style={{ width:wRateCol }} /><col style={{ width:wRateCol }} /><col style={{ width:wRateCol }} /><col style={{ width:64 }} />
                    <col style={{ width:118 }} />
                    <col style={{ width:92 }} /><col style={{ width:92 }} />
                    <col style={{ width:60 }} /><col style={{ width:64 }} />
                    <col style={{ width:100 }} /><col style={{ width:wGps }} /><col style={{ width:wFuel }} />
                    <col style={{ width:96 }} /><col style={{ width:90 }} />
                    <col style={{ width:70 }} /><col style={{ width:64 }} /><col style={{ width:70 }} />
                  </colgroup>
                  <thead><tr>
                    {heads.map(([key,label,align]) => (
                      <th key={key} onClick={()=>setMsort(s => s.key===key ? (s.dir==='asc' ? {key,dir:'desc'} : {key:'',dir:'asc'}) : {key,dir:'asc'})}
                        title={msort.key===key ? 'Click again to reverse, once more to return to Unit order' : `Sort by ${label}`}
                        style={{ ...th, textAlign:align, cursor:'pointer', userSelect:'none',
                          color: msort.key===key ? F.accent : F.muted }}>
                        {label}<span style={{ marginLeft:4, fontSize:8.5, opacity: msort.key===key ? 1 : .35 }}>
                          {msort.key===key ? (msort.dir==='asc' ? '▲' : '▼') : '↕'}</span>
                      </th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {list.map((r,i) => {
                      const v = unitVsVin(r.unit, r.vin);
                      const c = v.level==='ok'?F.green:v.level==='bad'?F.red:v.level==='warn'?F.amber:F.muted;
                      const isRental = String(r.rentalType||'').trim();
                      // #FLEET_ASSETMGMT_V1 — Toll Tag / Inspection are looked up live off their own
                      // tabs (same tollByKey/inspByKey the edit-modal summary already uses), not stored
                      // on the master row, so they can never drift out of sync with those reports.
                      const tRow = tollByKey[unitKey(r.unit)];
                      const iRow = inspByKey[unitKey(r.unit)];
                      const iSt = iRow ? inspStatus(iRow.nextDate) : null;
                      const plate = r.licPlate || (tRow && tRow.plate) || '';
                      return (
                        <tr key={i} onClick={()=>masterEdit && (isRental
                            ? setRentalEdit({ unit:r.unit, rentalType:r.rentalType||'Rental', rentalVendor:r.rentalVendor||'', desc:r.desc||'', vin:r.vin||'', monthlyRate:r.monthlyRate||'', rentalStart:r.rentalStart||'', rentalDue:r.rentalDue||'', notes:r.notes||'', _orig:r })
                            : setEdit({ ...r, _orig:r }))}
                          style={{ borderTop:`1px solid ${F.line}`, cursor:masterEdit?'pointer':'default', background: v.level==='bad'?`${F.red}12`:'transparent' }}>
                          <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap', textAlign:'center' }}>{r.unit}
                            {r.needsFollowUp && <span title={`Added ${fmtDate(r.addedAt)||'recently'}${r.addedBy?` by ${r.addedBy}`:''} — still missing ${[!String(r.vin||'').trim()&&'VIN', !String(r.licPlate||'').trim()&&'Lic Plate'].filter(Boolean).join(', ')||'info'}`}
                              style={{ marginLeft:4, fontSize:9, fontWeight:900, color:F.amber }}>⚑</span>}
                            {shopHistoryByUnit[unitKey(r.unit)] && <span title={`Shop History: ${shopHistoryByUnit[unitKey(r.unit)].count} visit${shopHistoryByUnit[unitKey(r.unit)].count===1?'':'s'} · ${shopHistoryByUnit[unitKey(r.unit)].days} day${shopHistoryByUnit[unitKey(r.unit)].days===1?'':'s'} down all-time — click to view`}
                              onClick={(e)=>{ e.stopPropagation(); setEdit(null); setView('shophistory'); }}
                              style={{ marginLeft:4, fontSize:9, fontWeight:900, color:F.orange, cursor:'pointer' }}>🔧×{shopHistoryByUnit[unitKey(r.unit)].count}</span>}
                          </td>
                          {/* #FLEET_RENTAL_V2 — same red circled "R" as Daily, so rentals are visible and sortable here too */}
                          <td style={{ ...td, textAlign:'center', padding:'4px 2px' }}>
                            {isRental && <span title={`Rental/RPO — ${r.rentalType||'Rental/RPO'}`}
                              style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:16, height:16,
                                borderRadius:'50%', border:`1.5px solid ${F.red}`, color:F.red, fontSize:9.5, fontWeight:900 }}>R</span>}
                          </td>
                          <td style={{ ...td, color:F.muted, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.cat}>{r.cat}</td>
                          <td style={{ ...td, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.desc}>{r.desc}</td>
                          <td style={{ ...td, color:F.muted, fontSize:11.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.vin||''}>{r.vin||'—'}</td>
                          <td style={{ ...td, paddingRight:12 }}><Money v={rate(r)} /></td>
                          <td style={{ ...td, paddingRight:6, textAlign:'right' }}>{rsplit(rcOf(r).insDay, 'INS')}</td>
                          <td style={{ ...td, paddingRight:6, textAlign:'right' }}>{rsplit(rcOf(r).terpDay, 'TERP')}</td>
                          <td style={{ ...td, paddingRight:6, textAlign:'right' }}>{rsplit(rcOf(r).taxDay, 'TAX')}</td>
                          <td style={{ ...td, paddingRight:6, textAlign:'right' }}>{rsplit(rcOf(r).mtxDay, 'MTX')}</td>
                          {(() => { const c = num(rcOf(r).costDay); const rt = rate(r); if (!c) return <td style={{ ...td, textAlign:'right', color:F.line }}>—</td>;
                            return <td title={`Cost $${c.toFixed(2)}/day vs rate $${rt.toFixed(2)} — ${rt ? Math.round(c/rt*100) : '∞'}% of rate`} style={{ ...td, paddingRight:6, textAlign:'right', fontWeight:800, color: c > rt ? F.red : c > rt*0.9 ? F.amber : F.green }}>${Math.round(c).toLocaleString()}</td>; })()}
                          <td style={{ ...td, paddingRight:12 }}><Money v={num(r.purAmt)} dim /></td>
                          <td style={{ ...td, color:F.muted, textAlign:'center', whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{fmtDate(r.purDate)||'—'}</td>
                          <td style={{ ...td, color:F.muted, textAlign:'center', whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{fmtDate(r.maturity)||'—'}</td>
                          {/* #FLEET_GPSCAM_IMPORT_V1 — Bill, 2026-09-15: GPS/Camera install status, imported
                              from Jerry's yard list + the Geotab/impac reconciliation. Vocabulary is Bill's:
                              X=has & working, NW=has one but not working, NO GPS/NO CAM=missing & needs one,
                              N/A=not required, blank=not yet checked. Same color language as the VIN-rule /
                              Inspection columns (green ✓ / amber ⚠ / red ✕) so the row reads at a glance. */}
                          <td style={{ ...td, textAlign:'center' }}>
                            {(() => { const s=String(r.gpsStatus||'').trim();
                              const color = s==='X'?F.green : s==='NW'?F.amber : s==='NO GPS'?F.red : s==='N/A'?F.muted : F.line;
                              const label = s==='X'?'✓' : s==='NW'?'⚠' : s==='NO GPS'?'✕' : s==='N/A'?'—' : '·';
                              return <span title={s || 'Not yet checked'} style={{ fontSize:10.5, fontWeight:800, color }}>{label}</span>; })()}
                          </td>
                          <td style={{ ...td, textAlign:'center' }}>
                            {(() => { const s=String(r.cameraStatus||'').trim();
                              const color = s==='X'?F.green : s==='NW'?F.amber : s==='NO CAM'?F.red : s==='N/A'?F.muted : F.line;
                              const label = s==='X'?'✓' : s==='NW'?'⚠' : s==='NO CAM'?'✕' : s==='N/A'?'—' : '·';
                              return <span title={s || 'Not yet checked'} style={{ fontSize:10.5, fontWeight:800, color }}>{label}</span>; })()}
                          </td>
                          {/* #FLEET_ASSETMGMT_V1 — Plate, GPS/Asset Tracker S/N, and the two telematics
                              bundle costs Bill asked for on the Equipment Master itself (not just the
                              edit modal). Plate falls back to the Toll Tag report's plate when the
                              master row itself has none typed in yet. */}
                          <td style={{ ...td, color:F.muted, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }} title={plate}>{plate || '—'}</td>
                          <td style={{ ...td, color:F.muted, fontSize:11.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}
                            title={(() => { const t = imeiBySerial[String(r.gpsSerial||'').trim().toUpperCase()]; return (r.gpsSerial||'') + (t && t.imei ? `\nIMEI ${t.imei}` : ''); })()}>{r.gpsSerial || '—'}
                            {(() => { const t = imeiBySerial[String(r.gpsSerial||'').trim().toUpperCase()]; const im = t && t.imei ? String(t.imei) : '';   // #FLEET_TRACKER_IMEI_V1
                              return im ? <div style={{ fontSize:10, lineHeight:1.2 }}>IMEI …<b style={{ color:F.text }}>{im.slice(-4)}</b></div> : null; })()}</td>
                          {/* #FLEET_FUEL_CARDS_V1 — the active Voyager card(s) on this unit, from Fleet → Fuel's card list (matched by VIN) */}
                          <td style={{ ...td, fontSize:11.5, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:(fuelCardIdx[unitKey(r.unit)]||[]).length>1?F.amber:F.text }}
                            title={(fuelCardIdx[unitKey(r.unit)]||[]).length ? `Voyager card ${(fuelCardIdx[unitKey(r.unit)]||[]).join(', ')}` : 'No active Voyager card on this unit'}>{(fuelCardIdx[unitKey(r.unit)]||[]).join(', ') || '—'}</td>
                          <td style={{ ...td, paddingRight:12 }}><Money v={num(r.gpsCamCost)} dim /></td>
                          <td style={{ ...td, paddingRight:12 }}><Money v={num(r.assetCost)} dim /></td>
                          {/* #FLEET_ASSETMGMT_V1 — "checked" (✓, green) only once it's both listed AND
                              current; overdue on file still shows (✕, red) rather than a blank box,
                              same OK/DUE/OVERDUE language as the Inspections tab itself. Click jumps
                              straight to that unit's record on the matching tab, same as the modal. */}
                          <td style={{ ...td, textAlign:'center', cursor:'pointer' }}
                            onClick={(e)=>{ e.stopPropagation(); jumpToInsp(r.unit); }}
                            title={iRow ? `${iSt.label} · next ${iRow.nextDate||'—'} — click to open Inspections` : 'Not on the Inspections tab — click to add'}>
                            <span style={{ fontSize:10.5, fontWeight:800, color: iRow ? iSt.color : F.muted }}>{iRow ? (iSt.color===F.red ? '✕' : '✓') : '—'}</span>
                          </td>
                          <td style={{ ...td, textAlign:'center', cursor:'pointer' }}
                            onClick={(e)=>{ e.stopPropagation(); jumpToToll(r.unit); }}
                            title={tRow ? `${tRow.tagId} · ${tRow.status||'—'} — click to open Toll Tags` : 'Not on the Toll Tags report — click to add'}>
                            <span style={{ fontSize:10.5, fontWeight:800, color: tRow ? (String(tRow.status||'').trim() && tRow.status!=='Active' ? F.amber : F.green) : F.muted }}>{tRow ? '✓' : '—'}</span>
                          </td>
                          <td style={{ ...td, textAlign:'center' }}><span style={{ fontSize:10, fontWeight:800, color:c }}>{v.level==='ok'?'✓':v.level==='bad'?'✕ conflict':v.level==='warn'?'⚠':'—'}</span></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })()}

          {retired.length > 0 && (
            <div style={{ marginTop:12, background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px' }}>
              <div style={{ fontSize:13, fontWeight:900, marginBottom:8 }}>Retired · {retired.length}
                <span style={{ fontSize:11.5, color:F.muted, fontWeight:600, marginLeft:8 }}>
                  sold total {money(retired.reduce((s,r)=>s+num(r.saleAmt),0))} · original cost {money(retired.reduce((s,r)=>s+num(r.purAmt),0))}
                </span>
              </div>
              <div style={{ maxHeight:240, overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr><th style={th}>Unit</th><th style={th}>Category</th><th style={th}>Description</th><th style={th}>Sold</th><th style={th}>Sold/traded to</th><th style={{ ...th, textAlign:'right' }}>Sale $</th><th style={{ ...th, textAlign:'right' }}>Cost $</th><th style={th}></th></tr></thead>
                  <tbody>
                    {retired.map((r,i) => (
                      <tr key={i} style={{ borderTop:`1px solid ${F.line}` }}>
                        <td style={{ ...td, fontWeight:800 }}>{r.unit}</td>
                        <td style={{ ...td, color:F.muted }}>{r.cat}</td>
                        <td style={{ ...td, color:F.muted, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.desc}</td>
                        <td style={{ ...td, color:F.muted, whiteSpace:'nowrap', fontVariantNumeric:'tabular-nums' }}>{fmtDate(r.saleDate)||'—'}</td>
                        <td style={{ ...td, color:F.muted, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.saleTo||'—'}</td>
                        <td style={{ ...td, paddingRight:12 }}><Money v={num(r.saleAmt)} color={F.green} /></td>
                        <td style={{ ...td, paddingRight:12 }}><Money v={num(r.purAmt)} dim /></td>
                        <td style={{ ...td }}>{masterEdit && <button onClick={()=>unretire(r)} style={{ ...btn, padding:'3px 9px', fontSize:11 }}>Un-retire</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Fragment>
      )}

      {/* ═══════════════ MAINTENANCE (#FLEET_MAINT_V1) — src/FleetMaint.js ═══════════════ */}
      {view === 'maint' && (
        <FleetMaint F={F} sg={sg} board={svcBoard} canEdit={editable}
          onServiced={serviceFromBoard} onOpenWeekly={() => setView('weeklyinv')}
          focusUnit={maintFocus} onFocusUsed={() => setMaintFocus(null)} />
      )}

      {/* ═══════════════ CAMERA / GPS NOT REPORTING (#FLEET_TELEM_HOT_V1) — src/FleetTelemAlert.js ═══════════════ */}
      {view === 'telem' && (
        <FleetTelemList data={telem} sg={sg} ss={ss} upn={upn} canAck={!!editable} shopUnits={shopRows.map(x => x.unit)}
          onBack={() => setView(isShopStaff ? 'mydash' : (masterEdit && !restrictToYard ? 'fleetdash' : 'daily'))}   /* #TELEM_CLOSE_V1 (2026-10-20) — Bill: couldn't close the Cam / GPS list */
          theme={{ panel:F.card, panelBorder:F.line, accent:F.accent, accent2:F.gold, steel:F.muted, text:F.text, good:F.green }}
          openViewer={(t) => (hrsViewerIsMobile() ? null : hrsViewerWindow(t, '🖨 Print · Letter landscape')) || hrsViewerOverlay(t, 1010)} />
      )}

      {/* ═══════════════ WEEKLY INVENTORY / HRS (#FLEET_WEEKLYHRS_V1) ═══════════════ */}
      {view === 'weeklyinv' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <button onClick={()=>setHrsWkStart(iso(addDays(new Date(hrsWkStart+'T12:00:00'),-7)))} title="Previous week" style={{ ...btnS, padding:'6px 10px' }}>‹</button>
            <span title={`Readings taken Mon ${hrsWeekOf(hrsWkStart).read} — they close out ${hrsWeekOf(hrsWkStart).label}`} style={{ fontSize:12.5, fontWeight:800, minWidth:96, textAlign:'center', whiteSpace:'nowrap' }}>{hrsWeekOf(hrsWkStart).label}<span style={{ fontSize:10.5, fontWeight:600, color:F.muted }}> · read Mon {hrsWeekOf(hrsWkStart).read}</span></span>
            <button onClick={()=>setHrsWkStart(iso(addDays(new Date(hrsWkStart+'T12:00:00'),7)))} title="Next week" style={{ ...btnS, padding:'6px 10px' }}>›</button>
            <button onClick={()=>setHrsWkStart(iso(mondayOf(new Date())))} style={{ ...btnS, color:F.accent }}>This week</button>
            <input value={hrsQ} onChange={e=>setHrsQ(e.target.value)} placeholder="Search unit, VIN, assigned…"
              style={{ ...inp, width:200, padding:'6px 9px', fontSize:11.5, marginLeft:3 }} />
            {hrsQ && <button onClick={()=>setHrsQ('')} style={{ ...btnS, padding:'6px 9px', color:F.muted }}>✕</button>}
            <div style={{ flex:1 }} />
            {hrsBusy && <span style={{ fontSize:11.5, color:F.gold, fontWeight:700 }}>{hrsBusy}</span>}
            {!hrsBusy && hrsDirty && (hrsAutoErr
              ? <span title={hrsAutoErr} style={{ fontSize:11.5, color:F.red, fontWeight:800 }}>⚠ not saved yet — trying again</span>
              : <span style={{ fontSize:11.5, color:F.amber, fontWeight:800 }}>saving as you go…</span>)}
            {!hrsBusy && !hrsDirty && hrsSavedAt && <span style={{ fontSize:11.5, color:F.green, fontWeight:700 }}>✓ all saved · {hrsSavedAt}</span>}
            {!hrsBusy && hrsBackfillMsg && <span style={{ fontSize:11.5, color:F.accent, fontWeight:700, maxWidth:320 }}>{hrsBackfillMsg}</span>}
            <button onClick={printHrsSummary} title="Bucketed 11x17 print — Description / Unit / Hours-Miles / Repair Cost / Purchase Price" style={{ ...btnS, color:F.gold, borderColor:`${F.gold}55` }}>🖨 Print HRS (11x17)</button>
            {/* #FLEET_GEOTAB_HISTORY_V1 — one-time-per-run action; only shows once the history pull has
                actually loaded, and never overwrites a baseline that's already set (hand-typed or from a
                prior run), so clicking it again after a fresh history pull is always safe. */}
            {masterEdit && hrsGeotabHistory && !!(hrsGeotabHistory.devices || []).length && (
              <button onClick={backfillBaselines} title="Fill Baseline Reading/Date on Equipment Master from the Geotab historical pull — never overwrites a value already set" style={{ ...btnS, color:F.muted }}>⟳ Backfill baselines</button>
            )}
            {masterEdit && hrsGeotabHistory && !!(hrsGeotabHistory.devices || []).length && (
              <button onClick={backfillWeeklyReadings} title="Fill every available week's reading (not just one baseline point) from the Geotab historical pull, so MTD/QTD/YTD see real weekly deltas — never overwrites a value already set" style={{ ...btnS, color:F.muted }}>⟳ Backfill weekly history</button>
            )}
            {/* #FLEET_VIN_TRIAGE_V1 — the punch list Bob/Bill take to Geotab/impac: which units aren't
                matching at all (blank VIN on the Master, or a VIN that's on file but Geotab doesn't
                recognize it). Shrinks on its own as VINs get fixed on either side — nothing to
                re-run, it's just the live gap between the Master and the Geotab device list. */}
            {hrsGeotabHistory && !!(hrsGeotabHistory.devices || []).length && hrsVinIssues.length > 0 && (
              <button onClick={()=>setHrsVinPanel(v=>!v)} title="Units that aren't matching Geotab at all — no VIN on the Master, or a VIN Geotab doesn't recognize" style={{ ...btnS, color:F.amber, borderColor:`${F.amber}55` }}>⚠ VIN issues · {hrsVinIssues.length}</button>
            )}
            {masterEdit && <button onClick={()=>setHrsBucketPanel(v=>!v)} style={{ ...btnS, color:F.muted }}>Buckets…</button>}
            {(editable) && <button onClick={()=>saveHrsWeek()} disabled={!hrsDirty} title="Everything here saves on its own a moment after you leave a row. This saves right now."  style={{ ...btnS, padding:'6px 16px', background: hrsDirty ? F.green : F.cardHi, color: hrsDirty ? '#04121C' : F.muted, cursor: hrsDirty ? 'pointer' : 'default' }}>Save week</button>}
          </div>

          {/* #FLEET_WKINV_NOTE_V1 (2026-10-22) — Bill: long note before the data on a phone. Collapsed on phone, open on desktop. */}
          <details open={!phone} style={{ fontSize:11, color:F.muted, marginBottom:10 }}>
            <summary style={{ cursor:'pointer', fontWeight:800, color:F.text, marginBottom:4 }}>ⓘ How this works — one reading per unit per week</summary>
            One reading per unit per week (Sunday night / Monday morning) — it becomes this week's <b>End</b> and next week's <b>Start</b> automatically.
            Assigned is pulled from the Daily Inventory of the Friday before this week and stays fixed until you override it.
            Trucks/18-Wheeler pull miles and Cleaners/Mixing/VACs/Excavators pull hours straight off the Geotab feed where a unit has a device reporting — RIGS fill from the Ditch Witch and Vermeer factory feeds once each maker's key is in (see the feed tiles on the Fleet dash); until then they stay manual.
            Serviced / DOS / Service Due: RIGS, Cleaners, VACs, Excavators, Mixing Systems and Forklifts are due every 250 hrs; 18-Wheelers every 15,000 mi; Trucks every 6,000 mi; 18-Wheelers are also due 6 months after DOS, whichever comes first. Service Due turns <b style={{ color:F.red }}>red</b> when the unit's last known reading reaches it (even if this week's reading isn't in yet) and <b style={{ color:F.amber }}>amber</b> inside the last 10%. The <b style={{ color:F.green }}>✓</b> beside Serviced stamps today's service: Serviced = current reading, DOS = today. Trailers (and anything else with no rule) skip scheduling but still keep a free Note.
          </details>

          {/* #FLEET_GEOTAB_HOURS_V1 — at-a-glance counts so Bob doesn't have to scan the whole grid to know how many units still need a look */}
          <div style={{ display:'flex', gap:14, marginBottom:10, flexWrap:'wrap', fontSize:11.5 }}>
            {(() => { const rg = hrsRows.filter(x => x.bucket === 'rigs' && x.delta > 0); const tot = rg.reduce((a, x) => a + x.delta, 0);   // #FLEET_WKNUM_V1 — avg rig hours for the week
              return <span title="Average hours per rig that ran this week (weekly Δ above 0)" style={{ color:F.accent, fontWeight:900 }}>⛏ {hrsWeekOf(hrsWkStart).label} · RIGS avg {rg.length ? (tot / rg.length).toFixed(1) : '—'} hrs · {rg.length} ran · {Math.round(tot).toLocaleString()} total</span>; })()}
            <span style={{ color:F.green, fontWeight:800 }}>🛰 {hrsRows.filter(x=>isApiSrc(x.source)).length} auto-filled</span>
            <span style={{ color:F.muted, fontWeight:700 }}>✎ {hrsRows.filter(x=>x.source==='manual').length} manual</span>
            <span style={{ color:F.amber, fontWeight:800 }}>⚠ {hrsRows.filter(x=>x.geotabStatus==='no-signal'||x.geotabStatus==='no-device').length} need a look (bad unit / unplugged / not installed)</span>
            <span style={{ color:F.red, fontWeight:800 }}>🔧 {hrsRows.filter(x=>x.dueFlag).length} service due</span>
            <span style={{ color:F.amber, fontWeight:800 }}>◔ {hrsRows.filter(x=>x.dueSoon).length} due soon</span>
            <button onClick={()=>setView('maint')} style={{ ...btnS, padding:'2px 9px', fontSize:11, color:F.accent }}>Maintenance list →</button>
            {(!hrsGeotab || !hrsGeotab.devices || !hrsGeotab.devices.length) && <span style={{ color:F.red, fontWeight:800 }}>Geotab Fleet sync hasn't reported any devices yet — check api/geotab-fleet-sync</span>}
          </div>

          {/* bucket filter chips */}
          <div style={{ display:'flex', gap:6, marginBottom:10, flexWrap:'wrap' }}>
            <button onClick={()=>setHrsBucketFilter(null)} style={{ ...btnS, padding:'5px 11px',
              background: hrsBucketFilter===null ? F.accent : F.cardHi, color: hrsBucketFilter===null ? '#04121C' : F.text }}>All · {hrsRows.length}</button>
            {HRS_BUCKET_ORDER.map(bk => {
              const n = hrsRows.filter(x=>x.bucket===bk).length; if (!n) return null;
              return (
                <button key={bk} onClick={()=>setHrsBucketFilter(f => f===bk ? null : bk)} style={{ ...btnS, padding:'5px 11px',
                  background: hrsBucketFilter===bk ? F.accent : F.cardHi, color: hrsBucketFilter===bk ? '#04121C' : F.text }}>
                  {HRS_BUCKET_LABEL[bk]} · {n}
                </button>
              );
            })}
          </div>

          {hrsVinPanel && (
            <div style={{ background:F.card, border:`1px solid ${F.amber}55`, borderRadius:10, padding:12, marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                <div style={{ fontSize:12.5, fontWeight:800 }}>VIN issues — {hrsVinIssues.length} unit{hrsVinIssues.length===1?'':'s'} not matching Geotab</div>
                <button onClick={copyVinIssues} style={{ ...btnS, padding:'4px 10px', fontSize:11 }}>📋 Copy list</button>
              </div>
              <div style={{ fontSize:11, color:F.muted, marginBottom:8 }}>
                <b>No VIN on Master</b> — fill in the VIN on Equipment Master first, nothing to match against yet.
                <b style={{ marginLeft:10 }}>VIN on file, no Geotab match</b> — the VIN is on the Master but Geotab doesn't have a device reporting that VIN; check/fix it in Geotab or impac Fleet.
              </div>
              <div style={{ maxHeight:320, overflowY:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:11.5 }}>
                  <thead><tr>
                    {['Unit','Description','Category','VIN on file','Issue'].map(l => (
                      <th key={l} style={{ textAlign:'left', padding:'5px 8px', fontSize:9.5, fontWeight:800, textTransform:'uppercase', color:F.muted, borderBottom:`1px solid ${F.line}` }}>{l}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {hrsVinIssues.map((v,i) => (
                      <tr key={i} style={{ borderBottom:`1px solid ${F.line}` }}>
                        <td style={{ padding:'5px 8px', fontWeight:800 }}>{v.unit}</td>
                        <td style={{ padding:'5px 8px' }}>{v.desc}</td>
                        <td style={{ padding:'5px 8px', color:F.muted }}>{v.cat}</td>
                        <td style={{ padding:'5px 8px', color:F.muted }}>{v.vin || '—'}</td>
                        <td style={{ padding:'5px 8px', color: v.reason==='no-vin' ? F.red : F.amber, fontWeight:700 }}>
                          {v.reason==='no-vin' ? 'No VIN on Master' : 'VIN on file, no Geotab match'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {hrsBucketPanel && masterEdit && (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:10, padding:12, marginBottom:12 }}>
              <div style={{ fontSize:12.5, fontWeight:800, marginBottom:8 }}>Category → bucket mapping</div>
              <div style={{ fontSize:11, color:F.muted, marginBottom:8 }}>Every Equipment Master category, and which of the 8 buckets it currently lands in. Move any of them — saves immediately.</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 180px', gap:'4px 10px', maxHeight:320, overflowY:'auto' }}>
                {cats.map(c => {
                  const n = normCat(c);
                  const cur = hrsBucketMap[n] || HRS_CAT_DEFAULT[n] || 'other';
                  return (
                    <Fragment key={c}>
                      <div style={{ fontSize:12, padding:'4px 0' }}>{c}</div>
                      <select value={cur} onChange={e => saveHrsBucketMap({ ...hrsBucketMap, [n]: e.target.value })} style={{ ...inp, padding:'3px 6px', fontSize:11.5 }}>
                        {HRS_BUCKETS.map(([bk,bl]) => <option key={bk} value={bk}>{bl}</option>)}
                      </select>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}

          {/* #FLEET_WEEKLYINV_STICKY_V1 (2026-09-17) — Bill: "Need Table to either stick freeze or
              repeat for each section." Bounded height + overflow:auto makes this table its own scroll
              container (same pattern Equipment Master already uses), so the shared `th` style's
              built-in position:sticky/top:0 actually has something to stick within — without a bounded
              container, sticky has no effect and just scrolls off with the rest of the page, which is
              what was happening before. The bucket/section header (RIGS · 27 · Hours, etc.) sticks
              just below the column header, so both stay visible together while scrolling a long section. */}
          <div style={{ overflowX:'auto', overflowY:'auto', maxHeight:'calc(100vh - 300px)', border:`1px solid ${F.line}`, borderRadius:10 }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr ref={hrsHdrRef}>
                  {[['Unit','left'],['Description','left'],['VIN','left'],['Assigned','left'],['Start','right'],
                    ['This week (End)','right'],['Source','left'],['Weekly Δ','right'],['MTD','right'],['QTD','right'],['YTD','right'],
                    ['Serviced','right'],['DOS','center'],['Service Due','right'],['Note','left']].map(([l,a]) => (
                    <th key={l} style={{ ...th, textAlign:a, ...(l==='Unit' ? { minWidth:104, whiteSpace:'nowrap' } : {}) }}>{l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HRS_BUCKET_ORDER.map(bk => {
                  const rows = hrsFilteredRows.filter(x => x.bucket === bk);
                  if (!rows.length) return null;
                  return (
                    <Fragment key={bk}>
                      <tr><td colSpan={15} style={{ ...td, background:F.cardHi, fontWeight:900, fontSize:11, letterSpacing:.4, textTransform:'uppercase', color:F.accent,
                        position:'sticky', top:hrsHdrH, zIndex:1 }}>
                        {HRS_BUCKET_LABEL[bk]} <span style={{ color:F.muted, fontWeight:700 }}>· {rows.length} · {bk==='trailers' ? 'RIG ASSIGNED (in the This-week column)' : hrsMetricLabel(HRS_BUCKET_METRIC[bk])}</span>
                      </td></tr>
                      {rows.map(x => {
                        const roll = hrsRollups[x.uk] || { mtd:0, qtd:0, ytd:0 };
                        return (
                          <tr key={x.uk} data-uk={x.uk} onBlur={hrsRowBlur} style={{ borderBottom:`1px solid ${F.line}` }}>
                            <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap', minWidth:104 }}>{x.r.unit}</td>
                            <td style={td}>{x.r.desc}</td>
                            <td style={{ ...td, fontSize:11, color:F.muted }}>{x.r.vin}</td>
                            <td style={{ ...td, padding:'3px 10px' }}>
                              {editable ? (
                                <input value={x.assigned} onChange={e=>setHrsAssignedOverride(x.uk, e.target.value)}
                                  title={x.assignedIsOverride ? 'Manually overridden' : "From the prior Friday's Daily Inventory"}
                                  style={{ ...inp, width:120, padding:'4px 7px', fontSize:12, ...(x.assignedIsOverride ? { borderColor:F.gold } : {}) }} />
                              ) : x.assigned}
                            </td>
                            <td style={{ ...td, textAlign:'right', color:F.muted }}>{x.start!=null ? x.start.toLocaleString() : '—'}</td>
                            <td style={{ ...td, padding:'3px 10px', textAlign:'right' }}>
                              {x.bucket === 'trailers' ? (
                                editable ? (
                                  <select value={x.rig} onChange={e=>setHrsRig(x.uk, e.target.value)}
                                    title="Rig this trailer runs with — stays until changed"
                                    style={{ ...inp, width:150, padding:'4px 6px', fontSize:12, color: x.rig ? F.text : F.muted }}>
                                    <option value="">— Rig assigned —</option>
                                    {x.rig && !hrsRigOpts.some(o => o.unit === x.rig) && <option value={x.rig}>{x.rig}</option>}
                                    {hrsRigOpts.map(o => <option key={o.unit} value={o.unit}>{o.unit}{o.desc ? ' · '+o.desc : ''}</option>)}
                                  </select>
                                ) : <span style={{ fontWeight:700 }}>{x.rig || '—'}</span>
                              ) : editable ? (
                                <input type="text" inputMode="decimal" value={x.reading==null ? '' : fmtNumInput(x.reading)}
                                  onChange={e=>setHrsReading(x.uk, numInputStrip(e.target.value))}
                                  title={x.source==='geotab' ? `Auto-filled from Geotab${x.geotabAt ? ' · '+fmtMD(new Date(x.geotabAt)) : ''} — edit to override` : (x.source==='oem' ? `Auto-filled from the ${oemLabel(x.oemBrand)} factory feed${x.oemAt ? ' · '+fmtMD(new Date(x.oemAt)) : ''} — edit to override` : undefined)}
                                  style={{ ...inp, width:92, padding:'4px 7px', fontSize:12, textAlign:'right', ...(isApiSrc(x.source) ? { borderColor:F.green } : {}) }} />
                              ) : (x.reading!=null ? x.reading.toLocaleString() : '—')}
                            </td>
                            <td style={{ ...td, padding:'3px 10px' }}>
                              {x.source==='geotab' && <span title={x.geotabAt ? `Geotab, ${fmtMD(new Date(x.geotabAt))}` : 'Geotab'} style={{ fontSize:11, color:F.green, fontWeight:800 }}>🛰 Auto</span>}
                              {x.source==='oem' && <span title={`${oemLabel(x.oemBrand)} factory feed${x.oemAt ? ', '+fmtMD(new Date(x.oemAt)) : ''}`} style={{ fontSize:11, color:F.green, fontWeight:800 }}>🛰 {oemLabel(x.oemBrand)}</span>}
                              {x.source==='manual' && <span style={{ fontSize:11, color:F.muted, fontWeight:700 }}>✎ Manual</span>}
                              {!x.source && (x.geotabStatus==='no-signal' || x.geotabStatus==='no-device') && (
                                editable ? (
                                  <select value={x.flagReason} onChange={e=>setHrsFlagReason(x.uk, e.target.value)}
                                    title={x.geotabStatus==='no-device' ? "No Geotab device matched this unit's VIN" : 'Device matched but reported nothing this week'}
                                    style={{ ...inp, width:118, padding:'3px 5px', fontSize:11, color: x.flagReason ? F.text : F.amber, borderColor: x.flagReason ? F.line : F.amber }}>
                                    <option value="">⚠ needs a look</option>
                                    <option value="bad-unit">Bad unit</option>
                                    <option value="unplugged">Unplugged</option>
                                    <option value="not-installed">Not installed</option>
                                  </select>
                                ) : <span style={{ fontSize:11, color:F.amber }}>{x.flagReason ? x.flagReason : '⚠ needs a look'}</span>
                              )}
                              {!x.source && !x.geotabStatus && (x.oemSilent
                                ? <span title="The factory feed has this machine but no fresh reading for it. Type this week's hours in." style={{ fontSize:11, color:F.amber, fontWeight:700 }}>⚠ tracker silent{x.oemSince ? ' since '+fmtMD(new Date(x.oemSince)) : ''}</span>
                                : <span style={{ fontSize:11, color:F.muted }}>—</span>)}
                            </td>
                            <td style={{ ...td, textAlign:'right', fontWeight:800, color: x.delta==null ? F.muted : (x.delta<0 ? F.red : F.text) }}>
                              {x.delta!=null ? x.delta.toLocaleString() : '—'}
                            </td>
                            <td style={{ ...td, textAlign:'right', color:F.muted, fontSize:11.5 }}>{roll.mtd.toLocaleString()}</td>
                            <td style={{ ...td, textAlign:'right', color:F.muted, fontSize:11.5 }}>{roll.qtd.toLocaleString()}</td>
                            {/* #FLEET_YTD_BASELINE_V1 — tooltip says whether this YTD is anchored to a
                                real Baseline Reading/Date on Equipment Master, or just counting from
                                whenever weekly tracking started (no baseline entered yet for this unit). */}
                            <td style={{ ...td, textAlign:'right', color:F.muted, fontSize:11.5 }}
                              title={String(x.r.hrsBaseline||'').trim()
                                ? `Baseline ${Number(x.r.hrsBaseline).toLocaleString()}${x.r.hrsBaselineDate ? ' as of '+x.r.hrsBaselineDate : ' (no date set — defaulted to 1/1/26)'}`
                                : 'No baseline set on this unit — YTD is counting from its first tracked reading'}>
                              {roll.ytd.toLocaleString()}
                            </td>
                            {/* #FLEET_SERVICE_DUE_V1 — Serviced/DOS/Note always show; Service Due only exists where a
                                rule applies (serviceRuleFor — see the constant up top). Trailers and anything else
                                with no rule get a plain "—" here but keep the Note field either way. */}
                            <td style={{ ...td, padding:'3px 10px', textAlign:'right', whiteSpace:'nowrap' }}>
                              {x.serviceRule && editable && (
                                <button onClick={()=>markServicedNow(x)}
                                  title="Serviced today — sets Serviced to this unit's current reading and DOS to today. It saves on its own."
                                  style={{ ...btnS, padding:'2px 5px', fontSize:11, marginRight:3, color:F.green, borderColor:`${F.green}66` }}>✓</button>
                              )}
                              {x.serviceRule ? (
                                editable ? (
                                  <input type="text" inputMode="decimal" value={x.serviced==null ? '' : fmtNumInput(x.serviced)}
                                    onChange={e=>setHrsServiced(x.uk, numInputStrip(e.target.value))} placeholder="—"
                                    title={`Last serviced reading, in ${hrsMetricLabel(x.serviceRule.metric).toLowerCase()}`}
                                    style={{ ...inp, width:74, padding:'4px 6px', fontSize:12, textAlign:'right' }} />
                                ) : (x.serviced!=null ? x.serviced.toLocaleString() : '—')
                              ) : <span style={{ color:F.muted }}>—</span>}
                            </td>
                            {/* #FLEET_WEEKLYINV_DOS_TEXT_V1 (2026-09-17) — Bill: "Date follow MM/DD/YY sequence."
                                A native <input type="date"> always renders the browser's own 4-digit-year format
                                and can't be overridden — swapped for a free-typed text field, same MM/DD/YY
                                convention the Inspections tab's date fields already use. fmtDate reads either
                                MM/DD/YY or an old ISO value fine, so nothing already saved needs fixing up. */}
                            <td style={{ ...td, padding:'3px 10px', textAlign:'center' }}>
                              {x.serviceRule ? (
                                editable ? (
                                  <input value={svcNormDos(x.dos)} onChange={e=>setHrsDos(x.uk, e.target.value)} placeholder="MM/DD/YY"
                                    title="Date of Service — when that last-serviced reading was taken"
                                    style={{ ...inp, width:88, padding:'4px 6px', fontSize:11.5, textAlign:'center' }} />
                                ) : (x.dos ? fmtDate(x.dos) : '—')
                              ) : <span style={{ color:F.muted }}>—</span>}
                            </td>
                            <td style={{ ...td, textAlign:'right', fontWeight: (x.dueFlag || x.dueSoon) ? 900 : 700,
                              color: x.dueFlag ? F.red : (x.dueSoon ? F.amber : (x.serviceRule ? F.text : F.muted)) }}
                              title={x.serviceRule && (x.dueFlag || x.dueSoon) ? `${svcSummary(x.svc, x.serviceRule)} — judged on ${x.end != null ? "this week's reading" : (x.knownAt ? 'the last reading on file, week of ' + fmtDate(x.knownAt) : 'DOS')}` : x.serviceRule ? (x.due!=null ? `Due at ${x.due.toLocaleString()} ${hrsMetricLabel(x.serviceRule.metric)} — every ${x.serviceRule.interval.toLocaleString()} ${hrsMetricLabel(x.serviceRule.metric).toLowerCase()}` : `Every ${x.serviceRule.interval.toLocaleString()} ${hrsMetricLabel(x.serviceRule.metric).toLowerCase()} — enter a Serviced reading to compute Due`) : 'No service schedule for this unit'}>
                              {x.serviceRule ? (x.due!=null ? <>{x.dueFlag && '⚠ '}{x.dueSoon && '◔ '}{x.due.toLocaleString()}</> : ((x.dueFlag || x.dueSoon) ? '⚠' : '—')) : '—'}
                              {x.serviceRule && (x.dueFlag || x.dueSoon) && <div style={{ fontSize:9.5, fontWeight:800, whiteSpace:'nowrap' }}>{svcShort(x.svc, x.serviceRule)}</div>}
                            </td>
                            <td style={{ ...td, padding:'3px 10px' }}>
                              {editable ? (
                                <input value={x.note||''} onChange={e=>setHrsNote(x.uk, e.target.value)} placeholder="Note…"
                                  style={{ ...inp, width:150, padding:'4px 7px', fontSize:11.5 }} />
                              ) : (x.note || '—')}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
                {!hrsFilteredRows.length && (
                  <tr><td colSpan={15} style={{ ...td, textAlign:'center', color:F.muted, padding:20 }}>No units match.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}

      {/* ═══════════════ RENTAL / RPO ═══════════════ */}
      {/* #FLEET_RENTAL_V2 — Bill, 2026-08-21: "should this be pipped into Equipment master and then
          follow through to Daily to be assigned to a JOB." This tab is now a filtered view over the
          Equipment Master (active rows with rentalType set) instead of a separate list — a saved
          rental IS a Master record, so it's immediately assignable on Daily and costed everywhere
          else. Monthly Charge is the field Bill types; Weekly and the Daily rate (ducDay) are derived
          — see rentalRates() above. A red "R" badge marks these on the Daily grid, and this same list
          feeds the Owner Dash tile. */}
      {view === 'rental' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:900 }}>Rental / RPO list</div>
            <div style={{ fontSize:11.5, color:F.muted }}>Filtered from the Equipment Master · {rentalRecords.length} item{rentalRecords.length===1?'':'s'}</div>
            <div style={{ flex:1 }} />
            {rentalRecords.length > 0 && <button onClick={downloadRentalCsv} style={btnS}>Print / CSV</button>}
            {!masterEdit && <span style={{ fontSize:11.5, color:F.muted, border:`1px solid ${F.line}`, borderRadius:8, padding:'6px 10px' }}>View only — the master is Owner / Fleet Manager</span>}
            {masterEdit && <button onClick={()=>setRentalEdit({ unit:'', rentalType:'Rental', rentalVendor:'', desc:'', vin:'', monthlyRate:'', rentalStart:'', rentalDue:'', notes:'' })} style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add item</button>}
          </div>

          {rentalEdit && (() => {
            const derived = rentalRates(rentalEdit.monthlyRate);
            const v = unitVsVin(rentalEdit.unit, '');
            return (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:10 }}>{rentalEdit._orig ? `Edit ${rentalEdit._orig.unit}` : 'Add Rental/RPO item'}</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
                <div>
                  <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Unit # (format RNN-XXXX)</div>
                  <input value={rentalEdit.unit==null?'':rentalEdit.unit} onChange={e=>setRentalEdit(d=>({ ...d, unit:e.target.value }))} style={inp} />
                </div>
                <div>
                  <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>Rental or RPO</div>
                  <select value={rentalEdit.rentalType||'Rental'} onChange={e=>setRentalEdit(d=>({ ...d, rentalType:e.target.value }))} style={inp}>
                    <option value="Rental">Rental</option>
                    <option value="RPO">RPO (rent-to-purchase)</option>
                  </select>
                </div>
                {[['rentalVendor','Vendor / source'],['desc','Description'],['vin','Serial / VIN'],['monthlyRate','Monthly Charge ($)'],   // #FLEET_RENTAL_VIN_V1 — Bill: RPO units need their VIN on the Master (Voyager / Geotab match by it)
                  ['rentalStart','Start date  MM/DD/YY'],['rentalDue','Matures / return date  MM/DD/YY'],['notes','Notes']].map(([k,l]) => (
                  <div key={k}>
                    <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}</div>
                    <input value={rentalEdit[k]==null?'':rentalEdit[k]} onChange={e=>setRentalEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} />
                  </div>
                ))}
              </div>
              <div style={{ marginTop:10, fontSize:11.5, color:F.muted }}>
                Auto-calculated from Monthly Charge — <b style={{ color:F.text }}>{money(derived.weekly)}/week</b>, <b style={{ color:F.text }}>{money(derived.daily)}/day</b> (Monthly × 12 ÷ 52 ÷ 5). The daily figure is what Daily Inventory and every cost report reads.
              </div>
              {rentalEdit.unit && (
                <div style={{ marginTop:6, fontSize:11.5, fontWeight:700, color: v.level==='bad'?F.red:v.level==='warn'?F.amber:F.muted }}>
                  Unit # format: {v.msg}
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                <button onClick={saveRentalItem} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
                <button onClick={()=>setRentalEdit(null)} style={{ ...btn }}>Close without saving</button>
                {rentalEdit._orig && rentalEdit.rentalType==='RPO' && (
                  <button onClick={()=>transitionRentalToMaster(rentalEdit._orig)} style={{ ...btn, color:F.green, borderColor:`${F.green}66` }}>Matured — move to owned…</button>
                )}
                {rentalEdit._orig && <button onClick={()=>returnRentalItem(rentalEdit._orig)} style={{ ...btn, color:F.red, borderColor:`${F.red}66` }}>Returned to vendor…</button>}
              </div>
            </div>
            );
          })()}

          {!rentalRecords.length ? (
            <div style={{ fontSize:12.5, color:F.muted }}>Nothing on the Rental/RPO list right now.</div>
          ) : (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, textAlign:'center' }}>Unit</th><th style={th}>Type</th><th style={th}>Vendor / source</th>
                  <th style={th}>Description</th><th style={{ ...th, textAlign:'right' }}>Monthly</th>
                  <th style={{ ...th, textAlign:'right' }}>Weekly</th><th style={{ ...th, textAlign:'right' }}>Daily</th>
                  <th style={{ ...th, textAlign:'right' }}>Total paid</th>
                  <th style={{ ...th, textAlign:'center' }}>Start</th><th style={{ ...th, textAlign:'center' }}>Matures / return</th>
                </tr></thead>
                <tbody>
                  {rentalRecords.map((x,i) => (
                    <tr key={i} onClick={()=>masterEdit && setRentalEdit({ unit:x.unit, rentalType:x.rentalType||'Rental', rentalVendor:x.rentalVendor||'', desc:x.desc||'', vin:x.vin||'', monthlyRate:x.monthlyRate||'', rentalStart:x.rentalStart||'', rentalDue:x.rentalDue||'', notes:x.notes||'', _orig:x })}
                      style={{ borderTop:`1px solid ${F.line}`, cursor:masterEdit?'pointer':'default' }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center' }}>{x.unit}</td>
                      <td style={{ ...td, color: x.rentalType==='RPO'?F.gold:F.muted, fontWeight:800 }}>{x.rentalType||'Rental'}</td>
                      <td style={{ ...td, color:F.muted }}>{x.rentalVendor||'—'}</td>
                      <td style={{ ...td }}>{x.desc||'—'}</td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={num(x.monthlyRate)} /></td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={num(x.weeklyRate)} dim /></td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={rate(x)} dim /></td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={rentalPaidToDate(x)} dim /></td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.rentalStart||'—'}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.rentalDue||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Fragment>
      )}

      {/* ═══════════════ IN SHOP ═══════════════ */}
      {/* #FLEET_INSHOP_V1 — Bill, 2026-08-21: "In Shop 'Down equip' list pending.. see attached report
          bob makes every week, he would need to complete and make notes from days down etc. so a
          report to combined Rental & units down to be developed for weekly review inside of Fleet."
          Modeled directly on Bob's own Friday-meeting spreadsheet (Unit / Description / Diagnosis /
          Date In / Days Down / Est. Date Out / Mechanic) — this REPLACES that spreadsheet rather than
          exporting to match it. Unit/Description/Date In/Days Down are already known from the same
          shop digest the Owner Dash "In Shop" tile reads; Diagnosis/Mechanic/Est-Out are the only
          fields Bob actually has to type, right here instead of in Excel. */}
      {view === 'inshop' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:900 }}>In Shop — down equipment</div>
              <div style={{ fontSize:11.5, color:F.muted }}>
                {shopSum && shopSum.at ? `as of ${new Date(shopSum.at).toLocaleString()}` : 'No digest yet'} · {shopRows.length} unit{shopRows.length===1?'':'s'} down
                {shopRows.some(x => x.thirdParty) && ` · ${shopRows.filter(x => x.thirdParty).length} at a 3rd-party shop`}
                {shopRows.some(x => x.hot) && <b style={{ color:F.gold }}> · 🔥 {shopRows.filter(x => x.hot).length} hot</b>}
                {hotSetter && <span> · tap 🔥 on a row to put the team on it</span>}
              </div>
            </div>
            <div style={{ flex:1 }} />
            {yardOpsEdit && <button onClick={()=>setShopEdit({ _new:true, manual:true, unit:'', desc:'', autoDateIn:'', dateIn:iso(new Date()), days:1, diagnosis:'', mechanic:'', estOut:'', thirdParty:true })}
              title="Add a unit that is down but not coded A-SHOP on Daily — e.g. out at a 3rd-party shop" style={{ ...btnS, color:F.accent, borderColor:`${F.accent}66` }}>+ Add unit</button>}
            <button onClick={loadShopSum} style={btnS}>Refresh</button>
            <button onClick={printShopReport} title="Bob's weekly Fleet Maintenance Report layout — letter, landscape" style={{ ...btnS, color:F.gold, borderColor:`${F.gold}55` }}>🖨 Print Report</button>
            <button onClick={downloadInShopCsv} title="Down equipment + Rental/RPO as one spreadsheet file" style={btnS}>CSV</button>
          </div>

          {shopEdit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              {/* #FLEET_SHOP3P_V1 — every field of the row is editable; "+ Add unit" opens this same panel empty. */}
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:10 }}>
                {shopEdit._new ? 'Add a unit to In Shop' : `${shopEdit.unit} — ${shopEdit.desc}`}
                {shopEdit.manual && !shopEdit._new && <span style={{ marginLeft:8, fontSize:10, fontWeight:800, color:F.muted }}>ADDED BY HAND</span>}
              </div>
              {(() => {
                const lab = { fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 };
                const pick = shopEdit._new ? (active||[]).find(r => unitKey(r.unit) === unitKey(shopEdit.unit)) : null;
                return (
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px,1fr))', gap:10 }}>
                    {shopEdit._new && (
                      <div>
                        <div style={lab}>Unit #</div>
                        <input list="fleet-master-unit-list" value={shopEdit.unit} onChange={e=>setShopEdit(d=>({ ...d, unit:e.target.value }))} placeholder="22-9052" autoFocus style={inp} />
                        <div style={{ fontSize:11, marginTop:3, color: pick ? F.text : F.muted }}>{pick ? (pick.desc || '—') : (shopEdit.unit ? 'not on the Master yet' : 'pick from the Equipment Master')}</div>
                      </div>
                    )}
                    <div>
                      <div style={lab}>Date In{shopEdit.manual ? '' : ' (blank = from Daily)'}</div>
                      <input type="date" value={shopEdit.dateIn||''} max={iso(new Date())} onChange={e=>setShopEdit(d=>({ ...d, dateIn:e.target.value }))} style={{ ...inp, colorScheme:'dark' }} />
                    </div>
                    {[['diagnosis','Diagnosis'],['mechanic','Mechanic / shop'],['estOut','Est. Date Out  MM/DD/YY']].map(([k,l]) => (
                      <div key={k}>
                        <div style={lab}>{l}</div>
                        <input value={shopEdit[k]==null?'':shopEdit[k]} onChange={e=>setShopEdit(d=>({ ...d, [k]:e.target.value }))} style={inp} />
                      </div>
                    ))}
                  </div>
                );
              })()}
              <label style={{ display:'inline-flex', alignItems:'center', gap:7, marginTop:10, fontSize:12.5, fontWeight:700, cursor:'pointer' }}>
                <input type="checkbox" checked={!!shopEdit.thirdParty} onChange={e=>setShopEdit(d=>({ ...d, thirdParty:e.target.checked }))} />
                At a 3rd-party shop <span style={{ fontWeight:500, color:F.muted }}>— put the shop's name in Mechanic / shop</span>
              </label>
              <div style={{ marginTop:8, fontSize:11.5, color:F.muted }}>
                {shopEdit.manual
                  ? 'Added by hand — it stays on this list until it is marked Complete or removed. Days Down counts from Date In.'
                  : `Daily has it in the shop since ${fmtDate(shopEdit.autoDateIn)}. Leave Date In blank to keep that, or set the real date — Days Down follows it.`}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12, flexWrap:'wrap' }}>
                <button onClick={saveShopNote} style={{ ...btn, background:F.accent, color:'#04121C' }}>{shopEdit._new ? 'Add to In Shop' : 'Save'}</button>
                <button onClick={()=>setShopEdit(null)} style={{ ...btn }}>Close without saving</button>
                {!shopEdit._new && shopEdit.manual && (
                  <button onClick={()=>{ if (window.confirm(`Remove ${shopEdit.unit} from In Shop?\n\nNothing goes to Shop History — use ✓ Complete for a finished repair.`)) clearShopNote(shopEdit.unit); }} style={{ ...btn, color:F.red, borderColor:`${F.red}66` }}>Remove from In Shop</button>
                )}
                {masterEdit && !shopEdit._new && !shopEdit.manual && (shopEdit.diagnosis || shopEdit.mechanic || shopEdit.estOut || shopEdit.dateIn || shopEdit.thirdParty) && (
                  <button onClick={()=>clearShopNote(shopEdit.unit)} style={{ ...btn, color:F.red, borderColor:`${F.red}66` }}>Clear note</button>
                )}
              </div>
            </div>
          )}

          {(!shopSum && !shopRows.length) ? (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'18px 20px', fontSize:12.5, color:F.muted }}>
              No shop digest yet. This fills in the first time Daily is saved, or from{' '}
              <b style={{ color:F.text, cursor:'pointer' }} onClick={()=>setView('idle')}>Idle 90 → Read last 14 weeks</b>.
            </div>
          ) : !shopRows.length ? (
            <div style={{ fontSize:12.5, color:F.green, fontWeight:700 }}>✓ Nothing in the shop right now.</div>
          ) : (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', marginBottom:16 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, textAlign:'center' }}>Unit</th><th style={th}>Description</th>
                  <th style={{ ...th, textAlign:'center' }}>Date In</th><th style={{ ...th, textAlign:'right' }}>Days Down</th>
                  <th style={th}>Diagnosis</th><th style={{ ...th, textAlign:'center' }}>Est. Date Out</th><th style={th}>Mechanic</th>
                  <th style={{ ...th, textAlign:'right' }}>Daily Rate</th><th style={{ ...th, textAlign:'center' }}>Complete</th>
                </tr></thead>
                <tbody>
                  {shopRows.map((x,i) => (
                    <tr key={x.k || i} data-shop-unit={x.unit} onClick={()=>yardOpsEdit && setShopEdit(shopEditOf(x))}
                      style={{ borderTop:`1px solid ${F.line}`, cursor:yardOpsEdit?'pointer':'default', background: x.hot ? `${F.gold}1f` : x.days>=30?`${F.red}0d`:'transparent',
                        boxShadow: x.hot ? `inset 4px 0 0 ${F.gold}` : 'none' }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center', whiteSpace:'nowrap' }}>
                        {/* #FLEET_SHOP_HOT_V1 — Bob's 🔥: lit = HOT, dim = tap to set (Bob only) */}
                        {(x.hot || hotSetter) && (
                          <button onClick={(e)=>{ e.stopPropagation(); toggleShopHot(x); }} disabled={x.hot ? !hotClearer : !hotSetter}
                            title={x.hot ? `HOT — set by ${String(x.hotBy||'').split('@')[0] || 'Bob'}${x.hotAt ? ' ' + fmtDate(String(x.hotAt).slice(0,10)) : ''}${x.hotWhy ? ` · ${x.hotWhy}` : ''}${hotClearer ? ' — tap to clear' : ''}` : 'Mark HOT — puts the fleet team on this unit first'}
                            style={{ background:'none', border:'none', padding:'0 4px 0 0', fontSize:15, lineHeight:1, verticalAlign:'-2px',
                              cursor: (x.hot ? hotClearer : hotSetter) ? 'pointer' : 'default', opacity: x.hot ? 1 : .28, filter: x.hot ? 'drop-shadow(0 0 4px rgba(231,183,59,.8))' : 'grayscale(1)' }}>🔥</button>
                        )}
                        {x.unit}<TelemFlag f={telemFlagFor(tflags, x.unit)} style={{ display:'flex', justifyContent:'center', marginTop:2 }} /></td>
                      <td style={{ ...td }}>{x.desc||'—'}{x.hot && <div style={{ fontSize:10.5, fontWeight:800, color:F.gold, marginTop:2 }}>🔥 HOT{x.hotWhy ? ` — ${x.hotWhy}` : ''}</div>}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted, whiteSpace:'nowrap' }} title={x.manual ? 'Added by hand' : x.dateInSet ? `Typed — Daily says ${fmtDate(x.autoDateIn)}` : 'From Daily'}>{fmtDate(x.dateIn)}{(x.manual || x.dateInSet) ? <span style={{ marginLeft:3, fontSize:10, color:F.accent }}>✎</span> : null}</td>
                      <td style={{ ...td, textAlign:'right', fontWeight:800, color: x.days>=30?F.red:F.amber }}>{x.days}</td>
                      <td style={{ ...td, color: x.diagnosis?F.text:F.muted }}>{x.diagnosis || '—'}{(x.yardNote || x.yardCity) && <div title="What the yard typed on this unit's A-SHOP line on Daily Inventory (Notes · City)" style={{ fontSize:10.5, color:F.muted, marginTop:2 }}>Yard: {[x.yardNote, x.yardCity].filter(Boolean).join(' · ')}</div>}</td>
                      {(() => { const sl = shopSlip(x), late = sl && sl.state === 'late', bad = sl && sl.state === 'bad', due = sl && sl.state === 'today';   // #FLEET_SHOP_SLIP_V1
                        const chip = { marginLeft:5, fontSize:10, fontWeight:800, color:'#fff', background:F.red, borderRadius:8, padding:'1px 6px' };
                        return <td style={{ ...td, textAlign:'center', whiteSpace:'nowrap', color: late||bad ? F.red : due ? F.amber : F.muted, fontWeight: late||bad||due ? 800 : 400 }}
                          title={bad ? 'Est. Done is before Date In — check the date' : late ? `Slipped ${sl.days} day${sl.days===1?'':'s'} past Est. Done` : due ? 'Due today' : ''}>
                          {x.estOut ? fmtDate(x.estOut) : '—'}{late && <span style={chip}>+{sl.days}d</span>}{bad && <span style={chip}>CHECK</span>}</td>; })()}
                      <td style={{ ...td, color:F.muted }}>{x.mechanic || (x.thirdParty ? '' : '—')}{x.thirdParty && <span style={{ marginLeft: x.mechanic ? 6 : 0, fontSize:9.5, fontWeight:800, letterSpacing:.3, color:F.amber, border:`1px solid ${F.amber}66`, borderRadius:4, padding:'1px 5px', whiteSpace:'nowrap' }}>3RD PARTY</span>}</td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={num(x.rate)} dim /></td>
                      <td style={{ ...td, textAlign:'center' }}>
                        {yardOpsEdit && <button onClick={(e)=>{ e.stopPropagation(); markShopComplete(x); }} style={{ ...btnS, color:F.green, borderColor:`${F.green}66` }} title="Archive to Shop History and drop off this list">✓ Complete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Combined report per Bill: same weekly review pulls Rental/RPO alongside down equipment. */}
          <div style={{ fontSize:12.5, fontWeight:900, marginBottom:8 }}>Rental / RPO <span style={{ fontWeight:600, color:F.muted }}>· {rentalRecords.length} item{rentalRecords.length===1?'':'s'} — edit from the Rental/RPO tab</span></div>
          {!rentalRecords.length ? (
            <div style={{ fontSize:12.5, color:F.muted }}>Nothing on the Rental/RPO list right now.</div>
          ) : (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, textAlign:'center' }}>Unit</th><th style={th}>Type</th><th style={th}>Vendor</th>
                  <th style={th}>Description</th><th style={{ ...th, textAlign:'right' }}>Monthly</th>
                  <th style={{ ...th, textAlign:'center' }}>Start</th><th style={{ ...th, textAlign:'center' }}>Matures</th>
                </tr></thead>
                <tbody>
                  {rentalRecords.map((x,i) => (
                    <tr key={i} onClick={()=>masterEdit && setView('rental')} style={{ borderTop:`1px solid ${F.line}`, cursor:masterEdit?'pointer':'default' }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center' }}>{x.unit}</td>
                      <td style={{ ...td, color: x.rentalType==='RPO'?F.gold:F.muted, fontWeight:800 }}>{x.rentalType||'Rental'}</td>
                      <td style={{ ...td, color:F.muted }}>{x.rentalVendor||'—'}</td>
                      <td style={{ ...td }}>{x.desc||'—'}</td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={num(x.monthlyRate)} /></td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.rentalStart||'—'}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.rentalDue||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* #FLEET_SHOPREPORT_V1 — the typed footer of the printed Fleet Maintenance Report. */}
          <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', marginTop:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:10 }}>
              <div style={{ fontSize:12.5, fontWeight:900 }}>Report notes <span style={{ fontWeight:600, color:F.muted }}>· PO spending + discussion items — print at the bottom of the report</span></div>
              <div style={{ flex:1 }} />
              {shopReport.updatedAt && <div style={{ fontSize:11, color:F.muted }}>saved {new Date(shopReport.updatedAt).toLocaleString()}{shopReport.by ? ` · ${shopReport.by}` : ''}</div>}
              {masterEdit && !shopReportDraft && <button onClick={()=>setShopReportDraft({ po:shopReport.po||'', notes:shopReport.notes||'' })} style={btnS}>Edit</button>}
            </div>
            {shopReportDraft ? (
              <Fragment>
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>PO spending</div>
                <input value={shopReportDraft.po} onChange={e=>setShopReportDraft(d=>({ ...d, po:e.target.value }))} placeholder="$4,425,235" style={{ ...inp, maxWidth:220 }} />
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, margin:'10px 0 3px' }}>Discussion questions</div>
                <textarea value={shopReportDraft.notes} onChange={e=>setShopReportDraft(d=>({ ...d, notes:e.target.value }))} rows={7}
                  placeholder={'One item per line — truck replacement, units to sell… (camera and GPS counts print automatically)'}
                  style={{ ...inp, resize:'vertical', fontFamily:'inherit', lineHeight:1.4 }} />
                <div style={{ display:'flex', gap:8, marginTop:10, flexWrap:'wrap' }}>
                  <button onClick={saveShopReport} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
                  <button onClick={()=>setShopReportDraft(null)} style={{ ...btn }}>Close without saving</button>
                </div>
              </Fragment>
            ) : (
              <div style={{ fontSize:12.5 }}>
                {/* #FLEET_SHOPREPORT_TELEM_V1 — counted from the Master, never typed */}
                <div style={{ marginBottom:8, paddingBottom:8, borderBottom:`1px solid ${F.line}` }}>
                  {telemLines.lines.map((l,i) => <div key={i}>{l}</div>)}
                  <div style={{ fontSize:11, color:F.muted, fontStyle:'italic' }}>{telemLines.foot} — change a unit's Camera / GPS on the Equipment Master tab</div>
                </div>
                <div><span style={{ color:F.muted, fontWeight:800 }}>PO SPENDING = </span>{shopReport.po || <span style={{ color:F.muted }}>—</span>}</div>
                <div style={{ marginTop:6, whiteSpace:'pre-wrap', color: shopReport.notes ? F.text : F.muted }}>{shopReport.notes || 'No discussion items yet.'}</div>
              </div>
            )}
          </div>
        </Fragment>
      )}

      {/* #FLEET_LIBRARY_V1 (2026-10-29) — Bill: "Just a fleet Library to store docs." OEM / Telematics folder = Owner / Fleet manager only */}
      {view === 'library' && (
        <div style={{ padding:'4px 0 24px' }}>
          <FleetLibrary sg={sg} ss={ss} supaUrl={supaUrl} supaKey={supaKey} getToken={getToken} userUpn={upn} editable={masterEdit} manager={masterEdit} />
        </div>
      )}

      {/* ═══════════════ SHOP HISTORY ═══════════════ */}
      {/* #FLEET_SHOPHISTORY_V1 (2026-09-02) — Bill: "should move down into another list to track,
          stay with unit activity as this would be useful to track units that are down often." Every
          completed In Shop record lands here via the "✓ Complete" button — Unit/Date In/Date Out/
          Days Down/Diagnosis/Mechanic/Down Cost, sortable, searchable by unit. */}
      {view === 'shophistory' && (() => {
        const ql = shQ.trim().toLowerCase();
        const filtered = shopHistory.filter(r => !ql || `${r.unit} ${r.desc||''} ${r.diagnosis||''} ${r.mechanic||''}`.toLowerCase().includes(ql));
        const dir = shSort.dir === 'desc' ? -1 : 1;
        const cmpDefault = (a,b) => String(b.dateOut||'').localeCompare(String(a.dateOut||''));   // newest first
        const cmp = shSort.key === 'days' ? (a,b) => ((Number(a.days)||0) - (Number(b.days)||0)) * dir
                  : shSort.key === 'cost' ? (a,b) => ((Number(a.cost)||0) - (Number(b.cost)||0)) * dir
                  : shSort.key === 'unit' ? (a,b) => String(a.unit||'').localeCompare(String(b.unit||'')) * dir
                  : shSort.key === 'dateIn' ? (a,b) => String(a.dateIn||'').localeCompare(String(b.dateIn||'')) * dir
                  : shSort.key === 'dateOut' ? (a,b) => String(a.dateOut||'').localeCompare(String(b.dateOut||'')) * dir
                  : cmpDefault;
        const list = [...filtered].sort(cmp);
        const totalDays = filtered.reduce((s,r) => s + (Number(r.days)||0), 0);
        const totalCost = filtered.reduce((s,r) => s + (Number(r.cost)||0), 0);
        const heads = [['unit','Unit','center'],['desc','Description','left'],['dateIn','Date In','center'],['dateOut','Date Out','center'],
          ['days','Days Down','right'],['diagnosis','Diagnosis','left'],['mechanic','Mechanic','left'],['cost','Down Cost','right']];
        return (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:13, fontWeight:900 }}>Shop History — completed visits</div>
              <div style={{ fontSize:11.5, color:F.muted }}>{list.length} record{list.length===1?'':'s'} · {totalDays} total day{totalDays===1?'':'s'} down · <Money v={totalCost} dim /> total</div>
            </div>
            <div style={{ flex:1 }} />
            <input placeholder="Search unit, diagnosis, mechanic…" value={shQ} onChange={e=>setShQ(e.target.value)} style={{ ...inp, width:220 }} />
          </div>
          {!list.length ? (
            <div style={{ fontSize:12.5, color:F.muted }}>{shopHistory.length ? 'No records match that search.' : 'Nothing completed yet — Mark Complete on an In Shop row archives it here.'}</div>
          ) : (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {heads.map(([key,label,align]) => (
                    <th key={key} onClick={()=>setShSort(s => s.key===key ? (s.dir==='asc' ? {key,dir:'desc'} : {key:'',dir:'asc'}) : {key,dir:'asc'})}
                      title={shSort.key===key ? 'Click again to reverse, once more for newest-first' : `Sort by ${label}`}
                      style={{ ...th, textAlign:align, cursor:'pointer', userSelect:'none', color: shSort.key===key ? F.accent : F.muted }}>
                      {label}<span style={{ marginLeft:4, fontSize:8.5, opacity: shSort.key===key ? 1 : .35 }}>
                        {shSort.key===key ? (shSort.dir==='asc' ? '▲' : '▼') : '↕'}</span>
                    </th>
                  ))}
                  {yardOpsEdit && <th style={{ ...th, textAlign:'center' }}>Undo</th>}
                </tr></thead>
                <tbody>
                  {list.map((r,i) => (
                    <tr key={i} style={{ borderTop:`1px solid ${F.line}` }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center' }}>{r.unit}</td>
                      <td style={{ ...td, color:F.muted, maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={unitDescByKey[unitKey(r.unit)] || r.desc || ''}>{unitDescByKey[unitKey(r.unit)] || r.desc || '—'}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted, whiteSpace:'nowrap' }}>{fmtDate(r.dateIn)}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted, whiteSpace:'nowrap' }}>{fmtDate(r.dateOut)}</td>
                      <td style={{ ...td, textAlign:'right', fontWeight:800, color: r.days>=30?F.red:F.text }}>{r.days}</td>
                      <td style={{ ...td, color: r.diagnosis?F.text:F.muted }}>{r.diagnosis || '—'}</td>
                      <td style={{ ...td, color:F.muted }}>{r.mechanic || '—'}</td>
                      <td style={{ ...td, textAlign:'right' }}><Money v={num(r.cost)} dim /></td>
                      {yardOpsEdit && <td style={{ ...td, textAlign:'center' }}>
                        <button onClick={()=>undoShopComplete(r)} style={{ ...btnS, color:F.amber, borderColor:`${F.amber}66` }} title="Remove from Shop History and drop the completion, so it's back in In Shop">Undo</button>
                      </td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Fragment>
        );
      })()}

      {/* ═══════════════ REVIEW ═══════════════ */}
      {/* ═══════════════ IDLE 90 ═══════════════ */}
      {view === 'idle' && (
        <Fragment>
          <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:12, flexWrap:'wrap' }}>
            <div>
              <div style={{ fontSize:14, fontWeight:900 }}>Idle {IDLE_TARGET} days</div>
              <div style={{ fontSize:11.5, color:F.muted }}>Units that have not touched a job — the keep-or-sell list</div>
            </div>
            <div style={{ flex:1 }} />
            {idleScan && <span style={{ fontSize:11, color:F.muted }}>read {idleScan.at}</span>}
            <button onClick={scanIdle} style={{ ...btn, background:F.accent, color:'#04121C' }}>
              {idleScan ? 'Re-read history' : 'Read last 14 weeks'}
            </button>
          </div>

          {!idleScan && (
            <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'18px 20px', fontSize:12.5, color:F.muted }}>
              This reads the last 14 stored weeks of Daily and works out, per unit, the last day it was on a real job.
              Nothing is written. Press <b style={{ color:F.text }}>Read last 14 weeks</b> to run it.
            </div>
          )}

          {idleReport && (
            <Fragment>
              {/* coverage first — the answer is worth exactly as much as the history behind it */}
              <div style={{ background: idleReport.coverage >= IDLE_TARGET ? F.card : `${F.amber}12`,
                border:`1px solid ${idleReport.coverage >= IDLE_TARGET ? F.line : `${F.amber}66`}`,
                borderRadius:12, padding:'11px 14px', marginBottom:12, fontSize:12.5 }}>
                {idleReport.coverage >= IDLE_TARGET ? (
                  <span><b style={{ color:F.green }}>{idleReport.coverage} days</b> of history on file across {idleReport.dates} logged days — enough to prove a {IDLE_TARGET}-day idle run.</span>
                ) : (
                  <span style={{ color:F.amber, fontWeight:700 }}>
                    Only {idleReport.coverage} days of history on file ({idleReport.dates} logged days).
                    <span style={{ color:F.text, fontWeight:400 }}> Nothing can be <i>proven</i> idle for {IDLE_TARGET} days yet — the list below is “no job seen in the window”, which is a starting point, not a verdict. It becomes real once Daily has run for {IDLE_TARGET} days.</span>
                  </span>
                )}
              </div>

              <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap' }}>
                {[['Flagged units', String(idleReport.flagged.length), F.amber],
                  ['Daily rate at risk', money(idleReport.burn), F.red],
                  ['Per 30 days', money(idleReport.burn*30), F.red]].map(([l,v,c]) => (
                  <div key={l} style={{ flex:'1 1 160px', background:F.card, border:`1px solid ${F.line}`, borderLeft:`3px solid ${c}`, borderRadius:10, padding:'8px 12px' }}>
                    <div style={{ fontSize:10, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted }}>{l}</div>
                    <div style={{ fontSize:17, fontWeight:900, color:c }}>{v}</div>
                  </div>
                ))}
              </div>

              <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'calc(100vh - 356px)' }}>
                <RivetCorners tone="silver" kind="rivet" />
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead><tr>
                    <th style={th}>Unit</th>
                    <th style={th}>Description</th>
                    <th style={th}>Category</th>
                    <th style={{ ...th, textAlign:'right' }}>Daily rate</th>
                    <th style={{ ...th, textAlign:'center' }}>Last on a job</th>
                    <th style={{ ...th, textAlign:'right' }}>Days</th>
                    <th style={{ ...th, textAlign:'center' }}>Idle / Shop / OH</th>
                    <th style={{ ...th, textAlign:'right' }}>Non-job burn</th>
                  </tr></thead>
                  <tbody>
                    {idleReport.flagged.map(x => (
                      <tr key={unitKey(x.unit)} style={{ borderTop:`1px solid ${F.line}` }}>
                        <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap' }}>{x.unit}</td>
                        <td style={{ ...td, color:F.muted, maxWidth:300, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.desc}</td>
                        <td style={{ ...td, color:F.muted, whiteSpace:'nowrap' }}>{x.cat}</td>
                        <td style={{ ...td, textAlign:'right', fontWeight:800, fontVariantNumeric:'tabular-nums' }}>{x.rate?money(x.rate):'—'}</td>
                        <td style={{ ...td, textAlign:'center', color: x.lastJob?F.text:F.muted, whiteSpace:'nowrap' }}>{x.lastJob || 'never in window'}</td>
                        <td style={{ ...td, textAlign:'right', fontWeight:900, whiteSpace:'nowrap',
                          color: x.daysIdle >= IDLE_TARGET ? F.red : x.daysIdle >= 60 ? F.amber : F.muted }}>
                          {x.proven ? '' : '≥ '}{x.daysIdle}
                        </td>
                        <td style={{ ...td, textAlign:'center', fontSize:11.5, color:F.muted, whiteSpace:'nowrap' }}>
                          <span style={{ color:F.muted }}>{x.nIdle}</span> / <span style={{ color:F.orange }}>{x.nShop}</span> / <span style={{ color:F.violet }}>{x.nOh}</span>
                        </td>
                        <td style={{ ...td, textAlign:'right', color:F.red, fontWeight:800, fontVariantNumeric:'tabular-nums' }}>{x.burn?money(x.burn):'—'}</td>
                      </tr>
                    ))}
                    {!idleReport.flagged.length && (
                      <tr><td colSpan={8} style={{ ...td, color:F.green, fontWeight:700, padding:'18px 12px' }}>Every unit in the stored history has been on a job. Nothing to review.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Fragment>
          )}
        </Fragment>
      )}

      {view === 'flags' && (
        <Fragment>
          {/* #PM_EQFLAG_V1 — PM-raised "not mine / wrong allocation" flags. Started report-only (Bill:
              "lets try report 1st") — #PM_EQFLAG_CLEAR_V1 (2026-08-24) added "Clear from job", which
              un-assigns the unit on the exact flagged day(s) so it drops to blank/unassigned. Putting
              it on the CORRECT job is still a manual step on Daily Inventory (which job it belongs on
              isn't something a flag can know) — Mark Reviewed alone still just dismisses the report,
              same as before, for flags that don't need an actual data change. */}
          <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:3, flexWrap:'wrap' }}>
              <div style={{ fontSize:13.5, fontWeight:900 }}>PM equipment flags</div>
              <div style={{ fontSize:11.5, color:F.muted }}>raised from each PM's Dashboard — "not mine / wrong allocation"</div>
            </div>
            {(() => {
              const open = pmFlags.filter(f => f.status === 'open');
              if (!open.length) return <div style={{ fontSize:12.5, color:F.green, marginTop:8 }}>Nothing flagged by a PM right now.</div>;
              return (
                <div style={{ marginTop:10, maxHeight:280, overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead><tr>
                      <th style={th}>Unit</th><th style={th}>Job</th><th style={th}>Sup</th>
                      <th style={th}>PM</th><th style={th}>Note</th><th style={th}>Raised</th><th style={th}></th>
                    </tr></thead>
                    <tbody>
                      {open.map((f,i) => (
                        <tr key={f.id||i} style={{ borderTop:`1px solid ${F.line}` }}>
                          <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap' }}>{f.unit}</td>
                          <td style={{ ...td, color:F.cyan, whiteSpace:'nowrap' }}>{f.job}</td>
                          <td style={{ ...td, color:F.muted, whiteSpace:'nowrap' }}>{f.sup}</td>
                          <td style={{ ...td, color:F.muted, whiteSpace:'nowrap' }}>{f.pmName || f.pm}</td>
                          <td style={{ ...td, color:F.text }}>{f.note || <span style={{ color:F.muted }}>—</span>}</td>
                          <td style={{ ...td, color:F.muted, whiteSpace:'nowrap', fontSize:11 }}>{f.at ? new Date(f.at).toLocaleDateString() : ''}</td>
                          <td style={{ ...td, whiteSpace:'nowrap' }}>
                            {/* #PM_EQFLAG_DAYLIGHT_V1 (2026-08-24) — jumps straight to the flagged unit's
                                line on the week/day it was raised (also carries the ⚠️ warning light). */}
                            <button onClick={()=>goToFlagInDaily(f)} title={f.dates && f.dates.length ? `Open Daily Inventory on ${f.dates[0]}, unit ${f.unit}` : 'Open Daily Inventory'}
                              style={{ ...btn, padding:'3px 9px', fontSize:11, marginRight:5, color:F.cyan, borderColor:`${F.cyan}66` }}>→ Daily</button>
                            {/* #PM_EQFLAG_CLEAR_V1 — real action alongside the report-only dismiss */}
                            <button onClick={()=>clearPmFlagAllocation(f)} title={f.dates && f.dates.length ? `Un-assigns ${f.unit} on ${f.dates.join(', ')}` : 'Raised before day-tracking — correct by hand first'}
                              style={{ ...btn, padding:'3px 9px', fontSize:11, marginRight:5, color:F.orange, borderColor:`${F.orange}66` }}>Clear from job</button>
                            <button onClick={()=>resolvePmFlag(f)} style={{ ...btn, padding:'3px 9px', fontSize:11 }}>Mark reviewed</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px' }}>
          <RivetCorners tone="brass" kind="bolt" />
          <div style={{ fontSize:13.5, fontWeight:900, marginBottom:3 }}>Data review</div>
          <div style={{ fontSize:11.5, color:F.muted, marginBottom:12 }}>
            Nothing here blocks anyone from working — the flags sit alongside the data so the team can clean it as they go.
            The rule being checked is <b style={{ color:F.text }}>unit # = year + last 4 of VIN</b>.
          </div>
          <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap' }}>
            {[['Conflicts', flags.filter(f=>f.sev==='bad').length, F.red],
              ['Shape / partial', flags.filter(f=>f.sev==='warn').length, F.amber],
              ['No VIN on file', flags.filter(f=>f.sev==='info').length, F.muted]].map(([l,n,c]) => (
              <div key={l} style={{ background:F.cardHi, border:`1px solid ${c}55`, borderRadius:10, padding:'9px 16px', minWidth:120 }}>
                <div style={{ fontSize:9.5, fontWeight:900, textTransform:'uppercase', letterSpacing:1, color:F.muted }}>{l}</div>
                <div style={{ fontSize:24, fontWeight:900, color:c }}>{n}</div>
              </div>
            ))}
          </div>
          {!flags.length ? <div style={{ fontSize:12.5, color:F.green }}>Nothing flagged — every unit matches its VIN.</div> : (
            <div style={{ maxHeight:'calc(100vh - 380px)', overflowY:'auto', border:`1px solid ${F.line}`, borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr><th style={th}></th><th style={th}>Unit</th><th style={th}>Issue</th><th style={th}>Detail</th></tr></thead>
                <tbody>
                  {flags.map((f,i) => {
                    const c = f.sev==='bad'?F.red:f.sev==='warn'?F.amber:F.muted;
                    return (
                      <tr key={i} style={{ borderTop:`1px solid ${F.line}` }}>
                        <td style={{ ...td, width:8 }}><span style={{ display:'inline-block', width:8, height:8, borderRadius:2, background:c }} /></td>
                        <td style={{ ...td, fontWeight:800, whiteSpace:'nowrap' }}>{f.unit}</td>
                        <td style={{ ...td, color:c, fontWeight:700, whiteSpace:'nowrap' }}>{f.kind}</td>
                        <td style={{ ...td, color:F.muted }}>{f.msg}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </Fragment>
      )}

      {/* ═══════════════ YARD WALK ═══════════════ */}
      {view === 'yard' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:900 }}>Yard Walk — {DAYS[dayIx]} {fmtMD(new Date(dayISO+'T12:00:00'))}</div>
            <div style={{ fontSize:11.5, color:F.muted }}>Tag units A-IDLE or A-SHOP as you walk — writes straight onto today's Daily Inventory.</div>
            <div style={{ flex:1 }} />
            {/* #FLEET_YARDWALK_WEEKNAV_V1 (2026-08-26) — Bill/Jerry: "ability to toggle weeks past for review." */}
            <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),-7)))} title="Previous week" style={{ ...btnS, padding:'6px 10px' }}>‹</button>
            <div style={{ fontSize:11.5, fontWeight:800, minWidth:64, textAlign:'center' }}>Wk {fmtMD(new Date(wkStart+'T12:00:00'))}</div>
            <button onClick={()=>switchWeek(iso(addDays(new Date(wkStart+'T12:00:00'),7)))} title="Next week" style={{ ...btnS, padding:'6px 10px' }}>›</button>
            <div style={{ display:'inline-flex', border:`1px solid ${F.line}`, borderRadius:9, overflow:'hidden' }}>
              {DAYS.map((d,i) => {
                const on = i===dayIx;
                return (
                  <button key={d} onClick={()=>setDayIx(i)}
                    style={{ background:on?F.accent:'transparent', color:on?'#04121C':F.text, border:'none',
                      borderLeft:i?`1px solid ${F.line}`:'none', padding:'10px 14px', fontSize:12.5, fontWeight:800, cursor:'pointer' }}>
                    {d} <span style={{ opacity:.75, fontSize:9.5 }}>{fmtMD(addDays(new Date(wkStart+'T12:00:00'), i))}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* #FLEET_YARDWALK_WEEKLOCK_V1 (2026-08-26) — Bill/Jerry: "toggle weeks past for review...
              but lock after 1 week." Current week + the immediately prior week stay editable; older
              weeks are still browsable (week arrows above) but read-only. */}
          {!walkEditable && (
            <div style={{ fontSize:11.5, color:F.amber, marginBottom:10, fontWeight:700 }}>
              {editable ? 'View only — this week is more than 1 week back and is locked. Use the week arrows above to come forward and edit.' : 'View only.'}
            </div>
          )}

          {/* #FLEET_YARDWALK_FINDINGS_V1 (2026-08-26) — Bill/Jerry: "need a section to add comments
              for findings he discovered" while walking — separate from any one unit's Note, a running
              log for the day. Own storage key, keyed by day, autosaves on blur. */}
          <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
            <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:4 }}>
              Findings — {DAYS[dayIx]} {fmtMD(new Date(dayISO+'T12:00:00'))}
              {findingsSavedAt && <span style={{ textTransform:'none', letterSpacing:0, opacity:.7 }}> · saved {findingsSavedAt}</span>}
            </div>
            <textarea value={walkFindings[dayISO]||''} disabled={!walkEditable}
              onChange={e=>setWalkFindings(f=>({ ...f, [dayISO]: e.target.value }))}
              onBlur={e=>saveFindings(e.target.value)} rows={2}
              placeholder="Anything found on the walk that isn't tied to one unit…"
              style={{ ...inp, resize:'vertical', fontFamily:'inherit' }} />
          </div>

          {/* #FLEET_YARDWALK_LOG_V1 — a real tracked table, not a note: each row is its own action
              item with a resolve toggle. Room on this screen for a second table later (e.g. the New
              Unit On-boarding checklist Bill flagged as a follow-up) — laid out to sit right below. */}
          <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
            <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:8 }}>
              Items to Resolve
              {walkLog.some(x=>!x.resolved) && <span style={{ color:F.amber }}> · {walkLog.filter(x=>!x.resolved).length} open</span>}
              <span style={{ textTransform:'none', letterSpacing:0, fontWeight:400, opacity:.75 }}> — logged here, emailed to you now and to Bob/Bill end of day until it's marked resolved. A unit # that isn't on the Equipment Master goes out as UNIT # FOUND — MUST RESOLVE and closes by itself the day it's added to the Master.</span>
            </div>
            {walkEditable && (
              <div style={{ display:'flex', gap:8, marginBottom:10 }}>
                <input value={walkLogText} onChange={e=>setWalkLogText(e.target.value)}
                  onKeyDown={e=>{ if (e.key==='Enter') { e.preventDefault(); addWalkLogItem(); } }}
                  placeholder="e.g. New unit in the yard, not on Master yet — needs to be added…"
                  disabled={walkLogBusy} style={{ ...inp, flex:1 }} />
                <button onClick={addWalkLogItem} disabled={walkLogBusy || !walkLogText.trim()}
                  style={{ padding:'0 18px', borderRadius:8, fontWeight:800, fontSize:12.5, cursor: walkLogBusy||!walkLogText.trim() ? 'default':'pointer',
                    background: walkLogBusy||!walkLogText.trim() ? F.cardHi : F.gold, color: walkLogBusy||!walkLogText.trim() ? F.muted : '#1A1300',
                    border:`1px solid ${walkLogBusy||!walkLogText.trim() ? F.line : F.gold}` }}>Add</button>
              </div>
            )}
            {!walkLog.length ? (
              <div style={{ fontSize:12, color:F.muted }}>Nothing logged yet.</div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
                  <thead>
                    <tr style={{ textAlign:'left', color:F.muted, fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4 }}>
                      <th style={{ padding:'4px 8px 6px 4px', whiteSpace:'nowrap' }}>Logged</th>
                      <th style={{ padding:'4px 8px 6px', whiteSpace:'nowrap' }}>By</th>
                      <th style={{ padding:'4px 8px 6px', width:'100%' }}>Item</th>
                      <th style={{ padding:'4px 4px 6px 8px', whiteSpace:'nowrap' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...walkLog].sort((a,b)=>(a.resolved===b.resolved?0:a.resolved?1:-1) || (b.at||'').localeCompare(a.at||'')).map(it => (
                      <tr key={it.id} style={{ borderTop:`1px solid ${F.line}`, opacity: it.resolved ? 0.5 : 1 }}>
                        <td style={{ padding:'7px 8px 7px 4px', whiteSpace:'nowrap', color:F.muted }}>{fmtMD(new Date(it.at))}</td>
                        <td style={{ padding:'7px 8px', whiteSpace:'nowrap' }}>{shortWho(it.by)}</td>
                        <td style={{ padding:'7px 8px' }}>{it.kind === 'assign' && <span title="Flagged from the GPS map — fix the name in Geotab and on the Daily Inventory, then mark Resolved" style={{ marginRight:8, fontSize:9.5, fontWeight:900, letterSpacing:.3, color:F.amber, border:`1px solid ${F.amber}`, borderRadius:4, padding:'1px 6px', whiteSpace:'nowrap' }}>GPS MAP · NAME FIX</span>}{/* #FLEET_GPS_ASSIGN_V1 */}<span style={{ textDecoration: it.resolved ? 'line-through' : 'none' }}>{it.text}</span>{!it.resolved && it.kind !== 'assign' && walkUnitsOf(it.text).map(u => { const st = walkMasterState(u); const c = st === 'active' ? F.green : (st === 'missing' || st === 'retired') ? F.red : null; return c ? <span key={u} title={st === 'active' ? `${u} is on the Equipment Master now — mark this resolved` : `${u} has not come through the front door — add it to the Equipment Master`} style={{ marginLeft:8, fontSize:9.5, fontWeight:900, letterSpacing:.3, color:c, border:`1px solid ${c}`, borderRadius:4, padding:'1px 6px', whiteSpace:'nowrap' }}>{st === 'active' ? `${u} ON MASTER` : st === 'retired' ? `${u} RETIRED ON MASTER` : `${u} NOT ON MASTER`}</span> : null; })}{it.resolved && it.autoResolved && <span style={{ marginLeft:8, fontSize:10.5, color:F.muted }}>added to Master {fmtMD(new Date(it.resolvedAt))}</span>}{it.resolved && it.dismissed && <span style={{ marginLeft:8, fontSize:10.5, color:F.muted }}>dismissed by {shortWho(it.resolvedBy)}, not on Master</span>}</td>{/* #YARD_UNIT_FOUND_V1 */}
                        <td style={{ padding:'7px 4px 7px 8px', whiteSpace:'nowrap' }}>
                          {(() => {
                            // #YARD_UNIT_AUTOCLOSE_V1 — a unit still off the Master: no Resolved button for Yard, Dismiss for Owner / MGMT
                            const waiting = !it.resolved && walkOffMaster(it.text).length > 0;
                            if (walkEditable && !(waiting && !masterEdit)) return (
                              <button onClick={()=>resolveWalkLogItem(it.id, !it.resolved)}
                                title={waiting ? 'Only if this is not a Dakota unit or was logged in error — otherwise add it to the Equipment Master and it closes by itself' : ''}
                                style={{ padding:'3px 10px', borderRadius:6, fontSize:10.5, fontWeight:800, cursor:'pointer',
                                  background: (it.resolved || waiting) ? F.cardHi : F.green, color: (it.resolved || waiting) ? F.muted : '#04121C',
                                  border:`1px solid ${(it.resolved || waiting) ? F.line : F.green}` }}>
                                {it.resolved ? 'Reopen' : waiting ? 'Dismiss' : 'Resolved'}
                              </button>
                            );
                            return (
                              <span title={waiting ? 'Closes by itself the day the unit is added to the Equipment Master' : ''} style={{ fontSize:10.5, fontWeight:800, color: it.resolved ? F.green : waiting ? F.red : F.amber }}>
                                {it.resolved ? 'Resolved' : waiting ? 'Waiting on Master' : 'Open'}
                              </span>
                            );
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {walkEditable && !walkUnit && (
            <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', marginBottom:14 }}>
              <RivetCorners tone="silver" kind="bolt" />
              <input value={qWalk} onChange={e=>setQWalk(e.target.value)} placeholder="Search unit # or description…" autoFocus
                style={{ ...inp, padding:'14px 14px', fontSize:15, marginBottom: walkResults.length ? 10 : 0 }} />
              {walkResults.map(r => {
                const k = unitKey(r.unit);
                const cur = (week[dayISO]||{})[k] || {};
                return (
                  <button key={k} onClick={()=>openWalkUnit(r)}
                    style={{ display:'flex', alignItems:'center', gap:10, width:'100%', textAlign:'left',
                      background:F.cardHi, border:`1px solid ${F.line}`, borderRadius:9, padding:'12px 14px',
                      marginBottom:6, cursor:'pointer' }}>
                    <span style={{ fontWeight:900, fontSize:14, minWidth:76 }}>{r.unit}</span>
                    <TelemFlag f={telemFlagFor(tflags, r.unit)} />
                    <span style={{ color:F.muted, fontSize:12, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.desc}</span>
                    {/* #FLEET_YARDWALK_PREFILL_V1 — assigned name for context while tagging */}
                    {cur.assigned && <span style={{ fontSize:10.5, color:F.muted, whiteSpace:'nowrap' }}>{cur.assigned}</span>}
                    {(cur.duc==='A-IDLE'||cur.duc==='A-SHOP') &&
                      <span style={{ fontSize:10, fontWeight:800, color: cur.duc==='A-SHOP'?F.orange:F.muted,
                        border:`1px solid ${cur.duc==='A-SHOP'?F.orange:F.line}`, borderRadius:6, padding:'2px 6px' }}>
                        already {cur.duc==='A-SHOP'?'SHOP':'IDLE'}
                      </span>}
                  </button>
                );
              })}
              {qWalk.trim() && !walkResults.length && <div style={{ fontSize:12, color:F.muted }}>No unit matches "{qWalk}".</div>}
            </div>
          )}

          {walkEditable && walkUnit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'16px', marginBottom:14 }}>
              <div style={{ fontSize:15, fontWeight:900, marginBottom:2 }}>{walkUnit.unit}</div>
              <div style={{ fontSize:12, color:F.muted, marginBottom:2 }}>{walkUnit.desc}</div>
              {walkAssignedName && <div style={{ fontSize:11.5, color:F.muted, marginBottom:12 }}>Assigned: <span style={{ color:F.text, fontWeight:800 }}>{walkAssignedName}</span></div>}

              <div style={{ display:'flex', gap:10, marginBottom:14 }}>
                {[['A-IDLE','IDLE',F.muted],['A-SHOP','SHOP',F.orange]].map(([code,lbl,clr]) => (
                  <button key={code} onClick={()=>setWalkBucket(code)}
                    style={{ flex:1, padding:'16px 10px', fontSize:15, fontWeight:900, borderRadius:10, cursor:'pointer',
                      background: walkBucket===code ? clr : F.cardHi,
                      color: walkBucket===code ? '#04121C' : F.text,
                      border:`2px solid ${walkBucket===code ? clr : F.line}` }}>{lbl}</button>
                ))}
              </div>

              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:4 }}>Location</div>
                <input list="fleet-yard-city-list" value={walkCity} onChange={e=>setWalkCity(e.target.value)}
                  placeholder="Ennis or type a city" style={{ ...inp, padding:'12px 12px', fontSize:14 }} />
                <datalist id="fleet-yard-city-list">
                  {walkCityOptions.map(c => <option key={c} value={c} />)}
                </datalist>
              </div>

              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:4 }}>Note (optional)</div>
                <textarea value={walkNote} onChange={e=>setWalkNote(e.target.value)} placeholder="—" rows={2}
                  style={{ ...inp, padding:'12px 12px', fontSize:14, resize:'vertical', fontFamily:'inherit' }} />
              </div>

              <div style={{ display:'flex', gap:8 }}>
                <button onClick={saveWalkEntry} style={{ ...btn, flex:1, padding:'14px', fontSize:14, background:F.green, color:'#04121C' }}>Save · Next unit</button>
                <button onClick={()=>{ setWalkUnit(null); setQWalk(''); }} style={{ ...btn, padding:'14px 18px' }}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ fontSize:12.5, fontWeight:800, marginBottom:8 }}>
            {DAYS[dayIx]} so far — {walkedToday.length} unit{walkedToday.length===1?'':'s'}
          </div>
          {!walkedToday.length ? (
            <div style={{ fontSize:12, color:F.muted }}>Nothing tagged yet today.</div>
          ) : (
            /* #FLEET_YARDWALK_GRID_V1 (2026-09-23) — Bill: "lots of dead space" in the old one-per-row
               full-width list; switched to a responsive card grid (same auto-fit pattern as the Yard
               home tiles) and added the unit's live Equipment Master description, which this list
               never showed before. */
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(260px,1fr))', gap:8 }}>
              {walkedToday.map(({ r, k, cur }) => (
                <div key={k} style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:9, padding:'10px 14px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <span style={{ fontSize:9.5, fontWeight:800, color: cur.duc==='A-SHOP'?F.orange:F.muted,
                      border:`1px solid ${cur.duc==='A-SHOP'?F.orange:F.line}`, borderRadius:6, padding:'3px 7px', whiteSpace:'nowrap' }}>
                      {cur.duc==='A-SHOP'?'SHOP':'IDLE'}
                    </span>
                    <span style={{ fontWeight:800, whiteSpace:'nowrap' }}>{r.unit}</span>
                    <div style={{ flex:1 }} />
                    {walkEditable && <Fragment>
                      <button onClick={()=>openWalkUnit(r)} style={{ ...btn, padding:'4px 8px', fontSize:10.5 }}>Edit</button>
                      <button onClick={()=>removeWalkEntry(r.unit)} style={{ ...btn, padding:'4px 8px', fontSize:10.5, color:F.muted }}>Remove</button>
                    </Fragment>}
                  </div>
                  <div style={{ color:F.muted, fontSize:11.5, marginBottom:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.desc || '—'}</div>
                  <div style={{ display:'flex', gap:6, alignItems:'center', minWidth:0 }}>
                    <span style={{ color:F.text, fontSize:12, whiteSpace:'nowrap' }}>{cur.city||'—'}</span>
                    {cur.note && <span style={{ color:F.text, fontStyle:'italic', fontSize:11.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', minWidth:0 }}>· {cur.note}</span>}
                    {/* #YARDWALK_BADGE_BR_V1 (2026-10-26) — Bill: GPS / CAM warnings moved to the bottom-right so they never squeeze the unit # */}
                    <TelemFlag f={telemFlagFor(tflags, r.unit)} style={{ marginLeft:'auto', flexShrink:0 }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Fragment>
      )}

      {/* ═══════════════ INSPECTIONS ═══════════════ */}
      {view === 'inspec' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:900 }}>Inspections</div>
            <div style={{ fontSize:11.5, color:F.muted }}>{inspRows.length} unit{inspRows.length===1?'':'s'} tracked</div>
            <input value={qInsp} onChange={e=>setQInsp(e.target.value)} placeholder="Search unit, model, supervisor…"
              style={{ ...inp, width:220 }} />
            <div style={{ flex:1 }} />
            {yardOpsEdit && <button onClick={()=>setInspEdit({ unit:'', type:'', model:'', use:'', sup:'', lastDate:'', nextDate:'', state:'', license:'' })}
              style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add unit</button>}
          </div>

          {inspDueThisMonth.length > 0 && (
            <div style={{ background:`${F.amber}18`, border:`1px solid ${F.amber}66`, borderRadius:10,
              padding:'10px 14px', marginBottom:12, fontSize:12.5, fontWeight:700, color:F.amber }}>
              ⚠ {inspDueThisMonth.length} unit{inspDueThisMonth.length===1?'':'s'} due for inspection this month — {inspDueThisMonth.map(x=>x.unit).join(', ')}
            </div>
          )}

          {/* #FLEET_TOLLINSP_CANDIDATES_V1 — Bill: "Master New or Inducted should auto populate into
              Toll tag & Inspection list to assume these items may need to be included... Jerry has the
              ability to remove if not required but not reappear." Every active Master unit with no
              Inspection record on file shows up here; Jerry adds it (prefills the Add form) or marks it
              not required, which is remembered for good. */}
          {yardOpsEdit && inspCandidates.length > 0 && (
            <div style={{ background:`${F.gold}14`, border:`1px solid ${F.gold}55`, borderRadius:10, padding:'10px 14px', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:12, fontWeight:900, color:F.gold }}>⚑ Suggested from Master — {inspCandidates.length} active unit{inspCandidates.length===1?'':'s'} not on this list</span>
                <span style={{ fontSize:11, color:F.muted }}>Add the ones that need one, or mark "not required" — dismissed units won't be suggested again.</span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {inspCandidates.map(r => (
                  <div key={r.unit} style={{ display:'flex', alignItems:'center', gap:6, background:F.card, border:`1px solid ${F.line}`, borderRadius:8, padding:'4px 6px 4px 10px' }}>
                    <span style={{ fontSize:11.5, fontWeight:800 }}>{r.unit}</span>
                    <span style={{ fontSize:10.5, color:F.muted, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.desc}>{r.desc}</span>
                    <button onClick={()=>setInspEdit({ unit:r.unit, type:'', model:r.desc||'', use:'', sup:'', lastDate:'', nextDate:'', state:'', license:'' })}
                      style={{ ...btnS, padding:'3px 8px', fontSize:10.5, background:F.accent, color:'#04121C' }}>+ Add</button>
                    <button onClick={()=>dismissCandidate('insp', r.unit)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5, color:F.muted }}>Not required</button>
                  </div>
                ))}
              </div>
              {masterEdit && Object.keys(tollInspDismissed?.insp||{}).length > 0 && (
                <div style={{ marginTop:8 }}>
                  <button onClick={()=>setInspDismissedOpen(v=>!v)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5, color:F.muted }}>
                    {inspDismissedOpen ? 'Hide' : 'Show'} dismissed ({Object.keys(tollInspDismissed.insp).length})
                  </button>
                  {inspDismissedOpen && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
                      {Object.keys(tollInspDismissed.insp).sort().map(uk => (
                        <div key={uk} style={{ display:'flex', alignItems:'center', gap:6, background:F.card, border:`1px solid ${F.line}`, borderRadius:8, padding:'4px 6px 4px 10px' }}>
                          <span style={{ fontSize:11, color:F.muted }}>{(activeByKey[uk]&&activeByKey[uk].unit) || uk}</span>
                          <button onClick={()=>undismissCandidate('insp', uk)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5 }}>Un-dismiss</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {inspEdit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:10 }}>{inspEdit._orig ? `Edit ${inspEdit._orig.unit}` : 'Add unit'}</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
                {[['unit','Unit #'],['type','Type'],['model','Model'],['use','Use'],['sup','Supervisor(s)'],
                  ['lastDate','Inspection  MM/DD/YY'],['nextDate','Next Inspection  MM/DD/YY'],['state','State'],['license','License Number']].map(([k,l]) => (
                  <div key={k}>
                    <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}</div>
                    <input value={inspEdit[k]==null?'':inspEdit[k]} onChange={e=>setInspEdit(d=>({ ...d, [k]:e.target.value }))}
                      list={k==='unit' ? 'fleet-master-unit-list' : undefined} style={inp} />
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <button onClick={saveInspItem} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
                <button onClick={()=>setInspEdit(null)} style={btn}>Close without saving</button>
                {inspEdit._orig && masterEdit && <button onClick={()=>deleteInspItem(inspEdit._orig)} style={{ ...btn, color:F.red, borderColor:`${F.red}66` }}>Delete…</button>}
              </div>
            </div>
          )}

          {insp === null ? (
            <div style={{ fontSize:12, color:F.muted }}>Loading…</div>
          ) : !inspRows.length ? (
            <div style={{ fontSize:12, color:F.muted }}>No inspection records yet.</div>
          ) : (
            <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto' }}>
              <RivetCorners tone="brass" kind="rivet" />
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {[['unit','ID','center'],['type','Type','left'],['model','Model','left'],['use','Use','left'],
                    ['sup','Supervisors','left'],['lastDate','Inspection','center'],['nextDate','Next Inspection','center'],
                    ['status','Status','center'],['state','State','center'],['license','License Number','left']].map(([key,label,align]) => (
                    <th key={key} onClick={()=>setInspSort(s => s.key===key ? (s.dir==='asc' ? {key,dir:'desc'} : {key:'',dir:'asc'}) : {key,dir:'asc'})}
                      title={inspSort.key===key ? 'Click again to reverse, once more for the overdue-first default' : `Sort by ${label}`}
                      style={{ ...th, textAlign:align, cursor:'pointer', userSelect:'none',
                        color: inspSort.key===key ? F.accent : F.muted }}>
                      {label}<span style={{ marginLeft:4, fontSize:8.5, opacity: inspSort.key===key ? 1 : .35 }}>
                        {inspSort.key===key ? (inspSort.dir==='asc' ? '▲' : '▼') : '↕'}</span>
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {inspRows.map(({ x, st }, i) => (
                    <tr key={i} onClick={()=>yardOpsEdit && setInspEdit({ ...x, _orig:x })}
                      style={{ borderTop:`1px solid ${F.line}`, cursor:yardOpsEdit?'pointer':'default' }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center' }}>{x.unit}</td>
                      <td style={{ ...td, color:F.muted }}>{x.type||'—'}</td>
                      <td style={td}>{x.model||'—'}</td>
                      <td style={{ ...td, color:F.muted }}>{x.use||'—'}</td>
                      <td style={td}>{x.sup||'—'}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.lastDate||'—'}</td>
                      <td style={{ ...td, textAlign:'center', fontWeight:800 }}>{x.nextDate||'—'}</td>
                      <td style={{ ...td, textAlign:'center', fontWeight:900, color:st.color }}>{st.label}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.state||'—'}</td>
                      <td style={{ ...td, color:F.muted }}>{x.license||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Fragment>
      )}

      {/* ═══════════════ TOLL TAGS ═══════════════ */}
      {view === 'toll' && (
        <Fragment>
          <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
            <div style={{ fontSize:13, fontWeight:900 }}>Toll Tags</div>
            <div style={{ fontSize:11.5, color:F.muted }}>{tollRows.length} tag{tollRows.length===1?'':'s'}</div>
            <input value={qToll} onChange={e=>setQToll(e.target.value)} placeholder="Search unit, tag, plate…"
              style={{ ...inp, width:220 }} />
            <div style={{ flex:1 }} />
            {yardOpsEdit && <button onClick={()=>setTollEdit({ unit:'', tagId:'', plate:'', vehInfo:'', dateIssued:'', status:'Active' })}
              style={{ ...btn, background:F.accent, color:'#04121C' }}>+ Add tag</button>}
          </div>

          {/* #FLEET_TOLLINSP_CANDIDATES_V1 — same "Suggested from Master" pattern as Inspections above. */}
          {yardOpsEdit && tollCandidates.length > 0 && (
            <div style={{ background:`${F.gold}14`, border:`1px solid ${F.gold}55`, borderRadius:10, padding:'10px 14px', marginBottom:12 }}>
              <div style={{ display:'flex', alignItems:'baseline', gap:8, marginBottom:8, flexWrap:'wrap' }}>
                <span style={{ fontSize:12, fontWeight:900, color:F.gold }}>⚑ Suggested from Master — {tollCandidates.length} active unit{tollCandidates.length===1?'':'s'} not on this list</span>
                <span style={{ fontSize:11, color:F.muted }}>Add the ones that need a tag, or mark "not required" — dismissed units won't be suggested again.</span>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {tollCandidates.map(r => (
                  <div key={r.unit} style={{ display:'flex', alignItems:'center', gap:6, background:F.card, border:`1px solid ${F.line}`, borderRadius:8, padding:'4px 6px 4px 10px' }}>
                    <span style={{ fontSize:11.5, fontWeight:800 }}>{r.unit}</span>
                    <span style={{ fontSize:10.5, color:F.muted, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.desc}>{r.desc}</span>
                    <button onClick={()=>setTollEdit({ unit:r.unit, tagId:'', plate:r.licPlate||'', vehInfo:r.desc||'', dateIssued:'', status:'Active' })}
                      style={{ ...btnS, padding:'3px 8px', fontSize:10.5, background:F.accent, color:'#04121C' }}>+ Add</button>
                    <button onClick={()=>dismissCandidate('toll', r.unit)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5, color:F.muted }}>Not required</button>
                  </div>
                ))}
              </div>
              {masterEdit && Object.keys(tollInspDismissed?.toll||{}).length > 0 && (
                <div style={{ marginTop:8 }}>
                  <button onClick={()=>setTollDismissedOpen(v=>!v)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5, color:F.muted }}>
                    {tollDismissedOpen ? 'Hide' : 'Show'} dismissed ({Object.keys(tollInspDismissed.toll).length})
                  </button>
                  {tollDismissedOpen && (
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
                      {Object.keys(tollInspDismissed.toll).sort().map(uk => (
                        <div key={uk} style={{ display:'flex', alignItems:'center', gap:6, background:F.card, border:`1px solid ${F.line}`, borderRadius:8, padding:'4px 6px 4px 10px' }}>
                          <span style={{ fontSize:11, color:F.muted }}>{(activeByKey[uk]&&activeByKey[uk].unit) || uk}</span>
                          <button onClick={()=>undismissCandidate('toll', uk)} style={{ ...btnS, padding:'3px 8px', fontSize:10.5 }}>Un-dismiss</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tollEdit && (
            <div style={{ background:F.card, border:`1px solid ${F.accent}66`, borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
              <div style={{ fontSize:13, fontWeight:900, color:F.accent, marginBottom:10 }}>{tollEdit._orig ? `Edit ${tollEdit._orig.tagId}` : 'Add toll tag'}</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px,1fr))', gap:10 }}>
                {[['unit','Unit'],['tagId','TollTag ID'],['plate','License Plate'],['vehInfo','Vehicle Information'],
                  ['dateIssued','Date Issued'],['status','Vehicle Status'],['dateDeactivated','Date Deactivated']].map(([k,l]) => (   // #TOLL_DEACT_DATE_V1 — label was wrong (dates are MM/DD/YYYY); Deactivated auto-fills on save when status leaves Active
                  <div key={k}>
                    <div style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:F.muted, marginBottom:3 }}>{l}</div>
                    <input value={tollEdit[k]==null?'':tollEdit[k]} onChange={e=>setTollEdit(d=>({ ...d, [k]:e.target.value }))}
                      list={k==='unit' ? 'fleet-master-unit-list' : k==='status' ? 'toll-status-list' : undefined}
                      placeholder={k==='dateDeactivated' ? 'auto-fills when status leaves Active' : undefined} style={inp} />
                  </div>
                ))}
                <datalist id="toll-status-list"><option value="Active" /><option value="Not Active" /></datalist>
              </div>
              {Array.isArray(tollEdit.history) && tollEdit.history.length > 0 && (
                <div style={{ marginTop:10, fontSize:11, color:F.muted }}>
                  <b style={{ color:F.text }}>History:</b>{' '}
                  {tollEdit.history.map((h,i) => <span key={i} style={{ marginRight:12 }}>{h.tagId} · issued {h.dateIssued||'—'} → deactivated {h.dateDeactivated||'—'}</span>)}
                </div>
              )}
              <div style={{ display:'flex', gap:8, marginTop:12 }}>
                <button onClick={saveTollItem} style={{ ...btn, background:F.accent, color:'#04121C' }}>Save</button>
                <button onClick={()=>setTollEdit(null)} style={btn}>Close without saving</button>
                {tollEdit._orig && masterEdit && <button onClick={()=>deleteTollItem(tollEdit._orig)} style={{ ...btn, color:F.red, borderColor:`${F.red}66` }}>Delete…</button>}
              </div>
            </div>
          )}

          {toll === null ? (
            <div style={{ fontSize:12, color:F.muted }}>Loading…</div>
          ) : !tollRows.length ? (
            <div style={{ fontSize:12, color:F.muted }}>No toll tags yet.</div>
          ) : (
            <div style={{ position:'relative', background:F.card, border:`1px solid ${F.line}`, borderRadius:12, overflow:'auto', maxHeight:'calc(100vh - 356px)' }}>
              <RivetCorners tone="silver" kind="rivet" />
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr>
                  {[['unit','Unit','center'],['tagId','TollTag ID','left'],['plate','License Plate','left'],
                    ['vehInfo','Vehicle Information','left'],['desc','Description','left'],
                    ['dateIssued','Date Issued','center'],['dateDeactivated','Deactivated','center'],['status','Vehicle Status','center']].map(([key,label,align]) => (
                    <th key={key} onClick={()=>setTollSort(s => s.key===key ? (s.dir==='asc' ? {key,dir:'desc'} : {key:'',dir:'asc'}) : {key,dir:'asc'})}
                      title={tollSort.key===key ? 'Click again to reverse, once more for sheet order' : `Sort by ${label}`}
                      style={{ ...th, textAlign:align, cursor:'pointer', userSelect:'none', color: tollSort.key===key ? F.accent : F.muted }}>
                      {label}<span style={{ marginLeft:4, fontSize:8.5, opacity: tollSort.key===key ? 1 : .35 }}>
                        {tollSort.key===key ? (tollSort.dir==='asc' ? '▲' : '▼') : '↕'}</span>
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {tollRows.map((x,i) => (
                    <tr key={i} onClick={()=>yardOpsEdit && setTollEdit({ ...x, _orig:x })}
                      style={{ borderTop:`1px solid ${F.line}`, cursor:yardOpsEdit?'pointer':'default' }}>
                      <td style={{ ...td, fontWeight:800, textAlign:'center' }}>{x.unit}</td>
                      <td style={td}>{x.tagId}</td>
                      <td style={{ ...td, color:F.muted }}>{x.plate||'—'}</td>
                      <td style={{ ...td, color:F.muted }}>{x.vehInfo||'—'}</td>
                      <td style={{ ...td, color:F.muted }}>{unitDescByKey[unitKey(x.unit)] || x.vehInfo || '—'}</td>
                      <td style={{ ...td, textAlign:'center', color:F.muted }}>{x.dateIssued||'—'}</td>
                      <td style={{ ...td, textAlign:'center', color: x.dateDeactivated ? F.red : F.muted }} title={Array.isArray(x.history)&&x.history.length?`${x.history.length} earlier stint(s) — open to see history`:''}>{x.dateDeactivated||'—'}{Array.isArray(x.history)&&x.history.length?' ⟲':''}</td>
                      <td style={{ ...td, textAlign:'center', color: /^\s*active\s*$/i.test(String(x.status||''))?F.green:F.muted, fontWeight:800 }}>{x.status||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Fragment>
      )}

      {/* ═══════════════ YARD HOME — Jerry's landing (#FLEET_YARD_SPLIT_V1) ═══════════════ */}
      {view === 'home' && (
        <Fragment>
          {/* #FLEET_YARD_HERO_V1 (2026-09-23) — Bill: greet Jerry on his Yard landing, matching the
              PM Command hero (PM COMMAND / Good morning, {name} / DAKOTA OPERATIONAL PLATFORM ENGINE).
              #FLEET_YARD_HERO_V2 (2026-09-23) — Bill: move the "Yard — date" + Bob/Owners note to the
              right of the hero instead of stacking below it, same flex-split layout as PM Command's
              hero row (name block left, extras flex-end right). */}
          {/* #FLEET_YARD_NASCAR_V1 (2026-09-24) — Bill/Jerry: swap the hearts for a NASCAR theme —
              checkered flags + a nod to the #17 Fastenal Ford (RFK Racing, Jerry's pick). Kept to
              emoji/text + a plain black-white checker stripe rather than redrawing the actual car —
              that livery carries real sponsor trademarks (Fastenal/3M/Goodyear/Fifth Third) that
              aren't ours to reproduce, even in a car-mounted afterthought. Core DUC branding (gold/
              orange) stays put everywhere else in the app; this is Jerry's page only. */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:10 }}>
            <div>
              {/* #YARD_HERO_3ROW_V1 (2026-10-08) — Bill: lose the checked flags, put the #17 Fastenal Ford · RFK Racing
                  button next to his name, and take the left side from 5 rows to 3 so it mirrors the right side. */}
              <div style={{ fontSize:12, fontWeight:800, letterSpacing:2, color:F.gold }}>YARD COMMAND</div>
              <div style={{ fontSize:22, fontWeight:900, marginTop:2 }}>{isYard ? <>{(() => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening'; })()}, Jerry</> : <>Jerry&rsquo;s Yard</>}
                <span style={{ display:'inline-flex', alignItems:'center', gap:6, marginLeft:12, verticalAlign:'middle', background:'#12295E4D', border:'1px solid #2B6CD466', borderRadius:20, padding:'3px 10px 3px 8px', boxShadow:'0 0 14px -4px #2B6CD4' }}>
                  <span style={{ fontSize:12, fontWeight:900, color:'#6FA2FF' }}>🏎️ #17</span>
                  <span style={{ fontSize:10.5, fontWeight:700, color:F.muted }}>Fastenal Ford · RFK Racing</span>
                </span></div>{/* #FLEET_SHOPDASH_V1 — greet Jerry only when it IS Jerry */}
              <div style={{ fontSize:10, fontWeight:800, letterSpacing:2.5, color:F.accent, textTransform:'uppercase', marginTop:4, textShadow:`0 0 12px ${F.accent}66` }}>Dakota Operational Platform Engine</div>
            </div>
            <div style={{ textAlign:'right', maxWidth:280 }}>
              <div style={{ fontSize:13, fontWeight:900, marginBottom:4 }}>Yard — {DAYS[dayIx]} {fmtMD(new Date(dayISO+'T12:00:00'))}</div>
              <div style={{ fontSize:12, color:F.muted }}>Pick where you're working. Bob and the Owners see everything here, plus their own Fleet Manager rollup.</div>
            </div>
          </div>
          {/* Checkered-flag start/finish stripe — pure black/white checker pattern, no team livery. */}
          <div style={{ height:7, borderRadius:4, marginBottom:16, opacity:0.9,
            backgroundImage:'repeating-linear-gradient(45deg, #101214 0, #101214 8px, #E7E9EC 8px, #E7E9EC 16px)' }} />
          {/* #PM_KIT_FLOW_V1 — PM kits Bob sent to build */}
          <PmKitBanner sg={sg} onOpen={() => setView('consign')} live={{ supaUrl, supaKey, getToken }} />
          {/* #FLEET_SHOPDASH_V1 — parts the shop is waiting on. Jerry fills them in Consignment. */}
          {openReqN > 0 && (
            <div onClick={()=>setView('consign')} style={{ cursor:'pointer', background:'#3A2410', border:`1px solid ${F.orange}`, borderRadius:10, padding:'10px 14px', marginBottom:12, fontSize:13, fontWeight:800, color:F.orange }}>
              🔧 {openReqN} parts requisition{openReqN===1?'':'s'} from the shop waiting — open Consignment to fill →
            </div>
          )}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px,1fr))', gap:12 }}>
            {(() => {
              // Toll's own count/due — off the raw list, not the search-filtered `tollRows`, so the
              // tile number doesn't wobble with whatever's typed in the Toll Tags search box.
              const tollAll = toll || [];
              const tollDueCount = tollAll.filter(x => { const s = String(x.status||'').trim(); return s && s !== 'Active'; }).length;
              return [
                ['daily','Daily Inventory', `${dayFilled} of ${active.length} allocated`, F.accent],
                ['yard','Yard Walk', `${walkedToday.length} tagged today`, F.green],
                ['inspec','Inspections', inspDueThisMonth.length ? `${inspDueThisMonth.length} due this month` : 'up to date', inspDueThisMonth.length ? F.amber : F.green],
                ['maint','Maintenance', `${svcBoard.filter(x => x.state === 'overdue').length} overdue · ${svcBoard.filter(x => x.state === 'soon').length} due soon`, svcBoard.some(x => x.state === 'overdue') ? F.red : F.green],   // #FLEET_MAINT_NAV_V1
                ['inshop','In Shop', `${shopRows.length} unit${shopRows.length===1?'':'s'} down${shopRows.some(x => x.hot) ? ` · 🔥 ${shopRows.filter(x => x.hot).length} hot` : ''}`, shopRows.length ? F.orange : F.green],
                ['shophistory','Shop History', `${shopHistory.length} record${shopHistory.length===1?'':'s'}`, F.gold],
                ['toll','Toll Tags', tollDueCount ? `${tollAll.length} tags · ${tollDueCount} due` : `${tollAll.length} tag${tollAll.length===1?'':'s'}`, tollDueCount ? F.red : F.violet],
                ['chem','Chemicals', chemOnHand==null ? 'inventory' : `${money(chemOnHand)} on hand`, F.green],
                ['consign','Consignment', csgnSnap ? (csgnSnap.oh > 0 ? `${money(csgnSnap.oh)} on hand · ${money(csgnSnap.total)} ceiling` : `${money(csgnSnap.total)} ceiling · ${csgnSnap.n} vendor${csgnSnap.n===1?'':'s'}`) : 'Vermeer · Ditch Witch · Filters & Tires · MSC', F.orange],   // #YARD_CONSIGN_V1
                ['tooling','Tooling', 'inventory', F.amber],
                ['board','Digital Board', 'tap units to place them', F.accent],
                ['timeclock','Time Clock', 'clock in/out', F.violet],
                ['fuel','Fuel · Weekly', fuelSnap ? `${money(fuelSnap.total)} · WK${fuelSnap.wk}` : 'gallons & $ by week', F.gold],
                ['master','Equipment Master', canFlagUnit ? `${active.length} units · flag a new one` : `${active.length} units`, F.accent],   // #ASSETLEDGER_V1 (2026-09-16)
              ];
            })().map(([k,l,sub,clr]) => (
              <button key={k} onClick={()=>setView(k)} style={{ textAlign:'left', background:F.card,
                border:`1px solid ${F.line}`, borderTop:`3px solid ${clr}`,
                borderRadius:12, padding:'18px 16px', cursor:'pointer', ...glow(clr) }}>
                <div style={{ fontSize:15, fontWeight:900, color:F.text, marginBottom:6 }}>{l}</div>
                <div style={{ fontSize:11.5, fontWeight:700, color:clr }}>{sub}</div>
              </button>
            ))}
          </div>

          {/* #YARD_STAFFCAL_V1 / #YARD_CELEB_V1 (2026-09-02) — Bill: pipe the Staff Calendar and a
              Celebrations widget onto the Yard hub. Same source data HR already keeps (hr-timeoff /
              hr-employees), duplicated here rather than imported — FleetModule has no shared scope
              with App.js/PMDashboard.js, same pattern every other cross-dashboard read in this
              codebase already uses (#PM_CELEB_V1, #OWNER_STAFFCAL_V1). */}
          {/* #FLEET_OWNER_TILES_SHARED_V1 — Rental/RPO, Idle Equipment, Units Down for Repair (same as Owner Dash). Taps
              only open sections Jerry can see (In Shop); Rental/Idle are read-only lists for him. */}
          <div style={{ marginTop:16 }}>
            <FleetOwnerTiles sg={sg} T={{ card:F.card, line:F.line, text:F.text, muted:F.muted, row:F.line }} locOf={tileLocOf}
              onOpen={(k) => { if (!yardScoped || YARD_VISIBLE.has(k)) setView(k); }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(320px, 1fr))', gap:12, marginTop:16 }}>
            {/* #YARD_MAP_V1 (2026-10-09) — Bill: "Jerry (Yard) would like integrated MAP on his dash and we can park under
                Staff Calendar (splitting this tile) as he could open to expand." The Staff Calendar keeps the top of its
                cell; under it, ONE map with everything on it — Geotab trackers as dots, the factory-feed rigs as
                diamonds — with its key, and "⤢ Open" for the full-screen map (✕ or Esc closes). Read-only. */}
            <div style={{ display:'flex', flexDirection:'column', gap:12, minWidth:0 }}>
              <YardStaffCalendar sg={sg} F={F} />
              <div style={{ flex:'1 1 auto', minHeight:240 }}>
                <FleetMapsMini sg={sg} ss={ss} upn={upn} show="all" tile={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12 }} minHeight={240} />
              </div>
            </div>
            <YardCelebrations sg={sg} F={F} />
            <YardFuelWeekly sg={sg} F={F} onOpen={()=>setView('fuel')} />
            <YardChemSpend sg={sg} F={F} onOpen={()=>setView('chem')} />
          </div>
        </Fragment>
      )}

      {/* ═══════════════ SHOP STAFF — each mechanic's own dash (#FLEET_SHOPDASH_V1) ═══════════════ */}
      {view === 'mydash' && (isShopStaff || masterEdit) && (
        <ShopDash sg={sg} ss={ss} upn={upn} jobs={jobs} manager={masterEdit && !isShopStaff} units={active} svcBoard={svcBoard}
          staffUpn={staffView} onPickStaff={setStaffView} onBack={masterEdit ? () => setView('fleetdash') : null}
          onOpen={(k, whoEmail) => { setEdit(null); setRentalEdit(null); if (k === 'timeclock') setTcFor(masterEdit ? String(whoEmail || '') : ''); setView(k); }}
          onPatchUnit={patchUnitTelem} onServiced={serviceFromBoard} live={{ supaUrl, supaKey, getToken }} telem={telem} downUnits={shopRows} />
      )}

      {/* ═══════════════ FLEET MANAGER — Bob / Owner rollup (#FLEET_YARD_SPLIT_V1) ═══════════════ */}
      {/* #FLEET_BOBDASH_THEME_V1 (2026-08-27) — Bill: "give him a Mechanic, Steel, welding shop tough
          theme." Scoped to this one landing view only — every view Bob clicks into from here (Master,
          Rental, etc.) keeps the app's normal look; nothing else changes visually or functionally. */}
      {view === 'fleetdash' && (() => {
        const SHOP = { bg:'linear-gradient(180deg,#1B1D21 0%,#121316 100%)', panel:'#22252A', panelBorder:'#3A3D42',
          accent:'#FF7A1A', accent2:'#FFB020', steel:'#9AA0A8', text:'#EDEEF0', good:'#4ADE80' };
        // #FLEET_DASH_GLOW_V1 (2026-10-08) — Bill: "add some Glow and softer edges for his Tiles and Overall Dashboard."
        const shopTile = { cursor:'pointer', background:SHOP.panel, border:`1px solid ${SHOP.panelBorder}`,
          borderLeft:`3px solid ${SHOP.accent}`, borderRadius:12, padding:'12px 14px',
          boxShadow:`0 0 20px -8px ${SHOP.accent}99, inset 0 1px 0 #ffffff0A`, transition:'box-shadow .15s, transform .15s' };
        const sectionHead = { fontSize:11, fontWeight:900, letterSpacing:1, textTransform:'uppercase', color:SHOP.accent, marginBottom:8, marginTop:20 };
        // #FLEET_RIG_FILE_V1 — the rig board sits well down the dash; a jump out of a rig file lands at the top of the screen it opens.
        const rigJumpTop = () => setTimeout(() => { try { window.scrollTo(0, 0); const r = document.querySelector('.app-scroll-root'); if (r) r.scrollTop = 0; } catch { /* nothing to scroll */ } }, 60);
        // #FLEET_CAMGPS_V1 — % complete = Installed OR Void (both are "resolved", nothing left to do
        // on that unit) ÷ active fleet. "Combined" = resolved on BOTH, since that's what "fully done"
        // actually means, not an average of the two.
        const camDone = (active||[]).filter(r => String(r.cameraStatus||'').trim()).length;
        const gpsDone = (active||[]).filter(r => String(r.gpsStatus||'').trim()).length;
        const bothDone = (active||[]).filter(r => String(r.cameraStatus||'').trim() && String(r.gpsStatus||'').trim()).length;
        const pct = (n) => active.length ? Math.round((n / active.length) * 100) : 0;
        const weeklyDueCount = hrsRows.filter(x=>x.dueFlag).length;
        const weeklyNeedsLookCount = hrsRows.filter(x=>x.geotabStatus==='no-signal'||x.geotabStatus==='no-device').length;
        const dueByBucket = (bk) => hrsRows.filter(x=>x.bucket===bk && x.dueFlag).length;
        const geotabAutoCount = hrsRows.filter(x=>x.source==='geotab').length;
        const geotabConnected = !!(hrsGeotab && hrsGeotab.devices && hrsGeotab.devices.length);
        const rigBoardOn = (RIG_BOARD_PUBLIC || /^bill\.payne@/i.test(String(upn || '')) || /[?&]rigboard=1/.test(String(window.location.search || '')))
          && hrsRows.some(x => x.bucket === 'rigs' && x.oem);   // #FLEET_DASH_MAPROW_V1 — same gate as the Rig Board
        return (
        <div style={{ background:SHOP.bg, padding: phone ? 11 : 16, borderRadius:16, border:`1px solid ${SHOP.panelBorder}`, boxShadow:'0 0 30px -12px #FF7A1A66' }}>
          {/* #FLEET_MOBILE_HDR_V1 (2026-10-16) — phone: one tight block — "Welcome, Bob · 10/16 · 306 active", a short snapshot line,
              then ⏱ Time cards first and the team chips beside it. Desktop below is unchanged. */}
          {phone && (
            <div style={{ marginBottom:12, paddingBottom:10, borderBottom:`1px solid ${SHOP.panelBorder}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ display:'flex', alignItems:'baseline', gap:8, flexWrap:'wrap', flex:1, minWidth:0 }}>
                  <span style={{ fontSize:18, fontWeight:900, color:SHOP.text }}>{masterEdit ? <>Welcome, <span style={{ color:SHOP.accent }}>Bob</span></> : <>Fleet <span style={{ color:SHOP.accent }}>dash</span></>}</span>
                  <span style={{ fontSize:12, fontWeight:900, color:SHOP.steel }}>{fmtMD(new Date(dayISO+'T12:00:00'))}</span>
                  <span style={{ fontSize:12, fontWeight:900, color:SHOP.accent }}>· {active.length} active</span>
                </div>
                {masterEdit && <button onClick={() => setTcReviewOpen(o => !o)} title="Consolidated time cards for the week"
                  style={{ flexShrink:0, cursor:'pointer', background: tcReviewOpen ? SHOP.accent : 'transparent', color: tcReviewOpen ? '#1B1D21' : SHOP.accent2, border:`1px solid ${SHOP.accent2}`,
                    borderRadius:20, padding:'4px 10px', fontSize:10.5, fontWeight:900, letterSpacing:.3, fontFamily:'inherit', textTransform:'uppercase', whiteSpace:'nowrap' }}>⏱ Time cards {tcReviewOpen ? '▴' : '▾'}</button>}   {/* #FLEET_BOB_TOP_V1 — top right of the banner */}
              </div>
              <div style={{ fontSize:10, color:SHOP.steel, marginTop:2 }}>
                Fleet + Yard{retired.length ? ` · ${retired.length} retired` : ''}{shopSum && shopSum.at ? ` · summary ${new Date(shopSum.at).toLocaleString([], { month:'numeric', day:'numeric', hour:'numeric', minute:'2-digit' })}` : ''}
              </div>
              {masterEdit && (
                <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:8, overflowX:'auto', paddingBottom:2 }}>
                  <div style={{ flexShrink:0 }}><ShopStaffStrip compact sg={sg} onPick={(e) => { setStaffView(e); setView('mydash'); }} /></div>
                </div>
              )}
              {/* #FLEET_BOB_TOP_V1 — shortcut pills sit under the banner and crew on phones */}
              <div style={{ marginTop:10 }}>{renderFleetPills()}</div>
            </div>
          )}
          {/* #FLEET_DASH_V3_ROW2 (2026-09-24) — Bill: "2nd Row to Welcome Bob then remaining pills or
              quick access right indented." Status lights + the rest of the sections, right-aligned. */}
          <div style={{ display: phone ? 'none' : 'flex', alignItems:'center', gap:12, flexWrap:'wrap', marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${SHOP.panelBorder}` }}>
            <div style={{ minWidth:220 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
                <span style={{ fontSize:21, fontWeight:900, color:SHOP.text, letterSpacing:.3 }}>{masterEdit ? <>Welcome, <span style={{ color:SHOP.accent }}>Bob</span></> : <>Fleet Manager <span style={{ color:SHOP.accent }}>dash</span></>}</span>{/* #FLEET_SHOPDASH_V1 — "Welcome, Bob" was shown to every login */}
                <span style={{ fontSize:9, fontWeight:900, letterSpacing:1.5, color:SHOP.accent, textTransform:'uppercase',
                  border:`1px solid ${SHOP.accent}`, borderRadius:3, padding:'2px 6px' }}>Fleet Mgr</span>
                <span style={{ fontSize:13, fontWeight:900, color:SHOP.steel }}>{fmtMD(new Date(dayISO+'T12:00:00'))}</span>
              </div>
              <div style={{ fontSize:11, color:SHOP.steel, marginTop:3 }}>
                Combined snapshot — Fleet + Jerry's Yard.{shopSum && shopSum.at ? ` Fleet summary last published ${new Date(shopSum.at).toLocaleString()}.` : ''}
              </div>
            </div>
            <div style={{ flex:1 }} />
            {/* #FLEET_TEAM_ROW_V1 (2026-10-14) — Bill: "Missing his Teams buttons to be placed in top portion where yard is currently (idea to
                access and view their DASH). Also need a Time Card review." One chip per Fleet-role login (open count) → that person's dash;
                Time Cards opens the consolidated week below. YARD moved up to the top pill row. */}
            {masterEdit && (
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
                <span style={{ fontSize:9.5, fontWeight:900, letterSpacing:1, color:SHOP.steel, textTransform:'uppercase' }}>Team</span>
                <ShopStaffStrip compact sg={sg} onPick={(e) => { setStaffView(e); setView('mydash'); }} />
                <button onClick={() => setTcReviewOpen(o => !o)} title="Consolidated time cards for the week — every Fleet and Yard login, by day"
                  style={{ cursor:'pointer', background: tcReviewOpen ? SHOP.accent : 'transparent', color: tcReviewOpen ? '#1B1D21' : SHOP.accent2, border:`1px solid ${SHOP.accent2}`,
                    borderRadius:20, padding:'5px 12px', fontSize:11, fontWeight:900, letterSpacing:.4, fontFamily:'inherit', textTransform:'uppercase' }}>⏱ Time cards {tcReviewOpen ? '▴' : '▾'}</button>
              </div>
            )}
          </div>

          {masterEdit && tcReviewOpen && (
            <TeamTimeReview sg={sg} live={{ supaUrl, supaKey, getToken }} onClose={() => setTcReviewOpen(false)}
              onOpenCard={(email) => { setTcReviewOpen(false); setTcFor(email); setView('timeclock'); }} />   /* #FLEET_TC_REVIEW_V1 */
          )}
          {yardOpen && (
            <div style={{ background:'#15171A', border:`1px solid ${SHOP.panelBorder}`, borderRadius:12, padding:'12px 14px', marginBottom:14, boxShadow:`0 0 24px -10px ${SHOP.accent}88` }} data-yard-portal="1">
              <div style={{ display:'flex', alignItems:'baseline', gap:10, marginBottom:8 }}>
                <div style={{ ...sectionHead, margin:0 }}>Yard</div>
                <span style={{ fontSize:11, color:SHOP.steel }}>Jerry&rsquo;s yard screens — a red number needs attention. Tap to drill in.</span>
                <span style={{ flex:1 }} />
                <button onClick={() => setYardOpen(false)} style={{ cursor:'pointer', background:'transparent', border:`1px solid ${SHOP.panelBorder}`, borderRadius:20, padding:'3px 10px', color:SHOP.steel, fontSize:10.5, fontWeight:800, fontFamily:'inherit' }}>Close ▴</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(165px, 1fr))', gap:8 }}>
                {yardItems.map(([k, label, sub, n, alertTxt]) => { const ac = k === 'daily' ? '#FFB020' : '#F87171'; return (
                  <div key={k} onClick={() => { setYardOpen(false); setView(k); }} title={n ? alertTxt : sub}
                    style={{ ...shopTile, padding:'9px 11px', position:'relative', borderLeft:`3px solid ${n ? ac : SHOP.accent}`, ...(n ? { boxShadow:`0 0 18px -8px ${ac}CC, inset 0 1px 0 #ffffff0A` } : {}) }}>
                    {n > 0 && <span style={{ position:'absolute', top:7, right:8, background:ac, color: k === 'daily' ? '#1B1D21' : '#fff', borderRadius:99, padding:'1px 7px', fontSize:11, fontWeight:900 }}>{n}</span>}
                    <div style={{ fontSize:12.5, fontWeight:900, color:SHOP.text, paddingRight: n ? 30 : 0 }}>{label}</div>
                    <div style={{ fontSize:10.5, fontWeight:700, color: n ? ac : SHOP.steel, marginTop:3 }}>{n ? alertTxt : sub}</div>
                  </div>
                ); })}
              </div>
            </div>
          )}

          {/* #FLEET_DASH_V3_SUMMARY (2026-09-24) — Bill: "Summary tile can also rest on top as this
              needs focus." Camera/GPS tile moved into the Geotab telematics tile below. */}
          <div style={{ ...sectionHead, marginTop:0 }}>Fleet summary</div>
          {/* #FLEET_SUMMARY_PHONE_V1 (2026-10-16) — phone: 3 across (smaller numbers) and the red / hot tiles first, so problems lead. */}
          <div style={{ display:'grid', gridTemplateColumns: phone ? 'repeat(3, minmax(0,1fr))' : 'repeat(auto-fit, minmax(150px,1fr))', gap: phone ? 7 : 10, marginBottom:6 }}>
            {(phone ? (arr => [...arr.filter(t => t[4] || t[2] === '#F87171'), ...arr.filter(t => !(t[4] || t[2] === '#F87171'))]) : (arr => arr))([
              ['Active fleet', active.length, SHOP.accent2, 'master'],
              ['Idle 30+ days', (shopSum && shopSum.idleCount) || 0, SHOP.steel, 'idle'],
              ['Rental / RPO', rentalRecords.length, SHOP.accent2, 'rental'],
              ['Uptime', (shopSum && shopSum.uptimePct!=null) ? `${shopSum.uptimePct}%` : '—', SHOP.good, 'inshop'],
              ['Service overdue', svcBoard.filter(x => x.state === 'overdue').length, svcBoard.some(x => x.state === 'overdue') ? '#F87171' : SHOP.good, 'maint'],   // #FLEET_MAINT_NAV_V1
              // #FLEET_TELEM_HOT_V1 — Bill: "This is a HOT one and should land up here in these tiles."
              ['Cam / GPS down', telem.loaded ? telem.res.onMasterN : '…', telem.res.onMasterN ? '#F87171' : SHOP.good, 'telem',
                telem.res.onMasterN > 0, telem.loaded ? `${telem.res.gpsN} GPS · ${telem.res.camN} camera` : ''],
              ["Today's allocation", `${dayFilled}/${active.length}`, unassigned ? SHOP.accent : SHOP.good, 'daily'],
              // #FLEET_ADDGATE_V1 — new units Ashlie fast-added, still missing VIN/Lic Plate/etc.
              ['Needs info', (active||[]).filter(r=>r.needsFollowUp).length, (active||[]).some(r=>r.needsFollowUp) ? SHOP.accent : SHOP.steel, 'master'],
            ]).map(([lbl,val,clr,jump,hot,sub]) => (
              <div key={lbl} onClick={()=>setView(jump)} style={{ ...(hot ? { ...shopTile, borderColor:'#F8717188', borderLeft:'3px solid #F87171', boxShadow:'0 0 22px -6px #F87171CC, inset 0 1px 0 #ffffff0A' } : shopTile), ...(phone ? { padding:'8px 9px', minWidth:0 } : {}) }}>
                <div style={{ fontSize: phone ? 8.5 : 9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color: hot ? '#F87171' : SHOP.steel, marginBottom: phone ? 3 : 6, whiteSpace: phone ? 'nowrap' : undefined, overflow:'hidden', textOverflow:'ellipsis' }}>{hot ? '● ' : ''}{lbl}</div>
                <div style={{ fontSize: phone ? 19 : 24, fontWeight:900, color:clr }}>{val}</div>
                {sub && !phone ? <div style={{ fontSize:10, color:SHOP.steel, marginTop:2 }}>{sub}</div> : null}
              </div>
            ))}
          </div>

          {/* #FLEET_OWNER_TILES_SHARED_V1 — same three lists the Owner Dash shows */}
          <div style={sectionHead}>{masterEdit ? 'Idle · Down for repair · Work orders' : 'Rental · Idle · Down for repair'}</div>
          <FleetOwnerTiles sg={sg} T={{ card:SHOP.panel, line:SHOP.panelBorder, row:'#2E3136', text:SHOP.text, muted:SHOP.steel, radius:12,
            glowRental:SHOP.accent2, glowIdle:SHOP.accent2, glowDown:SHOP.accent }} onOpen={setView} locOf={tileLocOf} hideRental={masterEdit}
            extraTile={masterEdit ? ((skin) => <BobWoQueue skin={skin} sg={sg} ss={ss} upn={upn} units={active} jobs={jobs} svcBoard={svcBoard} live={{ supaUrl, supaKey, getToken }} />) : undefined} />   {/* #FLEET_WO_TILE_V1 — Bob's work-order queue is the 4th tile here */}

          {/* #FLEET_TRAVEL_V1 (2026-10-27) — mechanics' hotel / per diem requests; Bob approves, approval drops into HR's Hotel Requests */}
          {masterEdit && (
            <FleetTravelApprovals sg={sg} ss={ss} upn={upn} live={{ supaUrl, supaKey, getToken }}
              approverName={String(upn || '').toLowerCase().startsWith('bob.') ? 'Bob' : String(upn || '').split('@')[0].split('.')[0].replace(/^./, c => c.toUpperCase())}
              T={{ panel:SHOP.panel, line:SHOP.panelBorder, text:SHOP.text, steel:SHOP.steel, good:SHOP.good, bad:'#F87171', blue:'#38BDF8', accent:SHOP.accent, accent2:'#FFB020' }} />
          )}

          {/* #MECH_TEAM_SCORECARD_V1 (2026-10-16) — Bill: team numbers so Bob can recognize his people (incentive later). */}
          {masterEdit && (<>
            <div style={sectionHead}>Team scorecard</div>
            <TeamScorecard sg={sg} onPick={(e) => { setStaffView(e); setView('mydash'); }} />
          </>)}

          {/* #FLEET_DASH_MAPROW_V1 (2026-10-12) — Bill: Bob "would like to have his Maps together like Aaron (Owner Dash) these
              small windows seem popular. The data to the right of his map view is awesome so … one row on top of his page below
              Rental - Idle - Down for repair." One strip: the four rig numbers · the iron map + GPS map side by side (same small
              windows as the Owner dash, ⤢ Open for full screen) · the four GPS state tiles. The Rig Board below keeps just its cards;
              the big GPS map block is gone (the small one opens the same full-screen map with towns + unit finder). */}
          <div style={sectionHead}>Rigs · iron &amp; GPS maps · trackers</div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'stretch', marginBottom:6 }}>
            {rigBoardOn && (
              <div style={{ flex:'1 1 320px', minWidth:0, display:'flex' }}>
                <FleetRigBoard part="stats" rows={hrsRows.filter(x => x.bucket === 'rigs')} blob={hrsOem} theme={SHOP} tile={shopTile} />
              </div>
            )}
            <div style={{ flex:'2.4 1 520px', minWidth:0 }}>
              <FleetMapsMini sg={sg} ss={ss} upn={upn} show="both" tile={{ ...shopTile, cursor:'default' }} minHeight={232} />
            </div>
            <div style={{ flex:'1 1 260px', minWidth:0 }}>
              <FleetGpsMap statsOnly blob={locNow} units={active} theme={SHOP} tile={shopTile} sg={sg} ss={ss} upn={upn} />
            </div>
          </div>

          {/* #FLEET_DASH_V3_TELEMATICS (2026-09-24) — Bill: "Move Telematics feeds to TOP. GEO TAB tile
              needs to have % complete on Cameras & GPS Units within tile." Geotab leads (wider), the
              install bars live inside it (#FLEET_CAMGPS_V1 math unchanged: Installed OR Void ÷ active),
              and tapping the bars jumps to Master filtered to what's still pending. */}
          {/* #FLEET_SHOPDASH_V1 — who is in the shop and what each has open. Tap a name to open that person's dash. */}
          {masterEdit && (
            <Fragment>
              <div style={sectionHead}>Safety reports · Fleet staff</div>
              {/* #FLEET_TEAM_ROW_V1 — the Shop Staff chips live in the welcome row now (Team) */}
              {/* #FLEET_WO_BOBQUEUE_V1 — Bob's work-order queue moved up into the Rental · Idle · Down for repair row (#FLEET_WO_TILE_V1) */}
              {/* #SAFETY_FLEET_COPY_V1 — SAFETY NAT reports his people filed; he is also copied on the email. */}
              <FleetSafetyInbox sg={sg} ss={ss} upn={upn} live={{ supaUrl, supaKey, getToken }} />
            </Fragment>
          )}
          <div style={sectionHead}>Telematics feeds</div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:6 }}>
            <div onClick={()=>setView('weeklyinv')} style={{ ...shopTile, flex:'1.6 1 320px' }}>   {/* #FLEET_WO_LAYOUT_V1 — was 2 1 380px; squeezed so Link-Belt + Rental/RPO fit the row */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <div style={{ fontSize:14, fontWeight:900, color:SHOP.text }}>Geotab</div>
                <span style={{ fontSize:8.5, fontWeight:900, letterSpacing:.6, color: geotabConnected ? SHOP.good : SHOP.accent2,
                  border:`1px solid ${geotabConnected ? SHOP.good : SHOP.accent2}`, borderRadius:3, padding:'2px 5px' }}>{geotabConnected ? 'LIVE' : 'NO DEVICES YET'}</span>
              </div>
              <div style={{ fontSize:11.5, color:SHOP.text }}>
                🛰 <span style={{ color:SHOP.good, fontWeight:800 }}>{geotabAutoCount}</span> auto-filled this week
                {' · '}⚠ <span style={{ color: weeklyNeedsLookCount ? SHOP.accent2 : SHOP.steel, fontWeight:800 }}>{weeklyNeedsLookCount}</span> need a look
              </div>
              <div onClick={(e)=>{ e.stopPropagation(); setCamGpsPendingOnly(true); setView('master'); }}
                title="Open Equipment Master filtered to units still pending a camera or GPS"
                style={{ marginTop:10, paddingTop:10, borderTop:`1px solid ${SHOP.panelBorder}`, display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px,1fr))', gap:10 }}>
                {[['Cameras', camDone], ['GPS units', gpsDone], ['Both', bothDone]].map(([lbl, n]) => {
                  const v = pct(n);
                  const c = v === 100 ? SHOP.good : (lbl === 'Both' ? SHOP.accent : SHOP.accent2);
                  return (
                    <div key={lbl}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
                        <span style={{ fontSize:9.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.4, color:SHOP.steel }}>{lbl}</span>
                        <span style={{ fontSize:16, fontWeight:900, color:c }}>{v}%</span>
                      </div>
                      <div style={{ height:6, background:'#15171A', borderRadius:4, overflow:'hidden' }}>
                        <div style={{ width:`${v}%`, height:'100%', background:c }} />
                      </div>
                      <div style={{ fontSize:9.5, color:SHOP.steel, marginTop:3 }}>{n} of {active.length}</div>
                    </div>
                  );
                })}
              </div>
              <div style={{ fontSize:10, color:SHOP.steel, marginTop:8 }}>Trucks, 18-Wheelers, Cleaners, VACs, Excavators, Mixing Systems</div>
            </div>
            {/* #FLEET_OEM_HOURS_V1 (2026-10-08) — factory feeds. WAITING ON KEY until the maker issues one,
                LIVE after the first good pull. Counts are Master units matched by Serial / VIN, not
                everything on the maker's account (which still lists sold machines). */}
            {OEM_BRANDS.map(b => {
              const st = oemBrandStatus(hrsOem, b.key);
              const live = st.state === 'live', err = st.state === 'error';
              const c = live ? SHOP.good : (err ? '#F87171' : SHOP.accent2);
              const matched = hrsRows.filter(x => x.oemProvider === b.key);
              const silent = matched.filter(x => x.oemSilent).length;
              const filled = hrsRows.filter(x => x.source === 'oem' && x.oemBrand === b.key).length;
              return (
                <div key={b.key} onClick={()=>{ setHrsBucketFilter(b.bucket); setView('weeklyinv'); }}
                  style={live ? { ...shopTile, flex:'1 1 150px' }
                    : { ...shopTile, flex:'1 1 150px', borderStyle:'dashed', borderColor:`${SHOP.steel}88`, borderLeft:`3px dashed ${SHOP.steel}` }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6, gap:6 }}>
                    <div style={{ fontSize:13, fontWeight:900, color:SHOP.text }}>{b.label}</div>
                    <span style={{ fontSize:8.5, fontWeight:900, letterSpacing:.6, color:c, border:`1px solid ${c}`, borderRadius:3, padding:'2px 5px', whiteSpace:'nowrap' }}>{live ? 'LIVE' : (err ? 'PULL FAILED' : 'WAITING ON KEY')}</span>
                  </div>
                  {live ? (
                    <div style={{ fontSize:11.5, color:SHOP.text }}>
                      🛰 <span style={{ color:SHOP.good, fontWeight:800 }}>{matched.length - silent}</span> of {matched.length} {b.what} reporting
                      {silent ? <Fragment>{' · '}⚠ <span style={{ color:SHOP.accent2, fontWeight:800 }}>{silent}</span> silent</Fragment> : null}
                      <div style={{ fontSize:10, color:SHOP.steel, marginTop:4 }}>{filled} auto-filled this week{st.at ? ` · pulled ${fmtMD(new Date(st.at))}` : ''}</div>
                    </div>
                  ) : (
                    <div style={{ fontSize:11, color:SHOP.steel }}>
                      {err ? `Last pull did not go through (${st.message || 'no detail'}). Hours stay manual until it does.`
                        : `The feed is built and waiting on ${b.label}'s API key. ${b.what === 'rigs' ? 'Rig' : 'Excavator'} hours stay manual in Weekly Inventory until then.`}
                    </div>
                  )}
                </div>
              );
            })}
            {/* #FLEET_WO_LAYOUT_V1 (2026-10-14) — Bill: "Kobelco (missing Link-Belt)". Not in the OEM feed yet: Bane sent RemoteCARE website
                logins 10/13 (not the AEMP API). Static until the API credentials come; then it joins OEM_BRANDS like the others. */}
            {(() => {
              const lb = active.filter(r => /link\s*-?\s*belt|\bLB\s?\d{3}\b/i.test(`${r.desc || ''} ${r.make || ''}`));
              return (
                <div onClick={()=>setView('weeklyinv')} style={{ ...shopTile, flex:'1 1 150px', borderStyle:'dashed', borderColor:`${SHOP.steel}88`, borderLeft:`3px dashed ${SHOP.steel}` }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6, gap:6 }}>
                    <div style={{ fontSize:13, fontWeight:900, color:SHOP.text }}>Link-Belt</div>
                    <span style={{ fontSize:8.5, fontWeight:900, letterSpacing:.6, color:SHOP.accent2, border:`1px solid ${SHOP.accent2}`, borderRadius:3, padding:'2px 5px', whiteSpace:'nowrap' }}>WAITING ON API</span>
                  </div>
                  <div style={{ fontSize:11, color:SHOP.steel }}>RemoteCARE portal login came 10/13; the API is requested from Bane. {lb.length ? `${lb.length} Link-Belt unit${lb.length === 1 ? '' : 's'} on the Master — ` : ''}hours stay manual, GPS shows through Geotab.</div>
                </div>
              );
            })()}
            {masterEdit && (
              <div style={{ flex:'1.4 1 280px', minWidth:0, display:'flex', flexDirection:'column' }}>
                <FleetOwnerTiles only="rental" maxH={214} sg={sg} T={{ card:SHOP.panel, line:SHOP.panelBorder, row:'#2E3136', text:SHOP.text, muted:SHOP.steel, radius:12, glowRental:SHOP.accent2 }} onOpen={setView} />
              </div>
            )}
          </div>

          {/* #GEO_WEEKLY_LISTS_V1 (2026-10-21) — Bill #5: "Fish out Idle alert for trucks only to pipe into Fleet" — over 25 idle events last week */}
          <div style={sectionHead}>Truck idling · last week</div>
          <GeoIdleTrucksPanel sg={sg} units={active} theme={{ card:SHOP.panel, line:SHOP.panelBorder, text:SHOP.text, muted:SHOP.steel, amber:SHOP.accent2 }} />

          {/* #FLEET_RIG_BOARD_V1 (2026-10-08) — Bill: "What can we pull into Bobs Dashboard for Vermeer? Codes are
              important or inop issues, idle, inner rod etc. … RIGs are the Backbone of our company." One card per rig
              a factory feed reports on. PREVIEW: only Bill's login (or ?rigboard=1 on the address) sees it until
              RIG_BOARD_PUBLIC is flipped to true, so Bob's first look is the finished one. */}
          {rigBoardOn && (
            <FleetRigBoard part="cards" rows={/* #FLEET_EXCAV_TILES_V1 (2026-10-22) — fed excavators (Kobelco) get cards too; unfed ones stay off */ hrsRows.filter(x => x.bucket === 'rigs' || (x.bucket === 'excavators' && x.oem))} blob={hrsOem} theme={SHOP} tile={shopTile} head={sectionHead}
              preview={!RIG_BOARD_PUBLIC} onOpenWeekly={() => { setHrsBucketFilter('rigs'); setView('weeklyinv'); rigJumpTop(); }}
              sg={sg} shopRows={shopRows} shopHistory={shopHistory}
              onOpenMaint={(u) => { setMaintFocus(u); setView('maint'); rigJumpTop(); }}
              onOpenInShop={(u) => { setView('inshop'); if (!u) { rigJumpTop(); return; }
                setTimeout(() => { const el = document.querySelector(`[data-shop-unit="${u}"]`); if (el) el.scrollIntoView({ behavior:'smooth', block:'center' }); }, 150); }} />
          )}

          {/* #FLEET_GPS_MAP_V1 (2026-10-09) — Bill: "Want to pipe in a GEO tab map to Fleet … Separate from iron map we
              have in place." Every Geotab tracker (trucks, VACs, trailers) on its own map, under the Rig Board, which
              keeps its own iron map. Everyone who can open this dash sees it. */}
          {/* #FLEET_DASH_MAPROW_V1 — the big GPS map block moved up into the one-row strip (small map + state tiles). */}

          {/* #FLEET_DASH_PO_V1 — Bob's PO log/form (App.js passes it as poPanel). #FLEET_DASH_V3 moves it
              under Summary + Telematics; the PO Log pill in the top row scrolls straight here. */}
          {poPanel && (
            <Fragment>
              <div id="fleet-dash-po" style={{ ...sectionHead, scrollMarginTop:12 }}>PO Log</div>
              <div style={{ marginBottom:6 }}>{poPanel}</div>
            </Fragment>
          )}

          <div style={sectionHead}>Vital — Weekly Inventory &amp; In Shop</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(340px,1fr))', gap:12, marginBottom:6 }}>
            <div style={{ ...shopTile, padding:'16px 16px', cursor:'default' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontSize:13.5, fontWeight:900, color:SHOP.text }}>Weekly Inventory</div>
                <span style={{ fontSize:10.5, color:SHOP.steel }}>{hrsWeekOf(hrsWkStart).label}</span>
              </div>
              <div style={{ display:'flex', gap:12, flexWrap:'wrap', fontSize:12, marginBottom:10 }}>
                <span style={{ color:SHOP.accent, fontWeight:800 }}>🔧 {weeklyDueCount} service due</span>
                <span style={{ color: weeklyNeedsLookCount ? SHOP.accent2 : SHOP.steel, fontWeight:800 }}>⚠ {weeklyNeedsLookCount} need a look</span>
              </div>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12 }}>
                {[['rigs','RIGS'],['trucks','Trucks'],['18wheeler','18 Wheeler'],['excavators','Excavators']].map(([bk,lbl]) => (
                  <span key={bk} onClick={()=>{ setHrsBucketFilter(bk); setView('weeklyinv'); }}
                    style={{ fontSize:10.5, fontWeight:800, padding:'4px 8px', borderRadius:4, cursor:'pointer',
                      background:SHOP.bg, border:`1px solid ${dueByBucket(bk) ? SHOP.accent+'88' : SHOP.panelBorder}`,
                      color: dueByBucket(bk) ? SHOP.accent2 : SHOP.steel }}>
                    {lbl} · {dueByBucket(bk)} due
                  </span>
                ))}
              </div>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                <button onClick={()=>setView('weeklyinv')} style={{ ...btnS, color:SHOP.accent2, borderColor:`${SHOP.accent2}55` }}>Open Weekly Inventory →</button>
              </div>
              <div style={{ fontSize:10, color:SHOP.steel, marginTop:8 }}>Vacs, Reclaimers, Mixing Systems, Forklifts &amp; Trailers stay one tap away inside Weekly Inventory.</div>
            </div>

            <div style={{ ...shopTile, padding:'16px 16px', cursor:'default' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                <div style={{ fontSize:13.5, fontWeight:900, color:SHOP.text }}>In Shop</div>
                <span style={{ fontSize:10.5, color:SHOP.steel }}>{shopRows.length} unit{shopRows.length===1?'':'s'} down{shopRows.some(x => x.hot) && <b style={{ color:'#E7B73B' }}> · 🔥 {shopRows.filter(x => x.hot).length} hot</b>}</span>
              </div>
              {!shopRows.length ? (
                <div style={{ fontSize:12, color:SHOP.good, fontWeight:700 }}>✓ Nothing in the shop right now.</div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:6, marginBottom:10 }}>
                  {shopRows.slice(0,5).map((x,i) => (
                    <div key={i} onClick={()=>setView('inshop')}
                      style={{ display:'flex', alignItems:'center', gap:8, fontSize:11.5, cursor:'pointer', borderTop: i? `1px solid ${SHOP.panelBorder}` : 'none', paddingTop: i?6:0 }}>
                      <span style={{ fontWeight:900, color: x.hot ? '#E7B73B' : SHOP.text, minWidth:56 }}>{x.hot ? '🔥 ' : ''}{x.unit}</span>
                      <span style={{ color:SHOP.steel, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.desc||'—'}</span>
                      <span style={{ color: x.days>=30?'#FF6B6B':SHOP.accent2, fontWeight:800 }}>{x.days}d</span>
                      <span style={{ color:SHOP.steel, minWidth:90, textAlign:'right', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{x.mechanic || 'unassigned'}</span>
                    </div>
                  ))}
                  {shopRows.length > 5 && <div style={{ fontSize:10.5, color:SHOP.steel }}>+{shopRows.length-5} more</div>}
                </div>
              )}
              <button onClick={()=>setView('inshop')} style={{ ...btnS, color:SHOP.accent2, borderColor:`${SHOP.accent2}55` }}>Open In Shop →</button>
              <div style={{ fontSize:10, color:SHOP.steel, marginTop:8 }}>Mechanic / shop column covers OEM-shop placements too — units aren't always down at the Dakota shop.</div>
            </div>
          </div>

          {walkFindings[dayISO] && (
            <div style={{ background:'#3A2A16', border:`1px solid ${SHOP.accent}66`, borderRadius:6, padding:'10px 14px', fontSize:12.5, marginTop:16 }}>
              <b style={{ color:SHOP.accent2 }}>Today's Yard Walk findings:</b> <span style={{ color:SHOP.text }}>{walkFindings[dayISO]}</span>
            </div>
          )}

          <ShopLaborWeekly sg={sg} ss={ss} SHOP={SHOP} canEdit={masterEdit} />

          {/* #FLEET_DASH_ROW3_V1 (2026-10-06) — Bill: bottom of Bob's dash, a row of three: Hotel booking, Staff Calendar
              (view only) and a spare tile. The PO panel above (PMDashboard) portals the row into this div. */}
          {poPanel && (
            <Fragment>
              <div style={sectionHead}>Hotel / Travel · Staff Calendar</div>
              <div id="fleet-dash-travel" />
            </Fragment>
          )}

          {/* #FLEET_DASH_PILLS_V1 — Open-a-Section grid moved to header pills. */}
        </div>
        );
      })()}

      {/* ═══════════════ CHEMICALS (#FLEET_YARD_SPLIT_V1) ═══════════════ */}
      {/* #FLEET_CHEM_INVENTORY_V1 (2026-08-27) — Chemicals is now a real Core Inventory (product/item#/UofM/cost/on-hand), not the flat list Tooling still uses below */}
      {view === 'chem' && <ChemicalsYardDashboard sg={sg} ss={ss} canEdit={yardOpsEdit} canDelete={masterEdit} F={F} jobIndex={jobIndex} liveJobs={liveJobs} sups={sups} userRole={userRole} upn={upn} sup2duc={sup2duc} />}

      {view === 'timeclock' && <YardTimeClock sg={sg} ss={ss} F={F} userRole={userRole} upn={upn} canApprove={masterEdit} jobIndex={jobIndex} selfClock={String(userRole||'') === 'Fleet'} forEmail={masterEdit ? tcFor : ''} />}

      {/* ═════════════════════ FUEL WEEKLY (#FLEET_FUELWEEKLY_V1) ═════════════════════ */}
      {view === 'fuel' && (
        <Fragment>
          <FuelWeeklyPage sg={sg} ss={ss} F={F} canEdit={yardOpsEdit} />
          <FuelCardsPanel sg={sg} ss={ss} F={F} canEdit={masterEdit} master={active} upn={upn} />   {/* #FLEET_FUEL_CARDS_V1 */}
        </Fragment>
      )}

      {/* ═══════════════ TOOLING (#FLEET_YARD_SPLIT_V1) ═══════════════ */}
      {/* ═══════════════ CONSIGNMENT (#YARD_CONSIGN_V1) ═══════════════ */}
      {/* #FLEET_SHOPDASH_V1 — parts requisitions from the shop. Fill takes the qty off On hand and logs the pull to the
          unit (same record the Pull button writes); the list below is re-read after each fill so it never saves stale. */}
      {/* #PM_KIT_FLOW_V1 — Jerry's PM kit desk: kits to build, pull ticket, pull & stage, kit lists */}
      {view === 'consign' && <PmKitDesk sg={sg} ss={ss} upn={upn} F={F} canEdit={yardOpsEdit} units={active || []} live={{ supaUrl, supaKey, getToken }} onConsignChanged={() => setReqKick(k => k + 1)} />}
      {view === 'consign' && (
        <div style={{ background:'#1B1D21', border:`1px solid ${F.line}`, borderRadius:12, padding:'12px 14px', marginBottom:14 }}>
          <PartsReqQueue sg={sg} ss={ss} upn={upn} canFill={yardOpsEdit} title="Parts requisitions from the shop"
            onConsignChanged={() => setReqKick(k => k + 1)} emptyText="Nothing waiting — the shop has no open parts requisitions." />
        </div>
      )}
      {view === 'consign' && <YardConsignment key={'csgn-'+reqKick} sg={sg} ss={ss} F={F} canEdit={yardOpsEdit} canDelete={masterEdit} userRole={userRole} upn={upn} units={(active||[]).map(r => r.unit)} />}

      {view === 'tooling' && <YardInventoryList title="Tooling Inventory" storageKey="fleet-yard-tooling" sg={sg} ss={ss} canEdit={editable} canDelete={masterEdit} F={F} />}

      {/* ═══════════════ DIGITAL BOARD (#FLEET_YARD_BOARD_V2, #FLEET_BOARD_V3) ═══════════════ */}
      {view === 'board' && (() => {
        const BD = boardDark
          ? { bg:F.bg, panel:F.card, panelHi:F.cardHi, line:F.line, text:F.text, muted:F.muted, sel:F.accent }
          : { bg:'#F4F6F8', panel:'#FFFFFF', panelHi:'#EEF2F6', line:'#D8DEE6', text:'#1A2330', muted:'#6B7686', sel:'#0EA5E9' };
        if (board === null) {
          // Still loading (or the load failed) — no controls rendered at all, so nothing can be
          // tapped that would save an empty board over real data. See #FLEET_BOARD_LOADGUARD_V1.
          return (
            <div style={{ background:BD.bg, border:`1px solid ${BD.line}`, borderRadius:12, padding:'40px 20px', textAlign:'center', color:BD.muted, fontSize:12.5 }}>
              {boardLoadFailed
                ? "Couldn't load the Digital Board — check your connection and refresh the page. Nothing here will save until it loads correctly, so your existing board is safe either way."
                : 'Loading the Digital Board…'}
            </div>
          );
        }
        const base = board;
        const positions = base.positions || {};
        const labels = base.labels || {};
        const supervisors = base.supervisors || [];
        const staff = base.staff || [];
        const laneMeta = {};
        BOARD_GROUPS.forEach(([id,label]) => { laneMeta[`grp:${id}`] = { label, kind:'group' }; });
        supervisors.forEach(name => { laneMeta[`sup:${name}`] = { label:name, kind:'sup' }; });
        const lanes = orderedLaneIds(base).map(id => ({ id, ...laneMeta[id] })).filter(l => l.label);

        // one tile pool covers both equipment (keyed by unit #) and staff (keyed staff:Name) so both
        // share the same Unplaced tray, tap-to-place, and per-tile note behavior
        const catColors = base.catColors || {};
        const catKeyOf = (cat) => String(cat||'').trim().toUpperCase();
        const equipTiles = (active||[]).map(r => ({ tkey:unitKey(r.unit), kind:'equip', label:r.unit, sub:`${r.desc||r.cat||''}`, cat:r.cat||'', color: catColors[catKeyOf(r.cat)] || catColor(r.cat) }));
        const staffTiles = staff.map(name => ({ tkey:`staff:${name}`, kind:'tag', label:name, sub:'Name tag', color:'#94A3B8' }));
        const allTiles = [...equipTiles, ...staffTiles];
        const tileOrderSaved = base.tileOrder || {};
        // #FLEET_BOARD_V4 (2026-09-18) — Bill: pick tile colors by unit type, manually reorder tiles
        // within a lane (buttons, not drag — same reasoning as everywhere else on this board), and a
        // one-tap sort-by-year action. `tileOrderSaved[laneId]` is a saved sequence of tkeys; anything
        // not in it yet (a brand-new tile, or a lane never manually ordered) just falls in at the end.
        const orderTiles = (laneId, tiles) => {
          const saved = (tileOrderSaved[laneId]||[]).filter(k => tiles.some(t=>t.tkey===k));
          const missing = tiles.filter(t => !saved.includes(t.tkey)).map(t=>t.tkey);
          const seq = [...saved, ...missing];
          return seq.map(k => tiles.find(t=>t.tkey===k)).filter(Boolean);
        };
        const tilesFor = (laneId) => orderTiles(laneId, allTiles.filter(t => positions[t.tkey] === laneId));
        const unplaced = orderTiles('__unplaced__', allTiles.filter(t => !positions[t.tkey]));
        const moveTile = (laneId, tkey, dir, tilesInLane) => {
          const b = board || boardDefault();
          const order = orderTiles(laneId, tilesInLane).map(t=>t.tkey);
          const i = order.indexOf(tkey); const j = i + dir;
          if (i<0 || j<0 || j>=order.length) return;
          const next = order.slice(); const tmp=next[i]; next[i]=next[j]; next[j]=tmp;
          saveBoardData({ ...b, tileOrder: { ...(b.tileOrder||{}), [laneId]: next } });
        };
        const cycleCatColor = (cat) => {
          const b = board || boardDefault();
          const key = catKeyOf(cat);
          const cur = (b.catColors||{})[key];
          const i = cur ? BOARD_PALETTE.indexOf(cur) : -1;
          const nextColor = BOARD_PALETTE[(i+1) % BOARD_PALETTE.length];
          saveBoardData({ ...b, catColors: { ...(b.catColors||{}), [key]: nextColor } });
        };
        const resetCatColor = (cat) => {
          const b = board || boardDefault();
          const key = catKeyOf(cat);
          const nextColors = { ...(b.catColors||{}) }; delete nextColors[key];
          saveBoardData({ ...b, catColors: nextColors });
        };
        const sortAllByYear = () => {
          const b = board || boardDefault();
          const nextOrder = {};
          const laneIdsAll = ['__unplaced__', ...orderedLaneIds(b)];
          laneIdsAll.forEach(laneId => {
            const tiles = laneId === '__unplaced__' ? allTiles.filter(t => !positions[t.tkey]) : allTiles.filter(t => positions[t.tkey] === laneId);
            const sorted = tiles.slice().sort((x,y) => {
              const xy = x.kind==='equip' ? (unitYear(x.label)||'99') : '99';
              const yy = y.kind==='equip' ? (unitYear(y.label)||'99') : '99';
              if (xy !== yy) return xy.localeCompare(yy);
              return String(x.label).localeCompare(String(y.label));
            });
            nextOrder[laneId] = sorted.map(t=>t.tkey);
          });
          saveBoardData({ ...b, tileOrder: nextOrder });
        };
        const distinctCats = [...new Set(equipTiles.map(t=>t.cat).filter(Boolean))].sort((a,b)=>a.localeCompare(b));

        // #FLEET_BOARD_SUPJOB_V7 (2026-09-18) — Bill: "Where does NUMBER or CITY to pipe into
        // selected tile? Having to free write how will it sync to SUP Name?" Answer: it doesn't need
        // free-typing — a Name Tag's job/city is looked up automatically from supJobByName, matched
        // on the tag's own label against Daily's "assigned" name (same lookup the Rig Fleet by Type
        // panel uses). If the label doesn't match anyone in this week's Daily (e.g. Bill's own "BILL"
        // tag, or a non-person label like "10X15"), nothing shows — that's expected, not a bug.
        const tagJobMatch = (t) => {
          if (t.kind !== 'tag') return null;
          const m = supJobByName[normPersonName(t.label)];
          if (!m) return null;
          return { duc: stripCP(m.duc), job: jobIndex[stripCP(m.duc)] || null };
        };
        const Tile = (t, laneId, tilesInLane) => {
          const isSel = boardSel === t.tkey;
          const note = labels[t.tkey];
          const idx = (laneId && tilesInLane) ? tilesInLane.indexOf(t) : -1;
          const canReorder = yardOpsEdit && laneId && tilesInLane && tilesInLane.length > 1;
          const tj = tagJobMatch(t);
          return (
            <div key={t.tkey} style={{ position:'relative' }}>
              <div onClick={()=>{ if(!yardOpsEdit) return; setBoardSel(isSel ? '' : t.tkey); }}
                title={`${t.label} — ${t.sub}`}
                style={{ cursor:yardOpsEdit?'pointer':'default',
                  background: t.kind==='tag' ? (boardDark ? '#F1F5F9' : '#FFFFFF') : t.color,
                  color: t.kind==='tag' ? '#0F172A' : '#0A0E14',
                  border: t.kind==='tag' ? '2px solid #94A3B8' : 'none',
                  textTransform: t.kind==='tag' ? 'uppercase' : 'none',
                  fontWeight:900, fontSize: t.kind==='tag' ? 12 : 11, borderRadius:6,
                  padding: note ? `5px 16px 4px ${canReorder?16:8}px` : `6px 16px 6px ${canReorder?16:8}px`,
                  minWidth: t.kind==='tag' ? 90 : 60, maxWidth: t.kind==='tag' ? 140 : 104, textAlign:'center',
                  boxShadow: isSel ? `0 0 0 3px ${BD.sel}` : '0 1px 2px rgba(0,0,0,.3)', opacity: isSel?0.85:1 }}>
                <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.label}</div>
                {tj && (
                  <div style={{ fontSize:8.5, fontWeight:800, marginTop:2, textTransform:'none', color:'#334155', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {tj.duc}{tj.job && tj.job.city ? ` · ${tj.job.city}` : ''}
                  </div>
                )}
                {note && <div style={{ fontSize:9, fontWeight:700, opacity:.75, marginTop:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{note}</div>}
              </div>
              {yardOpsEdit && (
                <span onClick={(e)=>{ e.stopPropagation(); setTileNote(t.tkey, note); }} title="Add/edit a short note under this tile"
                  style={{ position:'absolute', top:2, right:3, fontSize:10, cursor:'pointer', color:'#0A0E14', opacity:.6 }}>✎</span>
              )}
              {yardOpsEdit && t.kind==='tag' && (
                <span onClick={(e)=>{ e.stopPropagation(); removeNameTag(t.label); }} title="Delete this name tag"
                  style={{ position:'absolute', bottom:2, right:3, fontSize:11, fontWeight:900, cursor:'pointer', color:F.red, opacity:.8 }}>×</span>
              )}
              {canReorder && idx > 0 && (
                <span onClick={(e)=>{ e.stopPropagation(); moveTile(laneId, t.tkey, -1, tilesInLane); }} title="Move up in this lane"
                  style={{ position:'absolute', top:2, left:3, fontSize:10, cursor:'pointer', color:'#0A0E14', opacity:.6 }}>▲</span>
              )}
              {canReorder && idx >= 0 && idx < tilesInLane.length - 1 && (
                <span onClick={(e)=>{ e.stopPropagation(); moveTile(laneId, t.tkey, 1, tilesInLane); }} title="Move down in this lane"
                  style={{ position:'absolute', bottom:2, left:3, fontSize:10, cursor:'pointer', color:'#0A0E14', opacity:.6 }}>▼</span>
              )}
            </div>
          );
        };
        return (
          <div style={{ background:BD.bg, border:`1px solid ${BD.line}`, borderRadius:12, padding:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4, flexWrap:'wrap' }}>
              <div style={{ fontSize:14, fontWeight:900, color:BD.text }}>Digital Board</div>
              <div style={{ flex:1 }} />
              {board && board.updatedAt && <span style={{ fontSize:10.5, color:BD.muted }}>updated {board.updatedAt}</span>}
              <button onClick={()=>setBoardDark(d=>!d)} style={{ background:BD.panel, border:`1px solid ${BD.line}`, color:BD.text,
                borderRadius:8, padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer' }}>{boardDark?'☀ Light':'🌙 Dark'}</button>
            </div>
            <div style={{ fontSize:11.5, color:BD.muted, marginBottom:14, lineHeight:1.5 }}>
              Mirrors the physical board at the shop — tap a tile, then tap where it actually sits. Tap
              the ✎ on a tile for a short note underneath it. Use ▲ ▼ on a tile to reorder it within its
              lane, or ↕ Sort by Year to line every lane up oldest-unit-first in one tap. + Add name tag
              drops a bold paper-style tag into whichever lane you tap next — stack a person's gear
              right under their tag (order = grouping) to mimic a crew inside one lane, the way the real
              board nests names under a rig type. Use ◀ ▶ on a lane to reorder it to match the real
              board's layout, and 🎨 Unit colors to pick each unit type's own color instead of the
              auto-assigned one. This doesn't change anything else in the
              system; it's Jerry's own read of what's really out there, so drift between the two is easy
              to spot. ↻ Load Monday's Daily seeds the board from that week's Assigned names as a
              starting checkpoint — it's a weekly flash, not a live sync, so use it Monday and then place
              by hand the rest of the week. Below the lanes, <b style={{ color:BD.text }}>Rig Fleet by
              Type</b> is a separate, read-only view — one tile per rig, labeled with its size,
              Supervisor, and this week's Daily job; there's nothing to place there by hand.
            </div>

            {yardOpsEdit && (
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:14, flexWrap:'wrap' }}>
                <button onClick={syncFromDaily} style={{ background:BD.panel, border:`1px solid ${BD.sel}66`, color:BD.text,
                  borderRadius:8, padding:'6px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>↻ Load Monday's Daily</button>
                <button onClick={addSupervisor} style={{ background:BD.panel, border:`1px solid ${BD.line}`, color:BD.text,
                  borderRadius:8, padding:'6px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>+ Add supervisor</button>
                <button onClick={addNameTag} style={{ background:BD.panel, border:`1px solid ${BD.line}`, color:BD.text,
                  borderRadius:8, padding:'6px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>+ Add name tag</button>
                <button onClick={sortAllByYear} title="Reorders every lane's tiles oldest-year-first, using each unit's YY prefix" style={{ background:BD.panel, border:`1px solid ${BD.line}`, color:BD.text,
                  borderRadius:8, padding:'6px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>↕ Sort by Year</button>
                <button onClick={()=>setBoardColorsOpen(o=>!o)} style={{ background: boardColorsOpen ? `${BD.sel}22` : BD.panel, border:`1px solid ${BD.line}`, color:BD.text,
                  borderRadius:8, padding:'6px 12px', fontSize:11.5, fontWeight:700, cursor:'pointer' }}>🎨 Unit colors</button>
                {boardSel && <span style={{ fontSize:11.5, color:BD.sel, fontWeight:800 }}>Selected {boardSel.replace(/^staff:/,'')} — tap a lane below to place it</span>}
              </div>
            )}

            {yardOpsEdit && boardColorsOpen && (
              <div style={{ marginBottom:16, background:BD.panelHi, border:`1px dashed ${BD.line}`, borderRadius:10, padding:10 }}>
                <div style={{ fontSize:10.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.5, color:BD.muted, marginBottom:8 }}>
                  Unit-type colors — tap a swatch to cycle its color, × to go back to auto
                </div>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                  {distinctCats.map(cat => {
                    const key = catKeyOf(cat);
                    const isCustom = !!catColors[key];
                    const color = catColors[key] || catColor(cat);
                    return (
                      <div key={cat} onClick={()=>cycleCatColor(cat)} title="Tap to cycle color"
                        style={{ cursor:'pointer', display:'flex', alignItems:'center', gap:6, background:color, color:'#0A0E14',
                          borderRadius:6, padding:'4px 8px', fontSize:10.5, fontWeight:800 }}>
                        {cat}
                        {isCustom && <span onClick={(e)=>{ e.stopPropagation(); resetCatColor(cat); }} title="Reset to auto color" style={{ opacity:.7 }}>×</span>}
                      </div>
                    );
                  })}
                  {!distinctCats.length && <span style={{ fontSize:11, color:BD.muted, fontStyle:'italic' }}>No unit categories found yet.</span>}
                </div>
              </div>
            )}

            {unplaced.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:10.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.5, color:BD.muted, marginBottom:6 }}>
                  Unplaced · {unplaced.length}
                </div>
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', background:BD.panelHi, border:`1px dashed ${BD.line}`, borderRadius:10, padding:10 }}>
                  {unplaced.map(t => Tile(t, '__unplaced__', unplaced))}
                </div>
              </div>
            )}

            <div style={{ display:'flex', gap:12, flexWrap:'wrap', paddingBottom:8 }}>
              {lanes.map((lane, i) => {
                const laneTiles = tilesFor(lane.id);
                return (
                <div key={lane.id} style={{ flex:'0 0 180px', background:BD.panel, border:`1px solid ${BD.line}`, borderRadius:10, padding:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap:4 }}>
                    <div onClick={()=>{ if (boardSel) placeTile(boardSel, lane.id); }}
                      style={{ cursor: boardSel ? 'pointer' : 'default', background: boardSel ? `${BD.sel}22` : 'transparent',
                        borderRadius:6, padding:'4px 6px', flex:1, minWidth:0 }}>
                      <span style={{ fontSize:11.5, fontWeight:900, color:BD.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>{lane.label}</span>
                    </div>
                    {yardOpsEdit && (
                      <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                        <span onClick={()=>moveLane(lane.id,-1)} title="Move lane left"
                          style={{ color:BD.muted, fontSize:11, cursor: i>0 ? 'pointer':'default', opacity: i>0?1:.3 }}>◀</span>
                        <span onClick={()=>moveLane(lane.id,1)} title="Move lane right"
                          style={{ color:BD.muted, fontSize:11, cursor: i<lanes.length-1 ? 'pointer':'default', opacity: i<lanes.length-1?1:.3 }}>▶</span>
                        {lane.kind==='sup' && (
                          <span onClick={()=>renameSupervisor(lane.label)} title="Rename supervisor" style={{ color:BD.muted, fontSize:11, cursor:'pointer' }}>✎</span>
                        )}
                        {lane.kind==='sup' && (
                          <span onClick={()=>removeSupervisor(lane.label)} title="Remove supervisor" style={{ color:F.red, fontSize:12, cursor:'pointer' }}>×</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    {laneTiles.map(t => Tile(t, lane.id, laneTiles))}
                    {!laneTiles.length && <div style={{ fontSize:10.5, color:BD.muted, fontStyle:'italic' }}>empty</div>}
                  </div>
                </div>
                );
              })}
              {!supervisors.length && (
                <div style={{ flex:'0 0 200px', display:'flex', alignItems:'center', color:BD.muted, fontSize:11.5, fontStyle:'italic' }}>
                  {yardOpsEdit ? 'Add a supervisor to start building lanes.' : 'No supervisors added yet.'}
                </div>
              )}
            </div>

            {/* #FLEET_BOARD_RIGTYPE_V1 (2026-08-27), reworked #FLEET_BOARD_V4 (2026-09-18) — Rig
                Fleet by Type: a separate, read-only view below the tap-to-place lanes above. Bill,
                2026-09-18: "EACH RIG should have its Only Tile Section not grouped by type" — each rig
                is now its own standalone tile (unit, Supervisor, this week's job); the type line above
                a row is a plain scanning label, not a shared box grouping several rigs together like
                the original pass did. Auto-populated from this week's Daily (Monday, corrected by
                Tuesday) — nothing here is placed by hand or saved anywhere. */}
            <div style={{ marginTop:20, borderTop:`1px solid ${BD.line}`, paddingTop:14 }}>
              <div style={{ fontSize:12.5, fontWeight:900, color:BD.text, marginBottom:2 }}>Rig Fleet by Type</div>
              <div style={{ fontSize:10.5, color:BD.muted, marginBottom:10, lineHeight:1.4 }}>
                Auto-populated from {rigWeek ? rigWeek.monISO : "this week's Monday"}'s Daily, corrected
                by {rigWeek ? rigWeek.tueISO : 'Tuesday'} if PMs have updated it — read-only, not tap-to-place.
                A rig with no Daily entry yet this week shows under "Unassigned." Each rig gets its own
                tile below — the type line above a row is just a scanning label, not a shared box.
              </div>
              {rigSections === null && <div style={{ fontSize:11, color:BD.muted, fontStyle:'italic' }}>Loading…</div>}
              {rigSections !== null && rigSections.length === 0 && (
                <div style={{ fontSize:11, color:BD.muted, fontStyle:'italic' }}>No Drill rigs found on the active Equipment Master.</div>
              )}
              {rigSections !== null && rigSections.length > 0 && rigSections.map(sec => (
                <div key={sec.type} style={{ marginBottom:14 }}>
                  <div style={{ fontSize:10.5, fontWeight:800, color:BD.muted, textTransform:'uppercase', letterSpacing:.5, marginBottom:6 }}>
                    {sec.type} · {sec.units.length} rig{sec.units.length===1?'':'s'}
                  </div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                    {sec.units.slice().sort((a,b) => (a.assigned||'zzz').localeCompare(b.assigned||'zzz') || a.unit.localeCompare(b.unit)).map(u => (
                      <div key={u.tkey}
                        title={u.job ? `${u.job.cust||''} · ${u.job.city||''}${u.job.stat?' · '+u.job.stat:''}` : (u.duc || 'no Daily entry this week')}
                        style={{ flex:'0 0 150px', background:BD.panel, border:`1px solid ${BD.line}`, borderRadius:8, padding:'8px 10px' }}>
                        <div style={{ fontWeight:900, color:BD.text, fontSize:11.5 }}>{u.unit}</div>
                        <div style={{ fontSize:10, fontWeight:800, color: u.assigned ? BD.sel : BD.muted, marginTop:2 }}>{u.assigned || 'Unassigned'}</div>
                        {u.duc && u.duc.startsWith('A-') ? (
                          <div style={{ color:BD.muted, fontSize:10, marginTop:2 }}>{u.duc}</div>
                        ) : u.duc ? (
                          <>
                            <div style={{ color:BD.text, fontSize:10, fontWeight:800, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{stripCP(u.duc)}</div>
                            <div style={{ color:BD.muted, fontSize:9.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{(u.job && u.job.city) || '\u2014'}</div>
                          </>
                        ) : (
                          <div style={{ color:BD.muted, fontSize:10, marginTop:2, fontStyle:'italic' }}>no Daily entry</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {staff.length > 0 && yardOpsEdit && (
              <div style={{ marginTop:14, display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:10.5, fontWeight:800, textTransform:'uppercase', letterSpacing:.5, color:BD.muted }}>Name tags</span>
                {staff.map(name => (
                  <span key={name} style={{ fontSize:11, color:BD.muted, display:'flex', alignItems:'center', gap:4 }}>
                    {name} <span onClick={()=>removeNameTag(name)} title="Remove this name tag" style={{ color:F.red, cursor:'pointer' }}>×</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══════════════ IMPORT ═══════════════ */}
      {view === 'import' && (
        <div style={{ background:F.card, border:`1px solid ${F.line}`, borderRadius:12, padding:'14px 16px', maxWidth:940 }}>
          <div style={{ fontSize:13.5, fontWeight:900, marginBottom:3 }}>Import the master</div>
          <div style={{ fontSize:12, color:F.muted, marginBottom:12, lineHeight:1.5 }}>
            Two ways in. Either <b style={{ color:F.text }}>choose the fleet-master.json file</b>, or
            <b style={{ color:F.text }}> select the EQP tab in Excel, copy, and paste below</b> — first row must be the header.
            Importing <b style={{ color:F.amber }}>replaces the active master</b>; the retired list is left alone.
          </div>
          {masterEdit ? (
            <Fragment>
              <input type="file" accept=".json,.txt,.csv,.tsv"
                onChange={e => { const f=e.target.files && e.target.files[0]; if (!f) return;
                  const rd=new FileReader(); rd.onload=()=>{ setImp(String(rd.result||'')); setImpMsg(`Loaded ${f.name} — press Import.`); }; rd.readAsText(f); e.target.value=''; }}
                style={{ ...inp, padding:'8px', marginBottom:10 }} />
              <textarea value={imp} onChange={e=>setImp(e.target.value)} rows={10}
                placeholder={'Paste JSON, or TSV copied from Excel:\n\nDUC Unit\tCAT DESCRIPTION\tUNIT DESCRIPTION\tSERIAL / VIN\tDUC Day\n25-1221\t18Wheeler\t25-1221 Western Star\t5KKMBPDV5SPVR1221\t250'}
                style={{ ...inp, fontFamily:'Consolas, monospace', fontSize:11.5, resize:'vertical' }} />
              <div style={{ display:'flex', gap:8, marginTop:10, alignItems:'center', flexWrap:'wrap' }}>
                <button onClick={runImport} disabled={!imp.trim()} style={{ ...btn, background: imp.trim()?F.accent:F.cardHi, color: imp.trim()?'#04121C':F.muted }}>Import</button>
                <button onClick={()=>{ setImp(''); setImpMsg(''); }} style={btn}>Clear</button>
                {impMsg && <span style={{ fontSize:12, fontWeight:700, color: impMsg.startsWith('Could not')?F.red:F.green }}>{impMsg}</span>}
              </div>
            </Fragment>
          ) : <div style={{ fontSize:12.5, color:F.amber }}>Import is Owner / Fleet Manager only.</div>}
        </div>
      )}
    </div>
  );
}


   


   


       


           


   // ceiling value
const consignOnHandValue = (list) => (list||[]).reduce((s,r) => s + (_csgnHas(r.onHand) ? _csgnNum(r.onHand) * _csgnNum(r.unit) : 0), 0);   


   


   


